import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../api/axiosConfig';
import { addLiveSummaryHolding, getLiveSummary, getLiveSummaryChangeEvents, getLiveSummaryHoldings, getLiveSummaryIssues, prefetchLiveSummaryIssues, getLiveSummaryThemeLeaders, removeLiveSummaryHolding, searchLiveSummaryHoldings, LiveSummaryAlert, LiveSummaryApiResponse, LiveSummaryAttentionBanner, LiveSummaryChangeEvent, LiveSummaryHolding, LiveSummaryHoldingSearchItem, LiveSummaryIssuesPage, LiveSummaryMarketState, LiveSummaryLiveIssueSignal, LiveSummaryP1Shadow, LiveSummaryMetricGuide, LiveSummaryOpportunity, LiveSummarySector, LiveSummarySectorTopStock, LiveSummaryThemeFlow, LiveSummaryThemeStock, LiveSummarySourceStatus, LiveSummaryTone, LiveSummaryTab } from '../../api/liveSummaryApi';
import { trackUxEvent } from '../../analytics/analytics';
import {
  getTickerRiskEvidence,
  getTickerRiskEvidenceVerification,
  type RiskEvidenceChainVerification,
  type RiskEvidenceProof,
} from '../../api/riskEvidenceApi';
import {
  riskAssessmentPriority,
  isRiskAssessmentAttention,
  normalizeActionDecision,
  normalizeRiskAssessment,
  userFacingEntryStage,
  type NormalizedActionDecision,
  type NormalizedRiskAssessment,
} from './liveSummaryRiskAssessment';
import {
  buildPortfolioPosition,
  buildPositionSnapshot,
  buildReductionSimulation,
  POSITION_COPY,
} from './liveSummaryPositionSimulation';
import { summarizeWhyMovingRow, type WhyMovingRow, type WhyMovingRowInput } from './liveSummaryWhyMoving';
import LiveSummaryIntelligencePanel, { type IntelBridge } from './LiveSummaryIntelligencePanel';
import ShortCoveringEvidenceCard from './ShortCoveringEvidenceCard';
import LiveSummaryDecisionBriefCard, { type DecisionBriefV1, type OptionResearch } from './LiveSummaryDecisionBriefCard';
import DailyIssueStockExplorer from './DailyIssueStockExplorer';
import WebPushSettingsPanel from './WebPushSettingsPanel';
import {
  ensureTrackedStockWebPush,
  requestTrackingWebPushPermission,
  type TrackedStockWebPushStatus,
} from './webPushClient';
import MyScannerPanel from './MyScannerPanel';
import { getScannerDashboard, type ScannerDashboard, type ScannerMatch } from '../../api/scannerApi';
import {
  getPortfolioThresholdSettings,
  updatePortfolioThresholdSettings,
} from '../../api/portfolioAlerts';
import { getOffhoursMarketProxy, type OffhoursProxyResponse } from '../../api/offhoursMarketProxyApi';
import OffhoursMarketProxyCard from './OffhoursMarketProxyCard';
import './offhoursMarketProxy.css';
import './liveSummaryDock.css';
import './liveSummaryValueJudgment.css';
import './liveSummaryDataOnly.css';
import './liveSummaryFocus.css';
import './liveSummarySurfaceV4.css';
import './liveSummaryReliability.css';
import UserFeedbackLauncher, { requestUserFeedback } from '../feedback/UserFeedbackLauncher';

type Tone = LiveSummaryTone;
type SourceStatus = LiveSummarySourceStatus;

type TrackingTarget = {
  ticker: string;
  name: string;
};
type NormalizedMarketState = {
  label: string;
  score: number | null;
  tone: Tone;
  phase: string;
  baseDate: string;
  generatedAt: string;
  topEtfName: string;
  topEtfTicker: string;
  topSectorCode: string;
  topScore: number | null;
  comment: string;
};

type NormalizedLiveIssueSignalStock = {
  ticker: string;
  name: string;
  relation: string;
};

type NormalizedLiveIssueScenario = {
  eventValue: string;
  eventValueLabel: string;
  capitalDirection: string;
  capitalDirectionLabel: string;
  capitalStyle: string;
  capitalStyleLabel: string;
  smartMoneyPotential: string;
  smartMoneyPotentialLabel: string;
  marketDelivery: string;
  marketDeliveryLabel: string;
  entryEnvironment: string;
  entryEnvironmentLabel: string;
  investmentEnvironment: string;
  investmentEnvironmentLabel: string;
  catalystState: string;
  catalystStateLabel: string;
  horizon: string;
  horizonLabel: string;
  marketDirection: string;
  marketDirectionLabel: string;
  marketHeadwindBuckets: string[];
  marketTailwindBuckets: string[];
  preEventState: string;
  preEventStateLabel: string;
  preEventReturn15m: number | null;
  preEventReturn30m: number | null;
  marketReactionBaseTime: string;
  eventOccurredAt: string;
  eventFirstSeenAt: string;
  currentStateLabel: string;
  thesis: string;
  reasons: string[];
  checkpoints: string[];
  dataStatus: string;
};

type NormalizedLiveIssueCapitalScenario = {
  marketDirection: string;
  marketDirectionLabel: string;
  capitalFocus: string[];
  capitalFocusLabel: string;
  pressureFocus: string[];
  pressureFocusLabel: string;
  summary: string;
  dataStatus: string;
};

type NormalizedLiveIssueBriefAggregate = {
  state: string;
  direction: string;
  summary: string;
  evidenceLevel: string;
};

type NormalizedSectorImpact = {
  sectorName: string;
  industryCode: string;
  marketType: string;
  changeRate: number | null;
  mappedTickerCount: number;
  realtimeTickerCount: number;
  upTickerCount: number;
  downTickerCount: number;
  breadthPct: number | null;
  realtimeCoveragePct: number | null;
  realtimeTradingValue: number | null;
  latestRealtimeEventAt: string;
  dataStatus: string;
  classification: string;
  label: string;
  groupType: string;
  marketGroupName: string;
  background: string;
  reason: string;
};

type MovementAttributionCause = {
  type: string;
  title: string;
  summary: string;
};

type MovementAttribution = {
  direction: 'UP' | 'DOWN' | 'UNKNOWN' | string;
  status: 'CONFIRMED' | 'SUPPORTED' | 'MIXED' | 'UNKNOWN' | string;
  label: string;
  primaryCause: MovementAttributionCause | null;
  secondaryCauses: Array<{ type: string; summary: string }>;
  stockSpecificEvent: { status: string; summary: string } | null;
  nextCheck: string;
  causeConfirmed: boolean;
};

type NormalizedLiveIssueBriefSector = {
  name: string;
  direction: string;
  summary: string;
  evidenceLevel: string;
  sectorImpact: NormalizedSectorImpact | null;
};

type NormalizedLiveIssueBriefStock = {
  ticker: string;
  name: string;
  relation: string;
  direction: string;
  summary: string;
  evidenceLevel: string;
};

type NormalizedLiveIssueBriefCatalyst = {
  materialId: string;
  headline: string;
  scope: string;
  direction: string;
  summary: string;
  evidenceLevel: string;
  phase: string;
  lifecycleState: string;
  resolutionType: string;
};

type NormalizedLiveIssueBrief = {
  overview: string;
  policy: NormalizedLiveIssueBriefAggregate;
  market: NormalizedLiveIssueBriefAggregate;
  sectors: NormalizedLiveIssueBriefSector[];
  stocks: NormalizedLiveIssueBriefStock[];
  catalysts: {
    born: NormalizedLiveIssueBriefCatalyst[];
    strengthened: NormalizedLiveIssueBriefCatalyst[];
    weakened: NormalizedLiveIssueBriefCatalyst[];
    realized: NormalizedLiveIssueBriefCatalyst[];
    resolved: NormalizedLiveIssueBriefCatalyst[];
    invalidated: NormalizedLiveIssueBriefCatalyst[];
    expired: NormalizedLiveIssueBriefCatalyst[];
  };
  watch: string[];
};

type NormalizedLiveIssueSignalItem = {
  changeType: string;
  scope: string;
  eventType: string;
  headline: string;
  direction: string;
  importance: string;
  evidenceLevel: string;
  sectors: string[];
  themes: string[];
  stocks: NormalizedLiveIssueSignalStock[];
  reason: string;
  watch: string;
  sectorImpacts: NormalizedSectorImpact[];
  scenario: NormalizedLiveIssueScenario | null;
};

type NormalizedLiveIssueSignal = {
  p1Shadow: LiveSummaryP1Shadow | null;
  status: string;
  windowStart: string;
  windowEnd: string;
  sourceMessageCount: number;
  candidateMessageCount: number;
  newSignalCount: number;
  highImportanceCount: number;
  reason: string;
  signals: NormalizedLiveIssueSignalItem[];
  brief: NormalizedLiveIssueBrief | null;
  capitalScenario: NormalizedLiveIssueCapitalScenario | null;
};

type NormalizedMarketIndexPoint = {
  label: string;
  value: number | null;
  changeValue: number | null;
  changeRate: number | null;
  updatedAt: string;
  delayMinutes: number | null;
  stale: boolean;
  provider: string;
};

type NormalizedDerivativePoint = {
  label: string;
  futuresPrice: number | null;
  futuresChangeRate: number | null;
  marketBasis: number | null;
  basisChange5m: number | null;
  basisChange15m: number | null;
  oiChangeCumulative: number | null;
  oiChange5m: number | null;
  updatedAt: string;
  delayMinutes: number | null;
  stale: boolean;
  structure: string;
  structureLabel: string;
  directionState: string;
  directionLabel: string;
  directionDataStatus: string;
  futuresReturn5m: number | null;
  futuresReturn15m: number | null;
  spotReturn5m: number | null;
  spotReturn15m: number | null;
  futuresLead5m: number | null;
  futuresLead15m: number | null;
  oiState: string;
  oi5mState: string;
  foreignNetContracts: number | null;
  institutionNetContracts: number | null;
  foreignAlignment: string;
  participantFlowObservedAt: string;
  provider: string;
};

type NormalizedGlobalPoint = {
  label: string;
  last: number | null;
  changeRate: number | null;
  provider: string;
};

type NormalizedMetricGuide = {
  title: string;
  summary: string;
  action: string;
  tone: Tone;
};

type NormalizedOpportunity = {
  ticker: string;
  name: string;
  marketLabel: string;
  sectorName: string;
  price: number | null;
  changeRate: number | null;
  tradingValue: number | null;
  opportunityScore: number | null;
  riskPenalty: number | null;
  category: string;
  tone: Tone;
  marketReady: boolean;
  marketFitScore: number | null;
  marketFitLabel: string;
  marketFitTone: Tone;
  sectorChangeRate: number | null;
  sectorBreadthPct: number | null;
  vwapGapPct: number | null;
  smartMoneyNetBuyAmount: number | null;
  programNetBuyToTradingValuePct: number | null;
  investorTrendDirection: string;
  reasons: string[];
  primaryReason: string;
  warning: string;
  detailLocked: boolean;
  accessLevel: string;
};

type NormalizedAttentionBanner = {
  visible: boolean;
  severity: 'INFO' | 'WATCH' | 'HIGH' | 'CRITICAL';
  title: string;
  message: string;
  ticker: string;
  name: string;
  actionTab: string;
  tone: Tone;
};


type FlowAttributionView = {
  code: 'MARKET_DRIVEN' | 'STOCK_SPECIFIC' | 'MIXED_PRESSURE' | 'DIVERGENT_STRENGTH';
  label: string;
  tone: Tone;
  description: string;
  evidence: string[];
};



type RiskStripCandidate = {
  key: string;
  ticker: string;
  name: string;
  label: string;
  tone: Tone;
  score: number;
  holding: boolean;
};

type NormalizedMarketContext = {
  available: boolean;
  dataStatus: string;
  updatedAt: string;
  stale: boolean;
  headline: string;
  description: string;
  action: string;
  tone: Tone;
  alertBadge: string;
  transitionActive: boolean;
  transitionHeadline: string;
  transitionDescription: string;
  transitionTone: Tone;
  marketPressureScore: number | null;
  flowQualityScore: number | null;
  priceStretchScore: number | null;
  reversalWatchScore: number | null;
  positionFlowScore: number | null;
  basisFlowScore: number | null;
  confidenceScore: number | null;
  confidenceLabel: string;
  pressureLabel: string;
  positionFlowLabel: string;
  basisFlowLabel: string;
  qualityLabel: string;
  riskLabel: string;
  watchLabel: string;
  checkpoints: string[];
  intradayScore: number | null;
  intradayDirection: string;
  intradayConfidence: string;
  intradayCoveragePct: number | null;
  riskRealtimeScore: number | null;
  domesticIndices: Record<'KOSPI' | 'KOSDAQ' | 'KOSPI200' | 'KOSDAQ150', NormalizedMarketIndexPoint>;
  derivatives: Record<'KOSPI200' | 'KOSDAQ150', NormalizedDerivativePoint>;
  globalMarkets: Record<'USDKRW' | 'DXY' | 'NQ' | 'ES' | 'YM' | 'RTY' | 'US10Y_FUT' | 'VIX', NormalizedGlobalPoint>;
  sourcePolicy: Record<string, string>;
  metricGuides: Record<string, NormalizedMetricGuide>;
};

type NormalizedSessionPrice = {
  openPrice: number | null;
  highPrice: number | null;
  lowPrice: number | null;
  sessionVwap: number | null;
  sessionDate: string;
  lastEventAt: string;
};

type NormalizedStockRisk = {
  marketType: string;
  marketLabel: string;
  riskAvailable: boolean;
  riskStale: boolean;
  riskScore: number | null;
  riskLevel: string;
  riskLabel: string;
  riskTone: Tone;
  riskConfidence: string;
  riskCalculationVersion: string;
  riskDerivativeAvailable: boolean;
  marketDerivativeRisk: number | null;
  priceRisk: number | null;
  programRisk: number | null;
  foreignRisk: number | null;
  institutionRisk: number | null;
  marketSensitivityRisk: number | null;
  marketSensitivity: number | null;
  riskDerivativeStructure: string;
  riskDerivativeStructureLabel: string;
  riskFuturesChangeRate: number | null;
  riskMarketBasis: number | null;
  riskBasisChange5m: number | null;
  riskOiChange5m: number | null;
  riskOiChangeCumulative: number | null;
  riskReasons: string[];
};


type NormalizedValueJudgmentLayer = {
  state: string;
  label: string;
  reasons: string[];
};

type NormalizedValueJudgmentBusinessEvidence = {
  ready: boolean;
  stale: boolean;
  needsReview: boolean;
  financialIndustry: boolean;
  state: string;
  summary: string;
  businessDate: string;
  reportName: string;
  industry: string;
  subIndustry: string;
  reportShape: string;
  semanticStatus: string;
  semanticConfidence: string;
  profileReadinessScore: number | null;
  consistencyIssueCount: number;
};

type NormalizedVerifiedValueState = {
  available: boolean;
  state: string;
  label: string;
  tone: Tone;
  verifiedAt: string;
  source: string;
};

type NormalizedNewEvidenceStatus = {
  state: string;
  label: string;
  summary: string;
  conflict: boolean;
};

type NormalizedValueJudgment = {
  contractVersion: string;
  basisMode: string;
  basisDate: string;
  valueState: string;
  valueStateLabel: string;
  valueStateTone: Tone;
  confidence: string;
  confidenceLabel: string;
  summary: string;
  reasons: string[];
  nextChecks: string[];
  recentValueChanges: string[];
  recentMarketChanges: string[];
  recentEvidenceChanges: string[];
  businessEvidence: NormalizedValueJudgmentBusinessEvidence;
  lastVerifiedState: NormalizedVerifiedValueState;
  newEvidenceStatus: NormalizedNewEvidenceStatus;
  fundamental: NormalizedValueJudgmentLayer;
  thesis: NormalizedValueJudgmentLayer;
  marketDelivery: NormalizedValueJudgmentLayer;
};

const EMPTY_VALUE_JUDGMENT: NormalizedValueJudgment = {
  contractVersion: '', basisMode: '', basisDate: '', valueState: 'LIMITED', valueStateLabel: '판단 제한',
  valueStateTone: 'neutral', confidence: 'LOW', confidenceLabel: '확인 필요',
  summary: '핵심 재무·사업 근거를 확인 중입니다.', reasons: [], nextChecks: [],
  recentValueChanges: [], recentMarketChanges: [], recentEvidenceChanges: [],
  businessEvidence: { ready: false, stale: false, needsReview: false, financialIndustry: false, state: 'UNKNOWN', summary: '', businessDate: '', reportName: '', industry: '', subIndustry: '', reportShape: '', semanticStatus: '', semanticConfidence: '', profileReadinessScore: null, consistencyIssueCount: 0 },
  lastVerifiedState: { available: false, state: '', label: '', tone: 'neutral', verifiedAt: '', source: '' },
  newEvidenceStatus: { state: 'UNKNOWN', label: '', summary: '', conflict: false },
  fundamental: { state: 'UNKNOWN', label: '기업가치 확인 필요', reasons: [] },
  thesis: { state: 'UNKNOWN', label: '투자 논리 확인 중', reasons: [] },
  marketDelivery: { state: 'UNKNOWN', label: '확인 필요', reasons: [] },
};

type NormalizedFundamentalSnapshot = {
  fundamentalAvailable: boolean;
  fundamentalSnapshotDate: string;
  fundamentalSourceTradeDate: string;
  fundamentalFinancialBasisText: string;
  fundamentalFinancialWarningText: string;
  fundamentalScore: number | null;
  fundamentalQualityScore: number | null;
  fundamentalGrowthScore: number | null;
  fundamentalDirectionCode: string;
  fundamentalDirectionLabel: string;
  fundamentalDirectionTone: Tone;
  fundamentalValuationScore: number | null;
  fundamentalValuationCode: string;
  fundamentalValuationLabel: string;
  fundamentalValuationTone: Tone;
  fundamentalValuationStatus: string;
  fundamentalSummary: string;
  fundamentalReasons: string[];
  fundamentalRevenueCagr3yPct: number | null;
  fundamentalOperatingMarginPct: number | null;
  fundamentalRoePct: number | null;
  fundamentalRoicWaccSpreadPctp: number | null;
  fundamentalNetDebtToOperatingIncome: number | null;
  fundamentalFcfYieldPct: number | null;
  fundamentalPer: number | null;
  fundamentalPbr: number | null;
  fundamentalPegRatio: number | null;
};

type NormalizedStockEntryEvidence = {
  domain: string;
  state: string;
  label: string;
  reason: string;
  tone: Tone;
};

type NormalizedStockEntryDecision = {
  contractVersion: string;
  available: boolean;
  state: 'FAVORABLE' | 'CONDITIONAL' | 'RISK_DOMINANT' | 'LIMITED';
  label: string;
  tone: Tone;
  summary: string;
  confidence: string;
  evidence: NormalizedStockEntryEvidence[];
  confirmationConditions: string[];
  riskConditions: string[];
  nextCheck: string;
  recentMaterialChanges: string[];
  asOf: string;
};

const EMPTY_STOCK_ENTRY_DECISION: NormalizedStockEntryDecision = {
  contractVersion: 'STOCK_ENTRY_CONTEXT_V1',
  available: false,
  state: 'LIMITED',
  label: '판단 제한',
  tone: 'warn',
  summary: '핵심 회사·가치 근거를 확인 중이라 진입 환경을 확정하지 않습니다.',
  confidence: 'LOW',
  evidence: [],
  confirmationConditions: [],
  riskConditions: [],
  nextCheck: '핵심 회사·가치 근거 확인',
  recentMaterialChanges: [],
  asOf: '',
};

type NormalizedSectorTopStock = NormalizedSessionPrice & NormalizedStockRisk & NormalizedFundamentalSnapshot & {
  ticker: string;
  name: string;
  changeRate: number | null;
  price: number | null;
  tradingValue: number | null;
  volume: number | null;
  realtimeMatched: boolean;
  dataSource: string;
  priceAsOf: string;
  isRealtime: boolean;
  delayMinutes: number | null;
  tone: Tone;
  programMatched: boolean;
  programSessionDate: string;
  programSessionCurrent: boolean;
  programNetBuyExecAmount: number | null;
  programNetBuyToTradingValuePct: number | null;
  programReceivedAt: string;
  investorSessionDate: string;
  investorSessionCurrent: boolean;
  investorAggregateMatched: boolean;
  investorEstimateMatched: boolean;
  investorFlowStatus: string;
  foreignNetBuyAmount: number | null;
  institutionNetBuyAmount: number | null;
  smartMoneyNetBuyAmount: number | null;
  estimateForeignNetBuyQty: number | null;
  estimateInstitutionNetBuyQty: number | null;
  estimateSmartMoneyNetBuyQty: number | null;
  investorEstimateBucket: string;
  investorAggregateEventAt: string;
  investorEstimateEventAt: string;
  investorReceivedAt: string;

  investorTrendDirection: string;
  investorTrendStreakMinutes: number;
  investorTrendDelta1m: number | null;
  investorTrendDelta5m: number | null;
  investorTrendBaseline1m: number | null;
  investorTrendVelocityRatio: number | null;
  investorTrendAccelerationRatio: number | null;
  investorTrendBaselinePointCount: number;
  investorTrendTurnedAt: string;
  investorTrendUpdatedAt: string;
  investorTrendLeader: string;
  investorTrendConfidence: string;
  investorTrendPointCount: number;
  flowDecision: string;
  flowDecisionTone: Tone;
  flowReason: string;
  actionDecision: NormalizedActionDecision;
  riskAssessment: NormalizedRiskAssessment;
  decisionEvidence: DecisionEvidenceView;
  historicalEvidence: HistoricalEvidenceView;
  valueJudgment: NormalizedValueJudgment;
  entryDecision: NormalizedStockEntryDecision;
};

type StockRadarRow = NormalizedSectorTopStock & {
  sectorName: string;
  sectorChangeRate: number;
  sectorBreadthPct: number | null;
  sectorTone: Tone;
};

type NormalizedSector = {
  name: string;
  sectorCode: string;
  marketType: string;
  marketLabel: string;
  industryCode: string;
  industryName: string;
  changeRate: number;
  tone: Tone;
  mappedTickerCount: number;
  realtimeTickerCount: number;
  upTickerCount: number;
  downTickerCount: number;
  realtimeCoveragePct: number;
  realtimeTradingValue: number | null;
  latestRealtimeEventAt: string;
  dataSource: string;
  isRealtime: boolean;
  delayMinutes: number | null;
  topStocks: NormalizedSectorTopStock[];
  breadthPct: number | null;
  strengthScore: number;
};

type NormalizedThemeFlow = {
  themeCode: string;
  themeName: string;
  sessionDate: string;
  constituentCount: number;
  priceAvailableCount: number;
  flowAvailableCount: number;
  programAvailableCount: number;
  avgChangeRate: number | null;
  foreignNetBuyAmount: number | null;
  institutionNetBuyAmount: number | null;
  smartMoneyNetBuyAmount: number | null;
  programNetBuyAmount: number | null;
  netBuyStockCount: number;
  netSellStockCount: number;
  flatStockCount: number;
  smartMoneyBreadthPct: number | null;
  flowCoveragePct: number | null;
  priceCoveragePct: number | null;
  topSmartMoneyTicker: string;
  topSmartMoneyCorpName: string;
  topSmartMoneyAmount: number | null;
  sourceLatestAt: string;
  dataStatus: string;
  receivedAt: string;
  tone: Tone;
};

type NormalizedThemeStock = {
  themeCode: string;
  themeName: string;
  themeChangeRate: number | null;
  themePriceCoveragePct: number | null;
  themeDataStatus: string;
  sessionDate: string;
  ticker: string;
  name: string;
  market: string;
  price: number | null;
  changeRate: number | null;
  tradingValue: number | null;
  foreignNetBuyAmount: number | null;
  institutionNetBuyAmount: number | null;
  smartMoneyNetBuyAmount: number | null;
  programNetBuyAmount: number | null;
  priceAvailable: boolean;
  flowAvailable: boolean;
  programAvailable: boolean;
  dataStatus: string;
  sourceLatestAt: string;
  tone: Tone;
};

type DecisionEvidenceItem = {
  label: string;
  statusLabel: string;
  headline: string;
  value: string;
  detail: string;
  usedInDecision: boolean;
};

type DecisionEvidenceView = {
  available: boolean;
  strength: string;
  strengthLabel: string;
  summary: string;
  items: DecisionEvidenceItem[];
  riskConditions: string[];
  recoveryConditions: string[];
};

type HistoricalOutcomeView = {
  sampleSize: number;
  upCount: number;
  flatCount: number;
  downCount: number;
  positiveRate: number | null;
  averageReturn: number | null;
};

type HistoricalEvidenceView = {
  available: boolean;
  title: string;
  basisLabel: string;
  sampleSize: number;
  sampleNote: string;
  patternLabels: string[];
  forward5d: HistoricalOutcomeView;
  forward20d: HistoricalOutcomeView;
  interpretation: string;
  coverageNote: string;
};





type NormalizedBoxSupport = {
  available: boolean;
  analysisDate: string;
  referenceClose: number | null;
  upperPrice: number | null;
  middlePrice: number | null;
  lowerPrice: number | null;
  upperDistancePct: number | null;
};

type NormalizedHoldingThesis = {
  contractVersion: string;
  available: boolean;
  coreState: 'PRESERVED' | 'WEAKENED' | 'DAMAGED' | 'LIMITED' | string;
  coreLabel: string;
  displayState: string;
  displayLabel: string;
  tone: Tone;
  marketPressureState: string;
  marketPressureLabel: string;
  marketPressureTone: Tone;
  marketPressureScore: number | null;
  marketOnlyPressure: boolean;
  pressureOnly: boolean;
  fundamentalState: string;
  fundamentalLabel: string;
  thesisState: string;
  thesisLabel: string;
  verifiedBaselineAvailable: boolean;
  verifiedBaselineLabel: string;
  newEvidenceConflict: boolean;
  contextIssuePending: boolean;
  contextIssueHeadline: string;
  calculationMode: string;
  marketPressureBasis: string;
  marketSession: string;
  calculatedAt: string;
  referenceDate: string;
  eodFinalized: boolean;
  weekendDaily: boolean;
  reasons: string[];
  todayChanges: string[];
  invalidationConditions: string[];
  recheckConditions: string[];
  monitorConditions: string[];
  monitorConditionCount: number;
  triggerSignals: string[];
  triggeredCount: number;
  damageConfirmed: boolean;
  weakeningConfirmed: boolean;
  flowTone: string;
  flowLabel: string;
  basisDate: string;
};

const EMPTY_HOLDING_THESIS: NormalizedHoldingThesis = {
  contractVersion: '',
  available: false,
  coreState: 'LIMITED',
  coreLabel: '판단 제한',
  displayState: 'LIMITED',
  displayLabel: '판단 제한',
  tone: 'neutral',
  marketPressureState: 'UNKNOWN',
  marketPressureLabel: '시장 확인 중',
  marketPressureTone: 'neutral',
  marketPressureScore: null,
  marketOnlyPressure: false,
  pressureOnly: false,
  fundamentalState: 'UNKNOWN',
  fundamentalLabel: '기업가치 확인 필요',
  thesisState: 'UNKNOWN',
  thesisLabel: '투자 논리 확인 중',
  verifiedBaselineAvailable: false,
  verifiedBaselineLabel: '',
  newEvidenceConflict: false,
  contextIssuePending: false,
  contextIssueHeadline: '',
  calculationMode: '',
  marketPressureBasis: '',
  marketSession: '',
  calculatedAt: '',
  referenceDate: '',
  eodFinalized: false,
  weekendDaily: false,
  reasons: [],
  todayChanges: [],
  invalidationConditions: [],
  recheckConditions: [],
  monitorConditions: [],
  monitorConditionCount: 0,
  triggerSignals: [],
  triggeredCount: 0,
  damageConfirmed: false,
  weakeningConfirmed: false,
  flowTone: '',
  flowLabel: '수급 확인 중',
  basisDate: '',
};

type NormalizedHoldingJudgmentAxis = {
  state: string;
  label: string;
  tone: Tone;
  basis: string;
};

type NormalizedHoldingJudgmentContract = {
  contractVersion: string;
  available: boolean;
  ticker: string;
  name: string;
  asOf: string;
  marketSession: string;
  thesis: {
    state: string;
    label: string;
    tone: Tone;
    primaryReason: string;
    reasons: string[];
    damageConfirmed: boolean;
    companyEvidenceState: string;
    companyEvidenceLabel: string;
    companyEvidenceTone: Tone;
  };
  companyEvidence: {
    state: 'INTACT' | 'WEAKENING' | 'DAMAGED' | 'PENDING' | 'INSUFFICIENT' | string;
    label: string;
    tone: Tone;
    basis: string;
    damageConfirmed: boolean;
    weakeningConfirmed: boolean;
    pendingEvent: boolean;
  };
  projectedImpact: {
    state: string;
    label: string;
    tone: Tone;
    score: number | null;
    baseRoicWaccSpreadPctp: number | null;
    stressSpreadDeltaPctp: number | null;
    stressRoicWaccSpreadPctp: number | null;
    confidence: string;
    confidenceLabel: string;
    methodology: string;
    estimatedNotActual: boolean;
    stockSession: string;
    factors: Array<{
      code: string;
      label: string;
      score: number | null;
      basis: string;
      status: string;
      tone: Tone;
    }>;
    eventImpact: {
      scope: string;
      status: string;
      state: string;
      headline: string;
      direction: string;
      materiality: string;
      score: number | null;
      label: string;
      tone: Tone;
    };
    foreignFlowAmount: number | null;
    investorTrendDirection: string;
    usdkrwChangeRate: number | null;
    marketIndexKey: string;
    marketIndexChangeRate: number | null;
    summary: string;
  };
  pressure: {
    day: NormalizedHoldingJudgmentAxis & {
      stockChangeRate: number | null;
      flowLabel: string;
      marketLabel: string;
    };
    live: NormalizedHoldingJudgmentAxis & {
      price: number | null;
      sessionVwap: number | null;
      priceVsVwapPct: number | null;
      sessionLabel: string;
    };
  };
  riskManagement: {
    state: 'NORMAL' | 'WATCH' | 'CAUTION' | 'DEFENSE' | 'REVIEW' | string;
    label: string;
    tone: Tone;
    actionCode: string;
    actionLabel: string;
    reason: string;
  };
  invalidation: {
    triggered: number;
    total: number;
    label: string;
    items: Array<{
      label: string;
      status: string;
      statusLabel: string;
      tone: Tone;
    }>;
    triggerSignals: string[];
  };
  nextChecks: string[];
  dataQuality: {
    business: string;
    financial: string;
    price: string;
    flow: string;
    market: string;
    event: string;
  };
  confidence: {
    state: string;
    label: string;
    tone: Tone;
    readyCount: number;
    totalCount: number;
    coveragePct: number | null;
    missingEvidenceLabels: string[];
  };
  evidence: {
    maintain: string[];
    risk: string[];
    missing: string[];
  };
  changes: Array<{
    type: string;
    label: string;
    tone: Tone;
  }>;
  summary: string;
};

const EMPTY_HOLDING_JUDGMENT: NormalizedHoldingJudgmentContract = {
  contractVersion: '',
  available: false,
  ticker: '',
  name: '',
  asOf: '',
  marketSession: '',
  thesis: {
    state: 'LIMITED',
    label: '판단 제한',
    tone: 'neutral',
    primaryReason: '핵심 사업·재무 근거를 확인 중입니다.',
    reasons: [],
    damageConfirmed: false,
    companyEvidenceState: 'INSUFFICIENT',
    companyEvidenceLabel: '회사 판단 근거 부족',
    companyEvidenceTone: 'neutral',
  },
  companyEvidence: {
    state: 'INSUFFICIENT',
    label: '회사 판단 근거 부족',
    tone: 'neutral',
    basis: '',
    damageConfirmed: false,
    weakeningConfirmed: false,
    pendingEvent: false,
  },
  projectedImpact: {
    state: 'NO_DATA',
    label: '예상 영향 확인 중',
    tone: 'neutral',
    score: null,
    baseRoicWaccSpreadPctp: null,
    stressSpreadDeltaPctp: null,
    stressRoicWaccSpreadPctp: null,
    confidence: 'UNAVAILABLE',
    confidenceLabel: '산출 제한',
    methodology: '',
    estimatedNotActual: true,
    stockSession: '',
    factors: [],
    eventImpact: {
      scope: '',
      status: 'NONE',
      state: 'NO_EVENT',
      headline: '',
      direction: '',
      materiality: '',
      score: null,
      label: '직접 영향 Event 없음',
      tone: 'neutral',
    },
    foreignFlowAmount: null,
    investorTrendDirection: '',
    usdkrwChangeRate: null,
    marketIndexKey: '',
    marketIndexChangeRate: null,
    summary: '',
  },
  pressure: {
    day: {
      state: 'UNKNOWN',
      label: '오늘 압력 확인 중',
      tone: 'neutral',
      basis: '',
      stockChangeRate: null,
      flowLabel: '수급 확인 중',
      marketLabel: '시장 확인 중',
    },
    live: {
      state: 'NO_DATA',
      label: '현재 LIVE 확인 중',
      tone: 'neutral',
      basis: '',
      price: null,
      sessionVwap: null,
      priceVsVwapPct: null,
      sessionLabel: '',
    },
  },
  riskManagement: {
    state: 'WATCH',
    label: '관찰',
    tone: 'neutral',
    actionCode: 'REVIEW',
    actionLabel: '',
    reason: '',
  },
  invalidation: {
    triggered: 0,
    total: 0,
    label: '',
    items: [],
    triggerSignals: [],
  },
  nextChecks: [],
  dataQuality: {
    business: 'MISSING',
    financial: 'MISSING',
    price: 'MISSING',
    flow: 'MISSING',
    market: 'MISSING',
    event: 'MISSING',
  },
  confidence: {
    state: 'LOW',
    label: '낮음',
    tone: 'down',
    readyCount: 0,
    totalCount: 6,
    coveragePct: null,
    missingEvidenceLabels: [],
  },
  evidence: {
    maintain: [],
    risk: [],
    missing: [],
  },
  changes: [],
  summary: '',
};

type NormalizedHolding = NormalizedSessionPrice & NormalizedStockRisk & NormalizedFundamentalSnapshot & {
  ticker: string;
  name: string;
  changeRate: number | null;
  industryName: string;
  sectorRelativePct: number | null;
  price: number | null;
  tradingValue: number | null;
  dataSource: string;
  priceAsOf: string;
  isRealtime: boolean;
  delayMinutes: number | null;
  portfolioQuantity: number | null;
  portfolioAvgPrice: number | null;
  portfolioPositionAvailable: boolean;
  status: '준비중' | '위험중' | '관찰' | '양호';
  statusTone: Tone;
  programMatched: boolean;
  programSessionDate: string;
  programSessionCurrent: boolean;
  programNetBuyExecAmount: number | null;
  programNetBuyToTradingValuePct: number | null;
  programReceivedAt: string;
  investorSessionDate: string;
  investorSessionCurrent: boolean;
  investorAggregateMatched: boolean;
  investorEstimateMatched: boolean;
  investorFlowStatus: string;
  foreignNetBuyAmount: number | null;
  institutionNetBuyAmount: number | null;
  smartMoneyNetBuyAmount: number | null;
  estimateForeignNetBuyQty: number | null;
  estimateInstitutionNetBuyQty: number | null;
  estimateSmartMoneyNetBuyQty: number | null;
  investorEstimateBucket: string;
  investorAggregateEventAt: string;
  investorEstimateEventAt: string;
  investorReceivedAt: string;

  investorTrendDirection: string;
  investorTrendStreakMinutes: number;
  investorTrendDelta1m: number | null;
  investorTrendDelta5m: number | null;
  investorTrendBaseline1m: number | null;
  investorTrendVelocityRatio: number | null;
  investorTrendAccelerationRatio: number | null;
  investorTrendBaselinePointCount: number;
  investorTrendTurnedAt: string;
  investorTrendUpdatedAt: string;
  investorTrendLeader: string;
  investorTrendConfidence: string;
  investorTrendPointCount: number;
  flowDecision: string;
  flowDecisionTone: Tone;
  flowReason: string;
  actionDecision: NormalizedActionDecision;
  riskAssessment: NormalizedRiskAssessment;
  decisionEvidence: DecisionEvidenceView;
  historicalEvidence: HistoricalEvidenceView;
  valueJudgment: NormalizedValueJudgment;
  holdingThesis: NormalizedHoldingThesis;
  judgmentContract: NormalizedHoldingJudgmentContract;
  boxSupport: NormalizedBoxSupport;
  valuationAlertEnabled: boolean;
  valuationAlertTargetPrice: number | null;
  valuationAlertMarginPct: number;
  valuationAlertFairValue: number | null;
  valuationAlertFormulaVersion: string;
  valuationAlertCalculatedAt: string;
  valuationAlertTriggered: boolean;
};

type HoldingValuationModel = {
  code: string;
  label: string;
  familyCode?: string;
  role?: string;
  fairPrice: number | null;
  basis: string;
  status: string;
  reasonCode?: string;
  peerLevel?: string;
  peerValidCount?: number;
};

type HoldingValuationFamily = {
  familyCode: string;
  status: string;
  reasonCode?: string;
  representativeValue: number | null;
  lowerValue?: number | null;
  upperValue?: number | null;
  internalDispersionPct?: number | null;
  models?: HoldingValuationModel[];
};

type HoldingValuationChange = {
  previousConsensus: number | null;
  currentConsensus: number | null;
  consensusDeltaPct: number | null;
  direction: 'BASELINE' | 'UP' | 'DOWN' | 'UNCHANGED' | 'UNKNOWN' | string;
  primaryDriverCode: string;
  primaryDriverLabel: string;
  reasonSummary: string;
};

type HoldingValuationDetail = {
  ticker: string;
  name: string;
  engineVersion?: string;
  formulaVersion: string;
  snapshotDate: string;
  sourceTradeDate: string;
  valuationAsOf?: string;
  traceId?: number | null;
  valuationChange?: HoldingValuationChange | null;
  currentPrice: number | null;
  currentPriceSource: string;
  currentPriceAt: string;
  snapshotPrice: number | null;
  peerLevel: string;
  peerCount: number;
  modelCount: number;
  models: HoldingValuationModel[];
  families?: HoldingValuationFamily[];
  eligibleFamilyCount?: number | null;
  availableFamilyCount?: number | null;
  consensusStatus?: string;
  consensusReasonCodes?: string[];
  valuationReasonCodes?: string[];
  representativeFairValue?: number | null;
  consensusFairValue: number | null;
  centralEstimateStatus?: string;
  centralEstimate?: number | null;
  referenceMedian?: number | null;
  referenceGapPct?: number | null;

  rawModelExpectedCount?: number | null;
  rawModelCount?: number | null;
  rawModelMedian?: number | null;
  rawModelMedianSource?: string;
  rawModels?: HoldingValuationModel[];
  valuationLevel?: string;
  centralEstimateSource?: string;
  centralEstimateFamilyCount?: number | null;
  centralEstimateConfidence?: string;
  confidence?: string;
  centralGapPct?: number | null;
  centralValuationState?: string;
  operationalFairValue?: number | null;
  alertBasisType?: string;
  discountPct: number | null;
  modelDispersionPct: number | null;
  marginPct: number;
  alertTargetPrice: number | null;
  belowAlertTarget: boolean;
  valuationState: string;
  valuationLabel: string;
  qualityGatePassed: boolean;
  warnings: string[];
  metrics: Record<string, number | null>;
  alert: {
    enabled: boolean;
    configured?: boolean;
    active?: boolean;
    pauseReason?: string | null;
    targetPrice?: number | null;
    marginPct?: number | null;
    fairValue?: number | null;
    formulaVersion?: string;
    calculatedAt?: string;
  };
};

type NormalizedAlert = {
  level: 'info' | 'warning' | 'danger';
  message: string;
};

type NormalizedChangeEvent = {
  id: string;
  eventScope: string;
  entityType: string;
  entityKey: string;
  eventType: string;
  severity: 'INFO' | 'WARNING' | 'DANGER';
  title: string;
  message: string;
  previousValue: string;
  currentValue: string;
  causeLabels: string[];
  changeValue: number | null;
  sourceEventAt: string;
  detectedAt: string;
};

type LiveIssueStructuredUnit = {
  ticker: string;
  company: string;
  drug: string;
  stage: string;
  catalyst: string;
};

type LiveIssue = {
  id: string;
  feedId: number | null;
  title: string;
  summary: string;
  publishedAt: string;
  source: string;
  sources: string[];
  formatProfile: string;
  formatType: string;
  previewSource: string;
  sourceType: string;
  sourceVerificationStatus: string;
  sourceLinkValidity: string;
  telegramUpdateType: string;
  telegramInboxId: string;
  telegramChannel: string;
  structuredUnits: LiveIssueStructuredUnit[];
  structuredUnitCount: number;
  duplicateCount: number;
  sectorCode: string;
  industrySectorNames: string[];
  marketGroupNames: string[];
  sectorImpact: NormalizedSectorImpact | null;
  movementAttribution: MovementAttribution | null;
  impactScore: number | null;
  importance: number | null;
  eventId: string;
  eventType: string;
  eventDirection: string;
  eventLinkType: string;
  evidenceLevel: string;
  verificationStatus: string;
  reactionStatus: string;
  reactionDirection: string;
  reactionHorizon: string;
  reaction15m: number | null;
  reaction30m: number | null;
  reaction60m: number | null;
  reactionClose: number | null;
  excessSector: number | null;
  turnoverRatio: number | null;
  flowStatus: string;
  judgmentSupport: string;
  relatedTickers: string[];
  relatedStockNames: string[];
  relatedStockCount: number;
  stockLinkAuthority: string;
  stockLinkInferred: boolean;
  realtime: boolean;
  ageMinutes: number | null;
  url: string;
  sourceUrlAvailable: boolean;
};

function issueSectorLabels(issue: LiveIssue): string[] {
  const authority = issue.industrySectorNames.map(cleanText).filter(Boolean);
  return Array.from(new Set(authority)).slice(0, 4);
}

type LiveActivityToast = {
  id: number;
  kind: 'issue' | 'scanner-enter' | 'scanner-exit';
  title: string;
  message: string;
  target: 'ISSUES' | 'SCANNER';
};

type ScannerActivityItem = {
  key: string;
  ticker: string;
  name: string;
  ruleName: string;
  episodeNo: number;
};

function scannerActivityKey(match: ScannerMatch): string {
  const ticker = cleanText(match?.ticker);
  const ruleId = Number(match?.ruleId ?? 0);
  return `${Number.isFinite(ruleId) ? ruleId : 0}:${ticker}`;
}

function scannerActivityItem(match: ScannerMatch): ScannerActivityItem | null {
  const ticker = cleanText(match?.ticker);
  if (!ticker) return null;
  return {
    key: scannerActivityKey(match),
    ticker,
    name: cleanText(match?.name) || ticker,
    ruleName: cleanText(match?.ruleName) || 'My Scanner',
    episodeNo: Number.isFinite(Number(match?.episodeNo)) ? Number(match?.episodeNo) : 0,
  };
}

function safeReadStringArray(storageKey: string): string[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(cleanText).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function safeWriteStringArray(storageKey: string, values: string[], limit = 160): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(Array.from(new Set(values)).slice(-limit)));
  } catch {
    // Storage can be unavailable in private/locked-down browser contexts. UI still works in-memory.
  }
}

function safeReadScannerActivity(storageKey: string): Map<string, ScannerActivityItem> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Map();
    const map = new Map<string, ScannerActivityItem>();
    parsed.forEach((item: any) => {
      const key = cleanText(item?.key);
      const ticker = cleanText(item?.ticker);
      if (!key || !ticker) return;
      map.set(key, {
        key,
        ticker,
        name: cleanText(item?.name) || ticker,
        ruleName: cleanText(item?.ruleName) || 'My Scanner',
        episodeNo: Number.isFinite(Number(item?.episodeNo)) ? Number(item?.episodeNo) : 0,
      });
    });
    return map;
  } catch {
    return new Map();
  }
}

function safeWriteScannerActivity(storageKey: string, values: Map<string, ScannerActivityItem>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(Array.from(values.values()).slice(-160)));
  } catch {
    // Best-effort continuity only.
  }
}

function formatIssuePublishedAt(value: unknown): string {
  const raw = cleanText(value);
  if (!raw) return '';

  const normalized = raw.replace('T', ' ');
  const match = normalized.match(/^(\d{4}-\d{2}-\d{2})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!match) return normalized;

  const [, date, hour, minute, second] = match;
  if (!hour || !minute) return date;

  const isMidnight = hour === '00' && minute === '00' && (!second || second === '00');
  return isMidnight ? date : `${date} ${hour}:${minute}`;
}

function normalizeIssueDedupKey(issue: LiveIssue): string {
  // A news event is identified by its originating feed table + feed_id, not by title.
  // Two Telegram children may have similar wrapper titles but distinct URLs and facts.
  // The normalized id already includes the source sector; never union distinct feed rows.
  return `feed:${issue.id}`;
}

function isSafeIssueHttpUrl(value: unknown): boolean {
  const raw = cleanText(value);
  if (!/^https?:\/\//i.test(raw)) return false;
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase().replace(/\.$/, '');
    if (!['http:', 'https:'].includes(parsed.protocol) || !host || parsed.username || parsed.password) return false;
    // These are display links, never internal dashboard or local-network targets.
    if (host === 'localhost' || host.endsWith('.localhost') || host.endsWith('.local') || host.endsWith('.internal')) return false;
    if (/^(?:127|10|0)\.\d+\.\d+\.\d+$/.test(host)) return false;
    if (/^192\.168\.\d+\.\d+$/.test(host)) return false;
    if (/^172\.(?:1[6-9]|2\d|3[01])\.\d+\.\d+$/.test(host)) return false;
    if (host === '[::1]' || host === '[::]' || host === '::1' || host === '::') return false;
    return true;
  } catch {
    return false;
  }
}

function isTelegramIssueUrl(value: unknown): boolean {
  if (!isSafeIssueHttpUrl(value)) return false;
  const parsed = new URL(cleanText(value));
  const host = parsed.hostname.toLowerCase().replace(/^www\./, '').replace(/\.$/, '');
  if (!['t.me', 'telegram.me', 'telegram.dog'].includes(host)) return false;
  const path = parsed.pathname;
  // Message permalink only. A Telegram homepage/channel profile is not an article.
  return /^\/(?:s\/)?[A-Za-z0-9_]{4,32}\/\d+\/?$/.test(path)
    || /^\/c\/\d+\/\d+\/?$/.test(path);
}

function isExternalIssueUrl(value: unknown): boolean {
  if (!isSafeIssueHttpUrl(value)) return false;
  const hostname = new URL(cleanText(value)).hostname.toLowerCase().replace(/^www\./, '').replace(/\.$/, '');
  if (['t.me', 'telegram.me', 'telegram.dog', 'telegram.org'].some((host) => hostname === host || hostname.endsWith(`.${host}`))) return false;
  return true;
}

// Telegram provenance links are useful to readers even without an external article.
// Their presence never upgrades SOURCE_LINKED to article or event verification.
function issueOriginalLink(issue: Pick<LiveIssue, 'url' | 'sourceUrlAvailable'>): { url: string; kind: 'EXTERNAL' | 'TELEGRAM' } | null {
  if (isTelegramIssueUrl(issue.url)) return { url: issue.url, kind: 'TELEGRAM' };
  if (issue.sourceUrlAvailable && isExternalIssueUrl(issue.url)) return { url: issue.url, kind: 'EXTERNAL' };
  return null;
}

async function copyIssueOriginalUrl(url: string): Promise<boolean> {
  if (!isSafeIssueHttpUrl(url)) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch {
    // Some mobile browsers deny clipboard access; try the selected-text fallback.
  }
  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = url;
  textarea.readOnly = true;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  try {
    textarea.focus();
    textarea.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
}

function eventLinkLabel(value: unknown): string {
  const code = cleanText(value).toUpperCase();
  if (code === 'DIRECT') return '직접 관련';
  if (code === 'EXPOSURE') return '사업 노출 후보';
  if (code === 'SECTOR') return '업종 관측 후보';
  if (code === 'THEME') return '테마 연관';
  return '';
}

function eventEvidenceLabel(value: unknown): string {
  const code = cleanText(value).toUpperCase();
  if (code === 'CONFIRMED') return '공식 확인';
  if (code === 'REPORTED') return '보도 단계';
  if (code === 'UNVERIFIED') return '확인 전';
  return '';
}

function eventReactionLabel(value: unknown): string {
  const code = cleanText(value).toUpperCase();
  if (code === 'CONFIRMED') return '가격 반응 확인';
  if (code === 'NO_REACTION') return '뚜렷한 가격 반응 없음';
  if (code === 'OPPOSITE') return '반대 방향 움직임';
  return '가격 반응 확인 중';
}

function eventFlowLabel(value: unknown): string {
  const code = cleanText(value).toUpperCase();
  if (code === 'CONFIRMED') return '수급 동행 확인';
  if (code === 'CONFLICT') return '수급 엇갈림';
  if (code === 'UNAVAILABLE') return '수급 데이터 지연';
  return '수급 확인 중';
}

function eventJudgmentLabel(value: unknown): string {
  const code = cleanText(value).toUpperCase();
  if (code === 'STRONG_SUPPORT') return '판단 근거 강하게 확인';
  if (code === 'SUPPORT') return '판단 근거 확인';
  if (code === 'CONFLICT') return '이벤트 기대와 반응 충돌';
  if (code === 'NONE') return '판단 반영 근거 없음';
  return '판단 반영 대기';
}

function movementAttributionTone(value: MovementAttribution | null): Tone {
  if (!value) return 'neutral';
  if (value.status === 'MIXED') return 'warn';
  if (value.status === 'UNKNOWN') return 'neutral';
  if (value.direction === 'DOWN') return 'down';
  if (value.direction === 'UP') return 'up';
  return 'neutral';
}

function movementAttributionDirectionLabel(value: MovementAttribution | null): string {
  if (!value) return '방향 확인 중';
  if (value.direction === 'DOWN') return '하락 배경';
  if (value.direction === 'UP') return '상승 배경';
  return '움직임 배경';
}

function isResolvedEventReactionStatus(value: unknown): boolean {
  const code = cleanText(value).toUpperCase();
  return code === 'CONFIRMED' || code === 'NO_REACTION' || code === 'OPPOSITE';
}

function isResolvedEventFlowStatus(value: unknown): boolean {
  const code = cleanText(value).toUpperCase();
  return code === 'CONFIRMED' || code === 'CONFLICT' || code === 'UNAVAILABLE';
}

function isResolvedEventJudgmentStatus(value: unknown): boolean {
  const code = cleanText(value).toUpperCase();
  return code === 'STRONG_SUPPORT' || code === 'SUPPORT' || code === 'CONFLICT' || code === 'NONE';
}

function signedPctPoint(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%p`;
}

function issuePaginationPages(currentPage: number, totalPages: number): number[] {
  if (totalPages <= 0) return [];
  const windowSize = Math.min(5, totalPages);
  const maxStart = Math.max(1, totalPages - windowSize + 1);
  const start = Math.max(1, Math.min(currentPage - Math.floor(windowSize / 2), maxStart));
  return Array.from({ length: windowSize }, (_, index) => start + index);
}

function inferExactIssueStockFromTitle(title: unknown): { ticker: string; name: string } | null {
  const value = cleanText(title);
  if (!value) return null;
  const match = value.match(/([A-Za-z가-힣0-9&.+·_-]{1,40})\s*\(\s*(\d{6})\s*\)/);
  if (!match) return null;
  return { name: cleanText(match[1]), ticker: cleanText(match[2]) };
}

function normalizeLiveIssues(payload: any): LiveIssue[] {
  const candidates = [payload?.items, payload?.issues, payload?.todayIssues, payload?.today_issues, payload?.data?.items, payload?.data?.issues, payload];
  const rows = candidates.find(Array.isArray) ?? [];
  const toList = (value: unknown) => Array.isArray(value)
    ? value.map(cleanText).filter(Boolean)
    : cleanText(value).split(/[,|]/).map((item) => item.trim()).filter(Boolean);

  const normalized = rows.map((raw: any, index: number): LiveIssue => {
    const source = cleanText(raw?.source ?? raw?.sourceSector ?? raw?.source_sector ?? raw?.provider ?? raw?.publisher);
    const feedId = nullableNumber(raw?.feedId ?? raw?.feed_id ?? raw?.id ?? raw?.issueId ?? raw?.issue_id);
    const rawId = cleanText(raw?.id ?? raw?.issueId ?? raw?.issue_id ?? raw?.newsId ?? raw?.news_id ?? raw?.feedId ?? raw?.feed_id);
    const sourceKey = cleanText(raw?.sourceSector ?? raw?.source_sector ?? raw?.sectorCode ?? raw?.sector_code ?? source) || 'issue';
    const url = cleanText(raw?.url ?? raw?.link ?? raw?.sourceUrl ?? raw?.source_url ?? raw?.articleUrl ?? raw?.article_url);
    const sourceUrlAvailableRaw = raw?.sourceUrlAvailable ?? raw?.source_url_available;
    const sourceUrlAvailable = sourceUrlAvailableRaw === undefined || sourceUrlAvailableRaw === null
      ? isExternalIssueUrl(url)
      : normalizeBoolean(sourceUrlAvailableRaw) && isExternalIssueUrl(url);
    const rawStructured = raw?.formatStructuredJson ?? raw?.format_structured_json ?? raw?.structured ?? raw?.formatStructured;
    const structuredUnits = parseIssueStructuredUnits(rawStructured);
    const structuredUnitCount = parseIssueStructuredUnitCount(rawStructured, structuredUnits);
    const title = cleanText(raw?.title ?? raw?.headline ?? raw?.name) || '제목 없는 이슈';
    const titleStock = inferExactIssueStockFromTitle(title);
    const mappedRelatedTickers = toList(raw?.relatedTickers ?? raw?.related_tickers ?? raw?.tickers ?? raw?.stockCodes ?? raw?.stock_codes);
    const mappedRelatedStockNames = toList(raw?.relatedStockNames ?? raw?.related_stock_names ?? raw?.stockNames ?? raw?.stock_names);
    const relatedTickers = Array.from(new Set([
      ...mappedRelatedTickers,
      ...(titleStock?.ticker ? [titleStock.ticker] : []),
    ]));
    const relatedStockNames = Array.from(new Set([
      ...mappedRelatedStockNames,
      ...(titleStock?.name ? [titleStock.name] : []),
    ]));
    const stockLinkAuthority = cleanText(raw?.stockLinkAuthority ?? raw?.stock_link_authority)
      || (mappedRelatedTickers.length > 0 ? 'ARTICLE_TICKER_MAP' : titleStock ? 'TITLE_EXACT' : 'NONE');
    const stockLinkInferred = normalizeBoolean(raw?.stockLinkInferred ?? raw?.stock_link_inferred)
      || (mappedRelatedTickers.length === 0 && Boolean(titleStock?.ticker));
    return {
      // feed_id is only unique inside each sector feed table. Build a cross-sector React/UI key.
      id: rawId ? `${sourceKey}:${rawId}` : `live-issue-${sourceKey}-${index}`,
      feedId,
      title,
      summary: cleanText(raw?.summary ?? raw?.description ?? raw?.content ?? raw?.reason),
      publishedAt: cleanText(raw?.publishedAt ?? raw?.published_at ?? raw?.createdAt ?? raw?.created_at ?? raw?.eventAt ?? raw?.event_at),
      source,
      sources: source ? [source] : [],
      formatProfile: cleanText(raw?.formatProfile ?? raw?.format_profile),
      formatType: cleanText(raw?.formatType ?? raw?.format_type),
      previewSource: cleanText(raw?.previewSource ?? raw?.preview_source),
      sourceType: cleanText(raw?.sourceType ?? raw?.source_type),
      sourceVerificationStatus: cleanText(raw?.sourceVerificationStatus ?? raw?.source_verification_status),
      sourceLinkValidity: cleanText(raw?.sourceLinkValidity ?? raw?.source_link_validity),
      telegramUpdateType: cleanText(raw?.telegramUpdateType ?? raw?.telegram_update_type),
      telegramInboxId: cleanText(raw?.telegramInboxId ?? raw?.telegram_inbox_id),
      telegramChannel: cleanText(raw?.telegramChannel ?? raw?.telegram_channel),
      structuredUnits,
      structuredUnitCount,
      duplicateCount: 1,
      sectorCode: cleanText(raw?.sectorCode ?? raw?.sector_code ?? raw?.sector),
      industrySectorNames: toList(raw?.industrySectorNames ?? raw?.industry_sector_names).slice(0, 4),
      marketGroupNames: toList(raw?.marketGroupNames ?? raw?.market_group_names ?? raw?.marketGroupName ?? raw?.market_group_name).slice(0, 3),
      sectorImpact: normalizeSectorImpact(raw?.sectorImpact ?? raw?.sector_impact),
      movementAttribution: normalizeMovementAttribution(raw?.movementAttribution ?? raw?.movement_attribution),
      impactScore: nullableNumber(raw?.impactScore ?? raw?.impact_score ?? raw?.finalImpactScore ?? raw?.final_impact_score),
      importance: nullableNumber(raw?.importance ?? raw?.importanceScore ?? raw?.importance_score),
      eventId: cleanText(raw?.eventId ?? raw?.event_id),
      eventType: cleanText(raw?.eventType ?? raw?.event_type),
      eventDirection: cleanText(raw?.eventDirection ?? raw?.event_direction),
      eventLinkType: cleanText(raw?.eventLinkType ?? raw?.event_link_type),
      evidenceLevel: cleanText(raw?.evidenceLevel ?? raw?.evidence_level),
      verificationStatus: cleanText(raw?.verificationStatus ?? raw?.verification_status),
      reactionStatus: cleanText(raw?.reactionStatus ?? raw?.reaction_status),
      reactionDirection: cleanText(raw?.reactionDirection ?? raw?.reaction_direction),
      reactionHorizon: cleanText(raw?.reactionHorizon ?? raw?.reaction_horizon),
      reaction15m: nullableNumber(raw?.reaction15m ?? raw?.reaction_15m),
      reaction30m: nullableNumber(raw?.reaction30m ?? raw?.reaction_30m),
      reaction60m: nullableNumber(raw?.reaction60m ?? raw?.reaction_60m),
      reactionClose: nullableNumber(raw?.reactionClose ?? raw?.reaction_close),
      excessSector: nullableNumber(raw?.excessSector ?? raw?.excess_sector),
      turnoverRatio: nullableNumber(raw?.turnoverRatio ?? raw?.turnover_ratio),
      flowStatus: cleanText(raw?.flowStatus ?? raw?.flow_status),
      judgmentSupport: cleanText(raw?.judgmentSupport ?? raw?.judgment_support),
      relatedTickers,
      relatedStockNames,
      relatedStockCount: Math.max(
        relatedTickers.length,
        relatedStockNames.length,
        Math.max(0, Math.round(toNumber(raw?.relatedStockCount ?? raw?.related_stock_count, 0))),
      ),
      stockLinkAuthority,
      stockLinkInferred,
      realtime: normalizeBoolean(raw?.realtime ?? raw?.isRealtime ?? raw?.is_realtime),
      ageMinutes: nullableNumber(raw?.ageMinutes ?? raw?.age_minutes),
      url,
      sourceUrlAvailable,
    };
  }).filter((item: LiveIssue) => item.title && isIssueFeedDisplayable(item));

  const merged = new Map<string, LiveIssue>();
  normalized.forEach((issue) => {
    const key = normalizeIssueDedupKey(issue);
    const current = merged.get(key);
    if (!current) {
      merged.set(key, { ...issue });
      return;
    }

    current.duplicateCount += 1;
    current.sources = Array.from(new Set([...current.sources, ...issue.sources])).filter(Boolean);
    current.relatedTickers = Array.from(new Set([...current.relatedTickers, ...issue.relatedTickers]));
    current.relatedStockNames = Array.from(new Set([...current.relatedStockNames, ...issue.relatedStockNames]));
    current.industrySectorNames = Array.from(new Set([...current.industrySectorNames, ...issue.industrySectorNames])).slice(0, 4);
    current.marketGroupNames = Array.from(new Set([...current.marketGroupNames, ...issue.marketGroupNames])).slice(0, 3);
    if (!current.sectorImpact && issue.sectorImpact) current.sectorImpact = issue.sectorImpact;
    if (!current.movementAttribution && issue.movementAttribution) current.movementAttribution = issue.movementAttribution;
    current.relatedStockCount = Math.max(
      current.relatedStockCount,
      issue.relatedStockCount,
      current.relatedTickers.length,
      current.relatedStockNames.length,
    );
    current.realtime = current.realtime || issue.realtime;
    current.importance = Math.max(current.importance ?? 0, issue.importance ?? 0) || null;
    current.impactScore = Math.max(current.impactScore ?? 0, issue.impactScore ?? 0) || null;

    if (!current.summary && issue.summary) current.summary = issue.summary;
    if ((!current.sourceUrlAvailable && issue.sourceUrlAvailable) || (!current.url && issue.url)) {
      current.url = issue.url;
      current.sourceUrlAvailable = issue.sourceUrlAvailable;
    }
    if (!current.source && issue.source) current.source = issue.source;
    if (!current.formatProfile && issue.formatProfile) current.formatProfile = issue.formatProfile;
    if (!current.formatType && issue.formatType) current.formatType = issue.formatType;
    if (!current.previewSource && issue.previewSource) current.previewSource = issue.previewSource;
    if (!current.sourceType && issue.sourceType) current.sourceType = issue.sourceType;
    if (!current.telegramChannel && issue.telegramChannel) current.telegramChannel = issue.telegramChannel;
    if (!current.structuredUnits.length && issue.structuredUnits.length) {
      current.structuredUnits = issue.structuredUnits;
      current.structuredUnitCount = issue.structuredUnitCount;
    }
    if (current.impactScore === null || (issue.impactScore !== null && issue.impactScore > current.impactScore)) {
      current.impactScore = issue.impactScore;
    }
    if (current.ageMinutes === null || (issue.ageMinutes !== null && issue.ageMinutes < current.ageMinutes)) {
      current.ageMinutes = issue.ageMinutes;
    }
  });

  return Array.from(merged.values()).sort((a, b) => {
    // Keep the issue feed stable by publication time/feed id.
    // LIVE freshness and impact score are display metadata, not retention/ranking keys.
    const timeGap = issueEpoch(b.publishedAt) - issueEpoch(a.publishedAt);
    if (timeGap !== 0) return timeGap;
    const feedGap = (b.feedId ?? -1) - (a.feedId ?? -1);
    if (feedGap !== 0) return feedGap;
    return b.id.localeCompare(a.id);
  });
}

async function fetchStockLiveIssues (ticker: string, signal?: AbortSignal): Promise<LiveIssue[]> {
  const normalizedTicker = cleanText(ticker);
  if (!/^\d{6}$/.test(normalizedTicker)) return [];

  // IMPORTANT: use the shared axios instance rather than native fetch.
  // axiosConfig owns the application's auth header / refresh-token interceptor.
  // Native fetch with credentials:'include' only forwards cookies and therefore
  // receives 401 when /api/live-summary/** is protected by bearer authentication.
  const response = await api.get<unknown[]>(
    `/live-summary/stocks/${encodeURIComponent(normalizedTicker)}/issues`,
    {
      params: { limit: 20 },
      withCredentials: true,
      timeout: 5_000,
      signal,
    },
  );
  return normalizeLiveIssues(response.data).slice(0, 20);
}

type LiveSummaryState = {
  market: NormalizedMarketState;
  marketContext: NormalizedMarketContext;
  liveIssueSignal: NormalizedLiveIssueSignal;
  intelligenceBridge: IntelBridge | null;
  decisionBrief: DecisionBriefV1 | null;
  optionResearch: OptionResearch | null;
  opportunities: NormalizedOpportunity[];
  attentionBanner: NormalizedAttentionBanner;
  sectors: NormalizedSector[];
  themes: NormalizedThemeFlow[];
  holdings: NormalizedHolding[];
  alerts: NormalizedAlert[];
  issues: LiveIssue[];
  changeEvents: NormalizedChangeEvent[];
  sourceStatus: SourceStatus;
  marketSession: string;
  marketOpen: boolean;
  refreshedAt: string;
  cacheTtlSeconds: number;
};

type LiveSummaryProductAccessMode = 'FULL_PREVIEW' | 'ENFORCED';
type LiveSummaryPremiumFeature =
  | 'ADVANCED_MARKET_DETAIL'
  | 'CHANGE_HISTORY'
  | 'VALUATION_TRACKING'
  | 'BUSINESS_EVENT_TRACKING'
  | 'VERIFICATION_FINANCIAL'
  | 'NOTIFICATION_TRACKING';

type LiveSummaryProductAccess = {
  mode: LiveSummaryProductAccessMode;
  proEntitled: boolean;
};

const LIVE_SUMMARY_PRODUCT_ROLLOUT = {
  currentMode: 'FULL_PREVIEW' as LiveSummaryProductAccessMode,
  paidEnforcementFrom: '2026-11-01',
  note: 'October: every authenticated user receives the full PRO-shaped experience. November: switch to ENFORCED and pass the real subscription entitlement.',
};

const LIVE_SUMMARY_PREMIUM_FEATURE_COPY: Record<LiveSummaryPremiumFeature, { title: string; description: string }> = {
  ADVANCED_MARKET_DETAIL: { title: '가격·수급 상세', description: '실시간 가격 위치와 수급 변화를 함께 확인합니다.' },
  CHANGE_HISTORY: { title: '변화 이력', description: '현재 판단이 어떻게 바뀌었는지 시간순으로 추적합니다.' },
  VALUATION_TRACKING: { title: '가치평가 변화 추적', description: '적정가와 가치평가 기준 변화의 원인을 추적합니다.' },
  BUSINESS_EVENT_TRACKING: { title: '사업·이벤트 변화', description: '판단에 직접 연결된 사업·산업 이벤트를 추적합니다.' },
  VERIFICATION_FINANCIAL: { title: '검증·재무 상세', description: '과거 검증, 위험 근거, 재무·가치 상세를 확인합니다.' },
  NOTIFICATION_TRACKING: { title: '지속 변화 감시', description: '종목의 중요한 변화와 판단 변경을 계속 추적합니다.' },
};

function liveSummaryFeatureAllowed(access: LiveSummaryProductAccess, _feature: LiveSummaryPremiumFeature): boolean {
  return access.mode === 'FULL_PREVIEW' || access.proEntitled;
}

function PremiumFeatureBoundary({
  access,
  feature,
  ticker,
  children,
}: {
  access: LiveSummaryProductAccess;
  feature: LiveSummaryPremiumFeature;
  ticker?: string;
  children: React.ReactNode;
}) {
  const allowed = liveSummaryFeatureAllowed(access, feature);
  const trackedRef = useRef(false);

  useEffect(() => {
    if (allowed || trackedRef.current) return;
    trackedRef.current = true;
    trackUxEvent('pro_offer_view', {
      source: 'live-summary-feature-boundary',
      feature,
      ticker: ticker || undefined,
      rollout_mode: access.mode,
    });
  }, [access.mode, allowed, feature, ticker]);

  if (allowed) return <>{children}</>;
  const copy = LIVE_SUMMARY_PREMIUM_FEATURE_COPY[feature];
  return (
    <section className="live-summary-pro-boundary" data-pro-feature={feature}>
      <span>PRO</span>
      <b>{copy.title}</b>
    </section>
  );
}

type LiveSummaryDockProps = {
  enabled: boolean;
  userEmail?: string;
  defaultOpen?: boolean;
  standalone?: boolean;
  embedded?: boolean;
  authenticated?: boolean;
  productAccessMode?: LiveSummaryProductAccessMode;
  proEntitled?: boolean;
  onRequireLogin?: (tab: Extract<LiveSummaryTab, 'STOCK' | 'HOLDING' | 'ALERT'>) => void;
};

const REFRESH_INTERVAL_MS = 2_000;
const MIN_REFRESH_GAP_MS = 1_500;
const LIVE_SUMMARY_TIMEOUT_MS = 12_000;
const LIVE_SUMMARY_ISSUES_REFRESH_MS = 5_000;
const LIVE_SUMMARY_ISSUES_TIMEOUT_MS = 15_000;
const LIVE_SUMMARY_ISSUE_PAGE_SIZE = 30;
const LIVE_SUMMARY_ISSUE_SEARCH_DEBOUNCE_MS = 300;
const LIVE_SUMMARY_SCANNER_ACTIVITY_REFRESH_MS = 10_000;
const LIVE_SUMMARY_POPUP_NAME = 'policy-intel-live-summary';
const LIVE_SUMMARY_POPUP_PATH = '/live-summary-mini';

const EMPTY_MARKET: NormalizedMarketState = {
  label: 'NEUTRAL',
  score: null,
  tone: 'neutral',
  phase: 'PRE_MARKET',
  baseDate: '',
  generatedAt: '',
  topEtfName: '',
  topEtfTicker: '',
  topSectorCode: '',
  topScore: null,
  comment: '시장 신호를 확인하고 있습니다.',
};

const emptyIndexPoint = (label: string): NormalizedMarketIndexPoint => ({
  label, value: null, changeValue: null, changeRate: null, updatedAt: '',
  delayMinutes: null, stale: true, provider: 'KIS',
});

const emptyDerivativePoint = (label: string, provider: string): NormalizedDerivativePoint => ({
  label, futuresPrice: null, futuresChangeRate: null, marketBasis: null,
  basisChange5m: null, basisChange15m: null, oiChangeCumulative: null, oiChange5m: null,
  updatedAt: '', delayMinutes: null, stale: true, structure: 'MIXED',
  structureLabel: '판단 대기', directionState: 'NO_DATA', directionLabel: '계산 대기',
  directionDataStatus: 'NO_DATA', futuresReturn5m: null, futuresReturn15m: null,
  spotReturn5m: null, spotReturn15m: null, futuresLead5m: null, futuresLead15m: null,
  oiState: 'NO_DATA', oi5mState: 'NO_DATA', foreignNetContracts: null,
  institutionNetContracts: null, foreignAlignment: 'NO_DATA', participantFlowObservedAt: '',
  provider,
});

const emptyGlobalPoint = (label: string): NormalizedGlobalPoint => ({
  label, last: null, changeRate: null, provider: 'YAHOO_CHART_5M',
});

const EMPTY_MARKET_CONTEXT: NormalizedMarketContext = {
  available: false,
  dataStatus: 'NO_DATA',
  updatedAt: '',
  stale: true,
  headline: '장중 시장 데이터 확인 중',
  description: '국내외 시장 데이터를 불러오고 있습니다.',
  action: '데이터가 쌓일 때까지 종목 수급과 가격 흐름을 우선 확인하세요.',
  tone: 'neutral',
  alertBadge: '계산 중',
  transitionActive: false,
  transitionHeadline: '',
  transitionDescription: '',
  transitionTone: 'neutral',
  marketPressureScore: null,
  flowQualityScore: null,
  priceStretchScore: null,
  reversalWatchScore: null,
  positionFlowScore: null,
  basisFlowScore: null,
  confidenceScore: null,
  confidenceLabel: '확인 중',
  pressureLabel: '계산 중',
  positionFlowLabel: '계산 중',
  basisFlowLabel: '계산 중',
  qualityLabel: '계산 중',
  riskLabel: '계산 중',
  watchLabel: '계산 중',
  checkpoints: ['KOSPI·KOSDAQ 갱신 확인', 'KIS 파생 갱신 확인', '글로벌 스냅샷 갱신 확인'],
  intradayScore: null,
  intradayDirection: '계산 중',
  intradayConfidence: 'LOW',
  intradayCoveragePct: null,
  riskRealtimeScore: null,
  domesticIndices: {
    KOSPI: emptyIndexPoint('KOSPI'),
    KOSDAQ: emptyIndexPoint('KOSDAQ'),
    KOSPI200: emptyIndexPoint('KOSPI200'),
    KOSDAQ150: emptyIndexPoint('KOSDAQ150'),
  },
  derivatives: {
    KOSPI200: emptyDerivativePoint('KOSPI200 선물', 'KIS_H0IFCNT0'),
    KOSDAQ150: emptyDerivativePoint('KOSDAQ150 선물', 'KIS_FUTURES_REST'),
  },
  globalMarkets: {
    USDKRW: emptyGlobalPoint('USD/KRW'),
    DXY: emptyGlobalPoint('DXY'),
    NQ: emptyGlobalPoint('Nasdaq100 선물'),
    ES: emptyGlobalPoint('S&P500 선물'),
    YM: emptyGlobalPoint('Dow 선물'),
    RTY: emptyGlobalPoint('Russell2000 선물'),
    US10Y_FUT: emptyGlobalPoint('미국채 10Y 선물'),
    VIX: emptyGlobalPoint('VIX'),
  },
  sourcePolicy: { domestic: 'KIS', global: 'YAHOO_CHART_5M', treasury: 'US_TREASURY' },
  metricGuides: {},
};

const EMPTY_ATTENTION_BANNER: NormalizedAttentionBanner = {
  visible: false, severity: 'INFO', title: '', message: '', ticker: '', name: '',
  actionTab: 'ALERTS', tone: 'neutral',
};

const EMPTY_LIVE_ISSUE_SIGNAL: NormalizedLiveIssueSignal = {
  status: 'NO_DATA',
  windowStart: '',
  windowEnd: '',
  sourceMessageCount: 0,
  candidateMessageCount: 0,
  newSignalCount: 0,
  highImportanceCount: 0,
  reason: '',
  signals: [],
  brief: null,
  capitalScenario: null,
  p1Shadow: null,
};

const EMPTY_STATE: LiveSummaryState = {
  market: EMPTY_MARKET,
  marketContext: EMPTY_MARKET_CONTEXT,
  liveIssueSignal: EMPTY_LIVE_ISSUE_SIGNAL,
  intelligenceBridge: null,
  decisionBrief: null,
  optionResearch: null,
  opportunities: [],
  attentionBanner: EMPTY_ATTENTION_BANNER,
  sectors: [],
  themes: [],
  holdings: [],
  alerts: [{ level: 'info', message: '실시간 요약 데이터를 불러오는 중입니다.' }],
  issues: [],
  changeEvents: [],
  sourceStatus: 'idle',
  marketSession: 'PRE_MARKET',
  marketOpen: false,
  refreshedAt: '',
  cacheTtlSeconds: 5,
};

function cleanText(value: unknown): string {
  return String(value ?? '')
    .replace(/Â·/g, '·')
    .replace(/Â\u00a0/g, ' ')
    .replace(/â€¢/g, '•')
    .replace(/â†’/g, '→')
    .replace(/â€“/g, '–')
    .replace(/â€”/g, '—')
    .replace(/â€¦/g, '…')
    .replace(/âˆ’/g, '−')
    .replace(/Ã—/g, '×')
    .replace(/\s+/g, ' ')
    .trim();
}

function resolveStockDisplayName(source: Record<string, unknown>, ticker: string): string {
  const candidates = [
    source.name,
    source.corpName,
    source.corp_name,
    source.stockName,
    source.stock_name,
    source.tickerName,
    source.ticker_name,
    source.companyName,
    source.company_name,
  ];

  for (const candidate of candidates) {
    const value = cleanText(candidate);
    if (!value) continue;
    if (value === ticker || /^\d{6}$/.test(value)) continue;
    return value;
  }

  // Canonical company names are supplied by the backend. If every candidate is
  // missing or itself a six-digit code, keep only the ticker as a fail-closed fallback.
  return ticker;
}

function parseIssueStructuredUnits(value: unknown): LiveIssueStructuredUnit[] {
  let parsed: unknown = value;
  if (typeof parsed === 'string') {
    const raw = parsed.trim();
    if (!raw || raw === 'null') return [];
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  const container = parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
  const units = Array.isArray(parsed)
    ? parsed
    : Array.isArray(container.units)
      ? container.units
      : [];

  return units.map((item): LiveIssueStructuredUnit | null => {
    if (!item || typeof item !== 'object') return null;
    const row = item as Record<string, unknown>;
    const unit = {
      ticker: cleanText(row.ticker).toUpperCase(),
      company: cleanText(row.company),
      drug: cleanText(row.drug),
      stage: cleanText(row.stage),
      catalyst: cleanText(row.catalyst),
    };
    return unit.ticker || unit.company || unit.drug || unit.catalyst ? unit : null;
  }).filter((item): item is LiveIssueStructuredUnit => Boolean(item)).slice(0, 12);
}


function parseIssueStructuredUnitCount(value: unknown, units: LiveIssueStructuredUnit[]): number {
  let parsed: unknown = value;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { return units.length; }
  }
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const count = nullableNumber((parsed as Record<string, unknown>).unit_count);
    if (count !== null) return Math.max(units.length, Math.round(count));
  }
  return units.length;
}

function issueStructuredPreview(issue: Pick<LiveIssue, 'structuredUnits' | 'structuredUnitCount'>): string {
  if (!issue.structuredUnits.length) return '';
  const labels = issue.structuredUnits.slice(0, 3).map((unit) => {
    const identity = unit.ticker || unit.company;
    return [identity, unit.stage].filter(Boolean).join(' ');
  }).filter(Boolean);
  const total = Math.max(issue.structuredUnitCount, issue.structuredUnits.length);
  return `${total}개 파이프라인${labels.length ? ` · ${labels.join(' · ')}` : ''}`;
}

function issueSummaryForDisplay(title: unknown, summary: unknown): string {
  const rawTitle = cleanText(title);
  const rawSummary = cleanText(summary);
  if (!rawSummary) return '';

  // Source summaries sometimes repeat the headline verbatim and append only a URL.
  // Do not show that duplicate line in the compact Change tab.
  const stripDecorations = (value: string) => cleanText(
    value
      .replace(/https?:\/\/\S+/gi, ' ')
      .replace(/^[✅☑️✔️\s]+/u, '')
      .replace(/\s+/g, ' '),
  );
  const titleText = stripDecorations(rawTitle);
  const summaryText = stripDecorations(rawSummary);

  if (!summaryText || summaryText === titleText) return '';
  if (titleText && summaryText.startsWith(titleText)) {
    return cleanText(summaryText.slice(titleText.length).replace(/^[-–—:·|]+/, ''));
  }
  return summaryText;
}


function isHighPriorityIssue(issue: Pick<LiveIssue, 'importance' | 'impactScore'>): boolean {
  const importance = issue.importance ?? 0;
  const impactScore = issue.impactScore ?? 0;
  return importance >= 4 || impactScore >= 60;
}

function isIssueFeedDisplayable(issue: Pick<LiveIssue, 'title'>): boolean {
  // The full issue feed is recall-first. Wire feeds and link-preview channels often
  // have a complete factual headline with no distinct body; summary presence must
  // never decide whether the row survives. 10-minute Event Signal keeps its own
  // stricter precision gate.
  const title = cleanText(issue.title);
  return Boolean(title && title !== '제목 없는 이슈' && !/^https?:\/\//i.test(title));
}

function toNumber(value: unknown, fallback = Number.NaN): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/,/g, '').trim());
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function nullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return null;
  const parsed = toNumber(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function nullableAbsolutePrice(value: unknown): number | null {
  const parsed = nullableNumber(value);
  return parsed === null ? null : Math.abs(parsed);
}

function normalizeBoolean(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || cleanText(value).toLowerCase() === 'true';
}

function normalizeTone(value: unknown, fallback: Tone = 'neutral'): Tone {
  const raw = cleanText(value).toLowerCase();
  return raw === 'up' || raw === 'down' || raw === 'neutral' || raw === 'warn' || raw === 'good'
    ? raw
    : fallback;
}




function formatPct(value: unknown, digits = 2): string {
  const parsed = nullableNumber(value);
  if (parsed === null) return '-';
  return `${parsed > 0 ? '+' : ''}${parsed.toFixed(digits)}%`;
}


function formatPrice(value: unknown): string {
  const parsed = toNumber(value);
  return Number.isFinite(parsed) ? Math.round(parsed).toLocaleString('ko-KR') : '-';
}

function formatMarketValue(value: unknown, digits = 2): string {
  const parsed = nullableNumber(value);
  if (parsed === null) return '-';
  return parsed.toLocaleString('ko-KR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function formatTradingValue(value: unknown): string {
  const parsed = toNumber(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return '-';
  const eok = parsed / 100_000_000;
  if (eok >= 10_000) return `${(eok / 10_000).toFixed(eok >= 100_000 ? 0 : 1)}조`;
  if (eok >= 1) return `${eok.toFixed(eok >= 100 ? 0 : 1)}억`;
  return `${Math.round(parsed).toLocaleString('ko-KR')}원`;
}

function formatSignedMoney(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '-';
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${formatTradingValue(Math.abs(value))}`;
}



function formatSignedNumber(value: number | null, digits = 2): string {
  if (value === null || !Number.isFinite(value)) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(digits)}`;
}

function formatContractQty(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '-';
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${Math.abs(Math.round(value)).toLocaleString('ko-KR')}계약`;
}



function flowTone(value: number | null): Tone {
  if (value === null || value === 0) return 'neutral';
  return value > 0 ? 'up' : 'down';
}

type PanelIconName = 'open' | 'panel' | 'refresh' | 'minimize' | 'maximize' | 'close';

function PanelIcon({name}: {name: PanelIconName}) {
  if (name === 'open') {
    return (
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <path d="M7 5h8v8" />
        <path d="M15 5 6 14" />
      </svg>
    );
  }
  if (name === 'panel') {
    return (
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <rect x="3.5" y="4" width="13" height="12" rx="2" />
        <path d="M7.5 4v12" />
        <path d="M10.5 7.5h3" />
        <path d="M10.5 10h3" />
        <path d="M10.5 12.5h2" />
      </svg>
    );
  }
  if (name === 'refresh') {
    return (
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <path d="M15.5 7.2A6 6 0 1 0 16 12" />
        <path d="M15.5 3.8v3.7h-3.7" />
      </svg>
    );
  }
  if (name === 'minimize') {
    return (
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <path d="M5 10h10" />
      </svg>
    );
  }
  if (name === 'maximize') {
    return (
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <rect x="5" y="5" width="10" height="10" rx="1.5" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="m6 6 8 8" />
      <path d="m14 6-8 8" />
    </svg>
  );
}

function normalizeFlowFields(item: Record<string, unknown>) {
  const pick = (...values: unknown[]) => values.find((value) => value !== undefined && value !== null);

  const programNetBuyExecAmount = nullableNumber(
    pick(item.programNetBuyExecAmount, item.program_net_buy_exec_amount),
  );
  const programNetBuyToTradingValuePct = nullableNumber(
    pick(item.programNetBuyToTradingValuePct, item.program_net_buy_to_trading_value_pct),
  );

  const foreignNetBuyAmount = nullableNumber(
    pick(item.foreignNetBuyAmount, item.foreign_net_buy_amount),
  );
  const institutionNetBuyAmount = nullableNumber(
    pick(item.institutionNetBuyAmount, item.institution_net_buy_amount),
  );
  const estimateForeignNetBuyQty = nullableNumber(
    pick(item.estimateForeignNetBuyQty, item.estimate_foreign_net_buy_qty),
  );
  const estimateInstitutionNetBuyQty = nullableNumber(
    pick(item.estimateInstitutionNetBuyQty, item.estimate_institution_net_buy_qty),
  );

  const programMatched =
    normalizeBoolean(pick(item.programMatched, item.program_matched)) ||
    programNetBuyExecAmount !== null;

  const investorAggregateMatched =
    normalizeBoolean(pick(item.investorAggregateMatched, item.investor_aggregate_matched)) ||
    foreignNetBuyAmount !== null ||
    institutionNetBuyAmount !== null;

  const investorEstimateMatched =
    normalizeBoolean(pick(item.investorEstimateMatched, item.investor_estimate_matched)) ||
    estimateForeignNetBuyQty !== null ||
    estimateInstitutionNetBuyQty !== null;

  const smartMoneyNetBuyAmount =
    foreignNetBuyAmount !== null && institutionNetBuyAmount !== null
      ? foreignNetBuyAmount + institutionNetBuyAmount
      : null;

  const estimateSmartMoneyNetBuyQty =
    nullableNumber(pick(item.estimateSmartMoneyNetBuyQty, item.estimate_smart_money_net_buy_qty)) ??
    (estimateForeignNetBuyQty !== null || estimateInstitutionNetBuyQty !== null
      ? (estimateForeignNetBuyQty ?? 0) + (estimateInstitutionNetBuyQty ?? 0)
      : null);

  const investorFlowStatus =
    cleanText(pick(item.investorFlowStatus, item.investor_flow_status)) ||
    (investorAggregateMatched && investorEstimateMatched
      ? 'AGGREGATE_AND_ESTIMATE'
      : investorAggregateMatched
        ? 'AGGREGATE_ONLY'
        : investorEstimateMatched
          ? 'ESTIMATE_ONLY'
          : 'NO_DATA');

  return {
    programMatched,
    programSessionDate: cleanText(
      pick(item.programSessionDate, item.program_session_date),
    ),
    programSessionCurrent: normalizeBoolean(
      pick(item.programSessionCurrent, item.program_session_current),
    ),
    programNetBuyExecAmount,
    programNetBuyToTradingValuePct,
    programReceivedAt: cleanText(
      pick(item.programReceivedAt, item.program_received_at),
    ),
    investorSessionDate: cleanText(
      pick(item.investorSessionDate, item.investor_session_date),
    ),
    investorSessionCurrent: normalizeBoolean(
      pick(item.investorSessionCurrent, item.investor_session_current),
    ),
    investorAggregateMatched,
    investorEstimateMatched,
    investorFlowStatus,
    foreignNetBuyAmount,
    institutionNetBuyAmount,
    smartMoneyNetBuyAmount,
    estimateForeignNetBuyQty,
    estimateInstitutionNetBuyQty,
    estimateSmartMoneyNetBuyQty,
    investorEstimateBucket: cleanText(
      pick(item.investorEstimateBucket, item.investor_estimate_bucket),
    ),
    investorAggregateEventAt: cleanText(
      pick(item.investorAggregateEventAt, item.investor_aggregate_event_at),
    ),
    investorEstimateEventAt: cleanText(
      pick(item.investorEstimateEventAt, item.investor_estimate_event_at),
    ),
    investorReceivedAt: cleanText(
      pick(item.investorReceivedAt, item.investor_received_at),
    ),
    investorTrendDirection:
      cleanText(pick(item.investorTrendDirection, item.investor_trend_direction)) ||
      'INSUFFICIENT_DATA',
    investorTrendStreakMinutes: Math.max(
      0,
      Math.round(
        toNumber(
          pick(item.investorTrendStreakMinutes, item.investor_trend_streak_minutes),
          0,
        ),
      ),
    ),
    investorTrendDelta1m: nullableNumber(
      pick(item.investorTrendDelta1m, item.investor_trend_delta_1m),
    ),
    investorTrendDelta5m: nullableNumber(
      pick(item.investorTrendDelta5m, item.investor_trend_delta_5m),
    ),
    investorTrendBaseline1m: nullableNumber(
      pick(item.investorTrendBaseline1m, item.investor_trend_baseline_1m),
    ),
    investorTrendVelocityRatio: nullableNumber(
      pick(item.investorTrendVelocityRatio, item.investor_trend_velocity_ratio),
    ),
    investorTrendAccelerationRatio: nullableNumber(
      pick(item.investorTrendAccelerationRatio, item.investor_trend_acceleration_ratio),
    ),
    investorTrendBaselinePointCount: Math.max(
      0,
      Math.round(toNumber(
        pick(item.investorTrendBaselinePointCount, item.investor_trend_baseline_point_count),
        0,
      )),
    ),
    investorTrendTurnedAt: cleanText(
      pick(item.investorTrendTurnedAt, item.investor_trend_turned_at),
    ),
    investorTrendUpdatedAt: cleanText(
      pick(item.investorTrendUpdatedAt, item.investor_trend_updated_at),
    ),
    investorTrendLeader:
      cleanText(pick(item.investorTrendLeader, item.investor_trend_leader)) ||
      'BALANCED',
    investorTrendConfidence:
      cleanText(pick(item.investorTrendConfidence, item.investor_trend_confidence)) ||
      'LOW',
    investorTrendPointCount: Math.max(
      0,
      Math.round(
        toNumber(
          pick(item.investorTrendPointCount, item.investor_trend_point_count),
          0,
        ),
      ),
    ),
    flowDecision:
      cleanText(pick(item.flowDecision, item.flow_decision)) || '👀 관찰',
    flowDecisionTone: normalizeTone(
      pick(item.flowDecisionTone, item.flow_decision_tone),
      'neutral',
    ),
    flowReason:
      cleanText(pick(item.flowReason, item.flow_reason)),
  };
}


function normalizeFundamentalFields(item: Record<string, unknown>): NormalizedFundamentalSnapshot {
  const pick = (...values: unknown[]) =>
    values.find((value) => value !== undefined && value !== null);

  const reasonsRaw = pick(item.fundamentalReasons, item.fundamental_reasons);
  const fundamentalReasons = Array.isArray(reasonsRaw)
    ? reasonsRaw.map(cleanText).filter(Boolean).slice(0, 3)
    : [];

  return {
    fundamentalAvailable: normalizeBoolean(
      pick(item.fundamentalAvailable, item.fundamental_available),
    ),
    fundamentalSnapshotDate: cleanText(
      pick(item.fundamentalSnapshotDate, item.fundamental_snapshot_date),
    ),
    fundamentalSourceTradeDate: cleanText(
      pick(item.fundamentalSourceTradeDate, item.fundamental_source_trade_date),
    ),
    fundamentalFinancialBasisText: cleanText(
      pick(item.fundamentalFinancialBasisText, item.fundamental_financial_basis_text),
    ),
    fundamentalFinancialWarningText: cleanText(
      pick(item.fundamentalFinancialWarningText, item.fundamental_financial_warning_text),
    ),
    fundamentalScore: nullableNumber(
      pick(item.fundamentalScore, item.fundamental_score),
    ),
    fundamentalQualityScore: nullableNumber(
      pick(item.fundamentalQualityScore, item.fundamental_quality_score),
    ),
    fundamentalGrowthScore: nullableNumber(
      pick(item.fundamentalGrowthScore, item.fundamental_growth_score),
    ),
    fundamentalDirectionCode: cleanText(
      pick(item.fundamentalDirectionCode, item.fundamental_direction_code),
    ).toUpperCase(),
    fundamentalDirectionLabel:
      cleanText(pick(item.fundamentalDirectionLabel, item.fundamental_direction_label))
      || '재무 확인 중',
    fundamentalDirectionTone: normalizeTone(
      pick(item.fundamentalDirectionTone, item.fundamental_direction_tone),
      'neutral',
    ),
    fundamentalValuationScore: nullableNumber(
      pick(item.fundamentalValuationScore, item.fundamental_valuation_score),
    ),
    fundamentalValuationCode: cleanText(
      pick(item.fundamentalValuationCode, item.fundamental_valuation_code),
    ).toUpperCase(),
    fundamentalValuationLabel:
      cleanText(pick(item.fundamentalValuationLabel, item.fundamental_valuation_label))
      || '가치 확인 중',
    fundamentalValuationTone: normalizeTone(
      pick(item.fundamentalValuationTone, item.fundamental_valuation_tone),
      'neutral',
    ),
    fundamentalValuationStatus: cleanText(
      pick(item.fundamentalValuationStatus, item.fundamental_valuation_status),
    ),
    fundamentalSummary:
      cleanText(pick(item.fundamentalSummary, item.fundamental_summary))
      || '재무·가치 스냅샷 확인 중',
    fundamentalReasons,
    fundamentalRevenueCagr3yPct: nullableNumber(
      pick(item.fundamentalRevenueCagr3yPct, item.fundamental_revenue_cagr_3y_pct),
    ),
    fundamentalOperatingMarginPct: nullableNumber(
      pick(item.fundamentalOperatingMarginPct, item.fundamental_operating_margin_pct),
    ),
    fundamentalRoePct: nullableNumber(
      pick(item.fundamentalRoePct, item.fundamental_roe_pct),
    ),
    fundamentalRoicWaccSpreadPctp: nullableNumber(
      pick(item.fundamentalRoicWaccSpreadPctp, item.fundamental_roic_wacc_spread_pctp),
    ),
    fundamentalNetDebtToOperatingIncome: nullableNumber(
      pick(
        item.fundamentalNetDebtToOperatingIncome,
        item.fundamental_net_debt_to_operating_income,
      ),
    ),
    fundamentalFcfYieldPct: nullableNumber(
      pick(item.fundamentalFcfYieldPct, item.fundamental_fcf_yield_pct),
    ),
    fundamentalPer: nullableNumber(
      pick(item.fundamentalPer, item.fundamental_per),
    ),
    fundamentalPbr: nullableNumber(
      pick(item.fundamentalPbr, item.fundamental_pbr),
    ),
    fundamentalPegRatio: nullableNumber(
      pick(item.fundamentalPegRatio, item.fundamental_peg_ratio),
    ),
  };
}

function normalizeStockRiskFields(item: Record<string, unknown>): NormalizedStockRisk {
  const pick = (...values: unknown[]) =>
    values.find((value) => value !== undefined && value !== null);

  const reasonsRaw = pick(item.riskReasons, item.risk_reasons);
  const riskReasons = Array.isArray(reasonsRaw)
    ? reasonsRaw.map(cleanText).filter(Boolean).slice(0, 5)
    : [];

  return {
    marketType: cleanText(pick(item.marketType, item.market_type)),
    marketLabel: cleanText(pick(item.marketLabel, item.market_label)),
    riskAvailable: normalizeBoolean(pick(item.riskAvailable, item.risk_available)),
    riskStale: normalizeBoolean(pick(item.riskStale, item.risk_stale)),
    riskScore: nullableNumber(pick(item.riskScore, item.risk_score)),
    riskLevel: cleanText(pick(item.riskLevel, item.risk_level)) || 'NORMAL',
    riskLabel: cleanText(pick(item.riskLabel, item.risk_label)) || '확인 중',
    riskTone: normalizeTone(pick(item.riskTone, item.risk_tone), 'neutral'),
    riskConfidence: cleanText(pick(item.riskConfidence, item.risk_confidence)) || 'LOW',
    riskCalculationVersion: cleanText(pick(item.riskCalculationVersion, item.risk_calculation_version)),
    riskDerivativeAvailable: normalizeBoolean(pick(item.riskDerivativeAvailable, item.risk_derivative_available)),
    marketDerivativeRisk: nullableNumber(pick(item.marketDerivativeRisk, item.market_derivative_risk)),
    priceRisk: nullableNumber(pick(item.priceRisk, item.price_risk)),
    programRisk: nullableNumber(pick(item.programRisk, item.program_risk)),
    foreignRisk: nullableNumber(pick(item.foreignRisk, item.foreign_risk)),
    institutionRisk: nullableNumber(pick(item.institutionRisk, item.institution_risk)),
    marketSensitivityRisk: nullableNumber(pick(item.marketSensitivityRisk, item.market_sensitivity_risk)),
    marketSensitivity: nullableNumber(pick(item.marketSensitivity, item.market_sensitivity)),
    riskDerivativeStructure: cleanText(pick(item.riskDerivativeStructure, item.risk_derivative_structure)) || 'NO_DATA',
    riskDerivativeStructureLabel: cleanText(pick(item.riskDerivativeStructureLabel, item.risk_derivative_structure_label)) || '파생 확인 중',
    riskFuturesChangeRate: nullableNumber(pick(item.riskFuturesChangeRate, item.risk_futures_change_rate)),
    riskMarketBasis: nullableNumber(pick(item.riskMarketBasis, item.risk_market_basis)),
    riskBasisChange5m: nullableNumber(pick(item.riskBasisChange5m, item.risk_basis_change_5m)),
    riskOiChange5m: nullableNumber(pick(item.riskOiChange5m, item.risk_oi_change_5m)),
    riskOiChangeCumulative: nullableNumber(pick(item.riskOiChangeCumulative, item.risk_oi_change_cumulative)),
    riskReasons,
  };
}

function normalizeSessionPriceFields(item: Record<string, unknown>): NormalizedSessionPrice {
  return {
    openPrice: nullableAbsolutePrice(item.openPrice),
    highPrice: nullableAbsolutePrice(item.highPrice),
    lowPrice: nullableAbsolutePrice(item.lowPrice),
    sessionVwap: nullableAbsolutePrice(item.sessionVwap),
    sessionDate: cleanText(item.sessionDate),
    lastEventAt: cleanText(item.lastEventAt),
  };
}

type PlainTone = 'good' | 'up' | 'warn' | 'down' | 'neutral';

function stockStateSummary(item: NormalizedSectorTopStock | NormalizedHolding): {
  eyebrow: string;
  title: string;
  description: string;
  tone: PlainTone;
} {
  // Never convert an absent risk reading into a plausible neutral score.
  const score = item.riskScore;
  if (score === null || !Number.isFinite(score) || !item.riskAvailable || item.riskStale) {
    return {
      eyebrow: '현재 상태',
      title: '위험 판단에 필요한 자료 확인 중',
      description: '위험 점수가 없거나 이전 세션 자료입니다. 수급·가격을 별도로 확인하세요.',
      tone: 'neutral',
    };
  }
  const derivativeWeak = ['BEARISH_BUILD', 'LONG_LIQUIDATION'].includes(item.riskDerivativeStructure);
  const derivativeMixed = ['SHORT_COVERING', 'MIXED', 'NO_DATA'].includes(item.riskDerivativeStructure);
  const flowWeak = item.flowDecisionTone === 'down' || item.flowDecisionTone === 'warn';
  const flowStrong = item.flowDecisionTone === 'up' || item.flowDecisionTone === 'good';

  if (score >= 75) {
    return {
      eyebrow: '현재 상태',
      title: '위험 압력이 높은 상태',
      description: '가격 부담과 수급·시장 신호가 동시에 약해지는지 우선 확인이 필요합니다.',
      tone: 'down',
    };
  }
  if (score >= 60) {
    return {
      eyebrow: '현재 상태',
      title: '주의 신호가 확대된 상태',
      description: derivativeWeak
        ? '시장 선행 흐름과 종목 수급이 함께 약해지는지 확인이 필요합니다.'
        : '위험 점수가 높아 수급 안정과 가격 변동성 완화 여부를 함께 확인합니다.',
      tone: 'down',
    };
  }
  if (score >= 45) {
    return {
      eyebrow: '현재 상태',
      title: flowWeak ? '수급 혼조 · 변화 감시' : '혼조 구간 · 추가 확인',
      description: derivativeMixed
        ? '시장 신호와 종목 수급이 엇갈려 한 방향으로 단정하기 어려운 상태입니다.'
        : '일부 부담 요인이 남아 있어 최근 수급과 가격 위치 변화를 함께 봅니다.',
      tone: 'warn',
    };
  }
  if (score >= 25) {
    return {
      eyebrow: '현재 상태',
      title: flowStrong ? '수급 우호 · 가격 위치 확인' : '중립 범위',
      description: flowStrong
        ? '주요 수급은 우호적이며 가격 부담과 시장 동조 여부를 함께 확인합니다.'
        : '가격과 위험 지표가 중립 범위에 있어 방향 변화 여부를 확인합니다.',
      tone: flowStrong ? 'up' : 'neutral',
    };
  }
  return {
    eyebrow: '현재 상태',
    title: '상태 안정',
    description: '현재 위험 부담은 낮은 편이며 수급과 시장 동조가 유지되는지 확인합니다.',
    tone: 'good',
  };
}















function changeEventAnchorId(eventId: unknown): string {
  const normalized = cleanText(eventId).replace(/[^a-zA-Z0-9_-]/g, '-');
  return normalized ? `live-summary-change-event-${normalized}` : '';
}

function eventTimestampRaw(event: NormalizedChangeEvent): string {
  // sourceEventAt is the market/source timestamp. detectedAt is only the polling detection timestamp.
  return cleanText(event.sourceEventAt || event.detectedAt);
}

function normalizedEventTimestamp(value: string): number | null {
  const raw = cleanText(value);
  if (!raw) return null;
  const isoLike = /^\d{4}-\d{2}-\d{2}\s/.test(raw) ? raw.replace(' ', 'T') : raw;
  const parsed = Date.parse(isoLike);
  return Number.isFinite(parsed) ? parsed : null;
}

const PAID_VALUE_MEASUREMENT_VERSION = 'paid-value-v1';

function koreaTodayKey(at = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at);
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  return `${year}-${month}-${day}`;
}

function eventDateKey(value: string): string {
  const raw = cleanText(value);
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}



function compactChangeEventLabel(event: NormalizedChangeEvent): string {
  const type = cleanText(event.eventType).toUpperCase();
  if (type === 'INVESTOR_FLOW_TURN_POSITIVE') return '수급 회복 전환';
  if (type === 'INVESTOR_FLOW_TURN_NEGATIVE') return '수급 약화 전환';
  if (type === 'FLOW_VELOCITY_SURGE_POSITIVE') return '수급 속도 급증';
  if (type === 'FLOW_VELOCITY_SURGE_NEGATIVE') return '이탈 속도 급증';
  if (type === 'FLOW_DECISION_CHANGED') return cleanText(event.title) || '수급 판단 변경';
  if (type === 'HOLDING_THESIS_CHANGED') return cleanText(event.title) || '보유 논리 변화';
  if (type === 'HOLDING_THESIS_TRIGGERED') return cleanText(event.title) || '보유 논리 재검토 신호';
  if (type === 'HOLDING_MARKET_PRESSURE_CHANGED') return cleanText(event.title) || '시장 압력 변화';
  return cleanText(event.title) || cleanText(event.message) || '상태 변화';
}

function userFacingStateLabel(value: unknown): string {
  const raw = cleanText(value);
  const code = raw.toUpperCase();
  const labels: Record<string, string> = {
    DOWN_TO_UP: '수급 회복',
    UP_TO_DOWN: '수급 약화',
    UP_STREAK: '순매수 흐름 지속',
    DOWN_STREAK: '순매도 흐름 지속',
    FLAT: '수급 중립',
    INSUFFICIENT_DATA: '데이터 확인 중',
    DATA_STALE: '데이터 갱신 필요',
    ENTRY_READY: '진입 조건 충족',
    WAIT: '대기',
    AVOID: '진입 보류',
    HOLD: '보유 유지',
    REDUCE: '방어',
    EXIT: '재검토',
    REVIEW: '재확인',
    STABLE: '안정',
    WATCH: '주의',
    WEAKENING: '약화',
    RECOVERING: '회복',
    CONFIRMED: '조건 확인',
    DATA_LIMITED: '데이터 확인 중',
    PRESERVED: '논리 유지',
    PRESERVED_CAUTION: '논리 유지 · 시장 경계',
    WEAKENED: '논리 약화',
    DAMAGED: '논리 훼손',
    LIMITED: '판단 제한',
    DOWN_STRONG: '하락 압력 강함',
    DOWN: '하락 우위',
    UP: '상승 우위',
    UP_STRONG: '상승 압력 강함',
  };
  return labels[code] || raw.replaceAll('_', ' ');
}

function humanizeChangeMessage(value: unknown): string {
  let out = cleanText(value);
  if (!out) return '';
  const codes = [
    'DOWN_TO_UP', 'UP_TO_DOWN', 'UP_STREAK', 'DOWN_STREAK', 'FLAT',
    'INSUFFICIENT_DATA', 'DATA_STALE', 'ENTRY_READY', 'WAIT', 'AVOID',
    'HOLD', 'REDUCE', 'EXIT', 'REVIEW', 'STABLE', 'WATCH', 'WEAKENING',
    'RECOVERING', 'CONFIRMED', 'DATA_LIMITED',
  ];
  codes.forEach((code) => {
    out = out.replace(new RegExp(code, 'g'), userFacingStateLabel(code));
  });
  return out;
}

function changeTransitionLabel(event: NormalizedChangeEvent): string {
  const previous = cleanText(event.previousValue);
  const current = cleanText(event.currentValue);
  if (previous && current && previous !== current) {
    return `${userFacingStateLabel(previous)} → ${userFacingStateLabel(current)}`;
  }

  const message = cleanText(event.message);
  const match = message.match(/(.+?)에서\s*(.+?)\s*상태로\s*변경/);
  if (match) return `${userFacingStateLabel(match[1])} → ${userFacingStateLabel(match[2])}`;
  return compactChangeEventLabel(event);
}

function changeReasonLabel(event: NormalizedChangeEvent, stockName = ''): string {
  const explicit = event.causeLabels.map(cleanText).filter(Boolean).slice(0, 2);
  if (explicit.length) return explicit.join(' · ');

  const message = humanizeChangeMessage(event.message)
    .replace(/.+?에서\s*.+?\s*상태로\s*변경되었습니다?\.?/g, '')
    .trim();
  if (message) return message;

  const title = cleanText(event.title).replace(cleanText(stockName), '').trim();
  const transition = changeTransitionLabel(event);
  if (title && title !== transition && !/^보유 상태 (변화|악화|개선)$/.test(title)) return title;
  return '변경 원인 검증 중';
}

function materialChangeScore(event: NormalizedChangeEvent): number {
  const type = cleanText(event.eventType).toUpperCase();
  let score = event.severity === 'DANGER' ? 40 : event.severity === 'WARNING' ? 25 : 0;
  if (event.previousValue && event.currentValue && event.previousValue !== event.currentValue) score += 35;
  if (/STATUS|JUDG|THESIS|DECISION|RISK|VALUATION|FLOW|TURN|SURGE|VWAP|PRICE|PRESSURE|REGIME/.test(type)) score += 20;
  if (event.changeValue !== null && Number.isFinite(event.changeValue) && Math.abs(event.changeValue) > 0) score += 5;
  return score;
}

function materialChangeGroupKey(event: NormalizedChangeEvent): string {
  const type = cleanText(event.eventType).toUpperCase();
  if (/JUDG|THESIS|ACTION_DECISION/.test(type)) return 'JUDGMENT';
  if (/MARKET_PRESSURE/.test(type)) return 'MARKET_PRESSURE';
  if (/INVESTOR_FLOW|FLOW_DECISION|FLOW_VELOCITY|SMART_MONEY/.test(type)) return 'FLOW';
  if (/VWAP|PRICE/.test(type)) return 'PRICE';
  if (/RISK|STATUS/.test(type)) return 'RISK';
  if (/VALUATION|FAIR_VALUE/.test(type)) return 'VALUATION';
  if (/ISSUE/.test(type)) return 'ISSUE';
  return type || 'OTHER';
}

function materialChangeIdentity(event: NormalizedChangeEvent): string {
  const group = materialChangeGroupKey(event);

  if (group === 'JUDGMENT') {
    return [
      cleanText(event.entityKey),
      group,
      cleanText(event.previousValue),
      cleanText(event.currentValue),
    ].join('|');
  }

  return [
    cleanText(event.entityKey),
    group,
  ].join('|');
}

function holdingCompanyEvidenceShortLabel(value: string): string {
  const label = cleanText(value);

  if (label === '핵심 회사 근거 유지') return '핵심 근거 유지';
  if (label === '회사 논리 훼손 확인') return '논리 훼손 확인';
  if (label === '회사 근거 약화 징후') return '근거 약화 징후';
  if (label === '회사 판단 근거 부족') return '근거 부족';

  return label
    .replace(/^핵심 회사 근거\s*/, '핵심 근거 ')
    .replace(/^회사 근거\s*/, '')
    .replace(/^회사 판단\s*/, '')
    .trim();
}

function holdingProjectedImpactShortLabel(value: string): string {
  const label = cleanText(value)
    .replace(/^예상 영향\s*/, '')
    .trim();

  return label || '확인 중';
}

function dedupeMaterialChanges(events: NormalizedChangeEvent[], limit = 30): NormalizedChangeEvent[] {
  const seen = new Set<string>();
  const output: NormalizedChangeEvent[] = [];
  events.forEach((event) => {
    if (materialChangeScore(event) < 25) return;
    const key = materialChangeIdentity(event);
    if (seen.has(key)) return;
    seen.add(key);
    output.push(event);
  });
  return output.slice(0, limit);
}










function holdingSmartMoneyAmount(
  item: NormalizedSectorTopStock | NormalizedHolding,
): number | null {
  if (item.foreignNetBuyAmount === null || item.institutionNetBuyAmount === null) {
    return null;
  }
  return item.foreignNetBuyAmount + item.institutionNetBuyAmount;
}


type FlowDirectionMeta = {
  symbol: '▲' | '▼' | '•';
  label: '순매수' | '순매도' | '중립' | '확인 중';
  tone: Tone;
};

function flowDirectionMeta(amount: number | null, stale = false): FlowDirectionMeta {
  if (stale || amount === null || !Number.isFinite(amount)) {
    return { symbol: '•', label: '확인 중', tone: 'neutral' };
  }
  if (amount > 0) return { symbol: '▲', label: '순매수', tone: 'up' };
  if (amount < 0) return { symbol: '▼', label: '순매도', tone: 'down' };
  return { symbol: '•', label: '중립', tone: 'neutral' };
}

function hasCurrentFlowAuthority(
  item: NormalizedSectorTopStock | NormalizedHolding,
): boolean {
  return item.programSessionCurrent
    || item.investorSessionCurrent
    || item.foreignNetBuyAmount !== null
    || item.institutionNetBuyAmount !== null
    || (item.programMatched && item.programNetBuyExecAmount !== null);
}

function flowUnavailableBecauseOfRowStale(
  item: NormalizedSectorTopStock | NormalizedHolding,
  stale: boolean,
): boolean {
  return stale && !hasCurrentFlowAuthority(item);
}

function flowSessionPresentation(marketSession = ''): {
  title: string;
  note: string;
} {
  const session = cleanText(marketSession).toUpperCase();
  if (session.includes('NXT_AFTER')) {
    return {
      title: 'KRX 정규장 수급',
      note: 'NXT 가격과 별도로, 외국인·기관·프로그램은 당일 KRX 정규장 수급 기준입니다.',
    };
  }
  if (session.includes('TRANSITION')) {
    return {
      title: 'KRX 마감 수급',
      note: '정규장 종료 직후 확정되는 당일 KRX 수급 기준입니다.',
    };
  }
  if (session.includes('CLOSED') || session.includes('POST')) {
    return {
      title: '당일 KRX 수급',
      note: '장 종료 후에는 당일 KRX 최종 수급을 현재 기준으로 표시합니다.',
    };
  }
  if (session.includes('PRE')) {
    return {
      title: '최근 KRX 수급',
      note: '개장 전에는 새 장중 수급으로 해석하지 않고 직전 확인 기준을 구분합니다.',
    };
  }
  return {
    title: '실시간 수급 강도',
    note: '당일 KRX 수급 기준입니다.',
  };
}

function flowOverviewMeta(
  item: NormalizedSectorTopStock | NormalizedHolding,
  stale = false,
): { label: string; detail: string; tone: Tone } {
  if (flowUnavailableBecauseOfRowStale(item, stale)) {
    return { label: '수급 확인 중', detail: '현재 세션 수급 데이터가 확인되면 방향을 표시합니다.', tone: 'neutral' };
  }

  const smartMoney = holdingSmartMoneyAmount(item);
  const program = item.programMatched ? item.programNetBuyExecAmount : null;

  if (smartMoney !== null) {
    if (smartMoney > 0) {
      if (program !== null && program > 0) {
        return { label: '순매수 우세', detail: '외국인·기관 합계와 프로그램이 모두 순매수 방향입니다.', tone: 'up' };
      }
      if (program !== null && program < 0) {
        return { label: '순매수 우세 · 혼조', detail: '외국인·기관 합계는 순매수지만 프로그램은 순매도 방향입니다.', tone: 'warn' };
      }
      return { label: '외국인·기관 순매수', detail: '외국인과 기관을 합친 순수급이 순매수 방향입니다.', tone: 'up' };
    }
    if (smartMoney < 0) {
      if (program !== null && program < 0) {
        return { label: '순매도 우세', detail: '외국인·기관 합계와 프로그램이 모두 순매도 방향입니다.', tone: 'down' };
      }
      if (program !== null && program > 0) {
        return { label: '순매도 우세 · 혼조', detail: '외국인·기관 합계는 순매도지만 프로그램은 순매수 방향입니다.', tone: 'warn' };
      }
      return { label: '외국인·기관 순매도', detail: '외국인과 기관을 합친 순수급이 순매도 방향입니다.', tone: 'down' };
    }
  }

  const directions = [
    item.foreignNetBuyAmount,
    item.institutionNetBuyAmount,
    program,
  ].filter((value): value is number => value !== null && Number.isFinite(value));

  if (!directions.length) {
    return { label: '수급 확인 중', detail: '외국인·기관·프로그램 수급을 확인하고 있습니다.', tone: 'neutral' };
  }

  const buyCount = directions.filter((value) => value > 0).length;
  const sellCount = directions.filter((value) => value < 0).length;
  if (buyCount > sellCount) {
    return { label: '순매수 신호 우세', detail: '확인 가능한 수급 신호 중 순매수 방향이 더 많습니다.', tone: 'up' };
  }
  if (sellCount > buyCount) {
    return { label: '순매도 신호 우세', detail: '확인 가능한 수급 신호 중 순매도 방향이 더 많습니다.', tone: 'down' };
  }
  return { label: '수급 혼조', detail: '순매수와 순매도 신호가 섞여 있어 한쪽 방향이 뚜렷하지 않습니다.', tone: 'warn' };
}



function decisionPriorityText(
  item: NormalizedSectorTopStock | NormalizedHolding,
  stale = false,
): string {
  if (stale) return '최신 가격 확인 후 단기 판단 우선순위를 갱신합니다. 수급은 별도 세션 기준으로 확인합니다.';

  const smartMoney = holdingSmartMoneyAmount(item);
  const priceWeak = item.changeRate !== null && item.changeRate < 0;
  const flowWeak = smartMoney !== null && smartMoney < 0;
  const flowStrong = smartMoney !== null && smartMoney > 0;
  const fundamentalStrong = item.fundamentalAvailable
    && item.fundamentalScore !== null
    && item.fundamentalScore >= 70;
  const fundamentalWeak = item.fundamentalAvailable
    && item.fundamentalScore !== null
    && item.fundamentalScore < 45;

  if (fundamentalStrong && (priceWeak || flowWeak)) {
    return '재무 체력은 양호하지만 현재 판단은 단기 가격·수급 약화를 우선 반영합니다.';
  }
  if (fundamentalWeak && flowStrong && !priceWeak) {
    return '단기 수급이 버티더라도 재무 취약을 함께 우선 확인합니다.';
  }
  if (priceWeak || flowWeak) {
    return '현재 판단은 단기 가격·수급 약화를 우선 확인하고 재무는 중기 기준으로 분리합니다.';
  }
  if (flowStrong) {
    return '현재 판단은 가격·수급 지속성을 우선 확인하고 재무는 중기 확인 기준으로 사용합니다.';
  }
  return '현재 판단은 가격·수급을 우선하고 재무·가치는 중기 확인 기준으로 분리합니다.';
}

function flowRatioPct(value: number | null, tradingValue: number | null): number | null {
  if (value === null || tradingValue === null || !Number.isFinite(value) || !Number.isFinite(tradingValue) || tradingValue <= 0) {
    return null;
  }
  return (value / tradingValue) * 100;
}

function flowVisualWidth(value: number | null, maxAbs: number): string {
  if (value === null || !Number.isFinite(value) || maxAbs <= 0) return '0%';
  // The visual track is split at the 50% center line. Scale the largest actor to one half-track.
  return `${Math.max(3, Math.min(50, (Math.abs(value) / maxAbs) * 50))}%`;
}

function PricePositionBrief({
  item,
  stale = false,
}: {
  item: NormalizedSectorTopStock | NormalizedHolding;
  stale?: boolean;
}) {
  const price = stale ? null : item.price;
  const vwap = stale ? null : item.sessionVwap;
  const high = stale ? null : item.highPrice;
  const low = stale ? null : item.lowPrice;
  const vwapGapPct = price !== null && vwap !== null && vwap > 0
    ? ((price - vwap) / vwap) * 100
    : null;
  const rangePct = price !== null && low !== null && high !== null && high > low
    ? Math.max(0, Math.min(100, ((price - low) / (high - low)) * 100))
    : null;
  const box = 'boxSupport' in item ? item.boxSupport : null;
  const boxLabel = box?.available
    ? [
        box.upperPrice !== null ? `상단 ${formatPrice(box.upperPrice)}원` : '',
        box.middlePrice !== null ? `중앙 ${formatPrice(box.middlePrice)}원` : '',
        box.lowerPrice !== null ? `하단 ${formatPrice(box.lowerPrice)}원` : '',
      ].filter(Boolean).join(' · ')
    : '';

  if (price === null && vwap === null && high === null && low === null && !boxLabel) {
    return null;
  }

  return (
    <section className="live-summary-price-position" aria-label="현재 가격 위치">
      <header>
        <div><small>가격 위치</small><b>현재가 · VWAP · 당일 범위</b></div>
        <span>{stale ? '갱신 대기' : '현재 기준'}</span>
      </header>
      <div className="live-summary-price-position__metrics">
        <span><small>현재가</small><b>{price === null ? '—' : `${formatPrice(price)}원`}</b></span>
        <span><small>VWAP</small><b>{vwap === null ? '—' : `${formatPrice(vwap)}원`}</b><em>{vwapGapPct === null ? '' : `${formatSignedNumber(vwapGapPct, 2)}%`}</em></span>
        <span><small>당일 저가</small><b>{low === null ? '—' : `${formatPrice(low)}원`}</b></span>
        <span><small>당일 고가</small><b>{high === null ? '—' : `${formatPrice(high)}원`}</b></span>
      </div>
      {rangePct !== null && (
        <div className="live-summary-price-position__range" aria-label={`당일 가격 범위 내 현재 위치 ${Math.round(rangePct)}퍼센트`}>
          <span /><i style={{ left: `${rangePct}%` }} />
          <small>저가</small><small>현재 {Math.round(rangePct)}%</small><small>고가</small>
        </div>
      )}
      {boxLabel && <p><small>박스 기준</small><b>{boxLabel}</b></p>}
    </section>
  );
}

function CompactFlowBrief({
  item,
  marketSession = '',
}: {
  item: NormalizedSectorTopStock | NormalizedHolding;
  marketSession?: string;
}) {
  const overview = flowOverviewMeta(item, false);
  const session = flowSessionPresentation(marketSession);
  const metrics = [
    { key: 'foreign', label: '외국인', value: item.foreignNetBuyAmount },
    { key: 'institution', label: '기관', value: item.institutionNetBuyAmount },
    { key: 'program', label: '프로그램', value: item.programMatched ? item.programNetBuyExecAmount : null },
  ];
  const available = metrics.filter((metric) => metric.value !== null && Number.isFinite(metric.value));
  const maxAbs = Math.max(0, ...available.map((metric) => Math.abs(metric.value as number)));
  const smartMoney = holdingSmartMoneyAmount(item);
  const smartMoneyRatio = flowRatioPct(smartMoney, item.tradingValue);

  if (available.length === 0) {
    return (
      <section className="live-summary-flow-strength is-pending">
        <header><div><small>{session.title}</small><b>데이터 확인 중</b></div><span>—</span></header>
        <p>{session.note} 외국인·기관·프로그램 수급 데이터가 확인되는 대로 방향과 상대 강도를 표시합니다.</p>
      </section>
    );
  }

  return (
    <section className={`live-summary-flow-strength ${toneClass(overview.tone)}`} aria-label={session.title}>
      <header>
        <div><small>{session.title}</small><b>{overview.label}</b></div>
        <span>{smartMoneyRatio === null ? '거래대금 대비 확인 중' : `외인+기관 ${formatSignedNumber(smartMoneyRatio, 2)}%`}</span>
      </header>
      <div className="live-summary-flow-strength__rows">
        {metrics.map((metric) => {
          const meta = flowDirectionMeta(metric.value, false);
          const ratio = flowRatioPct(metric.value, item.tradingValue);
          return (
            <div key={metric.key} className={toneClass(meta.tone)}>
              <span><small>{metric.label}</small><b>{meta.label}</b></span>
              <span className="live-summary-flow-strength__track"><i className={metric.value !== null && metric.value < 0 ? 'is-negative' : 'is-positive'} style={{ width: flowVisualWidth(metric.value, maxAbs) }} /></span>
              <span><b>{metric.value === null ? '—' : formatSignedMoney(metric.value)}</b><small>{ratio === null ? '' : `${formatSignedNumber(ratio, 2)}%`}</small></span>
            </div>
          );
        })}
      </div>
      <footer>{session.note} {overview.detail} 막대 길이는 현재 세 주체 사이의 상대 크기이며 절대 강도 점수로 사용하지 않습니다.</footer>
    </section>
  );
}








function derivativeForItem(
  item: NormalizedSectorTopStock | NormalizedHolding,
  marketContext: NormalizedMarketContext,
): NormalizedDerivativePoint {
  const market = cleanText(item.marketLabel || item.marketType).toUpperCase();
  return ['1', 'KOSDAQ', 'KQ'].includes(market)
    ? marketContext.derivatives.KOSDAQ150
    : marketContext.derivatives.KOSPI200;
}

function indexForItem(
  item: NormalizedSectorTopStock | NormalizedHolding,
  marketContext: NormalizedMarketContext,
): NormalizedMarketIndexPoint {
  const market = cleanText(item.marketLabel || item.marketType).toUpperCase();
  return ['1', 'KOSDAQ', 'KQ'].includes(market)
    ? marketContext.domesticIndices.KOSDAQ
    : marketContext.domesticIndices.KOSPI;
}



function flowAttributionView(
  item: NormalizedSectorTopStock | NormalizedHolding,
  marketContext: NormalizedMarketContext,
  sectorContext: NormalizedSector | null = null,
  stale = false,
): FlowAttributionView | null {
  if (stale || !marketContext.available || item.changeRate === null || !Number.isFinite(item.changeRate)) return null;

  const index = indexForItem(item, marketContext);
  const derivative = derivativeForItem(item, marketContext);
  const stockChange = item.changeRate;
  const indexChange = index.stale ? null : index.changeRate;
  const futuresChange = derivative.stale ? null : derivative.futuresChangeRate;
  const sectorChange = sectorContext?.changeRate ?? null;
  const sectorBreadth = sectorContext?.breadthPct ?? null;
  const velocity = item.investorTrendVelocityRatio;
  const trend = cleanText(item.investorTrendDirection).toUpperCase();
  const programRatio = item.programMatched ? item.programNetBuyToTradingValuePct : null;

  const marketWeak = (
    (indexChange !== null && indexChange <= -0.7)
    || (futuresChange !== null && futuresChange <= -0.7)
    || marketContext.tone === 'down'
  );
  const sectorWeak = sectorChange !== null
    ? sectorChange <= -0.8 || (sectorBreadth !== null && sectorBreadth < 40)
    : false;
  const flowWeak = item.flowDecisionTone === 'down'
    || trend === 'DOWN_STREAK'
    || trend === 'UP_TO_DOWN'
    || (
      item.smartMoneyNetBuyAmount !== null
      && item.smartMoneyNetBuyAmount < 0
      && velocity !== null
      && velocity >= 1.3
    );
  const programWeak = programRatio !== null && programRatio <= -0.5;
  const stockWeak = stockChange <= -0.8;
  const relativeToMarket = indexChange === null ? null : stockChange - indexChange;
  const ownPressure = flowWeak || programWeak;

  const evidence: string[] = [];
  if (indexChange !== null) evidence.push(`${index.label} ${formatPct(indexChange, 1)}`);
  if (sectorContext && sectorChange !== null) {
    evidence.push(
      sectorBreadth !== null
        ? `${sectorContext.industryName} ${formatPct(sectorChange, 1)} · 상승 ${sectorBreadth.toFixed(0)}%`
        : `${sectorContext.industryName} ${formatPct(sectorChange, 1)}`,
    );
  }
  if (velocity !== null && Number.isFinite(velocity)) evidence.push(`수급 속도 ${velocity.toFixed(1)}x`);
  if (programRatio !== null && Number.isFinite(programRatio)) evidence.push(`프로그램 ${formatPct(programRatio, 1)}`);

  if (marketWeak && relativeToMarket !== null && relativeToMarket >= 1.0 && !ownPressure) {
    return {
      code: 'DIVERGENT_STRENGTH',
      label: '약한 시장 속 상대적 방어',
      tone: 'up',
      description: '시장 압력보다 종목의 상대 흐름이 강합니다. 시장 약세만으로 종목 약세를 단정하기 어렵습니다.',
      evidence: evidence.slice(0, 4),
    };
  }

  if (stockWeak && marketWeak && ownPressure) {
    return {
      code: 'MIXED_PRESSURE',
      label: '시장 + 종목 동시 압력',
      tone: 'down',
      description: '시장 하방 압력과 종목 자체 수급 이탈이 동시에 확인됩니다. 방어 우선순위를 높일 구간입니다.',
      evidence: evidence.slice(0, 4),
    };
  }

  if (stockWeak && ownPressure && !marketWeak) {
    return {
      code: 'STOCK_SPECIFIC',
      label: '개별 종목 수급 이탈',
      tone: 'down',
      description: '시장보다 종목 자체 수급 약화가 두드러집니다. 개별 재료·수급 이탈 여부를 먼저 확인하세요.',
      evidence: evidence.slice(0, 4),
    };
  }

  if (stockWeak && marketWeak && !ownPressure && (sectorWeak || relativeToMarket === null || relativeToMarket >= -0.5)) {
    return {
      code: 'MARKET_DRIVEN',
      label: '시장 동반 압력',
      tone: 'warn',
      description: '종목 자체 수급 이탈보다 시장·업종 하방 압력이 상대적으로 우세합니다.',
      evidence: evidence.slice(0, 4),
    };
  }

  return null;
}

function immediateRiskCandidate(
  item: NormalizedSectorTopStock | NormalizedHolding,
  marketContext: NormalizedMarketContext,
  sectorContext: NormalizedSector | null,
  holding: boolean,
): RiskStripCandidate | null {
  const stale = item.delayMinutes !== null && item.delayMinutes > 5;
  if (stale) return null;

  const attribution = flowAttributionView(item, marketContext, sectorContext, false);
  const velocity = item.investorTrendVelocityRatio;
  const programRatio = item.programNetBuyToTradingValuePct;
  let score = holding ? 12 : 0;

  if (item.riskScore !== null) score += Math.max(0, item.riskScore - 35) * 0.7;
  if (item.flowDecisionTone === 'down') score += 18;
  else if (item.flowDecisionTone === 'warn') score += 8;
  if (velocity !== null && velocity >= 2.0) score += 18;
  else if (velocity !== null && velocity >= 1.3) score += 8;
  if (programRatio !== null && programRatio <= -1.0) score += 12;
  else if (programRatio !== null && programRatio <= -0.5) score += 6;
  if (item.changeRate !== null && item.changeRate <= -3) score += 12;
  else if (item.changeRate !== null && item.changeRate <= -1.5) score += 6;

  if (attribution?.code === 'MIXED_PRESSURE') score += 22;
  else if (attribution?.code === 'STOCK_SPECIFIC') score += 18;
  else if (attribution?.code === 'MARKET_DRIVEN') score += 8;

  if (score < 30) return null;

  return {
    key: `${holding ? 'holding' : 'stock'}:${item.ticker || item.name}`,
    ticker: item.ticker,
    name: item.name,
    label: attribution?.label || (item.flowDecisionTone === 'down' ? '수급 약화 확인' : '위험 변화 확인'),
    tone: attribution?.tone || (score >= 55 ? 'down' : 'warn'),
    score,
    holding,
  };
}







type DiscoveryDecisionView = {
  headline: string;
  tone: Tone;
  summary: string;
  actionLabel: string;
  companyLabel: string;
  thesisLabel: string;
  flowLabel: string;
  marketLabel: string;
};

function discoveryDecisionView(
  item: NormalizedSectorTopStock | NormalizedHolding,
): DiscoveryDecisionView {
  const judgment = item.valueJudgment || EMPTY_VALUE_JUDGMENT;
  const hasJudgment = Boolean(judgment.contractVersion);
  const decision = item.actionDecision;
  const assessment = item.riskAssessment;
  const flow = flowInterpretation(item);

  const headline = hasJudgment
    ? (cleanText(judgment.valueStateLabel) || '가치 판단 확인 중')
    : (
        cleanText(decision.headline)
        || cleanText(decision.stateLabel)
        || cleanText(assessment.stateLabel)
        || cleanText(flow.headline)
        || '판단 데이터 준비 중'
      );

  const tone: Tone = hasJudgment
    ? judgment.valueStateTone
    : decision.available
      ? decision.tone
      : assessment.available
        ? assessment.tone
        : flow.tone;

  const summary = hasJudgment
    ? (
        cleanText(judgment.summary)
        || cleanText(judgment.reasons[0])
        || cleanText(decision.doNow)
        || cleanText(assessment.summary)
      )
    : (
        cleanText(item.decisionEvidence.summary)
        || cleanText(decision.reasons[0])
        || cleanText(decision.doNow)
        || cleanText(assessment.summary)
        || cleanText(flow.description)
      );

  const actionLabel =
    cleanText(decision.actionLabel)
    || userFacingStateLabel(decision.action)
    || cleanText(decision.stateLabel)
    || '대응 확인 중';

  const companyLabel = hasJudgment
    ? (
        cleanText(judgment.fundamental.label)
        || cleanText(judgment.businessEvidence.state)
        || '기업 근거 확인 중'
      )
    : item.fundamentalAvailable
      ? (
          cleanText(item.fundamentalDirectionLabel)
          || cleanText(item.fundamentalValuationLabel)
          || '기업 근거 확인'
        )
      : '기업 근거 확인 중';

  const thesisLabel = hasJudgment
    ? (
        cleanText(judgment.thesis.label)
        || cleanText(decision.stateLabel)
        || '투자 논리 확인 중'
      )
    : (
        cleanText(decision.stateLabel)
        || cleanText(decision.actionLabel)
        || cleanText(assessment.stateLabel)
        || '투자 논리 확인 중'
      );

  const flowLabel =
    cleanText(flow.headline)
    || cleanText(item.flowDecision)
    || '수급 확인 중';

  const marketLabel = hasJudgment
    ? (
        cleanText(judgment.marketDelivery.label)
        || cleanText(assessment.stateLabel)
        || '시장 영향 확인 중'
      )
    : (
        cleanText(assessment.stateLabel)
        || '시장 영향 확인 중'
      );

  return {
    headline,
    tone,
    summary,
    actionLabel,
    companyLabel,
    thesisLabel,
    flowLabel,
    marketLabel,
  };
}

function DiscoveryDecisionLine({
  item,
  sectorName,
  freshnessLabel,
}: {
  item: NormalizedSectorTopStock | NormalizedHolding;
  sectorName?: string;
  freshnessLabel?: string;
}) {
  const action = item.actionDecision;
  const risk = item.riskAssessment;
  const available = Boolean(action.available || risk.available);
  const raw = action.available
    ? (cleanText(action.actionLabel) || cleanText(action.stateLabel) || cleanText(action.headline))
    : (risk.available ? cleanText(risk.stateLabel) : '');
  const label = available && raw && !/판단 제한|확인 중|미확인|준비 중/.test(raw) ? raw : '';
  if (!label) return null;
  return (
    <div className="pi-v4-signal" title={[sectorName, freshnessLabel].filter(Boolean).join(' · ')}>
      <span className={toneClass(action.available ? action.tone : risk.tone)}>{label}</span>
    </div>
  );
}


type StockRiskSnapshot = {
  riskScore: number | null;
  flowTone: Tone;
  derivativeStructure: string;
};

type StockRiskPriority = {
  score: number;
  delta: number | null;
  tone: Tone;
  label: string;
  reason: string;
};

function stockRiskPriority(
  stock: NormalizedSectorTopStock,
  previous?: StockRiskSnapshot,
): StockRiskPriority {
  let score = 0;
  const risk = stock.riskScore;
  const delta = risk !== null && previous?.riskScore !== null && previous?.riskScore !== undefined
    ? risk - previous.riskScore
    : null;

  if (risk !== null) {
    score += risk * 0.7;
    if (risk >= 75) score += 30;
    else if (risk >= 60) score += 20;
    else if (risk >= 45) score += 10;
  }

  if (delta !== null) {
    if (delta >= 15) score += 35;
    else if (delta >= 8) score += 22;
    else if (delta >= 4) score += 12;
  }

  if (stock.flowDecisionTone === 'down') score += 25;
  else if (stock.flowDecisionTone === 'warn') score += 12;

  if (['BEARISH_BUILD', 'LONG_LIQUIDATION'].includes(stock.riskDerivativeStructure)) score += 18;
  else if (stock.riskDerivativeStructure === 'SHORT_COVERING') score += 10;

  const smartMoney = resolvedSmartMoney(stock);
  if (smartMoney !== null && smartMoney < 0) score += 16;
  if ((stock.foreignNetBuyAmount ?? 0) < 0) score += 6;
  if ((stock.institutionNetBuyAmount ?? 0) < 0) score += 6;
  if ((stock.programNetBuyExecAmount ?? 0) < 0) score += 4;

  const trend = cleanText(stock.investorTrendDirection).toUpperCase();
  if (/(DOWN|SELL|OUT|WEAK|BEAR|NEGATIVE)/.test(trend)) score += 14;

  if (previous) {
    const wasFlowWeak = previous.flowTone === 'down' || previous.flowTone === 'warn';
    const isFlowWeak = stock.flowDecisionTone === 'down' || stock.flowDecisionTone === 'warn';
    if (!wasFlowWeak && isFlowWeak) score += 16;

    const wasDerivativeRisk = ['BEARISH_BUILD', 'LONG_LIQUIDATION'].includes(previous.derivativeStructure);
    const isDerivativeRisk = ['BEARISH_BUILD', 'LONG_LIQUIDATION'].includes(stock.riskDerivativeStructure);
    if (!wasDerivativeRisk && isDerivativeRisk) score += 12;
  }

  // 등락률은 우선순위의 보조값일 뿐 1차 기준으로 사용하지 않는다.
  if ((stock.changeRate ?? 0) <= -4) score += 8;
  if ((stock.changeRate ?? 0) >= 12) score += 6;

  if (delta !== null && delta >= 8) {
    return {
      score,
      delta,
      tone: delta >= 15 || (risk ?? 0) >= 70 ? 'down' : 'warn',
      label: '리스크 급변',
      reason: `위험 점수가 직전 갱신보다 ${Math.round(delta)}점 상승했습니다.`,
    };
  }
  if ((smartMoney !== null && smartMoney < 0) || stock.flowDecisionTone === 'down') {
    return {
      score,
      delta,
      tone: (risk ?? 0) >= 60 ? 'down' : 'warn',
      label: '수급 약화',
      reason: '외국인·기관 중심 순수급 약화가 우선 확인 대상입니다.',
    };
  }
  if (['BEARISH_BUILD', 'LONG_LIQUIDATION'].includes(stock.riskDerivativeStructure)) {
    return {
      score,
      delta,
      tone: 'down',
      label: '시장 동조 약화',
      reason: '시장 선행 흐름과 종목 위험이 동시에 약해지는지 확인이 필요합니다.',
    };
  }
  if ((risk ?? 0) >= 55) {
    return {
      score,
      delta,
      tone: 'warn',
      label: '변동성 주의',
      reason: '현재 위험 점수와 수급·가격 부담을 함께 확인합니다.',
    };
  }
  return {
    score,
    delta,
    tone: 'neutral',
    label: '상태 확인',
    reason: '급격한 위험 변화는 없으며 수급과 가격 구조를 계속 확인합니다.',
  };
}


function marketPointTone(value: number | null, inverted = false): Tone {
  if (value === null || !Number.isFinite(value) || Math.abs(value) < 0.001) return 'neutral';
  const positive = inverted ? value < 0 : value > 0;
  return positive ? 'up' : 'down';
}


function derivativeDirectionTone(state: string): Tone {
  const normalized = cleanText(state).toUpperCase();
  if (normalized === 'UP' || normalized === 'UP_REVERSAL_WATCH') return 'up';
  if (normalized === 'DOWN' || normalized === 'DOWN_REVERSAL_WATCH') return 'down';
  return 'neutral';
}

function derivativeOiStateLabel(state: string): string {
  const normalized = cleanText(state).toUpperCase();
  if (normalized === 'EXPANDING') return 'OI 확대';
  if (normalized === 'CONTRACTING') return 'OI 축소';
  if (normalized === 'FLAT') return 'OI 보합';
  return 'OI 확인 중';
}

function derivativeFlowFooter(point: NormalizedDerivativePoint): string {
  const parts: string[] = [];
  if (point.foreignNetContracts !== null) parts.push(`외인 ${formatContractQty(point.foreignNetContracts)}`);
  if (point.institutionNetContracts !== null) parts.push(`기관 ${formatContractQty(point.institutionNetContracts)}`);
  parts.push(derivativeOiStateLabel(point.oiState));
  return parts.join(' · ');
}

function MetricHelp({
  guide,
  label,
}: {
  guide?: NormalizedMetricGuide;
  label: string;
}) {
  if (!guide) return null;
  return (
    <span className={`live-summary-metric-help ${toneClass(guide.tone)}`}>
      <button type="button" aria-label={`${label} 해설`} onClick={(event: { stopPropagation: () => void }) => event.stopPropagation()}>?</button>
      <span role="tooltip">
        {guide.title && <b>{guide.title}</b>}
        {guide.summary && <em>{guide.summary}</em>}
        {guide.action && <small>{guide.action}</small>}
      </span>
    </span>
  );
}

function liveIssueDirectionLabel(value: string): string {
  if (value === 'POSITIVE') return '긍정';
  if (value === 'NEGATIVE') return '부정';
  if (value === 'MIXED') return '혼재';
  return '판단불가';
}

function liveIssueImportanceLabel(value: string): string {
  if (value === 'HIGH') return '중요도 높음';
  if (value === 'MEDIUM') return '중요도 중간';
  return '중요도 낮음';
}

function liveIssueEvidenceLabel(value: string): string {
  if (value === 'CONFIRMED') return '확인됨';
  if (value === 'REPORTED') return '보도 단계';
  return '미확인';
}

function liveIssueChangeLabel(value: string): string {
  if (value === 'STRENGTHENED') return '강화';
  if (value === 'WEAKENED') return '약화';
  if (value === 'RESOLVED') return '해소';
  return '신규';
}

function liveIssueScopeLabel(value: string): string {
  const normalized = cleanText(value).toUpperCase();
  if (normalized === 'MARKET') return '시장';
  if (normalized === 'SECTOR') return '업종';
  if (normalized === 'THEME') return '테마';
  if (normalized === 'STOCK') return '종목';
  if (normalized === 'MACRO') return '거시';
  return '이슈';
}

function liveIssueWatchText(value: string): string {
  const text = cleanText(value).replace(/\s+/g, ' ').trim();
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?)?$/.test(text)) return '';

  const genericPatterns = [
    '시장 상황을 주시',
    '시장 상황 주시',
    '글로벌 시장 동향',
    '투자자 반응 확인',
    '향후 시장 상황',
  ];
  if (genericPatterns.some((pattern) => text.includes(pattern))) return '';
  return text;
}

function liveIssueBriefDirectionLabel(value: string, market = false): string {
  const normalized = cleanText(value).toUpperCase();
  if (normalized === 'POSITIVE') return market ? '상방 압력' : '긍정';
  if (normalized === 'NEGATIVE') return market ? '하방 압력' : '부정';
  if (normalized === 'MIXED') return '혼조';
  if (normalized === 'NEUTRAL') return '중립';
  return '판단 제한';
}

function liveIssueBriefDirectionTone(value: string): Tone {
  const normalized = cleanText(value).toUpperCase();
  if (normalized === 'POSITIVE') return 'up';
  if (normalized === 'NEGATIVE') return 'down';
  if (normalized === 'MIXED') return 'warn';
  return 'neutral';
}

function liveIssuePolicyStateLabel(value: string): string {
  const normalized = cleanText(value).toUpperCase();
  if (normalized === 'NEW') return '신규 정책 변화';
  if (normalized === 'STRENGTHENED') return '정책 영향 강화';
  if (normalized === 'WEAKENED') return '정책 영향 약화';
  if (normalized === 'RESOLVED') return '정책 이슈 해소';
  return '정책 변화 없음';
}



function sectorImpactTone(impact: NormalizedSectorImpact): Tone {
  if (impact.classification === 'DIRECT_CATALYST' || impact.classification === 'SUPPORTING_FACTOR') return 'up';
  if (impact.classification === 'DIRECT_PRESSURE' || impact.classification === 'PRESSURE_FACTOR') return 'down';
  if (impact.classification === 'MIXED' || impact.classification === 'CO_MOVE') return 'warn';
  return 'neutral';
}

function sectorImpactBreadthText(impact: NormalizedSectorImpact): string {
  if (impact.realtimeTickerCount <= 0) return '관련주 반응 확인 중';
  return `${impact.upTickerCount}/${impact.realtimeTickerCount} 상승`;
}

function sectorImpactBackgroundLabel(impact: NormalizedSectorImpact): string {
  if (impact.classification === 'DIRECT_CATALYST') return '상승 배경 유력';
  if (impact.classification === 'SUPPORTING_FACTOR') return '상승 배경 후보';
  if (impact.classification === 'DIRECT_PRESSURE') return '하락 배경 유력';
  if (impact.classification === 'PRESSURE_FACTOR') return '하락 배경 후보';
  if (impact.classification === 'CO_MOVE') return '동행 배경';
  return '산업 반응';
}

function SectorImpactSummary({ impact, compact = false }: { impact: NormalizedSectorImpact; compact?: boolean }) {
  return (
    <div className={`live-summary-sector-impact is-industry ${compact ? 'is-compact' : ''} ${toneClass(sectorImpactTone(impact))}`}>
      <div className="live-summary-sector-impact__head">
        <span><small>연관 산업</small><b>{impact.sectorName}</b></span>
        <em>{impact.changeRate === null ? '등락 확인 중' : formatPct(impact.changeRate, 2)}</em>
      </div>
      <div className="live-summary-sector-impact__facts">
        <span>{sectorImpactBreadthText(impact)}</span>
        <span>{impact.label}</span>
        {impact.marketGroupName && <span className="is-market-group">시장 그룹 · {impact.marketGroupName}</span>}
      </div>
      {(impact.background || impact.reason) && (
        <div className="live-summary-sector-impact__reason">
          <small>{sectorImpactBackgroundLabel(impact)}</small>
          {impact.background && <b>{impact.background}</b>}
          {impact.reason && <p>{impact.reason}</p>}
        </div>
      )}
    </div>
  );
}




type LiveIssueLifecycleGroupSpec = {
  key: 'born' | 'strengthened' | 'weakened' | 'realized' | 'resolved' | 'invalidated';
  label: string;
  shortLabel: string;
  description: string;
  tone: Tone;
};

const LIVE_ISSUE_LIFECYCLE_GROUPS: LiveIssueLifecycleGroupSpec[] = [
  { key: 'born', label: '신규', shortLabel: 'NEW', description: '새로 확인된 재료', tone: 'good' },
  { key: 'strengthened', label: '강화', shortLabel: 'UP', description: '근거가 추가된 재료', tone: 'up' },
  { key: 'weakened', label: '약화', shortLabel: 'DOWN', description: '전제가 약해진 재료', tone: 'warn' },
  { key: 'realized', label: '현실화', shortLabel: 'REALIZED', description: '승인·결과·체결 등 실제 발생', tone: 'up' },
  { key: 'resolved', label: '기대 해소', shortLabel: 'RESOLVED', description: '기대 단계가 결과 단계로 전환', tone: 'neutral' },
  { key: 'invalidated', label: '무효', shortLabel: 'INVALID', description: '취소·철회·거절로 논리 종료', tone: 'down' },
];

function liveIssueCatalystPhaseLabel(row: NormalizedLiveIssueBriefCatalyst): string {
  const resolution = cleanText(row.resolutionType).toUpperCase();
  if (resolution === 'EXPECTATION_REALIZED') return '기대 해소 → 결과';
  const phase = cleanText(row.phase).toUpperCase();
  if (phase === 'EXPECTATION') return '기대 단계';
  if (phase === 'REALIZED') return '결과 확인';
  if (phase === 'INVALIDATED') return '무효 확인';
  return '사실 확인';
}

function LiveIssueLifecycleGroup({
  spec,
  rows,
}: {
  spec: LiveIssueLifecycleGroupSpec;
  rows: NormalizedLiveIssueBriefCatalyst[];
}) {
  if (!rows.length) return null;
  return (
    <section className={`live-summary-live-issue__lifecycle-group is-${spec.key}`} aria-label={`재료 ${spec.label}`}>
      <header>
        <div>
          <span className={toneClass(spec.tone)}>{spec.shortLabel}</span>
          <b>{spec.label}</b>
        </div>
        <small>{rows.length}</small>
      </header>
      <div className="live-summary-live-issue__lifecycle-items">
        {rows.slice(0, 4).map((row, index) => (
          <article key={`${spec.key}-${row.materialId || row.headline}-${index}`}>
            <div className="live-summary-live-issue__lifecycle-copy">
              <strong>{row.headline}</strong>
              {row.summary && <p>{row.summary}</p>}
            </div>
            <div className="live-summary-live-issue__lifecycle-meta">
              <span>{liveIssueCatalystPhaseLabel(row)}</span>
              <span>{liveIssueBriefDirectionLabel(row.direction)}</span>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}


function liveIssueHasMarketWideAuthority(signal: NormalizedLiveIssueSignal): boolean {
  return signal.signals.some((item) => {
    const scope = cleanText(item.scope).toUpperCase();
    return scope === 'MARKET' || scope === 'MACRO';
  });
}

function LiveIssueTenMinuteBrief({ signal }: { signal: NormalizedLiveIssueSignal }) {
  const brief = signal.brief;
  if (!brief) return null;
  const marketWideAuthority = liveIssueHasMarketWideAuthority(signal);

  const realizedMaterialIds = new Set(brief.catalysts.realized.map((row) => row.materialId).filter(Boolean));
  const lifecycleRows: Record<LiveIssueLifecycleGroupSpec['key'], NormalizedLiveIssueBriefCatalyst[]> = {
    born: brief.catalysts.born,
    strengthened: brief.catalysts.strengthened,
    weakened: brief.catalysts.weakened,
    realized: brief.catalysts.realized,
    // When one event both resolves an expectation and becomes a realized catalyst,
    // show it once as "기대 해소 → 결과" instead of duplicating two cards.
    resolved: brief.catalysts.resolved.filter((row) => !row.materialId || !realizedMaterialIds.has(row.materialId)),
    invalidated: brief.catalysts.invalidated,
  };
  const lifecycleChangeCount = LIVE_ISSUE_LIFECYCLE_GROUPS.reduce(
    (total, spec) => total + lifecycleRows[spec.key].length,
    0,
  );
  const directTargetCount = brief.sectors.length + brief.stocks.length;
  const watch = brief.watch.slice(0, 4);
  const hasLifecycle = lifecycleChangeCount > 0;

  return (
    <section className="live-summary-live-issue__brief-shell" aria-label="최근 10분 이슈 브리프">
      <header className="live-summary-live-issue__brief-hero">
        <div className="live-summary-live-issue__brief-title">
          <span>10M INTELLIGENCE</span>
          <strong>{marketWideAuthority ? (brief.overview || '최근 10분 동안 확인된 이슈를 변화 기준으로 정리했습니다.') : '개별 종목·업종 이벤트가 확인됐습니다. 시장 전체 방향 원인으로는 아직 승격하지 않습니다.'}</strong>
        </div>
        <div className="live-summary-live-issue__brief-stats" aria-label="분석 입력 요약">
          <span><small>입력</small><b>{signal.sourceMessageCount}</b></span>
          <span><small>분석</small><b>{signal.candidateMessageCount}</b></span>
          <span><small>변화</small><b>{lifecycleChangeCount}</b></span>
        </div>
      </header>

      <div className="live-summary-live-issue__brief-signalbar">
        <article>
          <small>시장 압력</small>
          <b className={toneClass(marketWideAuthority ? liveIssueBriefDirectionTone(brief.market.direction) : 'neutral')}>
            {marketWideAuthority ? liveIssueBriefDirectionLabel(brief.market.direction, true) : '시장 방향 원인 미확인'}
          </b>
          <p>{marketWideAuthority ? (brief.market.summary || '시장 단위 Evidence를 확인했습니다.') : '개별 종목·업종 영향 후보로만 유지합니다.'}</p>
        </article>
        <article>
          <small>정책 변화</small>
          <b className={toneClass(liveIssueBriefDirectionTone(brief.policy.direction))}>
            {liveIssuePolicyStateLabel(brief.policy.state)}
          </b>
          {brief.policy.summary && <p>{brief.policy.summary}</p>}
        </article>
        <article>
          <small>직접 연결</small>
          <b>{directTargetCount > 0 ? `${directTargetCount}개 대상` : '직접 연결 없음'}</b>
          <p>{brief.stocks.length > 0 ? `종목 ${brief.stocks.length} · 업종 ${brief.sectors.length}` : '종목 추론 없이 확인된 근거만 표시'}</p>
        </article>
      </div>

      <section className="live-summary-live-issue__lifecycle" aria-label="재료 라이프사이클">
        <header className="live-summary-live-issue__section-heading">
          <div>
            <small>MATERIAL LIFECYCLE</small>
            <b>재료 변화</b>
          </div>
          <span>{hasLifecycle ? `${lifecycleChangeCount}건 변화` : '상태 변화 없음'}</span>
        </header>
        {hasLifecycle ? (
          <div className="live-summary-live-issue__lifecycle-grid">
            {LIVE_ISSUE_LIFECYCLE_GROUPS.map((spec) => (
              <LiveIssueLifecycleGroup key={spec.key} spec={spec} rows={lifecycleRows[spec.key]} />
            ))}
          </div>
        ) : (
          <p className="live-summary-live-issue__brief-empty">현재 구간에서 재료의 탄생·강화·약화·현실화·해소·무효 전환은 확인되지 않았습니다.</p>
        )}
      </section>

      {(brief.stocks.length > 0 || brief.sectors.length > 0) && (
        <section className="live-summary-live-issue__connections" aria-label="직접 연결 대상">
          <header className="live-summary-live-issue__section-heading">
            <div><small>DIRECT LINKS</small><b>직접 연결</b></div>
          </header>
          <div className="live-summary-live-issue__connection-grid">
            {brief.stocks.slice(0, 6).map((stock) => (
              <article key={`stock-${stock.ticker || stock.name}-${stock.summary}`}>
                <div><small>종목</small><strong>{stock.name || stock.ticker}</strong></div>
                <span className={toneClass(liveIssueBriefDirectionTone(stock.direction))}>{liveIssueBriefDirectionLabel(stock.direction)}</span>
                <p>{stock.summary}</p>
              </article>
            ))}
            {brief.sectors.slice(0, 4).map((sector) => (
              <article key={`sector-${sector.name}-${sector.summary}`}>
                <div><small>업종</small><strong>{sector.name}</strong></div>
                <span className={toneClass(sector.sectorImpact ? sectorImpactTone(sector.sectorImpact) : liveIssueBriefDirectionTone(sector.direction))}>
                  {sector.sectorImpact?.changeRate !== null && sector.sectorImpact?.changeRate !== undefined
                    ? formatPct(sector.sectorImpact.changeRate, 2)
                    : liveIssueBriefDirectionLabel(sector.direction)}
                </span>
                <p>{sector.summary}</p>
                {sector.sectorImpact && <SectorImpactSummary impact={sector.sectorImpact} compact />}
              </article>
            ))}
          </div>
        </section>
      )}

      {watch.length > 0 && (
        <section className="live-summary-live-issue__watchlist" aria-label="다음 확인 항목">
          <header className="live-summary-live-issue__section-heading">
            <div><small>NEXT CHECK</small><b>다음 확인</b></div>
            <span>{watch.length}개</span>
          </header>
          <div>
            {watch.map((item, index) => <p key={`${item}-${index}`}><span>{index + 1}</span>{item}</p>)}
          </div>
        </section>
      )}
    </section>
  );
}

function liveIssueScenarioPotentialTone(scenario: NormalizedLiveIssueScenario): Tone {
  if (scenario.capitalDirection === 'OUTFLOW') {
    if (scenario.smartMoneyPotential === 'HIGH' || scenario.smartMoneyPotential === 'MEDIUM') return 'down';
    return scenario.smartMoneyPotential === 'OPPOSITE' ? 'up' : 'neutral';
  }
  if (scenario.smartMoneyPotential === 'HIGH') return 'up';
  if (scenario.smartMoneyPotential === 'MEDIUM') return 'good';
  if (scenario.smartMoneyPotential === 'OPPOSITE') return 'warn';
  return 'neutral';
}

function liveIssueInvestmentTone(value: string): Tone {
  if (value === 'FAVORABLE') return 'up';
  if (value === 'UNFAVORABLE') return 'down';
  if (value === 'CONDITIONAL') return 'warn';
  return 'neutral';
}

function liveIssueCatalystTone(value: string): Tone {
  if (value === 'ACTIVE' || value === 'REACTIVATED') return 'up';
  if (value === 'PRICED_IN' || value === 'EXHAUSTED') return 'warn';
  if (value === 'INVALIDATED') return 'down';
  return 'neutral';
}

function liveIssueCurrentStateTone(scenario: NormalizedLiveIssueScenario): Tone {
  if (scenario.catalystState === 'INVALIDATED') return 'down';
  if (scenario.catalystState === 'PRICED_IN' || scenario.catalystState === 'EXHAUSTED') return 'warn';
  if (scenario.marketDelivery === 'SUPPRESSED') return 'warn';
  const entry = scenario.entryEnvironment || scenario.investmentEnvironment;
  return liveIssueInvestmentTone(entry);
}

function LiveIssueSignalCard({ signal }: { signal: NormalizedLiveIssueSignal }) {
  const [expandedEvidenceKey, setExpandedEvidenceKey] = useState('');
  const windowLabel = signal.windowStart && signal.windowEnd
    ? `${signal.windowStart.slice(11, 16)}~${signal.windowEnd.slice(11, 16)}`
    : '최근 10분';

  if (signal.status === 'NO_DATA') {
    return (
      <section className="live-summary-live-issue is-empty" aria-label="최근 10분 요약">
        <header className="live-summary-live-issue__header">
          <div>
            <small>{windowLabel}</small>
            <strong>최근 10분 요약</strong>
          </div>
          <span>입력 없음</span>
        </header>
        <p className="live-summary-live-issue__empty-copy">최근 구간에서 확인된 이벤트 입력이 없습니다.</p>
      </section>
    );
  }

  if (signal.status === 'NO_SIGNAL') {
    return (
      <section className="live-summary-live-issue is-empty" aria-label="최근 10분 요약">
        <header className="live-summary-live-issue__header">
          <div>
            <small>{windowLabel}</small>
            <strong>최근 10분 요약</strong>
          </div>
          <span>변화 없음</span>
        </header>
        <p className="live-summary-live-issue__empty-copy">입력은 확인했지만 정책·시장·종목·재료 변화로 정리할 유효 이슈가 없습니다.</p>
      </section>
    );
  }

  return (
    <section className="live-summary-live-issue" aria-label="최근 10분 요약">
      <header className="live-summary-live-issue__header">
        <div>
          <small>{windowLabel}</small>
          <strong>최근 10분 요약</strong>
        </div>
        <span>입력 {signal.sourceMessageCount} · 분석 {signal.candidateMessageCount}</span>
      </header>

      <LiveIssueTenMinuteBrief signal={signal} />

      {signal.capitalScenario && (
        <section className="live-summary-live-issue__delivery-strip" aria-label="시장 전달 상태">
          <div className="live-summary-live-issue__delivery-title">
            <small>MARKET DELIVERY</small>
            <b>{liveIssueHasMarketWideAuthority(signal) ? (signal.capitalScenario.marketDirectionLabel || '시장 방향 확인') : '시장 방향 원인으로 확인되지 않음'}</b>
          </div>
          <span className={liveIssueHasMarketWideAuthority(signal) && signal.capitalScenario.dataStatus === 'AVAILABLE' ? 'is-available' : 'is-limited'}>
            {liveIssueHasMarketWideAuthority(signal)
              ? (signal.capitalScenario.dataStatus === 'AVAILABLE' ? '시장 데이터 반영' : '판단 데이터 부족')
              : '개별 영향 후보'}
          </span>
          <p>{liveIssueHasMarketWideAuthority(signal)
            ? (signal.capitalScenario.summary || '시장 전달 근거를 확인했습니다.')
            : '단일 종목·업종 Event는 한국시장 전체 방향의 원인으로 사용하지 않습니다.'}</p>
          {(signal.capitalScenario.capitalFocus.length > 0 || signal.capitalScenario.pressureFocus.length > 0) && (
            <div className="live-summary-live-issue__delivery-chips">
              {signal.capitalScenario.capitalFocus.slice(0, 3).map((name) => <span className="is-inflow" key={`capital-${name}`}>자금 · {name}</span>)}
              {signal.capitalScenario.pressureFocus.slice(0, 3).map((name) => <span className="is-pressure" key={`pressure-${name}`}>압력 · {name}</span>)}
            </div>
          )}
        </section>
      )}

      {signal.signals.length > 0 && (
        <details className="live-summary-live-issue__evidence-drawer">
          <summary>
            <span><small>EVIDENCE EVENTS</small><b>근거 이벤트</b></span>
            <em>{signal.signals.length}건 · 펼쳐보기</em>
          </summary>
          <div className="live-summary-live-issue__list">
        {signal.signals.slice(0, 4).map((item, index) => {
          const directStocks = item.stocks
            .filter((stock) => stock.relation === 'DIRECT')
            .map((stock) => stock.name || stock.ticker)
            .filter(Boolean);
          const scopeNames = [...item.sectors, ...item.themes].filter(Boolean).slice(0, 3);
          const watchText = liveIssueWatchText(item.watch);
          const directionTone: Tone = item.direction === 'POSITIVE'
            ? 'up'
            : item.direction === 'NEGATIVE'
              ? 'down'
              : item.direction === 'MIXED'
                ? 'warn'
                : 'neutral';

          return (
            <article className="live-summary-live-issue__item" key={`${item.headline}-${index}`}>
              <div className="live-summary-live-issue__badges">
                <span>{liveIssueChangeLabel(item.changeType)}</span>
                <span>{liveIssueScopeLabel(item.scope)}</span>
                <span>{liveIssueEvidenceLabel(item.evidenceLevel)}</span>
              </div>

              <h4>{item.headline}</h4>

              {!item.scenario && (
                <div className="live-summary-live-issue__impact">
                  <b className={toneClass(directionTone)}>{liveIssueDirectionLabel(item.direction)}</b>
                  <span>{liveIssueImportanceLabel(item.importance)}</span>
                  {scopeNames.map((name) => <span key={name}>{name}</span>)}
                </div>
              )}

              {item.scenario && (() => {
                const issueKey = `${item.headline}-${index}`;
                const expanded = expandedEvidenceKey === issueKey;
                const entryEnvironment = item.scenario.entryEnvironment || item.scenario.investmentEnvironment;
                const entryEnvironmentLabel = item.scenario.entryEnvironmentLabel || item.scenario.investmentEnvironmentLabel;
                return (
                  <section className="live-summary-live-issue__scenario is-compact">
                    <div className="live-summary-live-issue__scenario-headline">
                      <span className={toneClass(item.scenario.eventValue === 'HIGH' ? 'up' : item.scenario.eventValue === 'MEDIUM' ? 'good' : 'neutral')}>재료 {item.scenario.eventValueLabel || '확인'}</span>
                      <span>{directStocks.length > 0 ? '종목 직접' : liveIssueScopeLabel(item.scope)}</span>
                      <span className={toneClass(liveIssueCatalystTone(item.scenario.catalystState))}>{item.scenario.catalystStateLabel || '재료 상태 확인'}</span>
                    </div>

                    <div className="live-summary-live-issue__scenario-answer">
                      <span>
                        <small>자금 후보 강도</small>
                        <b className={toneClass(liveIssueScenarioPotentialTone(item.scenario))}>{item.scenario.smartMoneyPotentialLabel || item.scenario.capitalDirectionLabel || '판단 제한'}</b>
                      </span>
                      <span>
                        <small>현재 상태</small>
                        <b className={toneClass(liveIssueCurrentStateTone(item.scenario))}>{item.scenario.currentStateLabel || item.scenario.marketDeliveryLabel || entryEnvironmentLabel || '조건 확인'}</b>
                      </span>
                      {item.scenario.horizonLabel && (
                        <span className="is-horizon">
                          <small>시간축</small>
                          <b>{item.scenario.horizonLabel}</b>
                        </span>
                      )}
                    </div>

                    {item.scenario.thesis && <p className="live-summary-live-issue__scenario-thesis">{item.scenario.thesis}</p>}
                    {item.scenario.checkpoints.length > 0 && (
                      <div className="live-summary-live-issue__scenario-check">
                        <small>다음 확인</small>
                        <p>{item.scenario.checkpoints.join(' · ')}</p>
                      </div>
                    )}

                    <button
                      type="button"
                      className="live-summary-live-issue__scenario-detail-toggle"
                      aria-expanded={expanded}
                      onClick={() => setExpandedEvidenceKey(expanded ? '' : issueKey)}
                    >
                      {expanded ? '근거 접기' : '근거 보기'}
                    </button>

                    {expanded && (
                      <div className="live-summary-live-issue__scenario-detail">
                        <div>
                          <span><small>근거 수준</small><b>{liveIssueEvidenceLabel(item.evidenceLevel)}</b></span>
                          <span><small>시장 전달</small><b>{item.scenario.marketDeliveryLabel || '조건 확인'}</b></span>
                          <span><small>현재 진입 환경</small><b className={toneClass(liveIssueInvestmentTone(entryEnvironment))}>{entryEnvironmentLabel || '관찰 우선'}</b></span>
                          <span><small>재료 상태</small><b>{item.scenario.catalystStateLabel || '확인'}</b></span>
                          {item.scenario.preEventStateLabel && item.scenario.preEventState !== 'NO_DATA' && (
                            <span><small>선반영</small><b>{item.scenario.preEventStateLabel}</b></span>
                          )}
                        </div>
                        {item.scenario.reasons.length > 0 && (
                          <div className="live-summary-live-issue__scenario-reasons">
                            {item.scenario.reasons.slice(0, 4).map((reason) => <span key={reason}>{reason}</span>)}
                          </div>
                        )}
                        {item.reason && <p className="live-summary-live-issue__reason">{item.reason}</p>}
                        {watchText && (
                          <div className="live-summary-live-issue__row is-watch">
                            <small>확인할 것</small>
                            <p>{watchText}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                );
              })()}

              {item.sectorImpacts.length > 0 && (
                <div className="live-summary-live-issue__sector-impacts">
                  {item.sectorImpacts.slice(0, 2).map((impact) => (
                    <SectorImpactSummary key={`${item.headline}-${impact.marketType}-${impact.industryCode || impact.sectorName}`} impact={impact} />
                  ))}
                </div>
              )}

              {directStocks.length > 0 && (
                <div className="live-summary-live-issue__row">
                  <small>관련 종목</small>
                  <p>{directStocks.slice(0, 3).join(' · ')}</p>
                </div>
              )}

              {!item.scenario && item.reason && (
                <p className="live-summary-live-issue__reason">{item.reason}</p>
              )}

              {!item.scenario && watchText && (
                <div className="live-summary-live-issue__row is-watch">
                  <small>확인할 것</small>
                  <p>{watchText}</p>
                </div>
              )}
            </article>
          );
        })}
          </div>
        </details>
      )}
    </section>
  );
}

function IntradayMarketBoard({ context }: { context: NormalizedMarketContext }) {
  const [expanded, setExpanded] = useState(false);
  const indexRows = [
    context.domesticIndices.KOSPI,
    context.domesticIndices.KOSDAQ,
    context.domesticIndices.KOSPI200,
    context.domesticIndices.KOSDAQ150,
  ];
  const derivativeRows = [
    { key: 'KOSPI200', point: context.derivatives.KOSPI200 },
    { key: 'KOSDAQ150', point: context.derivatives.KOSDAQ150 },
  ] as const;
  const globalRows = [
    context.globalMarkets.USDKRW,
    context.globalMarkets.NQ,
    context.globalMarkets.ES,
    context.globalMarkets.RTY,
    context.globalMarkets.DXY,
    context.globalMarkets.US10Y_FUT,
  ];

  const compactEvidence = (point: NormalizedDerivativePoint): string => {
    const parts: string[] = [];
    if (point.futuresReturn5m !== null) parts.push(`선물 5m ${formatPct(point.futuresReturn5m, 2)}`);
    if (point.futuresLead5m !== null) parts.push(`선행 ${formatSignedNumber(point.futuresLead5m, 3)}%p`);
    if (point.basisChange5m !== null) parts.push(`Basis ${formatSignedNumber(point.basisChange5m, 2)}`);
    return parts.slice(0, 3).join(' · ') || '5분 방향 데이터 확인 중';
  };

  return (
    <section className={`live-summary-intraday-board ${expanded ? 'is-expanded' : 'is-compact'}`} aria-label="장중 시장 방향">
      <header className="live-summary-intraday-board__compact-head">
        <div>
          <h4>시장 방향</h4>
        </div>
        <button
          type="button"
          className="live-summary-intraday-board__toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? '접기' : '상세'}
        </button>
      </header>

      <div className="live-summary-intraday-compact-grid">
        {[context.domesticIndices.KOSPI, context.domesticIndices.KOSDAQ].map((point) => (
          <article key={point.label} className={toneClass(marketPointTone(point.changeRate))}>
            <div className="live-summary-intraday-compact-grid__identity">
              <small>{point.label}</small>
              <strong>{formatMarketValue(point.value)}</strong>
            </div>
            <span>{point.stale ? '지수 데이터 지연' : 'KIS 실시간 지수'}</span>
            <em>{formatPct(point.changeRate)}</em>
          </article>
        ))}
        {derivativeRows.map(({ key, point }) => {
          const derivativeDelayed = point.stale || point.futuresPrice === null;
          return (
            <article key={key} className={toneClass(derivativeDelayed ? 'warn' : derivativeDirectionTone(point.directionState))}>
              <div className="live-summary-intraday-compact-grid__identity">
                <small>{key} 선물</small>
                <strong>{derivativeDelayed ? '갱신 대기' : point.directionLabel}</strong>
              </div>
              <span>{derivativeDelayed ? '선물 실시간 데이터 지연' : compactEvidence(point)}</span>
              <em>{derivativeDelayed
                ? point.delayMinutes !== null ? `${Math.max(0, Math.round(point.delayMinutes))}분 지연` : '지연'
                : derivativeOiStateLabel(point.oiState)}</em>
            </article>
          );
        })}
      </div>

      {expanded && (
        <div className="live-summary-intraday-board__details">
          <div className="live-summary-intraday-board__section">
            <div className="live-summary-intraday-board__section-head">
              <b>국내 지수</b>
            </div>
            <div className="live-summary-intraday-index-grid">
              {indexRows.map((point) => (
                <article key={point.label} className={toneClass(marketPointTone(point.changeRate))}>
                  <small>{point.label}</small>
                  <strong>{formatMarketValue(point.value)}</strong>
                  <em>{formatPct(point.changeRate)}</em>
                  <span>{point.stale ? '지연' : '실시간'}{point.delayMinutes !== null ? ` · ${Math.max(0, Math.round(point.delayMinutes))}분` : ''}</span>
                </article>
              ))}
            </div>
          </div>

          <div className="live-summary-intraday-board__section">
            <div className="live-summary-intraday-board__section-head">
              <b>선물 · Basis · OI</b>
            </div>
            <div className="live-summary-intraday-derivative-grid">
              {derivativeRows.map(({ key, point }) => (
                <article key={point.label} className={toneClass(derivativeDirectionTone(point.directionState))}>
                  <header>
                    <div><small>{point.label}</small><strong>{formatMarketValue(point.futuresPrice)}</strong></div>
                    <em>{formatPct(point.futuresChangeRate)}</em>
                  </header>
                  <div>
                    <span><small>선물 5m</small><b>{formatPct(point.futuresReturn5m, 3)}</b></span>
                    <span><small>현물 5m</small><b>{formatPct(point.spotReturn5m, 3)}</b></span>
                    <span><small>선물 선행</small><b>{point.futuresLead5m === null ? '—' : `${formatSignedNumber(point.futuresLead5m, 3)}%p`}</b></span>
                    <span><small>Basis 5m <MetricHelp guide={context.metricGuides[`${key}_BASIS`]} label={`${point.label} Basis 5분`} /></small><b>{formatSignedNumber(point.basisChange5m, 2)}</b></span>
                  </div>
                  <footer>
                    <b>{point.directionLabel} · 15m {formatPct(point.futuresReturn15m, 3)}</b>
                    <span>{derivativeFlowFooter(point)}</span>
                  </footer>
                </article>
              ))}
            </div>
          </div>

          <div className="live-summary-intraday-board__section is-global">
            <div className="live-summary-intraday-board__section-head">
              <b>글로벌 환경</b>
            </div>
            <div className="live-summary-intraday-global-strip">
              {globalRows.map((point) => {
                const inverted = point.label === 'USD/KRW' || point.label === 'DXY';
                return (
                  <span key={point.label} className={toneClass(marketPointTone(point.changeRate, inverted))}>
                    <small>{point.label}</small>
                    <b>{formatMarketValue(point.last, 2)}</b>
                    <em>{formatPct(point.changeRate)}</em>
                  </span>
                );
              })}
            </div>
          </div>

          <div className="live-summary-intraday-board__method-note">
            <span><b>방향</b> 선물 5·15분 수익률 · 선물-현물 상대강도 · Basis 변화</span>
            <span><b>확인</b> OI와 외인·기관 선물수급은 방향을 단정하지 않고 보조 근거로만 사용</span>
          </div>
        </div>
      )}
    </section>
  );
}


function priceStateMeta(
  item: NormalizedSectorTopStock | NormalizedHolding,
  vwapGap: number | null,
): { label: string; tone: Tone } {
  if (vwapGap !== null && Number.isFinite(vwapGap)) {
    if (vwapGap >= 2) return { label: 'VWAP 상단 이격', tone: 'warn' };
    if (vwapGap >= 0.4) return { label: 'VWAP 위', tone: 'up' };
    if (vwapGap <= -2) return { label: 'VWAP 하단 이격', tone: 'down' };
    if (vwapGap <= -0.4) return { label: 'VWAP 아래', tone: 'down' };
    return { label: 'VWAP 근접', tone: 'neutral' };
  }

  if (
    item.price !== null
    && item.highPrice !== null
    && item.lowPrice !== null
    && item.highPrice > item.lowPrice
  ) {
    const position = (item.price - item.lowPrice) / (item.highPrice - item.lowPrice);
    if (position >= 0.8) return { label: '장중 상단권', tone: 'up' };
    if (position <= 0.2) return { label: '장중 하단권', tone: 'down' };
    return { label: '장중 중간권', tone: 'neutral' };
  }

  const changeRate = item.changeRate;
  if (changeRate !== null && Number.isFinite(changeRate)) {
    if (changeRate >= 2) return { label: '강세 구간', tone: 'up' };
    if (changeRate >= 0.5) return { label: '상승 구간', tone: 'up' };
    if (changeRate <= -2) return { label: '약세 구간', tone: 'down' };
    if (changeRate <= -0.5) return { label: '하락 구간', tone: 'down' };
    return { label: '보합권', tone: 'neutral' };
  }

  return { label: '가격 변동 제한', tone: 'neutral' };
}

function holdingPriceStateMeta(item: NormalizedHolding): { label: string; tone: Tone } {
  if (
    item.portfolioPositionAvailable
    && item.price !== null
    && item.portfolioAvgPrice !== null
    && item.portfolioAvgPrice > 0
  ) {
    const avgGap = ((item.price - item.portfolioAvgPrice) / item.portfolioAvgPrice) * 100;
    if (avgGap >= 0.4) return { label: '평단 위', tone: 'up' };
    if (avgGap <= -0.4) return { label: '평단 아래', tone: 'down' };
    return { label: '평단 근접', tone: 'neutral' };
  }

  const vwapGap = item.price !== null && item.sessionVwap !== null && item.sessionVwap !== 0
    ? ((item.price - item.sessionVwap) / item.sessionVwap) * 100
    : null;
  return priceStateMeta(item, vwapGap);
}

type FlowMomentumMetric = {
  key: 'velocity' | 'acceleration' | 'delta1m' | 'delta5m';
  label: string;
  value: string;
  tone: Tone;
  available: boolean;
};

type FlowMomentumView = {
  label: string;
  tone: Tone;
  turnedAt: string;
  baselinePointCount: number;
  metrics: FlowMomentumMetric[];
};

function compactTrendTime(value: string): string {
  const raw = cleanText(value);
  if (!raw) return '';
  const timestamp = raw.match(/(?:T|\s)(\d{2}:\d{2})(?::\d{2})?/);
  if (timestamp) return timestamp[1];
  const compact = raw.match(/\b(\d{2}:\d{2})\b/);
  return compact ? compact[1] : '';
}


function flowMomentumView(
  item: NormalizedSectorTopStock | NormalizedHolding,
  stale = false,
): FlowMomentumView | null {
  if (flowUnavailableBecauseOfRowStale(item, stale) || item.investorTrendPointCount < 2) return null;

  const direction = cleanText(item.investorTrendDirection).toUpperCase();
  const minutes = Math.max(0, Math.round(item.investorTrendStreakMinutes));
  let label = '';
  let tone: Tone = 'neutral';

  switch (direction) {
    case 'UP_STREAK':
      label = minutes > 0 ? `개선 ${minutes}분째` : '개선 흐름 지속';
      tone = 'up';
      break;
    case 'DOWN_STREAK':
      label = minutes > 0 ? `약화 ${minutes}분째` : '약화 흐름 지속';
      tone = 'down';
      break;
    case 'UP_TO_DOWN':
      label = '약화 전환';
      tone = 'down';
      break;
    case 'DOWN_TO_UP':
      label = '회복 전환';
      tone = 'up';
      break;
    default:
      return null;
  }

  const velocityRatio = item.investorTrendVelocityRatio;
  const accelerationRatio = item.investorTrendAccelerationRatio;
  const turnedAt = compactTrendTime(item.investorTrendTurnedAt);

  const velocityTone: Tone = velocityRatio !== null && Number.isFinite(velocityRatio)
    ? velocityRatio >= 2
      ? tone
      : velocityRatio >= 1.3
        ? 'warn'
        : 'neutral'
    : 'neutral';

  const accelerationTone: Tone = accelerationRatio !== null && Number.isFinite(accelerationRatio)
    ? accelerationRatio >= 1.5
      ? tone
      : 'neutral'
    : 'neutral';

  const deltaTone = (value: number | null): Tone => {
    if (value === null || !Number.isFinite(value) || value === 0) return 'neutral';
    return value > 0 ? 'up' : 'down';
  };

  const metrics: FlowMomentumMetric[] = [
    {
      key: 'velocity',
      label: '최근 속도',
      value: velocityRatio !== null && Number.isFinite(velocityRatio) ? `${velocityRatio.toFixed(1)}x` : '—',
      tone: velocityTone,
      available: velocityRatio !== null && Number.isFinite(velocityRatio),
    },
    {
      key: 'acceleration',
      label: '5분 대비 가속',
      value: accelerationRatio !== null && Number.isFinite(accelerationRatio) ? `${accelerationRatio.toFixed(1)}x` : '—',
      tone: accelerationTone,
      available: accelerationRatio !== null && Number.isFinite(accelerationRatio),
    },
    {
      key: 'delta1m',
      label: '1분 변화',
      value: item.investorTrendDelta1m !== null && Number.isFinite(item.investorTrendDelta1m)
        ? formatSignedMoney(item.investorTrendDelta1m)
        : '—',
      tone: deltaTone(item.investorTrendDelta1m),
      available: item.investorTrendDelta1m !== null && Number.isFinite(item.investorTrendDelta1m),
    },
    {
      key: 'delta5m',
      label: '5분 변화',
      value: item.investorTrendDelta5m !== null && Number.isFinite(item.investorTrendDelta5m)
        ? formatSignedMoney(item.investorTrendDelta5m)
        : '—',
      tone: deltaTone(item.investorTrendDelta5m),
      available: item.investorTrendDelta5m !== null && Number.isFinite(item.investorTrendDelta5m),
    },
  ];

  const hasAnyMetric = metrics.some((metric) => metric.available);
  if (!hasAnyMetric && !turnedAt && minutes <= 0) return null;

  return {
    label,
    tone,
    turnedAt,
    baselinePointCount: Math.max(0, Math.round(item.investorTrendBaselinePointCount)),
    metrics,
  };
}



function signalHistoryTone(avgReturnPct: number | null | undefined): Tone {
  if (avgReturnPct === null || avgReturnPct === undefined || !Number.isFinite(avgReturnPct)) return 'neutral';
  if (avgReturnPct >= 0.3) return 'up';
  if (avgReturnPct <= -0.3) return 'down';
  return 'neutral';
}






type MonitoringDetailTab = 'EVIDENCE' | 'CHANGE' | 'WHY';

type WhyMovingView = {
  rows: WhyMovingRow[];
};

function normalizeWhyMovingTicker(value: unknown): string {
  const raw = cleanText(value).toUpperCase();
  const digits = raw.replace(/[^0-9]/g, '');
  if (digits.length >= 6) return digits.slice(-6);
  return digits;
}

function compactNameKey(value: unknown): string {
  return cleanText(value).replace(/\s+/g, '').toLowerCase();
}

function issueEpoch(value: unknown): number {
  const raw = cleanText(value);
  if (!raw) return 0;
  const parsed = Date.parse(raw.includes('T') ? raw : raw.replace(' ', 'T'));
  return Number.isFinite(parsed) ? parsed : 0;
}

function issueDirectlyMatchesStock(
  issue: LiveIssue,
  item: NormalizedSectorTopStock | NormalizedHolding,
): boolean {
  const ticker = normalizeWhyMovingTicker(item.ticker);
  if (ticker && issue.relatedTickers.some((value) => normalizeWhyMovingTicker(value) === ticker)) {
    return true;
  }

  const nameKey = compactNameKey(item.name);
  return Boolean(
    nameKey
      && issue.relatedStockNames.some((value) => compactNameKey(value) === nameKey),
  );
}

function buildWhyMovingView(
  item: NormalizedSectorTopStock | NormalizedHolding,
  sectorContext: NormalizedSector | null,
  stale: boolean,
  marketContext: NormalizedMarketContext,
  marketOpen: boolean,
): WhyMovingView {
  const priceAt = cleanText(item.lastEventAt || item.priceAsOf);
  const inputs: WhyMovingRowInput[] = [
    { key: 'price', label: '가격 반응', kind: 'price', changeRate: item.changeRate, observedAt: priceAt, stale },
  ];
  if (item.tradingValue !== null) {
    inputs.push({ key: 'trading-value', label: '거래대금', kind: 'turnover',
      amount: item.tradingValue, observedAt: priceAt, stale });
  }
  inputs.push(
    { key: 'smart-money', label: '외인·기관', kind: 'net-flow',
      amount: resolvedSmartMoney(item), observedAt: cleanText(item.investorAggregateEventAt || item.investorReceivedAt), stale },
    { key: 'program', label: '프로그램', kind: 'net-flow',
      amount: item.programNetBuyExecAmount, matched: item.programMatched,
      observedAt: item.programMatched ? cleanText(item.programReceivedAt) : '', stale },
  );
  if (item.investorTrendPointCount >= 2) {
    inputs.push({ key: 'flow-trend', label: '수급 변화', kind: 'flow-trend',
      directionState: item.investorTrendDirection, trendPointCount: item.investorTrendPointCount,
      turnedAt: item.investorTrendTurnedAt, observedAt: item.investorTrendUpdatedAt, stale });
  }
  if (sectorContext && Number.isFinite(sectorContext.changeRate)) {
    inputs.push({ key: 'sector', label: '소속 업종', kind: 'sector',
      changeRate: sectorContext.changeRate, observedAt: cleanText(sectorContext.latestRealtimeEventAt) });
  }
  for (const key of ['KOSPI200', 'KOSDAQ150'] as const) {
    const point = marketContext.derivatives[key];
    inputs.push({ key: `market-${key}`, label: key === 'KOSPI200' ? '코스피200 선물' : '코스닥150 선물', kind: 'derivative',
      changeRate: point.futuresChangeRate, marketBasis: point.marketBasis,
      oiChangeCumulative: point.oiChangeCumulative, directionState: point.directionState,
      directionDataStatus: point.directionDataStatus, observedAt: cleanText(point.updatedAt || marketContext.updatedAt),
      stale: marketContext.stale || point.stale });
  }
  for (const key of ['USDKRW', 'NQ', 'ES'] as const) {
    const point = marketContext.globalMarkets[key];
    const label = key === 'USDKRW' ? '원/달러' : key === 'NQ' ? '나스닥100 선물' : 'S&P500 선물';
    inputs.push({ key: `global-${key}`, label, kind: key === 'USDKRW' ? 'fx' : 'global',
      changeRate: point.changeRate, observedAt: cleanText((point as any).updatedAt || marketContext.updatedAt) });
  }
  const rows = inputs.map((input) => summarizeWhyMovingRow(input, { marketOpen })).map((row) => {
    if (row.timeLabel !== '시각 미확인') return row;
    if ((row.key === 'price' || row.key === 'trading-value') && item.sessionDate) {
      return { ...row, timeLabel: `${item.sessionDate} 기준`, timeDetail: '거래일 기준 데이터' };
    }
    if (row.key === 'smart-money' || row.key === 'program' || row.key === 'flow-trend') {
      return { ...row, timeLabel: '데이터 미확인', timeDetail: '원천 관측 시각 또는 해당 거래일 데이터가 확인되지 않았습니다.' };
    }
    return {
      ...row,
      timeLabel: '시간 신뢰도 확인 필요',
      timeDetail: '원천 시각이 없어 현재성 근거로 사용하지 않습니다.',
    };
  });
  return { rows };
}

function WhyMovingRows({ rows }: { rows: WhyMovingRow[] }) {
  if (!rows.length) return <p className="live-summary-monitor-pane__empty">확인 가능한 동행 데이터가 없습니다.</p>;
  return (
    <dl className="live-summary-why-moving__rows">
      {rows.map((row) => (
        <div key={row.key}>
          <dt>{row.label}</dt>
          <dd>
            <span className={toneClass(row.tone)} title={row.sourceDetail || undefined}>{row.summary}</span>
            <small title={row.timeDetail}>{row.timeLabel}</small>
          </dd>
        </div>
      ))}
    </dl>
  );
}

function WhyMovingPanel({
  item, sectorContext, stale, marketContext, marketOpen,
}: {
  item: NormalizedSectorTopStock | NormalizedHolding;
  sectorContext: NormalizedSector | null;
  stale: boolean;
  marketContext: NormalizedMarketContext;
  marketOpen: boolean;
}) {
  const view = useMemo(
    () => buildWhyMovingView(item, sectorContext, stale, marketContext, marketOpen),
    [item, sectorContext, stale, marketContext, marketOpen],
  );
  const stockRows = view.rows.filter((row) => !row.key.startsWith('market-') && !row.key.startsWith('global-'));
  const marketRows = view.rows.filter((row) => row.key.startsWith('market-') || row.key.startsWith('global-'));
  const conclusion = '현재 종목·수급·시장 동행을 함께 확인합니다.';

  return (
    <section className="live-summary-why-moving is-grouped" aria-label="왜 움직이나">
      <div className="live-summary-why-moving__conclusion">
        <small>핵심</small>
        <b>{conclusion}</b>
      </div>



      <section className="live-summary-why-moving__group">
        <header><b>종목·수급</b><small>현재 종목에서 직접 확인되는 값</small></header>
        <WhyMovingRows rows={stockRows} />
      </section>

      <section className="live-summary-why-moving__group">
        <header><b>시장 환경</b><small>국내 파생·환율·해외 선물 동행</small></header>
        <WhyMovingRows rows={marketRows} />
      </section>

      <p className="live-summary-why-moving__notice">현재 관측되는 가격·수급·시장 동행을 표시하며, 단순 동행을 인과관계로 확정하지 않습니다.</p>
    </section>
  );
}

function PositionImpactPanel({
  holding,
  assessment,
}: {
  holding: NormalizedHolding;
  assessment: NormalizedRiskAssessment;
}) {
  const [impactOpen, setImpactOpen] = useState(false);
  const [reductionPctInput, setReductionPctInput] = useState('');
  const [simulationOpen, setSimulationOpen] = useState(false);

  const position = useMemo(
    () => buildPortfolioPosition(holding.portfolioAvgPrice, holding.portfolioQuantity),
    [holding.portfolioAvgPrice, holding.portfolioQuantity],
  );

  useEffect(() => {
    setImpactOpen(false);
    setReductionPctInput('');
    setSimulationOpen(false);
  }, [holding.ticker]);

  const snapshot = useMemo(
    () => position ? buildPositionSnapshot(position, holding.price) : null,
    [holding.price, position],
  );
  const reductionPct = nullableNumber(reductionPctInput);
  const reductionSimulation = useMemo(
    () => position ? buildReductionSimulation(position, holding.price, reductionPct) : null,
    [holding.price, position, reductionPct],
  );

  if (!position) return null;

  return (
    <section className={`live-summary-position-impact is-trade-compact is-collapsible ${impactOpen ? 'is-open' : 'is-collapsed'} ${snapshot && snapshot.unrealizedPnl < 0 ? 'is-loss' : snapshot && snapshot.unrealizedPnl > 0 ? 'is-profit' : ''}`}>
      <button
        type="button"
        className="live-summary-detail-disclosure-toggle"
        onClick={() => setImpactOpen((open) => !open)}
        aria-expanded={impactOpen}
      >
        <span>
          <small>내 포지션</small>
          <strong>평단 {formatPrice(position.averagePrice)}원</strong>
        </span>
        <em className={snapshot ? toneClass(flowTone(snapshot.unrealizedPnl)) : ''}>
          {snapshot ? `${formatSignedMoney(snapshot.unrealizedPnl)} (${formatPct(snapshot.unrealizedPnlPct)})` : '손익 확인 중'}
        </em>
        <b>{impactOpen ? '닫기' : '포지션 관리'}</b>
      </button>

      {impactOpen && (
        <div className="live-summary-detail-disclosure-body">
          <div className="live-summary-position-impact__metrics">
            <span><small>내 평단</small><b>{formatPrice(position.averagePrice)}원</b></span>
            <span><small>보유 수량</small><b>{position.quantity.toLocaleString('ko-KR')}주</b></span>
            <span><small>현재 평가액</small><b>{snapshot ? formatTradingValue(snapshot.marketValue) : '확인 중'}</b></span>
            <span>
              <small>평가손익</small>
              <b className={snapshot ? toneClass(flowTone(snapshot.unrealizedPnl)) : ''}>
                {snapshot ? `${formatSignedMoney(snapshot.unrealizedPnl)} (${formatPct(snapshot.unrealizedPnlPct)})` : '확인 중'}
              </b>
            </span>
          </div>

          <div className="live-summary-position-impact__context">
            <span>종목 리스크 상태</span>
            <b>{assessment.stateLabel}</b>
            <small>저장된 평단·수량은 계좌 손익 계산에만 사용하며 종목 자체 리스크 상태 산정과는 분리합니다.</small>
          </div>

          <div className="live-summary-position-impact__quick-guide">
            <span><small>비중 조절 계산</small><b>필요할 때만 10% · 20% · 30% 가정</b></span>
            <div>
              {[10, 20, 30].map((pct) => (
                <button
                  type="button"
                  key={pct}
                  onClick={() => {
                    setReductionPctInput(String(pct));
                    setSimulationOpen(true);
                  }}
                >
                  {pct}% 축소 가정
                </button>
              ))}
            </div>
            <small>권고 비율이 아니라 사용자가 비교하기 위한 시뮬레이션 값입니다.</small>
          </div>

          <section className={`live-summary-position-impact__simulation ${simulationOpen ? 'is-open' : 'is-collapsed'}`}>
            <button
              type="button"
              className="live-summary-position-impact__simulation-toggle"
              onClick={() => setSimulationOpen((open) => !open)}
              aria-expanded={simulationOpen}
            >
              <span>
                <small>사용자 선택 기준</small>
                <strong>{POSITION_COPY.simulationTitle}</strong>
              </span>
              <b>{simulationOpen ? '접기' : '열기'}</b>
            </button>

            {simulationOpen && (
              <div className="live-summary-position-impact__simulation-body">
                <p>{POSITION_COPY.simulationDescription}</p>
                <label>
                  <span>매도 가정 비율</span>
                  <div><input
                    inputMode="decimal"
                    value={reductionPctInput}
                    onChange={(event) => setReductionPctInput(event.target.value.replace(/[^0-9.]/g, ''))}
                    placeholder="직접 입력"
                    aria-label={`${holding.name} 매도 가정 시뮬레이션 비율`}
                  /><em>%</em></div>
                </label>

                {reductionSimulation && (
                  <div className="live-summary-position-impact__simulation-result">
                    <span><small>가정 매도 수량</small><b>{reductionSimulation.reductionQuantity.toLocaleString('ko-KR')}주 ({formatPct(reductionSimulation.effectiveReductionPct, 1)})</b></span>
                    <span><small>가정 후 잔여 수량</small><b>{reductionSimulation.remainingQuantity.toLocaleString('ko-KR')}주</b></span>
                    <span><small>예상 거래대금</small><b>{formatTradingValue(reductionSimulation.estimatedProceeds)}</b></span>
                    <span>
                      <small>예상 실현손익</small>
                      <b className={toneClass(flowTone(reductionSimulation.estimatedRealizedPnl))}>
                        {formatSignedMoney(reductionSimulation.estimatedRealizedPnl)}
                      </b>
                    </span>
                  </div>
                )}
              </div>
            )}
          </section>

          <footer>{POSITION_COPY.legalNotice}</footer>
        </div>
      )}
    </section>
  );
}


function FundamentalBrief({
  item,
}: {
  item: NormalizedSectorTopStock | NormalizedHolding;
}) {
  const [open, setOpen] = useState(false);
  const sourceDate = item.fundamentalSourceTradeDate || item.fundamentalSnapshotDate;
  const metrics = [
    item.fundamentalRevenueCagr3yPct !== null
      ? `매출 3년 ${formatPct(item.fundamentalRevenueCagr3yPct, 1)}`
      : '',
    item.fundamentalOperatingMarginPct !== null
      ? `영업이익률 ${formatPct(item.fundamentalOperatingMarginPct, 1)}`
      : '',
    item.fundamentalRoePct !== null
      ? `ROE ${formatPct(item.fundamentalRoePct, 1)}`
      : '',
    item.fundamentalPer !== null
      ? `PER ${item.fundamentalPer.toFixed(1)}`
      : '',
  ].filter(Boolean).slice(0, 3);

  useEffect(() => {
    setOpen(false);
  }, [item.ticker]);

  if (!item.fundamentalAvailable) {
    return (
      <section className={`live-summary-fundamental-brief is-pending is-collapsible ${open ? 'is-open' : 'is-collapsed'}`}>
        <button
          type="button"
          className="live-summary-detail-disclosure-toggle"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
        >
          <span>
            <small>재무·가치</small>
            <strong>스냅샷 확인 중</strong>
          </span>
          <b>{open ? '접기' : '열기'}</b>
        </button>
        {open && (
          <div className="live-summary-detail-disclosure-body">
            <p className="live-summary-detail-disclosure-message">가격·수급 판단은 현재 제공 가능한 최신 데이터로 계산하며, 재무 방향성은 별도로 확인합니다.</p>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className={`live-summary-fundamental-brief is-collapsible ${open ? 'is-open' : 'is-collapsed'}`}>
      <button
        type="button"
        className="live-summary-detail-disclosure-toggle"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span>
          <small>재무·가치</small>
          <strong>{item.fundamentalSummary}</strong>
        </span>
        <b>{open ? '접기' : '열기'}</b>
      </button>

      {open && (
        <div className="live-summary-detail-disclosure-body">
          {sourceDate && <div className="live-summary-detail-disclosure-date">{sourceDate} 기준</div>}

          <div className="live-summary-fundamental-brief__scores">
            <span className={toneClass(item.fundamentalDirectionTone)}>
              <small>기업체력</small>
              <b>{item.fundamentalScore === null ? '—' : Math.round(item.fundamentalScore)}</b>
              <em>{item.fundamentalDirectionLabel}</em>
            </span>
            <span className={toneClass(item.fundamentalValuationTone)}>
              <small>밸류에이션</small>
              <b>{item.fundamentalValuationScore === null ? '—' : Math.round(item.fundamentalValuationScore)}</b>
              <em>{item.fundamentalValuationLabel}</em>
            </span>
          </div>

          {item.fundamentalReasons.length > 0 && (
            <div className="live-summary-fundamental-brief__reasons">
              {item.fundamentalReasons.slice(0, 2).map((reason) => (
                <span key={reason}>{reason}</span>
              ))}
            </div>
          )}

          {metrics.length > 0 && (
            <div className="live-summary-fundamental-brief__metrics">
              {metrics.map((metric) => <span key={metric}>{metric}</span>)}
            </div>
          )}

          {item.fundamentalFinancialWarningText && (
            <p className="live-summary-fundamental-brief__warning">
              {item.fundamentalFinancialWarningText}
            </p>
          )}
        </div>
      )}
    </section>
  );
}


function riskEvidenceEventOutcome(
  status: string | null | undefined,
  value: number | null | undefined,
  label: string,
): string {
  const normalized = cleanText(status).toUpperCase();
  if (normalized === 'AVAILABLE' && value !== null && value !== undefined && Number.isFinite(value)) {
    return `${label} ${formatPct(value)}`;
  }
  if (normalized === 'PENDING') return `${label} 대기`;
  if (normalized === 'NOT_APPLICABLE') return `${label} 해당 없음`;
  return `${label} 미확인`;
}

function RiskEvidenceProofPanel({ ticker }: { ticker: string }) {
  const normalizedTicker = cleanText(ticker).replace(/[^0-9]/g, '').slice(-6);
  const [proof, setProof] = useState<RiskEvidenceProof | null>(null);
  const [verification, setVerification] = useState<RiskEvidenceChainVerification | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [eventsExpanded, setEventsExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
    setEventsExpanded(false);
    if (!/^\d{6}$/.test(normalizedTicker)) {
      setProof(null);
      setVerification(null);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    Promise.all([
      getTickerRiskEvidence(normalizedTicker, controller.signal),
      getTickerRiskEvidenceVerification(normalizedTicker, controller.signal),
    ])
      .then(([nextProof, nextVerification]) => {
        if (!controller.signal.aborted) {
          setProof(nextProof);
          setVerification(nextVerification);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setProof(null);
          setVerification(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [normalizedTicker]);

  if (!normalizedTicker) return null;

  const sample = Math.max(0, Math.round(proof?.completed60mSampleCount ?? 0));
  const minimum = Math.max(1, Math.round(proof?.minimumSample ?? 10));
  const totalEvents = Math.max(0, Math.round(proof?.qualifyingEventCount ?? 0));
  const sessions = Math.max(0, Math.round(proof?.uniqueSessionCount ?? 0));
  const remaining = Math.max(0, Math.round(proof?.remainingToStatistics ?? (minimum - sample)));
  const statisticsReady = proof?.statisticsReady ?? proof?.proofReady ?? false;
  const recentEvents = proof?.recentEvents ?? [];
  const verifiableEvents = recentEvents.filter((event) => (
    cleanText(event.outcome10mStatus).toUpperCase() === 'AVAILABLE'
    || cleanText(event.outcome30mStatus).toUpperCase() === 'AVAILABLE'
    || cleanText(event.outcome60mStatus).toUpperCase() === 'AVAILABLE'
    || cleanText(event.closeStatus).toUpperCase() === 'AVAILABLE'
    || cleanText(event.excursionStatus).toUpperCase() === 'AVAILABLE'
  ));
  const hiddenPendingEvents = Math.max(0, recentEvents.length - verifiableEvents.length);
  const progressPct = Math.max(0, Math.min(100, minimum > 0 ? (sample / minimum) * 100 : 0));
  const verificationLabel = verification?.available
    ? verification.verified
      ? `기록 확인 ${Math.max(0, Math.round(verification.checkedRows ?? 0))}건`
      : '기록 확인 필요'
    : '기록 무결성 대기';

  const cardStyle: React.CSSProperties = {
    border: '1px solid #d9e3ec',
    borderRadius: 12,
    background: '#ffffff',
    overflow: 'hidden',
  };
  const mutedStyle: React.CSSProperties = {
    color: '#718397',
    fontSize: 9.5,
    lineHeight: 1.45,
  };
  const metricStyle: React.CSSProperties = {
    minWidth: 0,
    padding: '8px 9px',
    border: '1px solid #e4ebf1',
    borderRadius: 9,
    background: '#f8fafc',
  };

  return (
    <section style={cardStyle} aria-label="위험 변화 운영 검증">
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: 10,
        padding: '9px 10px',
        background: expanded ? '#fbfcfd' : '#ffffff',
      }}>
        <div style={{ minWidth: 0 }}>
          <small style={{ display: 'block', color: '#8090a2', fontSize: 8.5, fontWeight: 850 }}>
            운영 검증
          </small>
          <strong style={{ display: 'block', marginTop: 1, color: '#20354a', fontSize: 11.5, fontWeight: 900 }}>
            위험 변화 기록
          </strong>
          <span style={{ ...mutedStyle, display: 'block', marginTop: 2 }}>
            {loading && !proof
              ? '실제 위험 전환 기록을 확인하고 있습니다.'
              : statisticsReady
                ? `60분 결과 ${sample}건 · 거래일 ${sessions}일 · 통계 공개 중`
                : totalEvents > 0
                  ? `실제 위험 전환 ${totalEvents}건 · 60분 결과 ${sample}건 · 통계까지 ${remaining}건`
                  : '실제 위험 전환이 발생하면 그 시점과 이후 결과를 기록합니다.'}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          style={{
            flex: '0 0 auto',
            minHeight: 27,
            padding: '0 9px',
            border: '1px solid #cfdbe5',
            borderRadius: 8,
            background: '#f8fafc',
            color: '#36546d',
            fontSize: 9,
            fontWeight: 900,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {expanded ? '접기' : totalEvents > 0 ? '검증 기록 보기' : '설명 보기'}
        </button>
      </div>

      {expanded && (
        <div style={{ padding: '10px', borderTop: '1px solid #e7edf2' }}>
          {loading && !proof ? (
            <p style={{ ...mutedStyle, margin: 0 }}>운영 위험 경고의 실제 결과를 확인하고 있습니다.</p>
          ) : statisticsReady ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: 7 }}>
                <div style={metricStyle}>
                  <small style={mutedStyle}>60분 내 추가 하락</small>
                  <b style={{ display: 'block', marginTop: 3, color: '#c24141', fontSize: 13 }}>
                    {proof?.declineRate60mPct === null || proof?.declineRate60mPct === undefined
                      ? '—'
                      : `${proof.declineRate60mPct.toFixed(1)}%`}
                  </b>
                  <span style={{ ...mutedStyle, display: 'block', marginTop: 2 }}>{sample}건 완료</span>
                </div>
                <div style={metricStyle}>
                  <small style={mutedStyle}>60분 평균 반응</small>
                  <b className={toneClass(signalHistoryTone(proof?.avgReturn60mPct))} style={{ display: 'block', marginTop: 3, fontSize: 13 }}>
                    {formatPct(proof?.avgReturn60mPct)}
                  </b>
                  <span style={{ ...mutedStyle, display: 'block', marginTop: 2 }}>경고 시점 대비</span>
                </div>
                <div style={metricStyle}>
                  <small style={mutedStyle}>평균 최대 추가 하락</small>
                  <b style={{ display: 'block', marginTop: 3, color: '#c24141', fontSize: 13 }}>
                    {proof?.avgMaePct === null || proof?.avgMaePct === undefined ? '—' : `${Math.abs(proof.avgMaePct).toFixed(2)}%`}
                  </b>
                  <span style={{ ...mutedStyle, display: 'block', marginTop: 2 }}>경고 후 당일</span>
                </div>
              </div>
              <p style={{ ...mutedStyle, margin: '7px 0 0' }}>
                실제 위험 전환 {totalEvents}건 · 거래일 {sessions}일 · 같은 위험 상태가 계속되는 동안은 새 표본으로 늘리지 않습니다.
              </p>
            </>
          ) : totalEvents > 0 ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                <div>
                  <b style={{ color: '#2a4055', fontSize: 10.5 }}>사건별 증빙은 첫 기록부터 공개합니다.</b>
                  <span style={{ ...mutedStyle, display: 'block', marginTop: 3 }}>
                    실제 위험 전환 {totalEvents}건 · 60분 결과 확정 {sample}건
                  </span>
                </div>
                <strong style={{ color: '#9a6418', fontSize: 10, whiteSpace: 'nowrap' }}>
                  통계까지 {remaining}건
                </strong>
              </div>
              <div style={{ height: 4, marginTop: 8, borderRadius: 999, background: '#edf2f6', overflow: 'hidden' }}>
                <span style={{ display: 'block', width: `${progressPct}%`, height: '100%', background: '#d29a3a', borderRadius: 999 }} />
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '24px minmax(0, 1fr)', gap: 8, alignItems: 'start' }}>
              <span aria-hidden="true" style={{
                display: 'grid',
                placeItems: 'center',
                width: 24,
                height: 24,
                borderRadius: 7,
                background: '#fff7e8',
                color: '#ad741c',
                fontSize: 12,
                fontWeight: 950,
              }}>!</span>
              <div>
                <b style={{ color: '#2a4055', fontSize: 10.5 }}>아직 실제 위험 전환 기록이 없습니다.</b>
                <span style={{ ...mutedStyle, display: 'block', marginTop: 2 }}>
                  보유 종목이 새로 ‘방어 필요’ 또는 ‘위험도 매우 높음’으로 전환·격상되면 당시 가격과 판단 근거를 저장하고 10·30·60분 실제 결과를 이어서 확인합니다.
                </span>
              </div>
            </div>
          )}

          {verifiableEvents.length > 0 && (
            <div style={{ marginTop: 9, paddingTop: 8, borderTop: '1px solid #e7edf2' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <b style={{ color: '#2a4055', fontSize: 10.5 }}>검증 가능한 위험 사건</b>
                <small style={mutedStyle}>{verifiableEvents.length}건 결과 확보</small>
              </div>
              <div style={{ marginTop: 5, display: 'grid', gap: 5 }}>
                {(eventsExpanded ? verifiableEvents : verifiableEvents.slice(0, 3)).map((event) => {
                  const price = event.eventPrice !== null && event.eventPrice !== undefined && Number.isFinite(Number(event.eventPrice))
                    ? Number(event.eventPrice)
                    : null;
                  const mae = event.excursionStatus === 'AVAILABLE' && event.maePct !== null && event.maePct !== undefined
                    ? `최대 추가하락 -${Math.abs(event.maePct).toFixed(2)}%`
                    : '';
                  return (
                    <details key={`${event.id ?? ''}-${event.detectedAt ?? ''}`} style={{
                      padding: '7px 8px',
                      border: '1px solid #e5ebf0',
                      borderRadius: 9,
                      background: '#fbfcfd',
                    }}>
                      <summary style={{ cursor: 'pointer', listStyle: 'none' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'baseline', gap: 8 }}>
                          <span style={{ minWidth: 0, color: event.riskState === 'HIGH_RISK' ? '#b93a3a' : '#a86e18', fontSize: 9.5, fontWeight: 900 }}>
                            {event.riskStateLabel || '위험 변화'} · {formatIssuePublishedAt(event.detectedAt || '')}
                          </span>
                          <b style={{ color: '#24384b', fontSize: 10.5 }}>
                            {price !== null && price > 0 ? `${price.toLocaleString('ko-KR')}원` : '가격 확인 중'}
                          </b>
                        </div>
                        <span style={{ ...mutedStyle, display: 'block', marginTop: 3 }}>
                          {riskEvidenceEventOutcome(event.outcome60mStatus, event.outcome60mReturnPct, '60분')}
                          {mae ? ` · ${mae}` : ''}
                        </span>
                      </summary>
                      <div style={{ marginTop: 7, paddingTop: 7, borderTop: '1px dashed #dfe7ee' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 9px', ...mutedStyle }}>
                          <span>{riskEvidenceEventOutcome(event.outcome10mStatus, event.outcome10mReturnPct, '10분')}</span>
                          <span>{riskEvidenceEventOutcome(event.outcome30mStatus, event.outcome30mReturnPct, '30분')}</span>
                          <span>{riskEvidenceEventOutcome(event.outcome60mStatus, event.outcome60mReturnPct, '60분')}</span>
                          <span>{riskEvidenceEventOutcome(event.closeStatus, event.closeReturnPct, '종가')}</span>
                          {mae && <span>{mae}</span>}
                        </div>
                        <details style={{ marginTop: 6 }}>
                          <summary style={{ cursor: 'pointer', color: '#66798d', fontSize: 8.5, fontWeight: 850 }}>
                            검증 정보
                          </summary>
                          <div style={{ ...mutedStyle, marginTop: 4, fontSize: 8.5 }}>
                            <span style={{ display: 'block' }}>정책 {event.actionPolicyVersion || '확인 중'}</span>
                            <span style={{ display: 'block' }}>위험 계산 {event.riskCalculationVersion || '확인 중'}</span>
                            <span style={{ display: 'block' }}>
                              {event.payloadHashPrefix ? `기록 ID ${event.payloadHashPrefix}` : '기록 ID 확인 중'}
                            </span>
                          </div>
                        </details>
                      </div>
                    </details>
                  );
                })}
              </div>
              {verifiableEvents.length > 3 && (
                <button
                  type="button"
                  onClick={() => setEventsExpanded((current) => !current)}
                  aria-expanded={eventsExpanded}
                  style={{
                    width: '100%',
                    minHeight: 27,
                    marginTop: 6,
                    border: '1px solid #d9e3ec',
                    borderRadius: 8,
                    background: '#ffffff',
                    color: '#526b82',
                    fontSize: 9,
                    fontWeight: 900,
                    cursor: 'pointer',
                  }}
                >
                  {eventsExpanded ? '이전 검증 기록 접기' : `이전 검증 기록 ${verifiableEvents.length - 3}건 펼쳐보기`}
                </button>
              )}
            </div>
          )}

          {hiddenPendingEvents > 0 && (
            <div style={{ marginTop: 8, padding: '7px 8px', borderRadius: 8, background: '#f7f9fb', ...mutedStyle }}>
              결과가 아직 없거나 해당 없음인 최근 기록 {hiddenPendingEvents}건은 반복 노출하지 않고 결과 확보 후 목록에 표시합니다.
            </div>
          )}

          <div style={{ marginTop: 8, paddingTop: 7, borderTop: '1px dashed #dfe7ee', ...mutedStyle }}>
            초기 관찰 상태는 성과 통계에서 제외하고 실제 위험 전환·격상만 집계합니다. 과거 결과는 미래 성과를 보장하지 않습니다.
            {` · ${verificationLabel}`}
          </div>
        </div>
      )}
    </section>
  );
}

type HoldingSessionMeta = {
  eyebrow: string;
  badge: string;
  description: string;
};

function holdingSessionMeta(marketOpen: boolean, marketSession = ''): HoldingSessionMeta {
  const session = cleanText(marketSession).toUpperCase();
  if (session.includes('NXT_AFTER') || session === 'NXT') {
    return {
      eyebrow: '넥스트장 대응',
      badge: 'NXT',
      description: '가격은 NXT 세션을 보고, 외국인·기관·프로그램 수급은 당일 KRX 정규장 최종 기준으로 분리해 봅니다.',
    };
  }
  if (session.includes('TRANSITION')) {
    return {
      eyebrow: '장마감 전환',
      badge: '15:30–15:40',
      description: 'KRX 정규장 최종 가격·수급이 확정되는 전환 구간입니다.',
    };
  }
  if (marketOpen || session.includes('MARKET_OPEN') || session.includes('KRX_REGULAR') || session === 'REGULAR') {
    return { eyebrow: '위험관리', badge: '장중', description: '현재 KRX 가격·수급과 시장 환경을 기준으로 위험관리 단계를 봅니다.' };
  }
  if (session.includes('NIGHT')) {
    return { eyebrow: '최근 판단 유지', badge: '야간', description: '국내장은 마감 기준을 유지하고 외부 시장 압력과 다음 장 확인 조건을 함께 봅니다.' };
  }
  if (session.includes('PRE')) {
    return { eyebrow: '최근 판단 유지', badge: '장 시작 전', description: '직전 장 판단과 개장 전 변화를 함께 확인합니다.' };
  }
  if (session.includes('CLOSED') || session.includes('POST')) {
    return { eyebrow: '장마감 대응', badge: '마감', description: '당일 최종 가격·수급을 기준으로 다음 장 확인 조건을 봅니다.' };
  }
  return { eyebrow: '최근 장 기준 대응', badge: '마감', description: '최근 확정 판단을 유지하며 다음 확인 데이터를 기다립니다.' };
}

function holdingDecisionConfidence(item: NormalizedHolding): string {
  if (cleanText(item.decisionEvidence.strengthLabel)) return item.decisionEvidence.strengthLabel;
  const confidence = item.actionDecision.confidence;
  if (confidence !== null && Number.isFinite(confidence)) return `${Math.round(confidence)}%`;
  const coverage = item.actionDecision.evidenceCoveragePct;
  if (coverage !== null && Number.isFinite(coverage)) return `근거 ${Math.round(coverage)}%`;
  return '확인 중';
}

function holdingFactorRows(
  item: NormalizedHolding,
  marketContext: NormalizedMarketContext,
  stale = false,
): Array<{ label: string; value: string; tone: Tone }> {
  const price = holdingPriceStateMeta(item);
  const flow = flowOverviewMeta(item, false);
  const valuationLabel = cleanText(item.fundamentalValuationLabel) || '중기 가치 확인 중';
  return [
    { label: '가격', value: price.label, tone: price.tone },
    { label: '수급', value: flow.label, tone: flow.tone },
    {
      label: '시장',
      value: marketContext.available ? marketContext.headline : '시장 방향 확인 중',
      tone: marketContext.available ? marketContext.tone : 'neutral',
    },
    { label: '밸류에이션', value: valuationLabel, tone: item.fundamentalValuationTone || 'neutral' },
  ];
}

function HoldingCurrentStateSummary({ item, stale = false }: { item: NormalizedHolding; stale?: boolean }) {
  const priceState = holdingPriceStateMeta(item);
  const flow = flowOverviewMeta(item, false);
  const boxUpper = item.boxSupport.available ? item.boxSupport.upperPrice : null;
  const boxLower = item.boxSupport.available ? item.boxSupport.lowerPrice : null;
  const upper = boxUpper ?? item.highPrice;
  const downside = boxLower ?? item.lowPrice;
  const upperLabel = boxUpper !== null ? `박스 상단 ${formatPrice(boxUpper)}`
    : upper !== null ? `당일 고가 ${formatPrice(upper)}`
      : '기준 데이터 없음';
  const downsideLabel = boxLower !== null ? `박스 하단 ${formatPrice(boxLower)}`
    : downside !== null ? `당일 저가 ${formatPrice(downside)}`
      : '기준 데이터 없음';

  return (
    <section className="live-summary-holding-state-summary" aria-label="현재 상태 요약">
      <header><small>현재 상태</small><b>판단을 유지시키는 핵심 값</b></header>
      <div className="live-summary-holding-state-summary__metrics">
        <span className={toneClass(flow.tone)}><small>수급</small><b>{flow.label}</b></span>
        <span className={toneClass(priceState.tone)}><small>가격 위치</small><b>{priceState.label}</b></span>
        <span><small>거래대금</small><b>{formatTradingValue(item.tradingValue)}</b></span>
      </div>
      <div className="live-summary-holding-state-summary__levels">
        <span><small>현재 확인 기준</small><b>{item.sessionVwap !== null ? `VWAP ${formatPrice(item.sessionVwap)}` : 'VWAP 데이터 없음'}</b></span>
        <span><small>위로 확인</small><b>{upperLabel}</b></span>
        <span className={downside !== null ? 'is-risk' : ''}><small>아래 위험</small><b>{downsideLabel}</b></span>
      </div>
    </section>
  );
}


function DecisionAuthoritySummary({
  item,
  mode,
  marketContext,
  marketOpen,
  marketSession = '',
  latestChange = null,
  stale = false,
}: {
  item: NormalizedSectorTopStock | NormalizedHolding;
  mode: 'stock' | 'holding';
  marketContext: NormalizedMarketContext;
  marketOpen: boolean;
  marketSession?: string;
  latestChange?: NormalizedChangeEvent | null;
  stale?: boolean;
}) {
  const decision = item.actionDecision;
  const evidence = item.decisionEvidence;
  const headline = decision.available
    ? (decision.headline || decision.actionLabel || item.riskAssessment.stateLabel)
    : item.riskAssessment.stateLabel;
  const summary = evidence.summary
    || decision.reasons.slice(0, 2).join(' · ')
    || item.riskAssessment.summary
    || '현재 판단 근거를 확인 중입니다.';

  if (mode === 'holding') {
    const holding = item as NormalizedHolding;
    if (holding.judgmentContract?.available) {
      return <HoldingJudgmentContractPanel holding={holding} />;
    }
    const thesis = holding.holdingThesis || EMPTY_HOLDING_THESIS;
    const session = holdingSessionMeta(marketOpen, marketSession);
    const fallbackRisk = fallbackHoldingRiskStage(holding);
    const factors = holdingFactorRows(holding, marketContext, stale).filter((factor) => factor.label !== '밸류에이션');
    const returnPct = holding.price !== null && holding.portfolioAvgPrice !== null && holding.portfolioAvgPrice !== 0
      ? ((holding.price - holding.portfolioAvgPrice) / holding.portfolioAvgPrice) * 100
      : null;
    const changeHeadline = latestChange ? changeTransitionLabel(latestChange) : '변화 없음';

    return (
      <section className="live-summary-decision-authority is-holding-judgment is-thesis-aware" aria-label="보유 대응 데이터">
        <header className="live-summary-holding-judgment__head">
          <div><small>위험관리</small><b className={toneClass(fallbackRisk.tone)}>{fallbackRisk.label}</b></div>
          <span>{session.badge}</span>
        </header>

        <div className="live-summary-holding-thesis-mini">
          <span className={toneClass(thesis.tone)}><small>보유 논리</small><b>{thesis.coreLabel}</b></span>
          <span className={toneClass(thesis.marketPressureTone)}><small>시장 압력</small><b>{thesis.marketPressureLabel}</b></span>
        </div>

        <div className="live-summary-holding-judgment__position">
          <span><small>평단</small><b>{holding.portfolioAvgPrice !== null ? `${formatPrice(holding.portfolioAvgPrice)}원` : '—'}</b></span>
          <span className={toneClass(returnPct === null ? 'neutral' : returnPct > 0 ? 'up' : returnPct < 0 ? 'down' : 'neutral')}><small>수익률</small><b>{returnPct === null ? '—' : formatPct(returnPct)}</b></span>
          <span><small>현재가</small><b>{holding.price !== null ? `${formatPrice(holding.price)}원` : '—'}</b></span>
        </div>

        <div className="live-summary-holding-judgment__factors">
          {factors.map((factor) => <span key={factor.label} className={toneClass(factor.tone)}><small>{factor.label}</small><b>{factor.value}</b></span>)}
        </div>

        <div className="live-summary-holding-judgment__change">
          <small>최근 변화</small><b>{changeHeadline}</b>
        </div>
        <footer><span>신뢰도</span><b>{holdingDecisionConfidence(holding)}</b></footer>
      </section>
    );
  }

  const vwapGap = item.price !== null && item.sessionVwap !== null && item.sessionVwap !== 0
    ? ((item.price - item.sessionVwap) / item.sessionVwap) * 100
    : null;
  const priceMeta = priceStateMeta(item, vwapGap);
  const flowMeta = flowOverviewMeta(item, false);
  const marketUnavailable = !marketContext.available || marketContext.stale;
  const marketLabel = marketUnavailable
    ? '시장 데이터 확인 중'
    : cleanText(marketContext.pressureLabel || marketContext.headline) || '시장 흐름 확인';
  const marketTone: Tone = marketUnavailable ? 'neutral' : (marketContext.tone || 'neutral') as Tone;

  return (
    <section className="live-summary-decision-authority" aria-label="현재 진입 판단">
      <div className="live-summary-decision-authority__main">
        <small>현재 진입 판단</small>
        <b>{headline}</b>
        {evidence.strengthLabel && <span>근거 {evidence.strengthLabel}</span>}
      </div>
      <p>{summary}</p>
      <div className="live-summary-decision-at-glance" aria-label="진입 판단 핵심 상태">
        <span className={toneClass(priceMeta.tone)}><small>가격</small><b>{priceMeta.label}</b></span>
        <span className={toneClass(flowMeta.tone)}><small>수급</small><b>{flowMeta.label}</b></span>
        <span className={toneClass(marketTone)}><small>시장</small><b>{marketLabel}</b></span>
      </div>
      <div className="live-summary-decision-priority">
        <small>판단 우선순위</small>
        <b>{decisionPriorityText(item, stale)}</b>
      </div>
      {(decision.doNow || decision.doNot) && (
        <div className="live-summary-decision-authority__actions">
          {decision.doNow && <span><small>현재 행동</small><b>{decision.doNow}</b></span>}
          {decision.doNot && <span><small>주의</small><b>{decision.doNot}</b></span>}
        </div>
      )}
    </section>
  );
}




function historicalRateWidth(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '0%';
  return `${Math.max(0, Math.min(100, value))}%`;
}

function historicalDecisionBridge(
  evidence: HistoricalEvidenceView,
  item: NormalizedSectorTopStock | NormalizedHolding,
): string {
  const smartMoney = holdingSmartMoneyAmount(item);
  const priceWeak = item.changeRate !== null && item.changeRate < 0;
  const flowWeak = smartMoney !== null && smartMoney < 0;
  const rates = [evidence.forward5d.positiveRate, evidence.forward20d.positiveRate]
    .filter((value): value is number => value !== null && Number.isFinite(value));
  const historicalPositive = rates.some((value) => value >= 55);
  const historicalMixed = rates.length >= 2 && Math.abs(rates[0] - rates[1]) >= 7;

  if ((priceWeak || flowWeak) && historicalPositive) {
    return '과거 상승 비율이 일부 높아도 현재 가격·수급 약화를 우선해 현재 방어 판단을 유지합니다.';
  }
  if (historicalMixed) {
    return '단기·중기 과거 결과가 엇갈려 과거 통계만으로 방향 우위를 부여하지 않습니다.';
  }
  if (priceWeak || flowWeak) {
    return '과거 통계는 참고로 두고 현재 가격·수급 약화를 우선 반영합니다.';
  }
  return '과거 결과는 참고값이며 현재 가격·수급·시장 조건과 같은 방향인지 함께 확인합니다.';
}

function HistoricalEvidencePanel({
  evidence,
  item,
}: {
  evidence: HistoricalEvidenceView;
  item: NormalizedSectorTopStock | NormalizedHolding;
}) {
  if (!evidence.available) {
    return <p className="live-summary-monitor-pane__empty">과거 유사 흐름 데이터를 확인 중입니다.</p>;
  }
  const rows = [
    ['5거래일', evidence.forward5d],
    ['20거래일', evidence.forward20d],
  ] as const;
  return (
    <section className="live-summary-history-compact">
      <header>
        <div><small>과거 검증</small><b>{evidence.title || '과거 유사 흐름'}</b></div>
        <span>{evidence.sampleSize}회{evidence.sampleNote ? ` · ${evidence.sampleNote}` : ''}</span>
      </header>
      <div className="live-summary-history-compact__rows">
        {rows.map(([label, row]) => (
          <div key={label}>
            <span><b>{label}</b><small>{row.sampleSize || evidence.sampleSize}건</small></span>
            <span className="live-summary-history-compact__bar"><i style={{ width: historicalRateWidth(row.positiveRate) }} /></span>
            <span><b>{row.positiveRate === null ? '—' : `상승 ${Math.round(row.positiveRate)}%`}</b><small>{row.averageReturn === null ? '평균 —' : `평균 ${formatPct(row.averageReturn, 2)}`}</small></span>
            {(row.upCount || row.flatCount || row.downCount) > 0 && <small>상승 {row.upCount} · 횡보 {row.flatCount} · 하락 {row.downCount}</small>}
          </div>
        ))}
      </div>
      <div className="live-summary-history-compact__bridge">
        <small>현재 판단 연결</small>
        <b>{historicalDecisionBridge(evidence, item)}</b>
      </div>
      {evidence.interpretation && <p>{evidence.interpretation}</p>}
      <footer>ⓘ {evidence.coverageNote || 'KRX 일봉 기반 참고 통계이며 현재 장중 수급·시장·재무 조건은 포함하지 않습니다.'}</footer>
    </section>
  );
}

function holdingPositionReturnPct(holding: NormalizedHolding): number | null {
  if (!holding.portfolioPositionAvailable || holding.price === null || holding.portfolioAvgPrice === null || holding.portfolioAvgPrice <= 0) return null;
  return ((holding.price - holding.portfolioAvgPrice) / holding.portfolioAvgPrice) * 100;
}

function ValueJudgmentPurposeContext({
  item,
  mode,
  stale,
}: {
  item: NormalizedSectorTopStock | NormalizedHolding;
  mode: 'stock' | 'holding';
  stale: boolean;
}) {
  const freshness = freshnessMeta(item);

  if (mode === 'holding') {
    const holding = item as NormalizedHolding;
    const positionReturn = holdingPositionReturnPct(holding);
    const thesis = holding.holdingThesis || EMPTY_HOLDING_THESIS;
    const quantityLabel = holding.portfolioPositionAvailable && holding.portfolioQuantity !== null
      ? `${holding.portfolioQuantity.toLocaleString('ko-KR')}주`
      : '관심';

    return (
      <section className="live-summary-purpose-context is-holding is-data-only" aria-label="보유 데이터">
        <div className="live-summary-purpose-context__grid">
          <span><small>현재가</small><b>{formatPrice(holding.price)}원</b></span>
          <span><small>평단</small><b>{holding.portfolioPositionAvailable && holding.portfolioAvgPrice !== null ? `${formatPrice(holding.portfolioAvgPrice)}원` : '—'}</b></span>
          <span><small>손익</small><b className={toneClass(positionReturn === null ? 'neutral' : positionReturn > 0 ? 'up' : positionReturn < 0 ? 'down' : 'neutral')}>{positionReturn === null ? '—' : formatPct(positionReturn, 2)}</b></span>
          <span><small>훼손 신호</small><b className={toneClass(thesis.triggeredCount > 0 ? 'warn' : 'neutral')}>{thesis.triggeredCount}</b></span>
        </div>
        <footer><span>{quantityLabel}</span><span className={toneClass(freshness.tone)}>{freshness.label}</span>{stale ? <span>STALE</span> : null}</footer>
      </section>
    );
  }

  const stock = item as NormalizedSectorTopStock;
  const vwapGap = stock.price !== null && stock.sessionVwap !== null && stock.sessionVwap !== 0
    ? ((stock.price - stock.sessionVwap) / stock.sessionVwap) * 100
    : null;
  const entryStage = userFacingEntryStage(stock.riskAssessment, stock.actionDecision);

  return (
    <section className="live-summary-purpose-context is-stock is-data-only" aria-label="종목 데이터">
      <div className="live-summary-purpose-context__grid">
        <span><small>현재가</small><b>{formatPrice(stock.price)}원</b></span>
        <span><small>등락</small><b className={toneClass(stock.tone)}>{formatPct(stock.changeRate)}</b></span>
        <span><small>VWAP</small><b className={toneClass(vwapGap === null ? 'neutral' : vwapGap > 0 ? 'up' : vwapGap < 0 ? 'down' : 'neutral')}>{vwapGap === null ? '—' : formatPct(vwapGap, 2)}</b></span>
        <span><small>진입</small><b className={toneClass(entryStage.tone)}>{entryStage.label}</b></span>
      </div>
      <footer><span className={toneClass(freshness.tone)}>{freshness.label}</span>{stale ? <span>STALE</span> : null}</footer>
    </section>
  );
}

function valueStateToneFromCode(value: string, fallback: Tone = 'neutral'): Tone {
  const code = cleanText(value).toUpperCase();
  if (/STRENGTH|ENHANC|IMPROV|PRESERV|STABLE/.test(code)) return 'good';
  if (/SHAK|WEAK|WARN|REVIEW/.test(code)) return 'warn';
  if (/DAMAG|IMPAIR|FAIL|BROKEN/.test(code)) return 'down';
  return fallback;
}

function verifiedValueStateView(judgment: NormalizedValueJudgment): NormalizedVerifiedValueState {
  if (judgment.lastVerifiedState.available) {
    return {
      ...judgment.lastVerifiedState,
      tone: judgment.lastVerifiedState.tone === 'neutral'
        ? valueStateToneFromCode(judgment.lastVerifiedState.state, 'neutral')
        : judgment.lastVerifiedState.tone,
    };
  }
  const currentVerified = judgment.valueState !== 'LIMITED'
    && judgment.businessEvidence.ready
    && !judgment.businessEvidence.stale
    && !judgment.businessEvidence.needsReview;
  if (!currentVerified) return EMPTY_VALUE_JUDGMENT.lastVerifiedState;
  return {
    available: true,
    state: judgment.valueState,
    label: judgment.valueStateLabel,
    tone: judgment.valueStateTone,
    verifiedAt: judgment.basisDate || judgment.businessEvidence.businessDate,
    source: judgment.businessEvidence.reportName,
  };
}

function newEvidenceStatusView(judgment: NormalizedValueJudgment): NormalizedNewEvidenceStatus {
  if (judgment.newEvidenceStatus.label || judgment.newEvidenceStatus.state !== 'UNKNOWN') {
    return judgment.newEvidenceStatus;
  }
  if (judgment.businessEvidence.needsReview) {
    return { state: 'REVIEW', label: '신규 사업근거 검증 중', summary: '새 근거를 기존 투자 논리와 대조하고 있습니다.', conflict: false };
  }
  if (judgment.businessEvidence.stale) {
    return { state: 'STALE', label: '기존 사업근거 최신성 확인', summary: '기존 근거의 최신성을 다시 확인하고 있습니다.', conflict: false };
  }
  if (judgment.businessEvidence.ready) {
    return { state: 'VERIFIED', label: '신규 근거 검증 완료', summary: '현재 사업 근거가 검증 기준을 통과했습니다.', conflict: false };
  }
  return { state: 'PENDING', label: '사업근거 확인 중', summary: '검증 가능한 사업 근거를 확인하고 있습니다.', conflict: false };
}

function judgmentCoverageMeta(item: NormalizedSectorTopStock | NormalizedHolding, stale: boolean): { ready: number; total: number; missing: string[] } {
  const judgment = item.valueJudgment || EMPTY_VALUE_JUDGMENT;
  const checks = [
    { label: '사업근거', ready: judgment.businessEvidence.ready && !judgment.businessEvidence.needsReview && !judgment.businessEvidence.stale },
    { label: '수급', ready: hasCurrentFlowAuthority(item) },
    { label: '시각', ready: !stale },
  ];
  return {
    ready: checks.filter((row) => row.ready).length,
    total: checks.length,
    missing: checks.filter((row) => !row.ready).map((row) => row.label),
  };
}


function holdingRiskStageRank(state: string): number {
  const code = cleanText(state).toUpperCase();
  if (code === 'REVIEW') return 5;
  if (code === 'DEFENSE') return 4;
  if (code === 'CAUTION') return 3;
  if (code === 'WATCH') return 2;
  if (code === 'NORMAL') return 1;
  return 0;
}

function fallbackHoldingRiskStage(holding: NormalizedHolding): { state: string; label: string; tone: Tone } {
  const action = cleanText(holding.actionDecision.action).toUpperCase();
  const risk = cleanText(holding.riskAssessment.state).toUpperCase();
  const thesis = holding.holdingThesis || EMPTY_HOLDING_THESIS;

  if (thesis.coreState === 'DAMAGED' || action === 'EXIT' || risk === 'HIGH_RISK') {
    return { state: 'REVIEW', label: '재검토', tone: 'down' };
  }
  if (action === 'REDUCE' || risk === 'DEFENSIVE') {
    return { state: 'DEFENSE', label: '방어', tone: 'warn' };
  }
  if (thesis.coreState === 'WEAKENED' || thesis.displayState === 'PRESERVED_CAUTION'
      || risk === 'WATCH' || risk === 'WEAKENING' || risk === 'UNFAVORABLE') {
    return { state: 'CAUTION', label: '경계', tone: 'warn' };
  }
  if (thesis.coreState === 'LIMITED' || risk === 'DATA_LIMITED') {
    return { state: 'WATCH', label: '관찰', tone: 'neutral' };
  }
  return { state: 'NORMAL', label: '정상', tone: 'good' };
}

function holdingChangeLayerLabel(event: NormalizedChangeEvent): string {
  const type = cleanText(event.eventType).toUpperCase();

  if (type === 'HOLDING_THESIS_CHANGED') {
    return '핵심 판단 변화';
  }

  if (type === 'HOLDING_JUDGMENT_CHANGED') {
    return '대응 변화';
  }

  if (type === 'HOLDING_THESIS_TRIGGERED') {
    return '검증 변화';
  }

  if (type === 'HOLDING_MARKET_PRESSURE_CHANGED') {
    return '압력 변화';
  }

  if (/PRESSURE|FLOW|VWAP|PRICE|RISK|STATUS/.test(type)) {
    return '압력 변화';
  }

  if (/EVIDENCE|VALUATION|FUNDAMENTAL|FINANCIAL|BUSINESS/.test(type)) {
    return '근거 변화';
  }

  if (/DATA|SOURCE|STALE|FRESH|CHECK|VALIDATION/.test(type)) {
    return '검증 변화';
  }

  if (/THESIS/.test(type)) {
    return '핵심 판단 변화';
  }

  if (/JUDGMENT|ACTION/.test(type)) {
    return '대응 변화';
  }

  return '상태 변화';
}

function isJudgmentCheckResult(
  change: NormalizedHoldingJudgmentContract['changes'][number],
): boolean {
  const code = cleanText(change.type).toUpperCase();
  const label = cleanText(change.label);

  return (
    code.includes('PROJECTED')
    || code.includes('EVENT')
    || code.includes('CHECK')
    || code.includes('VALIDATION')
    || code.includes('DATA')
    || code.includes('QUALITY')
    || code.includes('STALE')
    || label.startsWith('이슈 검증 중')
    || label.includes('Event 영향 검증')
    || label.includes('이벤트 영향 검증')
  );
}

function judgmentSessionPhase(
  marketSession: string,
  sessionLabel: string,
): 'LIVE' | 'CLOSE' {
  const raw = `${cleanText(marketSession)} ${cleanText(sessionLabel)}`.toUpperCase();

  if (
    raw.includes('CLOSED')
    || raw.includes('POST')
    || raw.includes('TRANSITION')
    || raw.includes('장마감')
  ) {
    return 'CLOSE';
  }

  if (
    raw.includes('NXT')
    || raw.includes('MARKET_OPEN')
    || raw.includes('KRX_REGULAR')
    || raw === 'REGULAR'
  ) {
    return 'LIVE';
  }

  return 'CLOSE';
}

function judgmentMarketSessionLabel(value: string): string {
  const code = cleanText(value).toUpperCase();

  if (code === 'LAST_KRX_CLOSE') return 'KRX 장마감 기준';
  if (code === 'KRX_REGULAR' || code === 'REGULAR') return 'KRX 장중 기준';
  if (code.includes('NXT')) return 'NXT 기준';
  if (code === 'CLOSED' || code === 'POST') return 'KRX 장마감 기준';

  return cleanText(value).replaceAll('_', ' ');
}

function judgmentChangeTypeLabel(type: string): string {
  const code = cleanText(type).toUpperCase();

  if (code.includes('THESIS')) {
    return '핵심 판단 변화';
  }

  if (code.includes('JUDGMENT') || code.includes('ACTION')) {
    return '대응 변화';
  }

  if (
    code.includes('PROJECTED')
    || code.includes('EVENT')
    || code.includes('CHECK')
    || code.includes('VALIDATION')
    || code.includes('DATA')
    || code.includes('QUALITY')
    || code.includes('STALE')
  ) {
    return '검증 변화';
  }

  if (
    code.includes('PRESSURE')
    || code.includes('INVALIDATION')
    || code.includes('FLOW')
    || code.includes('PRICE')
  ) {
    return '압력 변화';
  }

  if (
    code.includes('EVIDENCE')
    || code.includes('VALUATION')
    || code.includes('FUNDAMENTAL')
  ) {
    return '근거 변화';
  }

  return '상태 변화';
}

function judgmentQualityLabel(value: string): string {
  const code = cleanText(value).toUpperCase();
  if (code === 'OK') return '확인';
  if (code === 'STALE') return '갱신 필요';
  if (code === 'PENDING') return '검증 중';
  if (code === 'MISSING') return '미확인';
  return code ? userFacingStateLabel(code) : '미확인';
}

function HoldingJudgmentContractPanel({
  holding,
}: {
  holding: NormalizedHolding;
}) {
  const contract = holding.judgmentContract || EMPTY_HOLDING_JUDGMENT;
  const positionReturn = holdingPositionReturnPct(holding);
  const invalidationTotal = contract.invalidation.total;
  const invalidationTriggered = Math.min(contract.invalidation.triggered, invalidationTotal || contract.invalidation.triggered);
  const nextCheck = contract.nextChecks[0] || '추가로 확인할 핵심 조건 없음';
  const dayMove = contract.pressure.day.stockChangeRate ?? holding.changeRate;

  const currentActionLabel =
    cleanText(contract.riskManagement.actionLabel)
    || userFacingStateLabel(contract.riskManagement.actionCode)
    || cleanText(holding.actionDecision.actionLabel)
    || userFacingStateLabel(holding.actionDecision.action)
    || '대응 확인 중';

  const sessionPhase = judgmentSessionPhase(
    contract.marketSession,
    contract.pressure.live.sessionLabel,
  );

  const materialChanges = contract.changes.filter(
    (change) => !isJudgmentCheckResult(change),
  );

  const checkResults = contract.changes.filter(
    (change) => isJudgmentCheckResult(change),
  );

  return (
    <section className="live-summary-holding-judgment-contract" aria-label="보유 판단 요약">
      <header className="live-summary-holding-judgment-contract__head">
        <div>
          <small>보유 논리</small>
          <strong className={toneClass(contract.thesis.tone)}>{contract.thesis.label}</strong>
        </div>
        <span className={toneClass(contract.riskManagement.tone)}>
          현재 대응 · {currentActionLabel}
        </span>
      </header>

      <p className="live-summary-holding-judgment-contract__summary">
        {contract.summary || `${contract.thesis.label} · 현재 대응 ${currentActionLabel}`}
      </p>

      <div className="live-summary-holding-judgment-contract__axes" aria-label="판단 4축">
        <span className={toneClass(contract.thesis.tone)}>
          <small>보유 논리</small>
          <b>{contract.thesis.label}</b>
        </span>
        <span className={toneClass(contract.pressure.day.tone)}>
          <small>현재 압력</small>
          <b>{contract.pressure.day.label}</b>
        </span>
        <span className={toneClass(contract.riskManagement.tone)}>
          <small>위험관리</small>
          <b>{contract.riskManagement.label}</b>
        </span>
        <span className={toneClass(contract.riskManagement.tone)}>
          <small>현재 대응</small>
          <b>{currentActionLabel}</b>
        </span>
      </div>

      <div className="live-summary-holding-judgment-contract__time" aria-label="종목 압력, 시장 환경, 세션 상태">
        <span className={toneClass(contract.pressure.day.tone)}>
          <small>DAY · 종목 압력</small>
          <b>{contract.pressure.day.label}</b>
          <em>
            {dayMove !== null ? `종목 ${formatPct(dayMove, 2)} · ` : ''}
            {contract.pressure.day.flowLabel}
          </em>
        </span>

        <span>
          <small>MARKET · 시장 환경</small>
          <b>{contract.pressure.day.marketLabel}</b>
          <em>
            {contract.pressure.day.basis
              ? judgmentMarketSessionLabel(contract.pressure.day.basis)
              : '시장 환경 기준 확인'}
          </em>
        </span>

        <span className={toneClass(contract.pressure.live.tone)}>
          <small>{sessionPhase} · {contract.pressure.live.sessionLabel || 'KRX 장마감'}</small>
          <b>{contract.pressure.live.label}</b>
          <em>{contract.pressure.live.basis || '세션 가격 기준 확인'}</em>
        </span>
      </div>

      <details className="live-summary-holding-judgment-contract__projected" aria-label="예상 영향 계산 근거">
        <summary>
          <span>
            <small>예상 영향</small>
            <b className={toneClass(contract.projectedImpact.tone)}>{contract.projectedImpact.label}</b>
          </span>
          <em>계산 근거 보기 · 신뢰 {contract.projectedImpact.confidenceLabel}</em>
        </summary>
        <div className="live-summary-holding-judgment-contract__projected-metrics">
          <span>
            <small>영향 점수</small>
            <b className={toneClass(contract.projectedImpact.tone)}>
              {contract.projectedImpact.score === null
                ? '—'
                : `${contract.projectedImpact.score > 0 ? '+' : ''}${contract.projectedImpact.score.toFixed(1)}`}
            </b>
          </span>
          <span>
            <small>ROIC-WACC 실제</small>
            <b>{contract.projectedImpact.baseRoicWaccSpreadPctp === null
              ? '—'
              : `${contract.projectedImpact.baseRoicWaccSpreadPctp.toFixed(2)}%p`}</b>
          </span>
          <span>
            <small>Stress Spread</small>
            <b>{contract.projectedImpact.stressRoicWaccSpreadPctp === null
              ? '—'
              : `${contract.projectedImpact.stressRoicWaccSpreadPctp.toFixed(2)}%p`}</b>
          </span>
        </div>
        {contract.projectedImpact.factors.length > 0 && (
          <div className="live-summary-holding-judgment-contract__projected-factors">
            {contract.projectedImpact.factors.slice(0, 4).map((factor) => (
              <span className={toneClass(factor.tone)} key={`${factor.code}:${factor.label}`}>
                <small>{factor.label}</small>
                <b>{factor.score === null ? '—' : `${factor.score > 0 ? '+' : ''}${factor.score.toFixed(1)}`}</b>
                <em>{factor.basis}</em>
              </span>
            ))}
          </div>
        )}
        {contract.projectedImpact.eventImpact.status !== 'NONE' && (
          <p className={toneClass(contract.projectedImpact.eventImpact.tone)}>
            <small>Event</small>
            <b>{contract.projectedImpact.eventImpact.label}</b>
            {contract.projectedImpact.eventImpact.headline && <em>{contract.projectedImpact.eventImpact.headline}</em>}
          </p>
        )}
        <footer>{contract.projectedImpact.methodology || '연간 재무값은 유지하고 장중 압력은 별도 예상 영향으로 계산합니다.'}</footer>
      </details>

      <div className="live-summary-holding-judgment-contract__why">
        <small>왜 이 판단인가</small>
        <b>{contract.thesis.primaryReason}</b>
        <span>회사 근거 · {contract.companyEvidence.label}</span>
        {contract.riskManagement.reason && contract.riskManagement.reason !== contract.thesis.primaryReason && (
          <span>현재 압력 · {contract.riskManagement.reason}</span>
        )}
      </div>

      {materialChanges.length > 0 && (
        <div className="live-summary-holding-judgment-contract__changes">
          <small>오늘 달라진 것</small>
          {materialChanges.slice(0, 2).map((change, index) => (
            <span className={toneClass(change.tone)} key={`${change.type}:${change.label}:${index}`}>
              <b>{change.label}</b>
            </span>
          ))}
        </div>
      )}

      {checkResults.length > 0 && (
        <div className="live-summary-holding-judgment-contract__changes is-check-results">
          <small>검증 결과</small>
          {checkResults.slice(0, 2).map((change, index) => (
            <span className={toneClass(change.tone)} key={`check:${change.type}:${change.label}:${index}`}>
              <b>{change.label}</b>
            </span>
          ))}
        </div>
      )}

      <div className="live-summary-holding-judgment-contract__invalidation">
        <header>
          <span>
            <small>보유 논리 재검토 조건</small>
            <b>{invalidationTotal > 0 ? `${invalidationTriggered} / ${invalidationTotal} 충족` : '조건 확인 중'}</b>
          </span>
        </header>
        {contract.invalidation.items.length > 0 ? (
          <div>
            {contract.invalidation.items.map((condition, index) => (
              <span className={toneClass(condition.tone)} key={`${condition.label}:${index}`}>
                <em>{index + 1}</em>
                <b>{condition.label}</b>
                <small>{condition.statusLabel}</small>
              </span>
            ))}
          </div>
        ) : (
          <p>서버에서 재검토 조건을 확인 중입니다.</p>
        )}
      </div>

      <div className="live-summary-holding-judgment-contract__next">
        <small>다음 확인</small>
        <b>{nextCheck}</b>
      </div>

      <div className="live-summary-holding-judgment-contract__position">
        <span><small>현재가</small><b>{holding.price !== null ? `${formatPrice(holding.price)}원` : '—'}</b></span>
        <span><small>평단</small><b>{holding.portfolioPositionAvailable && holding.portfolioAvgPrice !== null ? `${formatPrice(holding.portfolioAvgPrice)}원` : '—'}</b></span>
        <span className={toneClass(positionReturn === null ? 'neutral' : positionReturn > 0 ? 'up' : positionReturn < 0 ? 'down' : 'neutral')}>
          <small>손익</small><b>{positionReturn === null ? '—' : formatPct(positionReturn, 2)}</b>
        </span>
      </div>

      <footer className="live-summary-holding-judgment-contract__confidence">
        <span>
          <small>근거 충족</small>
          <b>{contract.confidence.totalCount > 0
            ? `${contract.confidence.readyCount}/${contract.confidence.totalCount}`
            : contract.confidence.coveragePct === null ? '—' : `${Math.round(contract.confidence.coveragePct)}%`}</b>
        </span>
        <span className={toneClass(contract.confidence.tone)}>
          <small>판단 신뢰도</small>
          <b>{contract.confidence.label}</b>
        </span>
        {contract.confidence.missingEvidenceLabels.length > 0 && (
          <em>미확인 · {contract.confidence.missingEvidenceLabels.join(' · ')}</em>
        )}
      </footer>
    </section>
  );
}
function HoldingJudgmentEvidenceSummary({ holding }: { holding: NormalizedHolding }) {
  const contract = holding.judgmentContract || EMPTY_HOLDING_JUDGMENT;
  if (!contract.available) return null;

  const qualityRows = [
    ['사업', contract.dataQuality.business],
    ['재무', contract.dataQuality.financial],
    ['가격', contract.dataQuality.price],
    ['수급', contract.dataQuality.flow],
    ['시장', contract.dataQuality.market],
    ['Event', contract.dataQuality.event],
  ] as const;

  /*
   * 같은 evidence gap이
   *
   *   외인·기관 수급
   *   수급
   *
   * 처럼 서로 다른 label로 중복 노출되는 것을 막는다.
   * 화면에서는 하나의 수급 Evidence gap으로 표현한다.
   */
  const missingEvidenceRows = (() => {
    const sourceRows = [
      ...contract.evidence.missing,
      ...contract.confidence.missingEvidenceLabels,
    ]
      .map(cleanText)
      .filter(Boolean);

    const flowMissing = (
      contract.dataQuality.flow === 'MISSING'
      || sourceRows.some((row) => /수급|외인|기관|프로그램/.test(row))
    );

    const rows = Array.from(
      new Set(
        sourceRows.filter(
          (row) => !/수급|외인|기관|프로그램/.test(row),
        ),
      ),
    );

    if (flowMissing) {
      rows.unshift(
        '수급 · 외국인·기관·프로그램 당일 최종 수급 확인 필요',
      );
    }

    return rows.slice(0, 6);
  })();

  return (
    <section className="live-summary-holding-evidence-summary" aria-label="이번 판단에 실제 사용된 근거">
      <header>
        <div>
          <small>이번 판단에 실제 사용된 Evidence</small>
          <b>{contract.thesis.label} · 위험관리 {contract.riskManagement.label}</b>
        </div>
        <span className={toneClass(contract.confidence.tone)}>
          신뢰도 {contract.confidence.label}
        </span>
      </header>

      <div className="live-summary-holding-evidence-summary__quality">
        {qualityRows.map(([label, value]) => (
          <span
            className={
              value === 'OK'
                ? 'is-good'
                : value === 'MISSING'
                  ? 'is-missing'
                  : 'is-pending'
            }
            key={label}
          >
            <small>{label}</small>
            <b>{judgmentQualityLabel(value)}</b>
          </span>
        ))}
      </div>

      {contract.evidence.maintain.length > 0 && (
        <div className="is-maintain">
          <small>유지 근거</small>
          {contract.evidence.maintain.map((row) => (
            <b key={row}>✓ {row}</b>
          ))}
        </div>
      )}

      {contract.evidence.risk.length > 0 && (
        <div className="is-risk">
          <small>위험 근거</small>
          {contract.evidence.risk.map((row) => (
            <b key={row}>! {row}</b>
          ))}
        </div>
      )}

      {missingEvidenceRows.length > 0 && (
        <div className="is-missing">
          <small>미확인</small>
          {missingEvidenceRows.map((row) => (
            <b key={row}>? {row}</b>
          ))}
        </div>
      )}
    </section>
  );
}

function ValueJudgmentSummary({
  item,
  mode,
  stale,
}: {
  item: NormalizedSectorTopStock | NormalizedHolding;
  mode: 'stock' | 'holding';
  stale: boolean;
}) {
  const judgment = item.valueJudgment || EMPTY_VALUE_JUDGMENT;
  const verifiedState = verifiedValueStateView(judgment);
  const newEvidence = newEvidenceStatusView(judgment);
  const coverage = judgmentCoverageMeta(item, stale);
  const coverageMissingKey = coverage.missing.join(',');
  const limitedWithVerifiedBaseline = judgment.valueState === 'LIMITED'
    && verifiedState.available
    && verifiedState.state !== 'LIMITED';
  useEffect(() => {
    if (!item.ticker || !judgment.contractVersion) return;
    trackUxEvent('value_judgment_view', {
      ticker: item.ticker,
      stock_code: item.ticker,
      stock_name: item.name,
      source: 'live-summary-detail',
      mode,
      value_state: judgment.valueState,
      market_delivery: judgment.marketDelivery.state,
      confidence: judgment.confidence,
      business_ready: judgment.businessEvidence.ready,
      coverage_ready: coverage.ready,
      coverage_total: coverage.total,
      coverage_missing: coverageMissingKey,
      last_verified_available: verifiedState.available,
      new_evidence_state: newEvidence.state,
      holding_thesis_state: mode === 'holding' ? (item as NormalizedHolding).holdingThesis.coreState : undefined,
      holding_thesis_display: mode === 'holding' ? (item as NormalizedHolding).holdingThesis.displayState : undefined,
      holding_market_pressure: mode === 'holding' ? (item as NormalizedHolding).holdingThesis.marketPressureState : undefined,
    });
  }, [item.ticker, item.name, judgment.contractVersion, judgment.valueState, judgment.marketDelivery.state, judgment.confidence, judgment.businessEvidence.ready, mode, coverage.ready, coverage.total, coverageMissingKey, verifiedState.available, newEvidence.state, item]);

  const stateTone = toneClass(judgment.valueStateTone);
  const deliveryTone: Tone = judgment.marketDelivery.state === 'FAVORABLE'
    ? 'good'
    : judgment.marketDelivery.state === 'SUPPRESSED' || judgment.marketDelivery.state === 'UNFAVORABLE'
      ? 'warn'
      : 'neutral';
  const fundamentalStatusLabel = judgment.fundamental.label === '우량·성장'
    || judgment.fundamental.label === '기업가치 유지'
    ? '안정'
    : judgment.fundamental.label;
  const thesisStatusLabel = judgment.thesis.label === '투자 논리 강화 근거'
    ? '유지 · 강화 근거'
    : judgment.thesis.label.replace(/^투자 논리\s*/, '') || judgment.thesis.label;
  const importantChanges = [
    ...judgment.recentValueChanges.map((change) => ({ type: 'value', text: change })),
    ...judgment.recentMarketChanges.map((change) => ({ type: 'market', text: change })),
  ].slice(0, 2);

  if (mode === 'holding') {
    const holding = item as NormalizedHolding;
    if (holding.judgmentContract?.available) {
      return <HoldingJudgmentContractPanel holding={holding} />;
    }
    const thesis = holding.holdingThesis || EMPTY_HOLDING_THESIS;
    const fallbackRisk = fallbackHoldingRiskStage(holding);
    const headlineParts = [thesis.coreLabel, `위험관리 ${fallbackRisk.label}`].map(cleanText).filter(Boolean);
    const combinedHeadline = Array.from(new Set(headlineParts)).join(' · ');
    const thesisReasons = thesis.reasons.slice(0, 2);
    const thesisChanges = thesis.todayChanges.slice(0, 2);
    const thesisConditions = (
      thesis.invalidationConditions.length
        ? thesis.invalidationConditions
        : thesis.monitorConditions
    ).slice(0, 3);

    return (
      <section className="live-summary-value-desk is-simplified is-data-only is-holding is-thesis-first" aria-label="보유 논리 데이터">
        <div className="live-summary-holding-thesis-head">
          <strong className={toneClass(thesis.tone)}>{combinedHeadline || thesis.coreLabel}</strong>
          <span className={toneClass(thesis.marketPressureTone)}>{thesis.marketPressureLabel}</span>
        </div>

        <div className="live-summary-holding-thesis-split" aria-label="보유 논리와 현재 대응">
          <span className={toneClass(thesis.tone)}>
            <small>보유 논리</small>
            <b>{thesis.coreLabel}</b>
          </span>
          <span className={toneClass(fallbackRisk.tone)}>
            <small>위험관리</small>
            <b>{fallbackRisk.label}</b>
          </span>
          <span className={toneClass(thesis.marketPressureTone)}>
            <small>시장 압력</small>
            <b>{thesis.marketPressureLabel}</b>
          </span>
        </div>

        <div className="live-summary-value-desk__rail" aria-label="보유 논리 상태">
          <span><small>기업가치</small><b>{thesis.fundamentalLabel}</b></span>
          <span><small>투자 논리</small><b>{thesis.thesisLabel}</b></span>
          <span className={toneClass(thesis.marketPressureTone)}><small>시장</small><b>{thesis.marketPressureLabel}</b></span>
        </div>

        <ValueJudgmentPurposeContext item={item} mode={mode} stale={stale} />

        {thesisReasons.length > 0 && (
          <div className="live-summary-holding-thesis-reasons">
            <small>{thesis.coreState === 'PRESERVED' ? '유지 근거' : thesis.coreState === 'DAMAGED' ? '훼손 근거' : '판단 근거'}</small>
            {thesisReasons.map((reason) => <b key={reason}>{reason}</b>)}
          </div>
        )}

        {thesisChanges.length > 0 ? (
          <div className="live-summary-value-desk__important-changes is-data-only">
            {thesisChanges.map((change, index) => (
              <p key={`${change}:${index}`}><span>변화</span><b>{change}</b></p>
            ))}
          </div>
        ) : thesis.available ? (
          <div className="live-summary-holding-thesis-no-change"><small>오늘 변화</small><b>핵심 논리 변화 없음</b></div>
        ) : null}

        {thesis.triggerSignals.length > 0 && (
          <div className="live-summary-holding-thesis-triggers is-triggered">
            <small>발생 신호</small>
            {thesis.triggerSignals.slice(0, 3).map((signal) => <b key={signal}>{signal}</b>)}
          </div>
        )}

        {thesisConditions.length > 0 && (
          <div className="live-summary-value-desk__conditions is-data-only">
            <small>무효화·재검토 조건</small>
            <ol>
              {thesisConditions.map((condition) => <li key={condition}>{condition}</li>)}
            </ol>
          </div>
        )}

        {coverage.ready < coverage.total && (
          <div className="live-summary-value-desk__coverage is-data-only" aria-label="검증 범위">
            <b>{coverage.ready}/{coverage.total}</b>
            {coverage.missing.map((label) => <span key={label}>{label}</span>)}
          </div>
        )}
      </section>
    );
  }

  return (
    <section className={`live-summary-value-desk is-simplified is-data-only is-${mode}`} aria-label="판단 데이터">
      <div className="live-summary-value-desk__data-head">
        <strong className={limitedWithVerifiedBaseline ? toneClass(verifiedState.tone) : stateTone}>
          {limitedWithVerifiedBaseline ? verifiedState.label : judgment.valueStateLabel}
        </strong>
        <span>{judgment.confidenceLabel}</span>
      </div>

      <div className="live-summary-value-desk__rail" aria-label="판단 상태">
        <span><small>기업가치</small><b>{fundamentalStatusLabel}</b></span>
        <span><small>논리</small><b>{thesisStatusLabel}</b></span>
        <span className={toneClass(deliveryTone)}><small>시장</small><b>{judgment.marketDelivery.label}</b></span>
      </div>

      {verifiedState.available && (limitedWithVerifiedBaseline || judgment.businessEvidence.needsReview || judgment.businessEvidence.stale || newEvidence.conflict) && (
        <div className="live-summary-value-desk__verification-bridge" aria-label="검증 상태">
          <span><small>검증 기준</small><b className={toneClass(verifiedState.tone)}>{verifiedState.label}</b>{verifiedState.verifiedAt && <em>{verifiedState.verifiedAt}</em>}</span>
          <span><small>신규 근거</small><b>{newEvidence.label}</b></span>
        </div>
      )}

      <ValueJudgmentPurposeContext item={item} mode={mode} stale={stale} />

      {coverage.ready < coverage.total && (
        <div className="live-summary-value-desk__coverage is-data-only" aria-label="검증 범위">
          <b>{coverage.ready}/{coverage.total}</b>
          {coverage.missing.map((label) => <span key={label}>{label}</span>)}
        </div>
      )}

      {importantChanges.length > 0 && (
        <div className="live-summary-value-desk__important-changes is-data-only">
          {importantChanges.map((change, index) => (
            <p key={`${change.type}:${change.text}:${index}`}>
              <span>{change.type === 'value' ? '가치' : '시장'}</span>
              <b>{change.text}</b>
            </p>
          ))}
        </div>
      )}

      {judgment.nextChecks.length > 0 && (
        <div className="live-summary-value-desk__conditions is-data-only">
          <small>조건</small>
          <ol>
            {judgment.nextChecks.slice(0, 2).map((condition) => <li key={condition}>{condition}</li>)}
          </ol>
        </div>
      )}
    </section>
  );
}

function stockEntryDisplayLabel(decision: NormalizedStockEntryDecision): string {
  // STOCK_ENTRY_CONTEXT_V1의 내부 LIMITED 상태는 유지하되,
  // 사용자에게는 서비스 실패처럼 보이는 '판단 제한' 대신
  // 아직 확인해야 할 근거가 남아 있다는 의미의 문구로 표시한다.
  if (decision.state === 'LIMITED') return '확인 필요';
  return cleanText(decision.label) || '조건 확인';
}

function StockEntryDecisionPanel({
  item,
  stale = false,
}: {
  item: NormalizedSectorTopStock;
  stale?: boolean;
}) {
  const decision = item.entryDecision.available ? item.entryDecision : EMPTY_STOCK_ENTRY_DECISION;
  const available = decision.available && !stale;
  const title = available ? stockEntryDisplayLabel(decision) : '판단 대기';
  const next = available ? cleanText(decision.nextCheck) : '';
  const evidence = available ? decision.evidence.slice(0, 1) : [];
  return (
    <section className={`pi-v4-decision ${toneClass(decision.tone)}`} aria-label="종목 현재 판단">
      <div className="pi-v4-decision__top">
        <span>현재 판단</span>
        <strong>{title}</strong>
      </div>
      {evidence.length > 0 && (
        <div className="pi-v4-decision__reason"><span>핵심 근거</span><b>{evidence[0].label}</b></div>
      )}
      {next && (
        <div className="pi-v4-decision__next"><span>다음 확인</span><b>{next}</b></div>
      )}
      {!available && <small>판단에 필요한 데이터가 확인되면 갱신됩니다.</small>}
    </section>
  );
}

function CompactHoldingDecision({ item, stale }: { item: NormalizedHolding; stale: boolean }) {
  const action = item.actionDecision;
  const risk = item.riskAssessment;
  const valid = !stale && (action.available || risk.available);
  const raw = action.available
    ? (cleanText(action.actionLabel) || cleanText(action.stateLabel) || cleanText(action.headline))
    : cleanText(risk.stateLabel);
  const title = valid && raw && !/판단 제한|미확인|준비 중/.test(raw) ? raw : '판단 대기';
  const reason = valid
    ? (cleanText(action.reasons?.[0]) || cleanText(risk.summary))
    : '';
  return (
    <section className="pi-v4-decision" aria-label="보유 종목 현재 판단">
      <div className="pi-v4-decision__top">
        <span>현재 판단</span>
        <strong className={toneClass(valid ? (action.available ? action.tone : risk.tone) : 'neutral')}>{title}</strong>
      </div>
      {title !== '판단 대기' && reason && <p>{reason}</p>}
      {title === '판단 대기' && <small>확인된 근거가 부족합니다.</small>}
    </section>
  );
}

function MonitoringDetailPanel({
  item,
  marketContext,
  marketOpen,
  marketSession = '',
  changeEvents,
  issues,
  sectorContext = null,
  stale = false,
  mode = 'stock',
  focusChangeEventId = '',
  onManualDetailTabChange,
  productAccess,
}: {
  item: NormalizedSectorTopStock | NormalizedHolding;
  marketContext: NormalizedMarketContext;
  marketOpen: boolean;
  marketSession?: string;
  changeEvents: NormalizedChangeEvent[];
  issues: LiveIssue[];
  sectorContext?: NormalizedSector | null;
  stale?: boolean;
  mode?: 'stock' | 'holding';
  focusChangeEventId?: string;
  onManualDetailTabChange?: () => void;
  productAccess: LiveSummaryProductAccess;
}) {
  const [detailTab, setDetailTab] = useState<MonitoringDetailTab>('EVIDENCE');
  const [changesExpanded, setChangesExpanded] = useState(false);
  const [stockIssues, setStockIssues] = useState<LiveIssue[]>([]);
  const [stockIssuesLoading, setStockIssuesLoading] = useState(false);
  const [stockIssuesLoadedTicker, setStockIssuesLoadedTicker] = useState('');
  const [stockIssuesLoadFailed, setStockIssuesLoadFailed] = useState(false);
  const [holdingValuationDetail, setHoldingValuationDetail] = useState<HoldingValuationDetail | null>(null);
  const [holdingValuationLoading, setHoldingValuationLoading] = useState(false);
  const [holdingValuationError, setHoldingValuationError] = useState('');
  const monitor = itemMonitorMeta(item, marketContext, changeEvents, stale, mode);
  const flowMomentum = flowMomentumView(item, stale);
  const valuationTrackingAllowed = liveSummaryFeatureAllowed(productAccess, 'VALUATION_TRACKING')
    || liveSummaryFeatureAllowed(productAccess, 'VERIFICATION_FINANCIAL');
  const businessEventTrackingAllowed = liveSummaryFeatureAllowed(productAccess, 'BUSINESS_EVENT_TRACKING');
  const fallbackStockIssues = useMemo(
    () => issues
      .filter((issue) => issueDirectlyMatchesStock(issue, item))
      .sort((a, b) => issueEpoch(b.publishedAt) - issueEpoch(a.publishedAt))
      .slice(0, 20),
    [issues, item],
  );
  const rankedStockIssues = useMemo(() => {
    const evidenceScore = (issue: LiveIssue) => {
      const evidence = cleanText(issue.evidenceLevel || issue.verificationStatus).toUpperCase();
      const judgment = cleanText(issue.judgmentSupport).toUpperCase();
      const attributionStatus = cleanText(issue.movementAttribution?.status).toUpperCase();
      const verified = /VERIFIED|CONFIRMED|HIGH/.test(evidence) ? 80 : /LOW|PENDING|UNKNOWN/.test(evidence) ? 0 : 30;
      const judgmentResolved = isResolvedEventJudgmentStatus(judgment) ? 70 : 0;
      const attributionScore = attributionStatus === 'CONFIRMED' ? 180
        : attributionStatus === 'SUPPORTED' ? 140
          : attributionStatus === 'MIXED' ? 50
            : 0;
      return attributionScore + verified + judgmentResolved + (issue.importance ?? 0) * 10 + (issue.impactScore ?? 0) * 5 + (issue.realtime ? 20 : 0);
    };
    return [...stockIssues].sort((a, b) => evidenceScore(b) - evidenceScore(a) || issueEpoch(b.publishedAt) - issueEpoch(a.publishedAt));
  }, [stockIssues]);
  const primaryMovementIssue = useMemo(
    () => rankedStockIssues.find((issue) => ['CONFIRMED', 'SUPPORTED', 'MIXED'].includes(cleanText(issue.movementAttribution?.status).toUpperCase()))
      ?? rankedStockIssues.find((issue) => Boolean(issue.movementAttribution))
      ?? null,
    [rankedStockIssues],
  );
  const focusedChangeEvent = useMemo(() => {
    const requestedId = cleanText(focusChangeEventId);
    if (!requestedId) return null;
    return changeEvents.find((event) => {
      if (cleanText(event.id) !== requestedId || cleanText(event.entityKey) !== cleanText(item.ticker)) return false;
      if (mode === 'holding') return event.entityType === 'HOLDING' || event.entityType === 'STOCK';
      return event.entityType === 'STOCK';
    }) ?? null;
  }, [changeEvents, focusChangeEventId, item.ticker, mode]);

  const latestMaterialChange = useMemo(() => {
    if (mode !== 'holding') return null;
    const today = koreaTodayKey();
    const current = recentEventsForItem(changeEvents, item.ticker, 'holding')
      .filter((event) => eventDateKey(eventTimestampRaw(event)) === today);
    return dedupeMaterialChanges(current, 1)[0] ?? null;
  }, [changeEvents, item.ticker, mode]);

  // Compact valuation must follow the same valuation-level authority as the full valuation panel.
  // Display priority: REPRESENTATIVE -> CENTRAL_ESTIMATE -> REFERENCE -> UNAVAILABLE.
  // A lower level must never be promoted to a higher label, but an available lower-level value
  // must not be hidden merely because the representative fair-value gate was withheld.
  const compactRepresentativeFairValue = nullableNumber(holdingValuationDetail?.representativeFairValue);
  const compactCentralEstimate = nullableNumber(holdingValuationDetail?.centralEstimate);
  const compactReferenceMedian = nullableNumber(holdingValuationDetail?.referenceMedian);
  const compactRawModelMedian = nullableNumber(holdingValuationDetail?.rawModelMedian);
  const compactRawModelCount = Math.max(
    0,
    Number(holdingValuationDetail?.rawModelCount ?? 0),
  );
  const compactRawModelExpectedCount = Math.max(
    0,
    Number(holdingValuationDetail?.rawModelExpectedCount ?? 6),
  );

  /*
   * Compact panel uses the historical six-formula raw median as the
   * reference display value. Actionable valuation authority remains:
   *
   * representativeFairValue -> centralEstimate
   *
   * rawModelMedian is display/reference only.
   */
  const compactDisplayReferenceMedian =
    compactRawModelMedian ?? compactReferenceMedian;
  const compactRepresentativeAvailable = compactRepresentativeFairValue !== null
    && cleanText(holdingValuationDetail?.consensusStatus).toUpperCase() === 'AVAILABLE';
  const compactCentralAvailable = compactCentralEstimate !== null
    && cleanText(holdingValuationDetail?.centralEstimateStatus).toUpperCase() === 'AVAILABLE';
  const compactReferenceAvailable = !compactRepresentativeAvailable
    && !compactCentralAvailable
    && compactDisplayReferenceMedian !== null;
  const compactValuationValue = compactRepresentativeAvailable
    ? compactRepresentativeFairValue
    : compactCentralAvailable
      ? compactCentralEstimate
      : compactReferenceAvailable
        ? compactDisplayReferenceMedian
        : null;

  const compactValuationLabel = compactRepresentativeAvailable
    ? '대표 적정가'
    : compactCentralAvailable
      ? '중앙 추정가'
      : compactReferenceAvailable
        ? '6산식 중앙값'
        : '가치 산출 제한';

  const compactValuationBasisNote = !compactRepresentativeAvailable && compactCentralAvailable
    ? '대표 적정가 Gate 미충족 · 중앙 추정가 사용'
    : compactReferenceAvailable
      ? `${compactRawModelExpectedCount}개 산식 중 ${compactRawModelCount}개 산출 · 유효 산식 중앙값`
      : '';

  const compactValuationReasonCodes = Array.from(new Set([
    ...(holdingValuationDetail?.consensusReasonCodes ?? []),
    ...(holdingValuationDetail?.valuationReasonCodes ?? []),
  ].map((value) => cleanText(value)).filter(Boolean)));
  const compactValuationFamilyReasons = (holdingValuationDetail?.families ?? [])
    .filter((family) => cleanText(family.status).toUpperCase() !== 'ELIGIBLE')
    .map((family) => `${valuationFamilyLabel(family.familyCode)}: ${valuationFamilyStatusLabel(family)}`)
    .filter(Boolean);
  const compactValuationReasonLabels = [
    ...compactValuationReasonCodes
      .map((code) => valuationReasonCodeLabel(code) || cleanText(code))
      .filter(Boolean),
    ...compactValuationFamilyReasons,
    ...(holdingValuationDetail?.warnings ?? []).map(cleanText).filter(Boolean),
  ].filter(Boolean);
  const compactValuationEligibleCount = Number(holdingValuationDetail?.eligibleFamilyCount ?? 0);
  const compactValuationAvailableCount = Number(holdingValuationDetail?.availableFamilyCount ?? 0);
  const compactValuationReason = compactValuationEligibleCount <= 0
      ? `대표값 적격 0/${Math.max(compactValuationAvailableCount, holdingValuationDetail?.families?.length ?? 0)} · ${compactValuationReasonLabels.slice(0, 2).join(' · ') || '대표값 투표 자격을 충족한 평가방식이 없습니다.'}`
      : compactValuationReasonLabels.slice(0, 2).join(' · ') || '대표 적정가 산출 기준을 충족하지 못했습니다.';

  const compactValuationExpectedModelCount = Math.max(
    0,
    Number(
      (holdingValuationDetail as any)?.rawModelExpectedCount
        ?? 6
    ),
  );

  const compactValuationActualModelCount = Math.max(
    0,
    Number(
      (holdingValuationDetail as any)?.rawModelCount
        ?? holdingValuationDetail?.modelCount
        ?? 0
    ),
  );

  const compactValuationCoverageLabel =
    compactValuationExpectedModelCount > 0
      && compactValuationActualModelCount >= compactValuationExpectedModelCount
      ? `${compactValuationExpectedModelCount}개 산식 모두 계산됨`
      : compactValuationExpectedModelCount > 0
        ? `${compactValuationExpectedModelCount}개 산식 중 ${compactValuationActualModelCount}개 산출`
        : `${compactValuationActualModelCount}개 평가모델 산출`;

  const loadHoldingValuation = useCallback(async (margin: number, signal?: AbortSignal) => {
    if (mode !== 'holding' || !valuationTrackingAllowed) return;
    const ticker = cleanText(item.ticker);
    if (!/^\d{6}$/.test(ticker)) return;

    setHoldingValuationLoading(true);
    setHoldingValuationError('');
    try {
      const response = await api.get<HoldingValuationDetail>(
        `/live-summary/holdings/${encodeURIComponent(ticker)}/valuation`,
        {
          params: { marginPct: margin },
          withCredentials: true,
          signal,
        },
      );
      if (!signal?.aborted) setHoldingValuationDetail(response.data);
    } catch (requestError) {
      const name = (requestError as Error)?.name;
      if (!signal?.aborted && name !== 'CanceledError' && name !== 'AbortError') {
        setHoldingValuationError(authErrorMessage(requestError));
      }
    } finally {
      if (!signal?.aborted) setHoldingValuationLoading(false);
    }
  }, [item.ticker, mode, valuationTrackingAllowed]);

  useEffect(() => {
    if (mode !== 'holding' || !valuationTrackingAllowed) {
      setHoldingValuationDetail(null);
      setHoldingValuationLoading(false);
      setHoldingValuationError('');
      return undefined;
    }

    const holding = item as NormalizedHolding;
    const ticker = cleanText(holding.ticker);
    if (!/^\d{6}$/.test(ticker)) {
      setHoldingValuationDetail(null);
      setHoldingValuationLoading(false);
      setHoldingValuationError('');
      return undefined;
    }

    const controller = new AbortController();
    const initialMargin = holding.valuationAlertEnabled
      ? (holding.valuationAlertMarginPct ?? 0)
      : 0;

    setHoldingValuationDetail(null);
    setHoldingValuationError('');
    void loadHoldingValuation(initialMargin, controller.signal);

    return () => controller.abort();
  }, [item.ticker, mode, loadHoldingValuation, valuationTrackingAllowed, item]);

  useEffect(() => {
    setStockIssues([]);
    setStockIssuesLoading(false);
    setStockIssuesLoadedTicker('');
    setStockIssuesLoadFailed(false);
  }, [item.ticker]);

  // Detail-tab state belongs to the selected security, not to every live polling update.
  // Reset only when the identity/mode changes so price/risk refreshes never steal the user's tab.
  useEffect(() => {
    setDetailTab('EVIDENCE');
    setChangesExpanded(false);
  }, [item.ticker, mode]);

  useEffect(() => {
    trackUxEvent('stock_detail_view', {
      ticker: item.ticker,
      stock_code: item.ticker,
      stock_name: item.name,
      mode,
      source: 'live-summary-monitor-detail',
      access_mode: 'authenticated',
    });
    trackUxEvent('judgement_view', {
      ticker: item.ticker,
      stock_code: item.ticker,
      stock_name: item.name,
      mode,
      source: 'live-summary-monitor-detail',
      risk_state: item.riskAssessment.state,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.ticker, mode]);

  useEffect(() => {
    if (detailTab !== 'CHANGE' || !businessEventTrackingAllowed) return undefined;
    const ticker = cleanText(item.ticker);
    if (!/^\d{6}$/.test(ticker) || stockIssuesLoadedTicker === ticker) return undefined;

    const controller = new AbortController();
    setStockIssuesLoading(true);
    setStockIssuesLoadFailed(false);

    fetchStockLiveIssues(ticker, controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;
        setStockIssues(rows);
        setStockIssuesLoadedTicker(ticker);
      })
      .catch((error) => {
        const name = (error as { name?: string })?.name;
        if (controller.signal.aborted || name === 'AbortError') return;
        console.warn('[LiveSummary] stock issues load failed', error);
        setStockIssues(fallbackStockIssues);
        setStockIssuesLoadedTicker(ticker);
        setStockIssuesLoadFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setStockIssuesLoading(false);
      });

    return () => controller.abort();
  }, [businessEventTrackingAllowed, detailTab, fallbackStockIssues, item.ticker, stockIssuesLoadedTicker]);

  const focusedChangeEventId = cleanText(focusedChangeEvent?.id);
  const autoFocusedChangeKeyRef = useRef('');

  useEffect(() => {
    if (!focusedChangeEventId) return;
    const focusKey = `${cleanText(item.ticker)}:${focusedChangeEventId}`;
    if (autoFocusedChangeKeyRef.current === focusKey) return;
    autoFocusedChangeKeyRef.current = focusKey;
    setDetailTab('CHANGE');
    setChangesExpanded(true);
    // The focused event is rendered at the top of CHANGE already. Do not call
    // scrollIntoView here: it scrolls the outer panel and fights manual navigation.
  }, [focusedChangeEventId, item.ticker]);

  const tabs: Array<[MonitoringDetailTab, string]> = [
    ['EVIDENCE', '판단'],
    ['CHANGE', '변화'],
    ['WHY', '근거'],
  ];

  const openDetailTab = (tab: MonitoringDetailTab) => {
    onManualDetailTabChange?.();
    setDetailTab(tab);
    trackUxEvent('live_summary_detail_tab_click', {
      ticker: item.ticker,
      name: item.name,
      mode,
      detailTab: tab,
      riskState: item.riskAssessment.state,
    });

    const canonicalEvent = tab === 'EVIDENCE'
      ? 'judgement_view'
      : tab === 'WHY'
        ? 'why_view'
        : '';
    if (canonicalEvent) {
      trackUxEvent(canonicalEvent, {
        ticker: item.ticker,
        stock_code: item.ticker,
        stock_name: item.name,
        mode,
        source: 'live-summary-detail-tab',
        risk_state: item.riskAssessment.state,
      });
    }
  };

  return (
    <section className="live-summary-monitor-detail is-progressive is-decision-centered is-simplified">
      <nav className={`live-summary-detail-switcher is-${tabs.length}-tabs`} aria-label="상세 상태 보기">
        {tabs.map(([tab, label]) => (
          <button
            type="button"
            key={tab}
            className={detailTab === tab ? 'is-active' : ''}
            onClick={() => openDetailTab(tab)}
            aria-pressed={detailTab === tab}
          >
            {label}
          </button>
        ))}
      </nav>
      <div className="live-summary-monitor-pane">
        {detailTab === 'EVIDENCE' && (
          <div className="live-summary-monitor-pane__stack is-trade-priority">
            {mode === 'stock' ? (
              <>
                <StockEntryDecisionPanel item={item as NormalizedSectorTopStock} stale={stale} />
                <details className="live-summary-secondary-disclosure is-entry-evidence">
                  <summary>기업·가치 근거 상세</summary>
                  <div className="live-summary-secondary-disclosure__body">
                    <ValueJudgmentSummary item={item} mode={mode} stale={stale} />
                  </div>
                </details>
              </>
            ) : (
              <>
                <CompactHoldingDecision item={item as NormalizedHolding} stale={stale} />
                <details className="live-summary-secondary-disclosure pi-v4-evidence-detail">
                  <summary>기업·가치 분석</summary>
                  <div className="live-summary-secondary-disclosure__body">
                    <ValueJudgmentSummary item={item} mode={mode} stale={stale} />
                  </div>
                </details>
              </>
            )}

            <PremiumFeatureBoundary access={productAccess} feature="ADVANCED_MARKET_DETAIL" ticker={item.ticker}>
            <details className="live-summary-secondary-disclosure">
              <summary>가격·수급</summary>
              <div className="live-summary-secondary-disclosure__body">
                {mode === 'holding' ? (
                  <>
                    <HoldingCurrentStateSummary item={item as NormalizedHolding} stale={stale} />
                    <PositionImpactPanel holding={item as NormalizedHolding} assessment={item.riskAssessment} />
                  </>
                ) : (
                  <>
                    <DecisionAuthoritySummary
                      item={item}
                      mode={mode}
                      marketContext={marketContext}
                      marketOpen={marketOpen}
                      marketSession={marketSession}
                      latestChange={latestMaterialChange}
                      stale={stale}
                    />
                    <CompactFlowBrief item={item} marketSession={marketSession} />
                    <PricePositionBrief item={item} stale={stale} />
                  </>
                )}
              </div>
            </details>
            </PremiumFeatureBoundary>
          </div>
        )}

        {detailTab === 'CHANGE' && (
          <div className="live-summary-monitor-pane__stack">
            {focusedChangeEvent && (
              <div
                id={changeEventAnchorId(focusedChangeEvent.id)}
                data-change-event-id={cleanText(focusedChangeEvent.id)}
                style={{
                  scrollMarginTop: 96,
                  padding: '9px 10px',
                  border: '1px solid #9dbcf2',
                  borderRadius: 10,
                  background: '#f4f8ff',
                  boxShadow: '0 0 0 2px rgba(41,98,255,.08)',
                }}
              >

                {mode === 'holding' && (
                  <small style={{ display: 'block', color: '#53739a', fontSize: 7.8, fontWeight: 900, letterSpacing: '.06em' }}>
                    {holdingChangeLayerLabel(focusedChangeEvent)}
                  </small>
                )}
                <b style={{ display: 'block', marginTop: 3, color: '#21364d', fontSize: 10.8 }}>
                  {mode === 'holding' ? changeTransitionLabel(focusedChangeEvent) : (focusedChangeEvent.title || focusedChangeEvent.message || '상태 변화 감지')}
                </b>
                <span style={{ display: 'block', marginTop: 3, color: '#6b7d90', fontSize: 9.2 }}>
                  {mode === 'holding' ? `${changeReasonLabel(focusedChangeEvent, item.name)} · ` : (
                    focusedChangeEvent.message && focusedChangeEvent.message !== focusedChangeEvent.title
                      ? `${focusedChangeEvent.message} · `
                      : ''
                  )}
                  {eventTimeLabel(focusedChangeEvent) || '최근 감지'}
                </span>
              </div>
            )}

            <section
              className={`live-summary-movement-attribution ${toneClass(movementAttributionTone(primaryMovementIssue?.movementAttribution ?? null))}`}
              aria-label={`${item.name} 오늘 움직임 배경`}
            >
              <header>
                <div>
                  <small>오늘 왜 움직였나</small>
                  <b>{movementAttributionDirectionLabel(primaryMovementIssue?.movementAttribution ?? null)}</b>
                </div>
                {primaryMovementIssue?.movementAttribution ? (
                  <span>{primaryMovementIssue.movementAttribution.label}</span>
                ) : stockIssuesLoading ? (
                  <span>근거 확인 중</span>
                ) : (
                  <span>원인 미확인</span>
                )}
              </header>

              {primaryMovementIssue?.movementAttribution ? (() => {
                const attribution = primaryMovementIssue.movementAttribution as MovementAttribution;
                const primary = attribution.primaryCause;
                const sectorImpact = primaryMovementIssue.sectorImpact;
                return (
                  <>
                    <div className="live-summary-movement-attribution__primary">
                      <small>주요 배경</small>
                      <strong>{primary?.title || attribution.label}</strong>
                      {primary?.summary && <p>{primary.summary}</p>}
                    </div>

                    {sectorImpact && (
                      <div className="live-summary-movement-attribution__sector">
                        <span><small>업종</small><b>{sectorImpact.sectorName}</b></span>
                        <span><small>업종 등락</small><b>{sectorImpact.changeRate === null ? '확인 중' : formatPct(sectorImpact.changeRate, 2)}</b></span>
                        <span><small>확산</small><b>{sectorImpact.realtimeTickerCount > 0 ? `${sectorImpact.upTickerCount}/${sectorImpact.realtimeTickerCount} 상승` : '확인 중'}</b></span>
                      </div>
                    )}

                    {attribution.secondaryCauses.length > 0 && (
                      <div className="live-summary-movement-attribution__secondary">
                        <small>추가 압력·근거</small>
                        {attribution.secondaryCauses.map((cause, index) => (
                          <p key={`${cause.type}:${index}`}><b>{cause.type === 'FLOW' ? '수급' : cause.type === 'SECTOR' ? '업종' : cause.type}</b>{cause.summary}</p>
                        ))}
                      </div>
                    )}

                    {attribution.stockSpecificEvent && (
                      <div className="live-summary-movement-attribution__stock-event">
                        <small>종목 고유 Event</small>
                        <b>{attribution.stockSpecificEvent.status === 'PRESENT' ? '직접 Event 확인' : '신규 직접 Event 아님'}</b>
                        {attribution.stockSpecificEvent.summary && <span>{attribution.stockSpecificEvent.summary}</span>}
                      </div>
                    )}

                    <div className="live-summary-movement-attribution__next">
                      <small>다음 확인</small>
                      <b>{attribution.nextCheck || '추가 Event와 가격·수급 갱신을 확인합니다.'}</b>
                    </div>
                  </>
                );
              })() : (
                <div className="live-summary-movement-attribution__empty">
                  <strong>{stockIssuesLoading ? '가격 원인을 검증하고 있습니다.' : '뚜렷한 원인은 아직 확인되지 않았습니다.'}</strong>
                  <p>
                    {stockIssuesLoadFailed
                      ? 'Event 조회에 실패해 원인을 확정하지 않습니다.'
                      : '확인되지 않은 뉴스에 원인을 억지로 붙이지 않고, Event·업종 확산·가격·수급이 함께 확인될 때만 배경으로 표시합니다.'}
                  </p>
                </div>
              )}
            </section>

            {mode === 'holding'
              && (item as NormalizedHolding).judgmentContract?.available && (
              <div className="live-summary-current-action-strip">
                <small>현재 대응</small>
                <b>
                  {(item as NormalizedHolding).judgmentContract.riskManagement.actionLabel}
                </b>
                <span>현재 상태 기준 · 아래는 변경 이력</span>
              </div>
            )}

            <div className="live-summary-change-heading">
              <div><small>이전과 무엇이 달라졌나</small><b>변화 타임라인</b></div>
              {monitor.changes.length > 0 && <span>{monitor.changes.length}건</span>}
            </div>
            {mode === 'holding' && (item as NormalizedHolding).judgmentContract?.available && (() => {
              const judgmentRows = (item as NormalizedHolding).judgmentContract.changes;
              const actualChanges = judgmentRows.filter((change) => !isJudgmentCheckResult(change));
              const resultChecks = judgmentRows.filter((change) => isJudgmentCheckResult(change));

              return (
                <>
                  <div className="live-summary-change-heading is-judgment-impact">
                    <div><small>이것이 기존 판단을 바꿨나</small><b>현재 판단 영향</b></div>
                  </div>
                  <div className="live-summary-judgment-change-layers" aria-label="판단 변화 분류">
                    {actualChanges.length > 0 ? (
                      actualChanges.slice(0, 4).map((change, index) => (
                        <span className={toneClass(change.tone)} key={`${change.type}:${change.label}:${index}`}>
                          <small>{judgmentChangeTypeLabel(change.type)}</small>
                          <b>{change.label}</b>
                        </span>
                      ))
                    ) : (
                      <span><small>핵심 판단 변화</small><b>핵심 논리 변화 없음</b></span>
                    )}
                  </div>

                  {resultChecks.length > 0 && (
                    <div className="live-summary-judgment-change-layers is-check-results" aria-label="판단 검증 결과">
                      {resultChecks.slice(0, 4).map((change, index) => (
                        <span className={toneClass(change.tone)} key={`check:${change.type}:${change.label}:${index}`}>
                          <small>검증 변화</small>
                          <b>{change.label}</b>
                        </span>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
            {mode === 'holding' && (
              <PremiumFeatureBoundary access={productAccess} feature="VALUATION_TRACKING" ticker={item.ticker}>
              <details className="live-summary-secondary-disclosure is-valuation-support">
                <summary>{holdingValuationDetail ? '평가모델 입력 변화' : '가치평가'}</summary>
                <section className="live-summary-change-issues" aria-label={`${item.name} 가치평가 변화`}>
                  {holdingValuationLoading && !holdingValuationDetail ? (
                    <p className="live-summary-monitor-pane__empty">조회 중</p>
                  ) : holdingValuationError ? (
                    <p className="live-summary-monitor-pane__empty">{holdingValuationError}</p>
                  ) : holdingValuationDetail ? (
                    <div className="live-summary-change-issues__list">
                      <div>
                        <b>
                          {compactReferenceAvailable
                            ? '평가모델 입력 변화'
                            : compactValuationLabel}
                        </b>

                        <small>
                          {compactReferenceAvailable
                            ? compactValuationCoverageLabel
                            : compactValuationValue !== null
                              ? `${formatPrice(compactValuationValue)}원`
                              : compactValuationReason}
                        </small>

                        {holdingValuationDetail.valuationChange?.primaryDriverLabel ? (
                          <p>{valuationChangeNarrative(holdingValuationDetail.valuationChange)}</p>
                        ) : null}

                        {compactReferenceAvailable && compactValuationValue !== null ? (
                          <details className="live-summary-valuation-reference-detail">
                            <summary>평가모델 상세 보기</summary>
                            <div>
                              <b>{compactValuationLabel}</b>
                              <small>{formatPrice(compactValuationValue)}원</small>
                              <p>참고용 · 대표 적정가 아님</p>
                              {compactValuationBasisNote ? (
                                <p>{compactValuationBasisNote}</p>
                              ) : null}
                            </div>
                          </details>
                        ) : compactValuationValue !== null && compactValuationBasisNote ? (
                          <p>{compactValuationBasisNote}</p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </section>
              </details>
              </PremiumFeatureBoundary>
            )}
            {monitor.changes.length > 0 ? (
              <>
                <div className="live-summary-change-compact-list is-current-change">
                  {monitor.changes.slice(0, 1).map((change, index) => (
                    <div key={`current-${change.time}-${change.text}-${index}`}>
                      <i className={toneClass(change.tone)} />
                      <span><b>{change.text}</b>{change.detail && <em>{change.detail}</em>}<small>{change.time || '최근 감지'}</small></span>
                    </div>
                  ))}
                </div>
                {monitor.changes.length > 1 && (
                  <PremiumFeatureBoundary access={productAccess} feature="CHANGE_HISTORY" ticker={item.ticker}>
                    <div className="live-summary-change-history">
                      <header><small>이전 변화</small><span>{monitor.changes.length - 1}건</span></header>
                      <div className="live-summary-change-compact-list">
                        {(changesExpanded ? monitor.changes.slice(1) : monitor.changes.slice(1, 3)).map((change, index) => (
                          <div key={`history-${change.time}-${change.text}-${index}`}>
                            <i className={toneClass(change.tone)} />
                            <span><b>{change.text}</b>{change.detail && <em>{change.detail}</em>}<small>{change.time || '최근 감지'}</small></span>
                          </div>
                        ))}
                      </div>
                      {monitor.changes.length > 3 && (
                        <button type="button" className="live-summary-change-more" onClick={() => setChangesExpanded((current) => !current)} aria-expanded={changesExpanded}>
                          {changesExpanded ? '이전 변화 접기' : `이전 변화 ${monitor.changes.length - 3}건 더 보기`}
                        </button>
                      )}
                    </div>
                  </PremiumFeatureBoundary>
                )}
                {mode === 'holding' && monitor.suppressedChangeCount > 0 && (
                  <p className="live-summary-change-dedupe-note">반복·비핵심 변화 {monitor.suppressedChangeCount}건은 요약에서 제외했습니다.</p>
                )}
              </>
            ) : (
              <p className="live-summary-monitor-pane__empty">최근 판단을 바꿀 만한 중요한 변화는 없습니다.</p>
            )}
            <PremiumFeatureBoundary access={productAccess} feature="BUSINESS_EVENT_TRACKING" ticker={item.ticker}>
              <details className="live-summary-secondary-disclosure is-event-history">
                <summary>관련 Event {rankedStockIssues.length > 0 ? `${rankedStockIssues.length}건` : ''} · 원문/검증 보기</summary>
              <section className="live-summary-change-issues" aria-label={`${item.name} 관련 이벤트`}>
                {stockIssuesLoading ? (
                  <p className="live-summary-monitor-pane__empty">조회 중</p>
                ) : rankedStockIssues.length > 0 ? (
                  <div className="live-summary-change-issues__list">
                    {rankedStockIssues.map((issue) => {
                      const meta = [
                        formatIssuePublishedAt(issue.publishedAt),
                        issue.source,
                        eventLinkLabel(issue.eventLinkType),
                        eventEvidenceLabel(issue.evidenceLevel || issue.verificationStatus),
                      ].map(cleanText).filter(Boolean).join(' · ');
                      const reactionStatus = cleanText(issue.reactionStatus);
                      const flowStatus = cleanText(issue.flowStatus);
                      const judgmentSupport = cleanText(issue.judgmentSupport);
                      const hasResolvedReactionState = isResolvedEventReactionStatus(reactionStatus);
                      const hasReactionMetricOnly = !hasResolvedReactionState && issue.excessSector !== null;
                      const hasFlowState = isResolvedEventFlowStatus(flowStatus);
                      const hasJudgmentState = isResolvedEventJudgmentStatus(judgmentSupport);
                      const eventStateAvailable = hasResolvedReactionState || hasReactionMetricOnly || hasFlowState || hasJudgmentState;
                      const body = (
                        <>
                          <b className="live-summary-issue-title" title={issue.title}>{issue.title}</b>
                          {meta && <small className="live-summary-issue-meta">{meta}</small>}
                          {eventStateAvailable && (
                            <div className="live-summary-event-state" aria-label={`${issue.title} 이벤트 반응 상태`}>
                              {hasResolvedReactionState && (
                                <span><small>시장 반응</small><strong>{eventReactionLabel(reactionStatus)}</strong>{issue.excessSector !== null && <em>업종 대비 {signedPctPoint(issue.excessSector)}</em>}</span>
                              )}
                              {hasReactionMetricOnly && (
                                <span><small>업종 대비</small><strong>{signedPctPoint(issue.excessSector)}</strong></span>
                              )}
                              {hasFlowState && <span><small>수급</small><strong>{eventFlowLabel(flowStatus)}</strong></span>}
                              {hasJudgmentState && <span><small>판단 영향</small><strong>{eventJudgmentLabel(judgmentSupport)}</strong></span>}
                            </div>
                          )}
                        </>
                      );
                      return issue.sourceUrlAvailable && isExternalIssueUrl(issue.url) ? (
                        <a
                          key={`${issue.id}-${issue.title}`}
                          href={issue.url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {body}
                        </a>
                      ) : <div key={`${issue.id}-${issue.title}`}>{body}</div>;
                    })}
                  </div>
                ) : stockIssuesLoadFailed ? (
                  <p className="live-summary-monitor-pane__empty">조회 실패</p>
                ) : (
                  <p className="live-summary-monitor-pane__empty">없음</p>
                )}
              </section>
              </details>
            </PremiumFeatureBoundary>
            {flowMomentum && flowMomentum.metrics.some((metric) => metric.available) && (
              <div className={`live-summary-momentum-brief ${toneClass(flowMomentum.tone)}`}>
                <small>수급 변화</small><strong>{flowMomentum.label}</strong>{flowMomentum.turnedAt && <span>{flowMomentum.turnedAt} 전환</span>}
              </div>
            )}
          </div>
        )}

        {detailTab === 'WHY' && (
          <div className="live-summary-monitor-pane__stack">
            {mode === 'holding' && <HoldingJudgmentEvidenceSummary holding={item as NormalizedHolding} />}
            <WhyMovingPanel item={item} marketContext={marketContext} marketOpen={marketOpen} sectorContext={sectorContext} stale={stale} />

            {mode === 'holding' && (
              <PremiumFeatureBoundary access={productAccess} feature="VERIFICATION_FINANCIAL" ticker={item.ticker}>
              <details
                className="live-summary-secondary-disclosure is-verify"
                onToggle={(event) => {
                  if (!(event.currentTarget as HTMLDetailsElement).open) return;
                  trackUxEvent('validation_view', {
                    ticker: item.ticker,
                    stock_code: item.ticker,
                    stock_name: item.name,
                    mode,
                    source: 'live-summary-evidence-disclosure',
                    risk_state: item.riskAssessment.state,
                  });
                }}
              >
                <summary>검증·재무</summary>
                <div className="live-summary-secondary-disclosure__body live-summary-verify-stack">
                  <HistoricalEvidencePanel evidence={item.historicalEvidence} item={item} />
                  <RiskEvidenceProofPanel ticker={item.ticker} />
                  <div className="live-summary-verify-stack__secondary">

                    <HoldingValuationPanel
                      holding={item as NormalizedHolding}
                      detail={holdingValuationDetail}
                      loading={holdingValuationLoading}
                      loadError={holdingValuationError}
                      onLoad={loadHoldingValuation}
                      onDetailChange={(next) => {
                        setHoldingValuationDetail(next);
                        setHoldingValuationError('');
                      }}
                    />
                    <FundamentalBrief item={item} />
                  </div>
                </div>
              </details>
              </PremiumFeatureBoundary>
            )}
          </div>
        )}
      </div>
    </section>
  );
}





function investorTrendTone(direction: string): Tone {
  if (direction === 'UP_STREAK' || direction === 'DOWN_TO_UP') return 'up';
  if (direction === 'DOWN_STREAK' || direction === 'UP_TO_DOWN') return 'down';
  if (direction === 'DATA_STALE') return 'warn';
  return 'neutral';
}

function investorTrendLabel(item: NormalizedSectorTopStock | NormalizedHolding): string {
  const minutes = Math.max(0, item.investorTrendStreakMinutes);
  switch (item.investorTrendDirection) {
    case 'UP_STREAK':
      return `외인·기관 순매수 상승 ${minutes}분째`;
    case 'DOWN_STREAK':
      return `외인·기관 순매수 하락 ${minutes}분째`;
    case 'UP_TO_DOWN':
      return '외인·기관 수급 하락 전환';
    case 'DOWN_TO_UP':
      return '외인·기관 수급 회복 전환';
    case 'DATA_STALE':
      return '외인·기관 수급 데이터 지연';
    case 'FLAT':
      return '외인·기관 수급 방향성 약함';
    default:
      return '외인·기관 수급 추세 확인 중';
  }
}






function formatNow(): string {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
}

function marketFallbackTone(label: string, score: number | null): Tone {
  const upper = label.toUpperCase();
  if (upper.includes('OFF') || (score !== null && score <= 45)) return 'down';
  if (upper.includes('ON') || (score !== null && score >= 60)) return 'up';
  return 'neutral';
}

function normalizeMarketState(input?: LiveSummaryMarketState): NormalizedMarketState {
  const label = cleanText(input?.label) || 'NEUTRAL';
  const score = nullableNumber(input?.score);
  return {
    label,
    score,
    tone: normalizeTone(input?.tone, marketFallbackTone(label, score)),
    phase: cleanText(input?.phase) || 'PRE_MARKET',
    baseDate: cleanText(input?.baseDate),
    generatedAt: cleanText(input?.generatedAt),
    topEtfName: cleanText(input?.topEtfName),
    topEtfTicker: cleanText(input?.topEtfTicker),
    topSectorCode: cleanText(input?.topSectorCode),
    topScore: nullableNumber(input?.topScore),
    comment: cleanText(input?.comment) || EMPTY_MARKET.comment,
  };
}

function normalizeLiveIssueStock(input: unknown): NormalizedLiveIssueSignalStock | null {
  const source = (input ?? {}) as Record<string, unknown>;
  const ticker = cleanText(source.ticker);
  const name = resolveStockDisplayName(source, ticker);
  if (!ticker && !name) return null;
  return {
    ticker,
    name,
    relation: cleanText(source.relation).toUpperCase() || 'POSSIBLE',
  };
}

function normalizeLiveIssueScenario(input: unknown): NormalizedLiveIssueScenario | null {
  const source = asObject(input);
  const thesis = cleanText(source.thesis);
  const eventValueLabel = cleanText(source.eventValueLabel);
  const smartMoneyPotentialLabel = cleanText(source.smartMoneyPotentialLabel);
  const marketDeliveryLabel = cleanText(source.marketDeliveryLabel);
  const entryEnvironmentLabel = cleanText(source.entryEnvironmentLabel || source.investmentEnvironmentLabel);
  const investmentEnvironmentLabel = cleanText(source.investmentEnvironmentLabel || source.entryEnvironmentLabel);
  const catalystStateLabel = cleanText(source.catalystStateLabel);
  const hasScenario = Boolean(
    thesis || eventValueLabel || smartMoneyPotentialLabel || marketDeliveryLabel
    || entryEnvironmentLabel || investmentEnvironmentLabel || catalystStateLabel
  );
  if (!hasScenario) return null;
  return {
    eventValue: cleanText(source.eventValue).toUpperCase(),
    eventValueLabel,
    capitalDirection: cleanText(source.capitalDirection).toUpperCase(),
    capitalDirectionLabel: cleanText(source.capitalDirectionLabel),
    capitalStyle: cleanText(source.capitalStyle).toUpperCase(),
    capitalStyleLabel: cleanText(source.capitalStyleLabel),
    smartMoneyPotential: cleanText(source.smartMoneyPotential).toUpperCase(),
    smartMoneyPotentialLabel,
    marketDelivery: cleanText(source.marketDelivery).toUpperCase(),
    marketDeliveryLabel,
    entryEnvironment: cleanText(source.entryEnvironment || source.investmentEnvironment).toUpperCase(),
    entryEnvironmentLabel,
    investmentEnvironment: cleanText(source.investmentEnvironment || source.entryEnvironment).toUpperCase(),
    investmentEnvironmentLabel,
    catalystState: cleanText(source.catalystState).toUpperCase(),
    catalystStateLabel,
    horizon: cleanText(source.horizon).toUpperCase(),
    horizonLabel: cleanText(source.horizonLabel),
    marketDirection: cleanText(source.marketDirection).toUpperCase(),
    marketDirectionLabel: cleanText(source.marketDirectionLabel),
    marketHeadwindBuckets: stringList(source.marketHeadwindBuckets).slice(0, 4),
    marketTailwindBuckets: stringList(source.marketTailwindBuckets).slice(0, 4),
    preEventState: cleanText(source.preEventState).toUpperCase(),
    preEventStateLabel: cleanText(source.preEventStateLabel),
    preEventReturn15m: nullableNumber(source.preEventReturn15m),
    preEventReturn30m: nullableNumber(source.preEventReturn30m),
    marketReactionBaseTime: cleanText(source.marketReactionBaseTime),
    eventOccurredAt: cleanText(source.eventOccurredAt),
    eventFirstSeenAt: cleanText(source.eventFirstSeenAt),
    currentStateLabel: cleanText(source.currentStateLabel),
    thesis,
    reasons: stringList(source.reasons).slice(0, 4),
    checkpoints: stringList(source.checkpoints).slice(0, 3),
    dataStatus: cleanText(source.dataStatus).toUpperCase(),
  };
}

function normalizeLiveIssueCapitalScenario(input: unknown): NormalizedLiveIssueCapitalScenario | null {
  const source = asObject(input);
  const summary = cleanText(source.summary);
  const marketDirectionLabel = cleanText(source.marketDirectionLabel);
  if (!summary && !marketDirectionLabel) return null;
  return {
    marketDirection: cleanText(source.marketDirection).toUpperCase(),
    marketDirectionLabel,
    capitalFocus: stringList(source.capitalFocus).slice(0, 3),
    capitalFocusLabel: cleanText(source.capitalFocusLabel),
    pressureFocus: stringList(source.pressureFocus).slice(0, 3),
    pressureFocusLabel: cleanText(source.pressureFocusLabel),
    summary,
    dataStatus: cleanText(source.dataStatus).toUpperCase(),
  };
}

function normalizeLiveIssueBriefAggregate(input: unknown): NormalizedLiveIssueBriefAggregate {
  const source = asObject(input);
  return {
    state: cleanText(source.state).toUpperCase(),
    direction: cleanText(source.direction).toUpperCase() || 'UNKNOWN',
    summary: cleanText(source.summary),
    evidenceLevel: cleanText(source.evidenceLevel).toUpperCase() || 'UNVERIFIED',
  };
}

function normalizeSectorImpact(input: unknown): NormalizedSectorImpact | null {
  const source = asObject(input);
  const sectorName = cleanText(source.sectorName);
  if (!sectorName) return null;
  return {
    sectorName,
    industryCode: cleanText(source.industryCode),
    marketType: cleanText(source.marketType),
    changeRate: nullableNumber(source.changeRate),
    mappedTickerCount: Math.max(0, Math.round(toNumber(source.mappedTickerCount, 0))),
    realtimeTickerCount: Math.max(0, Math.round(toNumber(source.realtimeTickerCount, 0))),
    upTickerCount: Math.max(0, Math.round(toNumber(source.upTickerCount, 0))),
    downTickerCount: Math.max(0, Math.round(toNumber(source.downTickerCount, 0))),
    breadthPct: nullableNumber(source.breadthPct),
    realtimeCoveragePct: nullableNumber(source.realtimeCoveragePct),
    realtimeTradingValue: nullableNumber(source.realtimeTradingValue),
    latestRealtimeEventAt: cleanText(source.latestRealtimeEventAt),
    dataStatus: cleanText(source.dataStatus).toUpperCase() || 'NO_DATA',
    classification: cleanText(source.classification).toUpperCase() || 'UNCONFIRMED',
    label: cleanText(source.label) || '산업 반응 확인',
    groupType: cleanText(source.groupType).toUpperCase() || 'INDUSTRY',
    marketGroupName: cleanText(source.marketGroupName),
    background: cleanText(source.background),
    reason: cleanText(source.reason),
  };
}

function normalizeMovementAttribution(input: unknown): MovementAttribution | null {
  const source = asObject(input);
  const status = cleanText(source.status).toUpperCase();
  const label = cleanText(source.label);
  if (!status && !label) return null;
  const primaryRaw = asObject(source.primaryCause);
  const stockSpecificRaw = asObject(source.stockSpecificEvent);
  const secondaryRaw = Array.isArray(source.secondaryCauses) ? source.secondaryCauses : [];
  const primaryTitle = cleanText(primaryRaw.title);
  const primarySummary = cleanText(primaryRaw.summary);
  return {
    direction: cleanText(source.direction).toUpperCase() || 'UNKNOWN',
    status: status || 'UNKNOWN',
    label: label || '뚜렷한 원인 미확인',
    primaryCause: primaryTitle || primarySummary ? {
      type: cleanText(primaryRaw.type).toUpperCase() || 'UNKNOWN',
      title: primaryTitle || '현재 움직임 배경',
      summary: primarySummary,
    } : null,
    secondaryCauses: secondaryRaw
      .map((value) => asObject(value))
      .map((value) => ({
        type: cleanText(value.type).toUpperCase(),
        summary: cleanText(value.summary),
      }))
      .filter((value) => Boolean(value.summary))
      .slice(0, 3),
    stockSpecificEvent: cleanText(stockSpecificRaw.status) || cleanText(stockSpecificRaw.summary) ? {
      status: cleanText(stockSpecificRaw.status).toUpperCase() || 'UNKNOWN',
      summary: cleanText(stockSpecificRaw.summary),
    } : null,
    nextCheck: cleanText(source.nextCheck),
    causeConfirmed: normalizeBoolean(source.causeConfirmed),
  };
}

function normalizeLiveIssueBriefSector(input: unknown): NormalizedLiveIssueBriefSector | null {
  const source = asObject(input);
  const name = cleanText(source.name);
  const summary = cleanText(source.summary);
  if (!name || !summary) return null;
  return {
    name,
    direction: cleanText(source.direction).toUpperCase() || 'UNKNOWN',
    summary,
    evidenceLevel: cleanText(source.evidenceLevel).toUpperCase() || 'UNVERIFIED',
    sectorImpact: normalizeSectorImpact(source.sectorImpact),
  };
}

function normalizeLiveIssueBriefStock(input: unknown): NormalizedLiveIssueBriefStock | null {
  const source = asObject(input);
  const ticker = cleanText(source.ticker);
  const name = cleanText(source.name);
  const summary = cleanText(source.summary);
  if ((!ticker && !name) || !summary) return null;
  return {
    ticker,
    name,
    relation: cleanText(source.relation).toUpperCase() || 'POSSIBLE',
    direction: cleanText(source.direction).toUpperCase() || 'UNKNOWN',
    summary,
    evidenceLevel: cleanText(source.evidenceLevel).toUpperCase() || 'UNVERIFIED',
  };
}

function normalizeLiveIssueBriefCatalyst(input: unknown): NormalizedLiveIssueBriefCatalyst | null {
  const source = asObject(input);
  const headline = cleanText(source.headline);
  if (!headline) return null;
  return {
    materialId: cleanText(source.materialId),
    headline,
    scope: cleanText(source.scope).toUpperCase() || 'MARKET',
    direction: cleanText(source.direction).toUpperCase() || 'UNKNOWN',
    summary: cleanText(source.summary),
    evidenceLevel: cleanText(source.evidenceLevel).toUpperCase() || 'UNVERIFIED',
    phase: cleanText(source.phase).toUpperCase() || 'FACT',
    lifecycleState: cleanText(source.lifecycleState).toUpperCase(),
    resolutionType: cleanText(source.resolutionType).toUpperCase(),
  };
}

function normalizeLiveIssueBrief(input: unknown): NormalizedLiveIssueBrief | null {
  const source = asObject(input);
  if (!Object.keys(source).length) return null;
  const catalysts = asObject(source.catalysts);
  const sectors = Array.isArray(source.sectors)
    ? source.sectors.map(normalizeLiveIssueBriefSector).filter((item): item is NormalizedLiveIssueBriefSector => Boolean(item)).slice(0, 6)
    : [];
  const stocks = Array.isArray(source.stocks)
    ? source.stocks.map(normalizeLiveIssueBriefStock).filter((item): item is NormalizedLiveIssueBriefStock => Boolean(item)).slice(0, 8)
    : [];
  const normalizeCatalysts = (value: unknown): NormalizedLiveIssueBriefCatalyst[] => Array.isArray(value)
    ? value.map(normalizeLiveIssueBriefCatalyst).filter((item): item is NormalizedLiveIssueBriefCatalyst => Boolean(item)).slice(0, 6)
    : [];
  const watch = Array.isArray(source.watch)
    ? source.watch.map((raw) => cleanText(asObject(raw).text)).filter(Boolean).slice(0, 5)
    : [];
  const brief: NormalizedLiveIssueBrief = {
    overview: cleanText(source.overview),
    policy: normalizeLiveIssueBriefAggregate(source.policy),
    market: normalizeLiveIssueBriefAggregate(source.market),
    sectors,
    stocks,
    catalysts: {
      born: normalizeCatalysts(catalysts.born),
      strengthened: normalizeCatalysts(catalysts.strengthened),
      weakened: normalizeCatalysts(catalysts.weakened),
      realized: normalizeCatalysts(catalysts.realized),
      resolved: normalizeCatalysts(catalysts.resolved),
      invalidated: normalizeCatalysts(catalysts.invalidated ?? catalysts.expired),
      expired: normalizeCatalysts(catalysts.expired),
    },
    watch,
  };
  const hasContent = Boolean(
    brief.overview
    || brief.policy.summary
    || brief.market.summary
    || brief.sectors.length
    || brief.stocks.length
    || brief.catalysts.born.length
    || brief.catalysts.strengthened.length
    || brief.catalysts.weakened.length
    || brief.catalysts.realized.length
    || brief.catalysts.resolved.length
    || brief.catalysts.invalidated.length
    || brief.catalysts.expired.length
    || brief.watch.length
  );
  return hasContent ? brief : null;
}

function normalizeLiveIssueSignalItem(input: unknown): NormalizedLiveIssueSignalItem | null {
  const source = (input ?? {}) as Record<string, unknown>;
  const headline = cleanText(source.headline);
  const reason = cleanText(source.reason);
  if (!headline || !reason) return null;
  const stocks = Array.isArray(source.stocks)
    ? source.stocks.map(normalizeLiveIssueStock).filter((item): item is NormalizedLiveIssueSignalStock => Boolean(item)).slice(0, 6)
    : [];
  return {
    changeType: cleanText(source.changeType).toUpperCase() || 'NEW',
    scope: cleanText(source.scope).toUpperCase() || 'SECTOR',
    eventType: cleanText(source.eventType).toUpperCase(),
    headline,
    direction: cleanText(source.direction).toUpperCase() || 'UNKNOWN',
    importance: cleanText(source.importance).toUpperCase() || 'LOW',
    evidenceLevel: cleanText(source.evidenceLevel).toUpperCase() || 'UNVERIFIED',
    sectors: stringList(source.sectors).slice(0, 4),
    themes: stringList(source.themes).slice(0, 4),
    stocks,
    reason,
    watch: cleanText(source.watch),
    sectorImpacts: Array.isArray(source.sectorImpacts)
      ? source.sectorImpacts.map(normalizeSectorImpact).filter((item): item is NormalizedSectorImpact => Boolean(item)).slice(0, 3)
      : [],
    scenario: normalizeLiveIssueScenario(source.scenario),
  };
}

function normalizeLiveIssueSignal(input?: LiveSummaryLiveIssueSignal): NormalizedLiveIssueSignal {
  const typed = (input ?? {}) as LiveSummaryLiveIssueSignal;
  const source = (input ?? {}) as unknown as Record<string, unknown>;
  const window = typed.window ?? {};
  const summary = typed.summary ?? {};
  const signals = Array.isArray(typed.signals)
    ? typed.signals.map(normalizeLiveIssueSignalItem).filter((item): item is NormalizedLiveIssueSignalItem => Boolean(item)).slice(0, 5)
    : [];
  return {
    status: cleanText(typed.status).toUpperCase() || 'NO_DATA',
    windowStart: cleanText(window.start),
    windowEnd: cleanText(window.end),
    sourceMessageCount: Math.max(0, Math.round(nullableNumber(window.sourceMessageCount) ?? 0)),
    candidateMessageCount: Math.max(0, Math.round(nullableNumber(window.candidateMessageCount) ?? 0)),
    newSignalCount: Math.max(0, Math.round(nullableNumber(summary.newSignalCount) ?? 0)),
    highImportanceCount: Math.max(0, Math.round(nullableNumber(summary.highImportanceCount) ?? 0)),
    reason: cleanText(typed.reason),
    signals,
    brief: normalizeLiveIssueBrief(source.brief),
    capitalScenario: normalizeLiveIssueCapitalScenario(source.capitalScenario),
    p1Shadow: source.p1_shadow && Object.keys(asObject(source.p1_shadow)).length ? source.p1_shadow as LiveSummaryP1Shadow : null,
  };
}

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map(cleanText).filter(Boolean);
}

function normalizeDecisionEvidence(rawDecision: unknown): DecisionEvidenceView {
  const decision = asObject(rawDecision);
  const evidence = asObject(decision.evidence);
  const rawItems = Array.isArray(evidence.items) ? evidence.items : [];
  const items = rawItems.map((raw): DecisionEvidenceItem => {
    const item = asObject(raw);
    return {
      label: cleanText(item.label),
      statusLabel: cleanText(item.statusLabel ?? item.status_label ?? item.status),
      headline: cleanText(item.headline ?? item.summary),
      value: cleanText(item.value ?? item.displayValue ?? item.display_value),
      detail: cleanText(item.detail ?? item.reason),
      usedInDecision: normalizeBoolean(item.usedInDecision ?? item.used_in_decision ?? true),
    };
  }).filter((item) => item.label || item.headline || item.value);

  const strength = cleanText(evidence.strength).toUpperCase();
  const strengthLabel = cleanText(evidence.strengthLabel ?? evidence.strength_label);
  const summary = cleanText(evidence.summary);
  const riskConditions = stringList(evidence.riskConditions ?? evidence.risk_conditions);
  const recoveryConditions = stringList(evidence.recoveryConditions ?? evidence.recovery_conditions);

  return {
    available: Boolean(items.length || strengthLabel || summary || riskConditions.length || recoveryConditions.length),
    strength,
    strengthLabel,
    summary,
    items,
    riskConditions,
    recoveryConditions,
  };
}

function normalizeHistoricalOutcome(raw: unknown): HistoricalOutcomeView {
  const source = asObject(raw);
  return {
    sampleSize: Math.max(0, Math.round(toNumber(source.sampleSize ?? source.sample_size, 0))),
    upCount: Math.max(0, Math.round(toNumber(source.upCount ?? source.up_count, 0))),
    flatCount: Math.max(0, Math.round(toNumber(source.flatCount ?? source.flat_count, 0))),
    downCount: Math.max(0, Math.round(toNumber(source.downCount ?? source.down_count, 0))),
    positiveRate: nullableNumber(source.positiveRate ?? source.positive_rate),
    averageReturn: nullableNumber(source.averageReturn ?? source.average_return),
  };
}

function normalizeHistoricalEvidence(raw: unknown): HistoricalEvidenceView {
  const source = asObject(raw);
  const status = cleanText(source.status).toUpperCase();
  const pattern = asObject(source.pattern);
  const patternLabels = [
    pattern.shortTrend ?? pattern.short_trend,
    pattern.mediumTrend ?? pattern.medium_trend,
    pattern.activity,
    pattern.pricePosition ?? pattern.price_position,
  ].map(cleanText).filter(Boolean);
  const sampleSize = Math.max(0, Math.round(toNumber(source.sampleSize ?? source.sample_size, 0)));
  const forward5d = normalizeHistoricalOutcome(source.forward5d ?? source.forward_5d);
  const forward20d = normalizeHistoricalOutcome(source.forward20d ?? source.forward_20d);
  const available = status === 'AVAILABLE' || sampleSize > 0 || forward5d.sampleSize > 0 || forward20d.sampleSize > 0;

  return {
    available,
    title: cleanText(source.title) || '과거 유사 흐름',
    basisLabel: cleanText(source.basisLabel ?? source.basis_label),
    sampleSize,
    sampleNote: cleanText(source.sampleNote ?? source.sample_note),
    patternLabels,
    forward5d,
    forward20d,
    interpretation: cleanText(source.interpretation),
    coverageNote: cleanText(source.coverageNote ?? source.coverage_note),
  };
}

function normalizeValueJudgment(raw: unknown): NormalizedValueJudgment {
  const source = asObject(raw);
  if (!Object.keys(source).length) return EMPTY_VALUE_JUDGMENT;
  const layer = (value: unknown, fallback: NormalizedValueJudgmentLayer): NormalizedValueJudgmentLayer => {
    const row = asObject(value);
    return {
      state: cleanText(row.state).toUpperCase() || fallback.state,
      label: cleanText(row.label) || fallback.label,
      reasons: stringList(row.reasons).slice(0, 5),
    };
  };
  const businessSource = asObject(source.businessEvidence);
  const businessEvidence: NormalizedValueJudgmentBusinessEvidence = {
    ready: normalizeBoolean(businessSource.ready),
    stale: normalizeBoolean(businessSource.stale),
    needsReview: normalizeBoolean(businessSource.needsReview),
    financialIndustry: normalizeBoolean(businessSource.financialIndustry),
    state: cleanText(businessSource.state).toUpperCase() || 'UNKNOWN',
    summary: cleanText(businessSource.summary),
    businessDate: cleanText(businessSource.businessDate),
    reportName: cleanText(businessSource.reportName),
    industry: cleanText(businessSource.industry),
    subIndustry: cleanText(businessSource.subIndustry),
    reportShape: cleanText(businessSource.reportShape),
    semanticStatus: cleanText(businessSource.semanticStatus),
    semanticConfidence: cleanText(businessSource.semanticConfidence),
    profileReadinessScore: nullableNumber(businessSource.profileReadinessScore),
    consistencyIssueCount: Math.max(0, Math.round(nullableNumber(businessSource.consistencyIssueCount) || 0)),
  };
  const verifiedSource = asObject(source.lastVerifiedState ?? source.lastVerifiedValueState ?? source.last_verified_state);
  const verifiedState = cleanText(verifiedSource.state ?? source.lastVerifiedValueStateCode ?? source.last_verified_value_state).toUpperCase();
  const verifiedLabel = cleanText(verifiedSource.label ?? source.lastVerifiedValueStateLabel ?? source.last_verified_value_state_label);
  const lastVerifiedState: NormalizedVerifiedValueState = {
    available: Boolean(verifiedState || verifiedLabel),
    state: verifiedState,
    label: verifiedLabel,
    tone: normalizeTone(verifiedSource.tone ?? source.lastVerifiedValueStateTone, 'neutral'),
    verifiedAt: cleanText(verifiedSource.verifiedAt ?? verifiedSource.basisDate ?? source.lastVerifiedAt ?? source.last_verified_at),
    source: cleanText(verifiedSource.source ?? verifiedSource.reportName ?? source.lastVerifiedSource),
  };
  const newEvidenceSource = asObject(source.newEvidenceStatus ?? source.newEvidence ?? source.new_evidence_status);
  const newEvidenceStatus: NormalizedNewEvidenceStatus = {
    state: cleanText(newEvidenceSource.state ?? source.newEvidenceState).toUpperCase() || 'UNKNOWN',
    label: cleanText(newEvidenceSource.label ?? source.newEvidenceLabel),
    summary: cleanText(newEvidenceSource.summary ?? source.newEvidenceSummary),
    conflict: normalizeBoolean(newEvidenceSource.conflict ?? source.newEvidenceConflict),
  };
  return {
    contractVersion: cleanText(source.contractVersion),
    basisMode: cleanText(source.basisMode).toUpperCase(),
    basisDate: cleanText(source.basisDate),
    valueState: cleanText(source.valueState).toUpperCase() || 'LIMITED',
    valueStateLabel: cleanText(source.valueStateLabel) || '판단 제한',
    valueStateTone: normalizeTone(source.valueStateTone, 'neutral'),
    confidence: cleanText(source.confidence).toUpperCase() || 'LOW',
    confidenceLabel: cleanText(source.confidenceLabel) || '확인 필요',
    summary: cleanText(source.summary) || '핵심 재무·Event 근거를 확인 중입니다.',
    reasons: stringList(source.reasons).slice(0, 5),
    nextChecks: stringList(source.nextChecks).slice(0, 4),
    recentValueChanges: stringList(source.recentValueChanges).slice(0, 4),
    recentMarketChanges: stringList(source.recentMarketChanges).slice(0, 4),
    recentEvidenceChanges: stringList(source.recentEvidenceChanges).slice(0, 6),
    businessEvidence,
    lastVerifiedState,
    newEvidenceStatus,
    fundamental: layer(source.fundamental, EMPTY_VALUE_JUDGMENT.fundamental),
    thesis: layer(source.thesis, EMPTY_VALUE_JUDGMENT.thesis),
    marketDelivery: layer(source.marketDelivery, EMPTY_VALUE_JUDGMENT.marketDelivery),
  };
}

function normalizeStockEntryDecision(raw: unknown): NormalizedStockEntryDecision {
  const source = asObject(raw);
  if (!Object.keys(source).length) return EMPTY_STOCK_ENTRY_DECISION;

  const rawState = cleanText(source.state).toUpperCase();
  const state: NormalizedStockEntryDecision['state'] = rawState === 'FAVORABLE'
    || rawState === 'RISK_DOMINANT'
    || rawState === 'LIMITED'
    ? rawState
    : 'CONDITIONAL';
  const rawEvidence = Array.isArray(source.evidence) ? source.evidence : [];
  const evidence = rawEvidence.map((rawItem): NormalizedStockEntryEvidence => {
    const item = asObject(rawItem);
    return {
      domain: cleanText(item.domain).toUpperCase(),
      state: cleanText(item.state).toUpperCase(),
      label: cleanText(item.label),
      reason: cleanText(item.reason),
      tone: normalizeTone(item.tone, 'neutral'),
    };
  }).filter((item) => item.label || item.reason).slice(0, 3);

  return {
    contractVersion: cleanText(source.contractVersion) || 'STOCK_ENTRY_CONTEXT_V1',
    available: normalizeBoolean(source.available ?? true),
    state,
    label: cleanText(source.label) || (state === 'FAVORABLE'
      ? '진입 환경 우호'
      : state === 'RISK_DOMINANT'
        ? '위험 우세'
        : state === 'LIMITED'
          ? '판단 제한'
          : '조건 확인'),
    tone: normalizeTone(source.tone, state === 'FAVORABLE' ? 'good' : state === 'RISK_DOMINANT' ? 'down' : state === 'LIMITED' ? 'warn' : 'neutral'),
    summary: cleanText(source.summary) || EMPTY_STOCK_ENTRY_DECISION.summary,
    confidence: cleanText(source.confidence).toUpperCase() || 'LOW',
    evidence,
    confirmationConditions: stringList(source.confirmationConditions).slice(0, 4),
    riskConditions: stringList(source.riskConditions).slice(0, 4),
    nextCheck: cleanText(source.nextCheck) || '다음 확인 조건 산출 중',
    recentMaterialChanges: stringList(source.recentMaterialChanges).slice(0, 3),
    asOf: cleanText(source.asOf),
  };
}

function scannerMatchToStockRadarRow(match: ScannerMatch): StockRadarRow | null {
  const ticker = cleanText(match.ticker);
  if (!/^\d{6}$/.test(ticker)) return null;

  const base = normalizeTopStock({
    ticker,
    name: cleanText(match.name) || ticker,
    price: match.price ?? null,
    changeRate: match.changeRate ?? null,
    tradingValue: match.tradingValue ?? null,
    volume: match.volume ?? null,
    foreignNetBuyAmount: match.foreignNetBuyAmount ?? null,
    institutionNetBuyAmount: match.institutionNetBuyAmount ?? null,
    programNetBuyExecAmount: match.programNetBuyExecAmount ?? null,
    dataSource: 'SCANNER_MATCH',
    priceAsOf: match.lastEventAt || match.lastMatchedAt || '',
    isRealtime: true,
    delayMinutes: 0,
  } as LiveSummarySectorTopStock);
  if (!base) return null;

  const sectorChangeRate = nullableNumber(match.sectorChangeRate) ?? 0;
  const limitedEntry: NormalizedStockEntryDecision = {
    contractVersion: 'SCANNER_SNAPSHOT_LIMITED_V1',
    available: true,
    state: 'LIMITED',
    label: '판단 제한',
    tone: 'warn',
    summary: '스캐너 실시간 조건에는 포착됐지만 회사·가치 근거가 아직 종목 상세에 연결되지 않아 진입 환경을 확정하지 않습니다.',
    confidence: 'LOW',
    evidence: [{
      domain: 'SCANNER',
      state: 'OBSERVED',
      label: '스캐너 조건 매칭',
      reason: cleanText(match.ruleName) || '현재 설정한 스캐너 조건을 충족했습니다.',
      tone: (match.changeRate ?? 0) >= 0 ? 'up' : 'warn',
    }],
    confirmationConditions: [
      '회사·가치 근거 연결 상태 확인',
      '현재 스캐너 조건 유지 여부 확인',
    ],
    riskConditions: [],
    nextCheck: '회사·가치 근거 연결 상태 확인',
    recentMaterialChanges: [],
    asOf: cleanText(match.lastEventAt || match.lastMatchedAt),
  };

  return {
    ...base,
    entryDecision: base.entryDecision.available ? base.entryDecision : limitedEntry,
    sectorName: cleanText(match.sectorName) || '업종 확인 중',
    sectorChangeRate,
    sectorBreadthPct: null,
    sectorTone: sectorChangeRate > 0 ? 'up' : sectorChangeRate < 0 ? 'down' : 'neutral',
  };
}

function normalizeTopStock(item: LiveSummarySectorTopStock): NormalizedSectorTopStock | null {
  const source = item as unknown as Record<string, unknown>;
  const ticker = cleanText(item.ticker);
  const name = resolveStockDisplayName(source, ticker);
  if (!ticker && !name) return null;
  const changeRate = nullableNumber(item.changeRate);
  return {
    ticker,
    name,
    changeRate,
    price: nullableAbsolutePrice(item.price),
    tradingValue: nullableNumber(item.tradingValue),
    volume: nullableNumber(item.volume),
    realtimeMatched: normalizeBoolean(item.realtimeMatched),
    dataSource: cleanText(item.dataSource),
    priceAsOf: cleanText(item.priceAsOf),
    isRealtime: normalizeBoolean(item.isRealtime),
    delayMinutes: nullableNumber(item.delayMinutes),
    tone: normalizeTone(item.tone, (changeRate ?? 0) > 0 ? 'up' : (changeRate ?? 0) < 0 ? 'down' : 'neutral'),
    ...normalizeStockRiskFields(source),
    ...normalizeFundamentalFields(source),
    ...normalizeFlowFields(source),
    ...normalizeSessionPriceFields(source),
    actionDecision: normalizeActionDecision(source.actionDecision, 'WATCHLIST'),
    riskAssessment: normalizeRiskAssessment(source.riskAssessment, 'WATCHLIST'),
    decisionEvidence: normalizeDecisionEvidence(source.actionDecision),
    historicalEvidence: normalizeHistoricalEvidence(source.historicalEvidence ?? source.historical_evidence),
    valueJudgment: normalizeValueJudgment(source.valueJudgment ?? source.value_judgment),
    entryDecision: normalizeStockEntryDecision(source.entryDecision ?? source.entry_decision),
  };
}

function normalizeSector(item: LiveSummarySector): NormalizedSector | null {
  const name = cleanText(item.name || item.industryName || item.sectorCode);
  const changeRate = toNumber(item.changeRate);
  const mappedTickerCount = Math.max(0, Math.round(toNumber(item.mappedTickerCount, 0)));
  if (!name || !Number.isFinite(changeRate) || mappedTickerCount <= 0) return null;

  const realtimeTickerCount = Math.max(0, Math.round(toNumber(item.realtimeTickerCount, 0)));
  const upTickerCount = Math.max(0, Math.round(toNumber(item.upTickerCount, 0)));
  const downTickerCount = Math.max(0, Math.round(toNumber(item.downTickerCount, 0)));
  const breadthBase = upTickerCount + downTickerCount;
  const breadthPct = breadthBase > 0 ? (upTickerCount / breadthBase) * 100 : null;
  const realtimeCoveragePct = Math.max(0, Math.min(100, toNumber(item.realtimeCoveragePct, 0)));
  const breadthAdjustment = breadthPct === null ? 0 : (breadthPct - 50) * 0.35;
  const strengthScore = Math.max(0, Math.min(100, 50 + changeRate * 6 + breadthAdjustment + realtimeCoveragePct * 0.12));
  const topStocks = Array.isArray(item.topStocks)
    ? item.topStocks.map(normalizeTopStock).filter((stock): stock is NormalizedSectorTopStock => Boolean(stock)).slice(0, 30)
    : [];

  return {
    name,
    sectorCode: cleanText(item.sectorCode),
    marketType: cleanText(item.marketType),
    marketLabel: cleanText(item.marketLabel),
    industryCode: cleanText(item.industryCode),
    industryName: cleanText(item.industryName || name),
    changeRate,
    tone: normalizeTone(item.tone, changeRate > 0 ? 'up' : changeRate < 0 ? 'down' : 'neutral'),
    mappedTickerCount,
    realtimeTickerCount,
    upTickerCount,
    downTickerCount,
    realtimeCoveragePct,
    realtimeTradingValue: nullableNumber(item.realtimeTradingValue),
    latestRealtimeEventAt: cleanText(item.latestRealtimeEventAt),
    dataSource: cleanText(item.dataSource),
    isRealtime: normalizeBoolean(item.isRealtime),
    delayMinutes: nullableNumber(item.delayMinutes),
    topStocks,
    breadthPct,
    strengthScore,
  };
}

function normalizeThemeFlow(item: LiveSummaryThemeFlow): NormalizedThemeFlow | null {
  const themeCode = cleanText(item.themeCode);
  const themeName = cleanText(item.themeName);
  if (!themeCode || !themeName) return null;

  const smartMoneyNetBuyAmount = nullableNumber(item.smartMoneyNetBuyAmount);
  const dataStatus = cleanText(item.dataStatus).toUpperCase() || 'NO_DATA';
  return {
    themeCode,
    themeName,
    sessionDate: cleanText(item.sessionDate),
    constituentCount: Math.max(0, Math.round(toNumber(item.constituentCount, 0))),
    priceAvailableCount: Math.max(0, Math.round(toNumber(item.priceAvailableCount, 0))),
    flowAvailableCount: Math.max(0, Math.round(toNumber(item.flowAvailableCount, 0))),
    programAvailableCount: Math.max(0, Math.round(toNumber(item.programAvailableCount, 0))),
    avgChangeRate: nullableNumber(item.avgChangeRate),
    foreignNetBuyAmount: nullableNumber(item.foreignNetBuyAmount),
    institutionNetBuyAmount: nullableNumber(item.institutionNetBuyAmount),
    smartMoneyNetBuyAmount,
    programNetBuyAmount: nullableNumber(item.programNetBuyAmount),
    netBuyStockCount: Math.max(0, Math.round(toNumber(item.netBuyStockCount, 0))),
    netSellStockCount: Math.max(0, Math.round(toNumber(item.netSellStockCount, 0))),
    flatStockCount: Math.max(0, Math.round(toNumber(item.flatStockCount, 0))),
    smartMoneyBreadthPct: nullableNumber(item.smartMoneyBreadthPct),
    flowCoveragePct: nullableNumber(item.flowCoveragePct),
    priceCoveragePct: nullableNumber(item.priceCoveragePct),
    topSmartMoneyTicker: cleanText(item.topSmartMoneyTicker),
    topSmartMoneyCorpName: cleanText(item.topSmartMoneyCorpName),
    topSmartMoneyAmount: nullableNumber(item.topSmartMoneyAmount),
    sourceLatestAt: cleanText(item.sourceLatestAt),
    dataStatus,
    receivedAt: cleanText(item.receivedAt),
    tone: smartMoneyNetBuyAmount === null
      ? 'neutral'
      : smartMoneyNetBuyAmount > 0
        ? 'up'
        : smartMoneyNetBuyAmount < 0
          ? 'down'
          : 'neutral',
  };
}

function normalizeThemeStock(item: LiveSummaryThemeStock): NormalizedThemeStock | null {
  const source = item as unknown as Record<string, unknown>;
  const ticker = cleanText(item.ticker);
  const name = resolveStockDisplayName(source, ticker);
  if (!/^\d{6}$/.test(ticker) || !name) return null;

  const changeRate = nullableNumber(item.changeRate);
  const smartMoneyNetBuyAmount = nullableNumber(item.smartMoneyNetBuyAmount);
  return {
    themeCode: cleanText(item.themeCode),
    themeName: cleanText(item.themeName),
    themeChangeRate: nullableNumber(item.themeChangeRate),
    themePriceCoveragePct: nullableNumber(item.themePriceCoveragePct),
    themeDataStatus: cleanText(item.themeDataStatus).toUpperCase() || 'NO_DATA',
    sessionDate: cleanText(item.sessionDate),
    ticker,
    name,
    market: cleanText(item.market),
    price: nullableNumber(item.price),
    changeRate,
    tradingValue: nullableNumber(item.tradingValue),
    foreignNetBuyAmount: nullableNumber(item.foreignNetBuyAmount),
    institutionNetBuyAmount: nullableNumber(item.institutionNetBuyAmount),
    smartMoneyNetBuyAmount,
    programNetBuyAmount: nullableNumber(item.programNetBuyAmount),
    priceAvailable: normalizeBoolean(item.priceAvailable),
    flowAvailable: normalizeBoolean(item.flowAvailable),
    programAvailable: normalizeBoolean(item.programAvailable),
    dataStatus: cleanText(item.dataStatus).toUpperCase() || 'NO_DATA',
    sourceLatestAt: cleanText(item.sourceLatestAt),
    tone: changeRate === null
      ? 'neutral'
      : changeRate > 0
        ? 'up'
        : changeRate < 0
          ? 'down'
          : 'neutral',
  };
}


function normalizeBoxSupportFields(item: Record<string, unknown>): NormalizedBoxSupport {
  return {
    available: normalizeBoolean(item.boxSupportAvailable),
    analysisDate: cleanText(item.boxAnalysisDate),
    referenceClose: nullableNumber(item.boxReferenceClose),
    upperPrice: nullableNumber(item.boxEntrySupportPrice),
    middlePrice: nullableNumber(item.boxCoreSupportPrice),
    lowerPrice: nullableNumber(item.boxFloorSupportPrice),
    upperDistancePct: nullableNumber(item.boxEntryDistancePct),
  };
}

function normalizeHoldingThesis(raw: unknown): NormalizedHoldingThesis {
  const source = asObject(raw);
  if (!Object.keys(source).length) return EMPTY_HOLDING_THESIS;
  return {
    contractVersion: cleanText(source.contractVersion),
    available: normalizeBoolean(source.available),
    coreState: cleanText(source.coreState).toUpperCase() || 'LIMITED',
    coreLabel: cleanText(source.coreLabel) || '판단 제한',
    displayState: cleanText(source.displayState).toUpperCase() || 'LIMITED',
    displayLabel: cleanText(source.displayLabel) || cleanText(source.coreLabel) || '판단 제한',
    tone: normalizeTone(source.tone, 'neutral'),
    marketPressureState: cleanText(source.marketPressureState).toUpperCase() || 'UNKNOWN',
    marketPressureLabel: cleanText(source.marketPressureLabel) || '시장 확인 중',
    marketPressureTone: normalizeTone(source.marketPressureTone, 'neutral'),
    marketPressureScore: nullableNumber(source.marketPressureScore),
    marketOnlyPressure: normalizeBoolean(source.marketOnlyPressure),
    pressureOnly: normalizeBoolean(source.pressureOnly),
    fundamentalState: cleanText(source.fundamentalState).toUpperCase() || 'UNKNOWN',
    fundamentalLabel: cleanText(source.fundamentalLabel) || '기업가치 확인 필요',
    thesisState: cleanText(source.thesisState).toUpperCase() || 'UNKNOWN',
    thesisLabel: cleanText(source.thesisLabel) || '투자 논리 확인 중',
    verifiedBaselineAvailable: normalizeBoolean(source.verifiedBaselineAvailable),
    verifiedBaselineLabel: cleanText(source.verifiedBaselineLabel),
    newEvidenceConflict: normalizeBoolean(source.newEvidenceConflict),
    contextIssuePending: normalizeBoolean(source.contextIssuePending),
    contextIssueHeadline: cleanText(source.contextIssueHeadline),
    calculationMode: cleanText(source.calculationMode),
    marketPressureBasis: cleanText(source.marketPressureBasis),
    marketSession: cleanText(source.marketSession),
    calculatedAt: cleanText(source.calculatedAt),
    referenceDate: cleanText(source.referenceDate),
    eodFinalized: normalizeBoolean(source.eodFinalized),
    weekendDaily: normalizeBoolean(source.weekendDaily),
    reasons: stringList(source.reasons).slice(0, 3),
    todayChanges: stringList(source.todayChanges).slice(0, 3),
    invalidationConditions: stringList(source.invalidationConditions).slice(0, 4),
    recheckConditions: stringList(source.recheckConditions).slice(0, 4),
    monitorConditions: stringList(source.monitorConditions).slice(0, 4),
    monitorConditionCount: Math.max(0, Math.round(nullableNumber(source.monitorConditionCount) || 0)),
    triggerSignals: stringList(source.triggerSignals).slice(0, 4),
    triggeredCount: Math.max(0, Math.round(nullableNumber(source.triggeredCount) || 0)),
    damageConfirmed: normalizeBoolean(source.damageConfirmed),
    weakeningConfirmed: normalizeBoolean(source.weakeningConfirmed),
    flowTone: cleanText(source.flowTone),
    flowLabel: cleanText(source.flowLabel) || '수급 확인 중',
    basisDate: cleanText(source.basisDate),
  };
}


function normalizeHoldingJudgmentContract(raw: unknown): NormalizedHoldingJudgmentContract {
  const source = asObject(raw);
  if (!Object.keys(source).length) return EMPTY_HOLDING_JUDGMENT;

  const thesisSource = asObject(source.thesis);
  const companyEvidenceSource = asObject(source.companyEvidence);
  const projectedImpactSource = asObject(source.projectedImpact);
  const projectedEventSource = asObject(projectedImpactSource.eventImpact);
  const projectedFactors = Array.isArray(projectedImpactSource.factors)
    ? projectedImpactSource.factors.map((rawFactor) => {
      const factor = asObject(rawFactor);
      return {
        code: cleanText(factor.code).toUpperCase(),
        label: cleanText(factor.label),
        score: nullableNumber(factor.score),
        basis: cleanText(factor.basis),
        status: cleanText(factor.status).toUpperCase(),
        tone: normalizeTone(factor.tone, 'neutral'),
      };
    }).filter((factor) => factor.label).slice(0, 6)
    : [];
  const pressureSource = asObject(source.pressure);
  const daySource = asObject(pressureSource.day);
  const liveSource = asObject(pressureSource.live);
  const riskSource = asObject(source.riskManagement);
  const invalidationSource = asObject(source.invalidation);
  const qualitySource = asObject(source.dataQuality);
  const confidenceSource = asObject(source.confidence);
  const evidenceSource = asObject(source.evidence);

  const invalidationItems = Array.isArray(invalidationSource.items)
    ? invalidationSource.items.map((rawItem) => {
      const item = asObject(rawItem);
      return {
        label: cleanText(item.label),
        status: cleanText(item.status).toUpperCase(),
        statusLabel: cleanText(item.statusLabel) || '감시 중',
        tone: normalizeTone(item.tone, 'neutral'),
      };
    }).filter((item) => item.label).slice(0, 4)
    : [];

  const changes = Array.isArray(source.changes)
    ? source.changes.map((rawChange) => {
      const change = asObject(rawChange);
      return {
        type: cleanText(change.type).toUpperCase() || 'PRESSURE_CHANGE',
        label: cleanText(change.label),
        tone: normalizeTone(change.tone, 'neutral'),
      };
    }).filter((change) => change.label).slice(0, 5)
    : [];

  return {
    contractVersion: cleanText(source.contractVersion),
    available: normalizeBoolean(source.available),
    ticker: cleanText(source.ticker),
    name: cleanText(source.name),
    asOf: cleanText(source.asOf),
    marketSession: cleanText(source.marketSession),
    thesis: {
      state: cleanText(thesisSource.state).toUpperCase() || 'LIMITED',
      label: cleanText(thesisSource.label) || '판단 제한',
      tone: normalizeTone(thesisSource.tone, 'neutral'),
      primaryReason: cleanText(thesisSource.primaryReason) || '핵심 사업·재무 근거를 확인 중입니다.',
      reasons: stringList(thesisSource.reasons).slice(0, 3),
      damageConfirmed: normalizeBoolean(thesisSource.damageConfirmed),
      companyEvidenceState: cleanText(thesisSource.companyEvidenceState).toUpperCase()
        || cleanText(companyEvidenceSource.state).toUpperCase()
        || 'INSUFFICIENT',
      companyEvidenceLabel: cleanText(thesisSource.companyEvidenceLabel)
        || cleanText(companyEvidenceSource.label)
        || '회사 판단 근거 부족',
      companyEvidenceTone: normalizeTone(
        thesisSource.companyEvidenceTone ?? companyEvidenceSource.tone,
        'neutral',
      ),
    },
    companyEvidence: {
      state: cleanText(companyEvidenceSource.state).toUpperCase() || 'INSUFFICIENT',
      label: cleanText(companyEvidenceSource.label) || '회사 판단 근거 부족',
      tone: normalizeTone(companyEvidenceSource.tone, 'neutral'),
      basis: cleanText(companyEvidenceSource.basis),
      damageConfirmed: normalizeBoolean(companyEvidenceSource.damageConfirmed),
      weakeningConfirmed: normalizeBoolean(companyEvidenceSource.weakeningConfirmed),
      pendingEvent: normalizeBoolean(companyEvidenceSource.pendingEvent),
    },
    projectedImpact: {
      state: cleanText(projectedImpactSource.state).toUpperCase() || 'NO_DATA',
      label: cleanText(projectedImpactSource.label) || '예상 영향 확인 중',
      tone: normalizeTone(projectedImpactSource.tone, 'neutral'),
      score: nullableNumber(projectedImpactSource.score),
      baseRoicWaccSpreadPctp: nullableNumber(projectedImpactSource.baseRoicWaccSpreadPctp),
      stressSpreadDeltaPctp: nullableNumber(projectedImpactSource.stressSpreadDeltaPctp),
      stressRoicWaccSpreadPctp: nullableNumber(projectedImpactSource.stressRoicWaccSpreadPctp),
      confidence: cleanText(projectedImpactSource.confidence).toUpperCase() || 'UNAVAILABLE',
      confidenceLabel: cleanText(projectedImpactSource.confidenceLabel) || '산출 제한',
      methodology: cleanText(projectedImpactSource.methodology),
      estimatedNotActual: normalizeBoolean(projectedImpactSource.estimatedNotActual),
      stockSession: cleanText(projectedImpactSource.stockSession),
      factors: projectedFactors,
      eventImpact: {
        scope: cleanText(projectedEventSource.scope).toUpperCase(),
        status: cleanText(projectedEventSource.status).toUpperCase() || 'NONE',
        state: cleanText(projectedEventSource.state).toUpperCase() || 'NO_EVENT',
        headline: cleanText(projectedEventSource.headline),
        direction: cleanText(projectedEventSource.direction).toUpperCase(),
        materiality: cleanText(projectedEventSource.materiality).toUpperCase(),
        score: nullableNumber(projectedEventSource.score),
        label: cleanText(projectedEventSource.label) || '직접 영향 Event 없음',
        tone: normalizeTone(projectedEventSource.tone, 'neutral'),
      },
      foreignFlowAmount: nullableNumber(projectedImpactSource.foreignFlowAmount),
      investorTrendDirection: cleanText(projectedImpactSource.investorTrendDirection).toUpperCase(),
      usdkrwChangeRate: nullableNumber(projectedImpactSource.usdkrwChangeRate),
      marketIndexKey: cleanText(projectedImpactSource.marketIndexKey).toUpperCase(),
      marketIndexChangeRate: nullableNumber(projectedImpactSource.marketIndexChangeRate),
      summary: cleanText(projectedImpactSource.summary),
    },
    pressure: {
      day: {
        state: cleanText(daySource.state).toUpperCase() || 'UNKNOWN',
        label: cleanText(daySource.label) || '오늘 압력 확인 중',
        tone: normalizeTone(daySource.tone, 'neutral'),
        basis: cleanText(daySource.basis),
        stockChangeRate: nullableNumber(daySource.stockChangeRate),
        flowLabel: cleanText(daySource.flowLabel) || '수급 확인 중',
        marketLabel: cleanText(daySource.marketLabel) || '시장 확인 중',
      },
      live: {
        state: cleanText(liveSource.state).toUpperCase() || 'NO_DATA',
        label: cleanText(liveSource.label) || '현재 LIVE 확인 중',
        tone: normalizeTone(liveSource.tone, 'neutral'),
        basis: cleanText(liveSource.basis),
        price: nullableNumber(liveSource.price),
        sessionVwap: nullableNumber(liveSource.sessionVwap),
        priceVsVwapPct: nullableNumber(liveSource.priceVsVwapPct),
        sessionLabel: cleanText(liveSource.sessionLabel),
      },
    },
    riskManagement: {
      state: cleanText(riskSource.state).toUpperCase() || 'WATCH',
      label: cleanText(riskSource.label) || '관찰',
      tone: normalizeTone(riskSource.tone, 'neutral'),
      actionCode: cleanText(riskSource.actionCode).toUpperCase(),
      actionLabel: cleanText(riskSource.actionLabel),
      reason: cleanText(riskSource.reason),
    },
    invalidation: {
      triggered: Math.max(0, Math.round(nullableNumber(invalidationSource.triggered) || 0)),
      total: Math.max(0, Math.round(nullableNumber(invalidationSource.total) || 0)),
      label: cleanText(invalidationSource.label),
      items: invalidationItems,
      triggerSignals: stringList(invalidationSource.triggerSignals).slice(0, 4),
    },
    nextChecks: stringList(source.nextChecks).slice(0, 3),
    dataQuality: {
      business: cleanText(qualitySource.business).toUpperCase() || 'MISSING',
      financial: cleanText(qualitySource.financial).toUpperCase() || 'MISSING',
      price: cleanText(qualitySource.price).toUpperCase() || 'MISSING',
      flow: cleanText(qualitySource.flow).toUpperCase() || 'MISSING',
      market: cleanText(qualitySource.market).toUpperCase() || 'MISSING',
      event: cleanText(qualitySource.event).toUpperCase() || 'MISSING',
    },
    confidence: {
      state: cleanText(confidenceSource.state).toUpperCase() || 'LOW',
      label: cleanText(confidenceSource.label) || '낮음',
      tone: normalizeTone(confidenceSource.tone, 'down'),
      readyCount: Math.max(0, Math.round(nullableNumber(confidenceSource.readyCount) || 0)),
      totalCount: Math.max(0, Math.round(nullableNumber(confidenceSource.totalCount) || 6)),
      coveragePct: nullableNumber(confidenceSource.coveragePct),
      missingEvidenceLabels: stringList(confidenceSource.missingEvidenceLabels).slice(0, 6),
    },
    evidence: {
      maintain: stringList(evidenceSource.maintain).slice(0, 3),
      risk: stringList(evidenceSource.risk).slice(0, 3),
      missing: stringList(evidenceSource.missing).slice(0, 6),
    },
    changes,
    summary: cleanText(source.summary),
  };
}

function normalizeHolding(item: LiveSummaryHolding): NormalizedHolding | null {
  const source = item as unknown as Record<string, unknown>;
  const ticker = cleanText(item.ticker);
  const name = resolveStockDisplayName(source, ticker);
  if (!ticker && !name) return null;
  const changeRate = nullableNumber(item.changeRate);
  const statusRaw = cleanText(item.status);
  const status: NormalizedHolding['status'] =
    statusRaw === '준비중' || statusRaw === '위험중' || statusRaw === '관찰' || statusRaw === '양호'
      ? statusRaw
      : changeRate === null
        ? '준비중'
        : changeRate <= -1.5
          ? '위험중'
          : changeRate >= 1
            ? '양호'
            : '관찰';
  return {
    ticker,
    name,
    changeRate,
    industryName: cleanText(source.industryName),
    sectorRelativePct: nullableNumber(source.sectorRelativePct),
    price: nullableAbsolutePrice(item.price),
    tradingValue: nullableNumber(item.tradingValue),
    dataSource: cleanText(item.dataSource),
    priceAsOf: cleanText(item.priceAsOf),
    isRealtime: normalizeBoolean(item.isRealtime),
    delayMinutes: nullableNumber(item.delayMinutes),
    portfolioQuantity: nullableNumber(source.portfolioQuantity),
    portfolioAvgPrice: nullableNumber(source.portfolioAvgPrice),
    portfolioPositionAvailable: normalizeBoolean(source.portfolioPositionAvailable),
    status,
    statusTone: normalizeTone(item.statusTone, status === '위험중' ? 'down' : status === '양호' ? 'good' : 'neutral'),
    ...normalizeStockRiskFields(source),
    ...normalizeFundamentalFields(source),
    ...normalizeFlowFields(source),
    ...normalizeSessionPriceFields(source),
    actionDecision: normalizeActionDecision(source.actionDecision, 'HOLDING'),
    riskAssessment: normalizeRiskAssessment(source.riskAssessment, 'HOLDING'),
    decisionEvidence: normalizeDecisionEvidence(source.actionDecision),
    historicalEvidence: normalizeHistoricalEvidence(source.historicalEvidence ?? source.historical_evidence),
    valueJudgment: normalizeValueJudgment(source.valueJudgment ?? source.value_judgment),
    holdingThesis: normalizeHoldingThesis(source.holdingThesis ?? source.holding_thesis),
    judgmentContract: normalizeHoldingJudgmentContract(source.judgmentContract ?? source.judgment_contract),
    boxSupport: normalizeBoxSupportFields(source),
    valuationAlertEnabled: normalizeBoolean(source.valuationAlertEnabled),
    valuationAlertTargetPrice: nullableNumber(source.valuationAlertTargetPrice),
    valuationAlertMarginPct: nullableNumber(source.valuationAlertMarginPct) ?? 0,
    valuationAlertFairValue: nullableNumber(source.valuationAlertFairValue),
    valuationAlertFormulaVersion: cleanText(source.valuationAlertFormulaVersion),
    valuationAlertCalculatedAt: cleanText(source.valuationAlertCalculatedAt),
    valuationAlertTriggered: normalizeBoolean(source.valuationAlertTriggered),
  };
}


type PortfolioRiskPosition = {
  holding: NormalizedHolding;
  marketValue: number;
  weightPct: number;
  riskScore: number | null;
  rawRiskContribution: number | null;
  riskContributionPct: number | null;
};

type PortfolioRiskIndustry = {
  name: string;
  marketValue: number;
  weightPct: number;
  count: number;
};

type PortfolioRiskModel = {
  available: boolean;
  totalMarketValue: number;
  valuedPositionCount: number;
  riskPositionCount: number;
  riskCoveragePct: number | null;
  attentionWeightPct: number | null;
  attentionCount: number;
  improvingCount: number;
  stableCount: number;
  topPosition: PortfolioRiskPosition | null;
  riskContributors: PortfolioRiskPosition[];
  industries: PortfolioRiskIndustry[];
};

function portfolioPct(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '-';
  return `${value.toFixed(1)}%`;
}

function buildPortfolioRiskModel(holdings: NormalizedHolding[]): PortfolioRiskModel {
  const valued = holdings
    .map((holding) => {
      const quantity = holding.portfolioQuantity;
      const price = holding.price;
      if (quantity === null || price === null || quantity <= 0 || price <= 0) return null;
      const marketValue = quantity * price;
      if (!Number.isFinite(marketValue) || marketValue <= 0) return null;
      return { holding, marketValue };
    })
    .filter((item): item is { holding: NormalizedHolding; marketValue: number } => Boolean(item));

  const totalMarketValue = valued.reduce((sum, item) => sum + item.marketValue, 0);
  if (!(totalMarketValue > 0)) {
    return {
      available: false,
      totalMarketValue: 0,
      valuedPositionCount: 0,
      riskPositionCount: 0,
      riskCoveragePct: null,
      attentionWeightPct: null,
      attentionCount: 0,
      improvingCount: 0,
      stableCount: 0,
      topPosition: null,
      riskContributors: [],
      industries: [],
    };
  }

  let riskCoveredValue = 0;
  let attentionValue = 0;
  let attentionCount = 0;
  let improvingCount = 0;
  let stableCount = 0;

  const positions: PortfolioRiskPosition[] = valued.map(({ holding, marketValue }) => {
    const weightPct = (marketValue / totalMarketValue) * 100;
    const riskScore = holding.riskAvailable && !holding.riskStale && holding.riskScore !== null
      ? Math.max(0, Math.min(100, holding.riskScore))
      : null;

    if (riskScore !== null) riskCoveredValue += marketValue;

    if (isRiskAssessmentAttention(holding.riskAssessment)) {
      attentionValue += marketValue;
      attentionCount += 1;
    } else if (holding.riskAssessment.state === 'RECOVERING') {
      improvingCount += 1;
    } else {
      stableCount += 1;
    }

    return {
      holding,
      marketValue,
      weightPct,
      riskScore,
      rawRiskContribution: riskScore === null ? null : (marketValue / totalMarketValue) * riskScore,
      riskContributionPct: null,
    };
  });

  const totalRawRisk = positions.reduce(
    (sum, position) => sum + (position.rawRiskContribution ?? 0),
    0,
  );

  const resolvedPositions = positions.map((position): PortfolioRiskPosition => ({
    ...position,
    riskContributionPct: position.rawRiskContribution === null || !(totalRawRisk > 0)
      ? null
      : (position.rawRiskContribution / totalRawRisk) * 100,
  }));

  const riskContributors = resolvedPositions
    .filter((position) => position.riskContributionPct !== null)
    .sort((a, b) => (b.riskContributionPct ?? -1) - (a.riskContributionPct ?? -1));

  const industryMap = new Map<string, { marketValue: number; count: number }>();
  valued.forEach(({ holding, marketValue }) => {
    const industry = cleanText(holding.industryName);
    if (!industry) return;
    const current = industryMap.get(industry) ?? { marketValue: 0, count: 0 };
    current.marketValue += marketValue;
    current.count += 1;
    industryMap.set(industry, current);
  });

  const industries = Array.from(industryMap.entries())
    .map(([name, item]): PortfolioRiskIndustry => ({
      name,
      marketValue: item.marketValue,
      weightPct: (item.marketValue / totalMarketValue) * 100,
      count: item.count,
    }))
    .sort((a, b) => b.marketValue - a.marketValue);

  const topPosition = [...resolvedPositions].sort((a, b) => b.marketValue - a.marketValue)[0] ?? null;

  return {
    available: true,
    totalMarketValue,
    valuedPositionCount: valued.length,
    riskPositionCount: resolvedPositions.filter((position) => position.riskScore !== null).length,
    riskCoveragePct: (riskCoveredValue / totalMarketValue) * 100,
    attentionWeightPct: (attentionValue / totalMarketValue) * 100,
    attentionCount,
    improvingCount,
    stableCount,
    topPosition,
    riskContributors,
    industries,
  };
}

function PortfolioRiskOverview({ holdings }: { holdings: NormalizedHolding[] }) {
  const [open, setOpen] = useState(false);
  const model = useMemo(() => buildPortfolioRiskModel(holdings), [holdings]);

  useEffect(() => {
    if (!model.available) setOpen(false);
  }, [model.available]);

  if (!holdings.length) return null;

  if (!model.available) {
    return (
      <section className="live-summary-portfolio-risk is-unavailable">
        <div className="live-summary-portfolio-risk__empty">
          <span>
            <small>계좌 위험 · {holdings.length}개 보유</small>
            <strong>평가 가능한 포지션을 확인 중입니다.</strong>
          </span>
          <em>수량과 현재가가 모두 확인된 보유 종목부터 계산합니다.</em>
        </div>
      </section>
    );
  }

  const topPosition = model.topPosition;
  const contributionRows = model.riskContributors.slice(0, 4);
  const industryRows = model.industries.slice(0, 4);

  return (
    <section className={`live-summary-portfolio-risk ${open ? 'is-open' : 'is-collapsed'}`}>
      <button
        type="button"
        className="live-summary-portfolio-risk__toggle"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span className="live-summary-portfolio-risk__title">
          <small>계좌 위험 · {holdings.length}개 보유</small>
          <strong>{formatTradingValue(model.totalMarketValue)}</strong>
        </span>
        <span className="live-summary-portfolio-risk__metric">
          <small>최대 비중</small>
          <b>{topPosition ? `${topPosition.holding.name} ${portfolioPct(topPosition.weightPct)}` : '-'}</b>
        </span>
        <span className="live-summary-portfolio-risk__metric">
          <small>주의 상태 비중</small>
          <b className={model.attentionCount > 0 ? 'is-down' : ''}>{portfolioPct(model.attentionWeightPct)}</b>
        </span>
        <span className="live-summary-portfolio-risk__metric">
          <small>리스크 커버리지</small>
          <b>{portfolioPct(model.riskCoveragePct)}</b>
        </span>
        <em>{open ? '접기' : '자세히'}</em>
      </button>

      {open && (
        <div className="live-summary-portfolio-risk__body">
          <div className="live-summary-portfolio-risk__status-rail">
            <span>평가 가능 <b>{model.valuedPositionCount}</b></span>
            <span className={model.attentionCount > 0 ? 'is-down' : ''}>주의 <b>{model.attentionCount}</b></span>
            <span className="is-up">개선 <b>{model.improvingCount}</b></span>
            <span>기타 <b>{model.stableCount}</b></span>
            <span>리스크 산정 <b>{model.riskPositionCount}/{model.valuedPositionCount}</b></span>
          </div>

          <div className="live-summary-portfolio-risk__grids">
            <section className="live-summary-portfolio-risk__group">
              <div className="live-summary-portfolio-risk__group-title">
                <strong>리스크 기여</strong>
                <small>평가비중 × 기존 종목 Risk Score</small>
              </div>
              {contributionRows.length ? (
                <div className="live-summary-portfolio-risk__rows">
                  {contributionRows.map((position) => (
                    <div className="live-summary-portfolio-risk__row" key={position.holding.ticker}>
                      <span>
                        <b>{position.holding.name}</b>
                        <small>평가비중 {portfolioPct(position.weightPct)} · Risk {position.riskScore === null ? '-' : Math.round(position.riskScore)}</small>
                      </span>
                      <strong className={toneClass(position.holding.riskTone)}>{portfolioPct(position.riskContributionPct)}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="live-summary-portfolio-risk__note">현재 Risk Score가 유효한 보유 종목이 없습니다.</p>
              )}
            </section>

            <section className="live-summary-portfolio-risk__group">
              <div className="live-summary-portfolio-risk__group-title">
                <strong>업종 집중</strong>
                <small>현재 평가액 기준</small>
              </div>
              {industryRows.length ? (
                <div className="live-summary-portfolio-risk__rows">
                  {industryRows.map((industry) => (
                    <div className="live-summary-portfolio-risk__row" key={industry.name}>
                      <span>
                        <b>{industry.name}</b>
                        <small>{industry.count}종목 · {formatTradingValue(industry.marketValue)}</small>
                      </span>
                      <strong>{portfolioPct(industry.weightPct)}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="live-summary-portfolio-risk__note">업종 매핑을 확인 중입니다.</p>
              )}
            </section>
          </div>

          <p className="live-summary-portfolio-risk__disclaimer">
            리스크 기여도는 새로운 예측 모델이 아니라 현재 평가비중과 서버가 이미 계산한 종목 Risk Score를 포트폴리오 단위로 분해한 값입니다. Risk Score가 없거나 stale인 종목은 기여도 계산에서 제외합니다.
          </p>
        </div>
      )}
    </section>
  );
}

function normalizeAlert(item: LiveSummaryAlert): NormalizedAlert | null {
  const message = cleanText(item.message);
  if (!message) return null;
  const levelRaw = cleanText(item.level).toLowerCase();
  return {
    level: levelRaw === 'danger' || levelRaw === 'warning' ? levelRaw : 'info',
    message,
  };
}

function normalizeMetricGuide(item?: LiveSummaryMetricGuide): NormalizedMetricGuide | null {
  if (!item) return null;
  const summary = cleanText(item.summary);
  const action = cleanText(item.action);
  if (!summary && !action) return null;
  return {
    title: cleanText(item.title),
    summary,
    action,
    tone: normalizeTone(item.tone, 'neutral'),
  };
}

function normalizeOpportunity(item: LiveSummaryOpportunity): NormalizedOpportunity | null {
  const name = cleanText(item.name);
  if (!name) return null;
  const reasons = Array.isArray(item.reasons)
    ? item.reasons.map(cleanText).filter(Boolean).slice(0, 4)
    : [];
  return {
    ticker: cleanText(item.ticker),
    name,
    marketLabel: cleanText(item.marketLabel),
    sectorName: cleanText(item.sectorName),
    price: nullableAbsolutePrice(item.price),
    changeRate: nullableNumber(item.changeRate),
    tradingValue: nullableNumber(item.tradingValue),
    opportunityScore: nullableNumber(item.opportunityScore),
    riskPenalty: nullableNumber(item.riskPenalty),
    category: cleanText(item.category) || '관찰',
    tone: normalizeTone(item.tone, 'neutral'),
    marketReady: normalizeBoolean(item.marketReady),
    marketFitScore: nullableNumber(item.marketFitScore),
    marketFitLabel: cleanText(item.marketFitLabel) || '시장 확인 제한',
    marketFitTone: normalizeTone(item.marketFitTone, 'neutral'),
    sectorChangeRate: nullableNumber(item.sectorChangeRate),
    sectorBreadthPct: nullableNumber(item.sectorBreadthPct),
    vwapGapPct: nullableNumber(item.vwapGapPct),
    smartMoneyNetBuyAmount: nullableNumber(item.smartMoneyNetBuyAmount),
    programNetBuyToTradingValuePct: nullableNumber(item.programNetBuyToTradingValuePct),
    investorTrendDirection: cleanText(item.investorTrendDirection).toUpperCase(),
    reasons,
    primaryReason: cleanText(item.primaryReason) || reasons[0] || '실시간 수급·가격 조합 확인',
    warning: cleanText(item.warning),
    detailLocked: normalizeBoolean(item.detailLocked),
    accessLevel: cleanText(item.accessLevel),
  };
}

function normalizeAttentionBanner(item?: LiveSummaryAttentionBanner): NormalizedAttentionBanner {
  const severityRaw = cleanText(item?.severity).toUpperCase();
  const severity: NormalizedAttentionBanner['severity'] =
    severityRaw === 'CRITICAL' ? 'CRITICAL'
      : severityRaw === 'HIGH' ? 'HIGH'
        : severityRaw === 'WATCH' ? 'WATCH' : 'INFO';
  return {
    visible: normalizeBoolean(item?.visible),
    severity,
    title: cleanText(item?.title),
    message: cleanText(item?.message),
    ticker: cleanText(item?.ticker),
    name: cleanText(item?.name),
    actionTab: cleanText(item?.actionTab) || 'ALERTS',
    tone: normalizeTone(item?.tone, severity === 'CRITICAL' || severity === 'HIGH' ? 'down' : severity === 'WATCH' ? 'warn' : 'neutral'),
  };
}

function normalizeChangeEvent(item: LiveSummaryChangeEvent): NormalizedChangeEvent | null {
  const source = item as unknown as Record<string, unknown>;
  const entityKey = cleanText(source.entityKey);
  const title = cleanText(source.title);
  const message = cleanText(source.message);
  if (!entityKey && !title && !message) return null;

  const severityRaw = cleanText(source.severity).toUpperCase();
  const severity: NormalizedChangeEvent['severity'] = severityRaw === 'DANGER'
    ? 'DANGER'
    : severityRaw === 'WARNING'
      ? 'WARNING'
      : 'INFO';

  let payload: Record<string, unknown> = {};
  if (source.payload && typeof source.payload === 'object' && !Array.isArray(source.payload)) {
    payload = source.payload as Record<string, unknown>;
  } else if (typeof source.payload === 'string') {
    try {
      const parsed = JSON.parse(source.payload);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) payload = parsed as Record<string, unknown>;
    } catch {
      payload = {};
    }
  }

  const firstText = (...values: unknown[]) => {
    for (const value of values) {
      const normalized = cleanText(value);
      if (normalized) return normalized;
    }
    return '';
  };
  const causeLabels = [
    payload.primaryReason,
    payload.reason,
    payload.cause,
    ...(Array.isArray(payload.reasons) ? payload.reasons : []),
    ...(Array.isArray(payload.causes) ? payload.causes : []),
    ...(Array.isArray(payload.reasonLabels) ? payload.reasonLabels : []),
  ].map(cleanText).filter(Boolean);

  return {
    id: cleanText(source.id),
    eventScope: cleanText(source.eventScope).toUpperCase(),
    entityType: cleanText(source.entityType).toUpperCase(),
    entityKey,
    eventType: cleanText(source.eventType).toUpperCase(),
    severity,
    title,
    message,
    previousValue: firstText(source.previousValue, payload.previousValue, payload.previousState, payload.before),
    currentValue: firstText(source.currentValue, payload.currentValue, payload.currentState, payload.after),
    causeLabels: Array.from(new Set(causeLabels)).slice(0, 2),
    changeValue: nullableNumber(source.changeValue),
    sourceEventAt: cleanText(source.sourceEventAt),
    detectedAt: cleanText(source.detectedAt),
  };
}

function normalizeSourceStatus(value: unknown): SourceStatus {
  const raw = cleanText(value).toLowerCase();
  return raw === 'ok' || raw === 'partial' || raw === 'error' ? raw : 'idle';
}

function normalizeIndexPoint(value: unknown, fallbackLabel: string): NormalizedMarketIndexPoint {
  const source = (value ?? {}) as Record<string, unknown>;
  return {
    label: cleanText(source.label) || fallbackLabel,
    value: nullableNumber(source.value),
    changeValue: nullableNumber(source.changeValue),
    changeRate: nullableNumber(source.changeRate),
    updatedAt: cleanText(source.updatedAt),
    delayMinutes: nullableNumber(source.delayMinutes),
    stale: normalizeBoolean(source.stale),
    provider: cleanText(source.provider) || 'KIS',
  };
}

function normalizeDerivativePoint(
  value: unknown,
  fallbackLabel: string,
  fallbackProvider: string,
): NormalizedDerivativePoint {
  const source = (value ?? {}) as Record<string, unknown>;
  return {
    label: cleanText(source.label) || fallbackLabel,
    futuresPrice: nullableNumber(source.futuresPrice),
    futuresChangeRate: nullableNumber(source.futuresChangeRate),
    marketBasis: nullableNumber(source.marketBasis),
    basisChange5m: nullableNumber(source.basisChange5m),
    basisChange15m: nullableNumber(source.basisChange15m),
    oiChangeCumulative: nullableNumber(source.oiChangeCumulative),
    oiChange5m: nullableNumber(source.oiChange5m),
    updatedAt: cleanText(source.updatedAt),
    delayMinutes: nullableNumber(source.delayMinutes),
    stale: normalizeBoolean(source.stale),
    structure: cleanText(source.structure).toUpperCase() || 'MIXED',
    structureLabel: cleanText(source.structureLabel) || '판단 대기',
    directionState: cleanText(source.directionState).toUpperCase() || 'NO_DATA',
    directionLabel: cleanText(source.directionLabel) || '계산 대기',
    directionDataStatus: cleanText(source.directionDataStatus).toUpperCase() || 'NO_DATA',
    futuresReturn5m: nullableNumber(source.futuresReturn5m),
    futuresReturn15m: nullableNumber(source.futuresReturn15m),
    spotReturn5m: nullableNumber(source.spotReturn5m),
    spotReturn15m: nullableNumber(source.spotReturn15m),
    futuresLead5m: nullableNumber(source.futuresLead5m),
    futuresLead15m: nullableNumber(source.futuresLead15m),
    oiState: cleanText(source.oiState).toUpperCase() || 'NO_DATA',
    oi5mState: cleanText(source.oi5mState).toUpperCase() || 'NO_DATA',
    foreignNetContracts: nullableNumber(source.foreignNetContracts),
    institutionNetContracts: nullableNumber(source.institutionNetContracts),
    foreignAlignment: cleanText(source.foreignAlignment).toUpperCase() || 'NO_DATA',
    participantFlowObservedAt: cleanText(source.participantFlowObservedAt),
    provider: cleanText(source.provider) || fallbackProvider,
  };
}

function normalizeGlobalPoint(value: unknown, fallbackLabel: string): NormalizedGlobalPoint {
  const source = (value ?? {}) as Record<string, unknown>;
  return {
    label: cleanText(source.label) || fallbackLabel,
    last: nullableNumber(source.last),
    changeRate: nullableNumber(source.changeRate),
    provider: cleanText(source.provider) || 'YAHOO_CHART_5M',
  };
}

function normalizeMarketContext(item?: unknown): NormalizedMarketContext {
  const source = (item ?? {}) as Record<string, any>;
  const available = normalizeBoolean(source.available);
  const stale = normalizeBoolean(source.stale) || cleanText(source.dataStatus).toUpperCase() === 'STALE';
  const boundedScore = (value: unknown): number | null => {
    const parsed = nullableNumber(value);
    if (parsed === null) return null;
    return Math.max(0, Math.min(100, Math.round(parsed)));
  };
  const checkpoints = Array.isArray(source.checkpoints)
    ? source.checkpoints.map(cleanText).filter(Boolean).slice(0, 3)
    : [];
  const indices = (source.domesticIndices ?? {}) as Record<string, unknown>;
  const derivatives = (source.derivatives ?? {}) as Record<string, unknown>;
  const globals = (source.globalMarkets ?? {}) as Record<string, unknown>;
  const sourcePolicyRaw = (source.sourcePolicy ?? {}) as Record<string, unknown>;
  const sourcePolicy = Object.fromEntries(
    Object.entries(sourcePolicyRaw).map(([key, value]) => [key, cleanText(value)]),
  );
  const metricGuideRaw = (source.metricGuides ?? {}) as Record<string, LiveSummaryMetricGuide>;
  const metricGuides = Object.fromEntries(
    Object.entries(metricGuideRaw)
      .map(([key, value]) => [key, normalizeMetricGuide(value)] as const)
      .filter((entry): entry is readonly [string, NormalizedMetricGuide] => Boolean(entry[1])),
  );

  return {
    available,
    dataStatus: cleanText(source.dataStatus) || (available ? 'OK' : 'NO_DATA'),
    updatedAt: cleanText(source.updatedAt),
    stale,
    headline: cleanText(source.headline) || (available ? '최근 시장 방향 확인 중' : '시장 방향 데이터 확인 중'),
    description: cleanText(source.description) || '국내외 시장 흐름을 정리하고 있습니다.',
    action: cleanText(source.action) || '종목 수급과 가격 흐름을 함께 확인하세요.',
    tone: normalizeTone(source.tone, 'neutral'),
    alertBadge: cleanText(source.alertBadge) || (available ? '흐름 확인' : '계산 중'),
    transitionActive: normalizeBoolean(source.transitionActive),
    transitionHeadline: cleanText(source.transitionHeadline),
    transitionDescription: cleanText(source.transitionDescription),
    transitionTone: normalizeTone(source.transitionTone, 'neutral'),
    marketPressureScore: boundedScore(source.marketPressureScore),
    flowQualityScore: boundedScore(source.flowQualityScore),
    priceStretchScore: boundedScore(source.entryRiskScore),
    reversalWatchScore: boundedScore(source.reversalWatchScore),
    positionFlowScore: boundedScore(source.positionFlowScore),
    basisFlowScore: boundedScore(source.basisFlowScore),
    confidenceScore: boundedScore(source.confidenceScore),
    confidenceLabel: cleanText(source.confidenceLabel) || '확인 중',
    pressureLabel: cleanText(source.pressureLabel) || '계산 중',
    positionFlowLabel: cleanText(source.positionFlowLabel) || '계산 중',
    basisFlowLabel: cleanText(source.basisFlowLabel) || '계산 중',
    qualityLabel: cleanText(source.qualityLabel) || '계산 중',
    riskLabel: cleanText(source.riskLabel) || '계산 중',
    watchLabel: cleanText(source.watchLabel) || '계산 중',
    checkpoints: checkpoints.length ? checkpoints : ['국내 지수·파생 데이터 갱신 확인'],
    intradayScore: nullableNumber(source.intradayScore),
    intradayDirection: cleanText(source.intradayDirection) || '계산 중',
    intradayConfidence: cleanText(source.intradayConfidence).toUpperCase() || 'LOW',
    intradayCoveragePct: nullableNumber(source.intradayCoveragePct),
    riskRealtimeScore: nullableNumber(source.riskRealtimeScore),
    domesticIndices: {
      KOSPI: normalizeIndexPoint(indices.KOSPI, 'KOSPI'),
      KOSDAQ: normalizeIndexPoint(indices.KOSDAQ, 'KOSDAQ'),
      KOSPI200: normalizeIndexPoint(indices.KOSPI200, 'KOSPI200'),
      KOSDAQ150: normalizeIndexPoint(indices.KOSDAQ150, 'KOSDAQ150'),
    },
    derivatives: {
      KOSPI200: normalizeDerivativePoint(derivatives.KOSPI200, 'KOSPI200 선물', 'KIS_H0IFCNT0'),
      KOSDAQ150: normalizeDerivativePoint(derivatives.KOSDAQ150, 'KOSDAQ150 선물', 'KIS_FUTURES_REST'),
    },
    globalMarkets: {
      USDKRW: normalizeGlobalPoint(globals.USDKRW, 'USD/KRW'),
      DXY: normalizeGlobalPoint(globals.DXY, 'DXY'),
      NQ: normalizeGlobalPoint(globals.NQ, 'Nasdaq100 선물'),
      ES: normalizeGlobalPoint(globals.ES, 'S&P500 선물'),
      YM: normalizeGlobalPoint(globals.YM, 'Dow 선물'),
      RTY: normalizeGlobalPoint(globals.RTY, 'Russell2000 선물'),
      US10Y_FUT: normalizeGlobalPoint(globals.US10Y_FUT, '미국채 10Y 선물'),
      VIX: normalizeGlobalPoint(globals.VIX, 'VIX'),
    },
    sourcePolicy,
    metricGuides,
  };
}

function normalizeResponse(response: LiveSummaryApiResponse): LiveSummaryState {
  const market = normalizeMarketState(response.marketState);
  const opportunities = Array.isArray(response.opportunityRadar)
    ? response.opportunityRadar.map(normalizeOpportunity).filter((item): item is NormalizedOpportunity => Boolean(item)).slice(0, 20)
    : [];
  const sectors = Array.isArray(response.strongSectors)
    ? response.strongSectors.map(normalizeSector).filter((item): item is NormalizedSector => Boolean(item)).slice(0, 14)
    : [];
  const themes = Array.isArray(response.themeFlows)
    ? response.themeFlows.map(normalizeThemeFlow).filter((item): item is NormalizedThemeFlow => Boolean(item)).slice(0, 40)
    : [];
  const holdings = Array.isArray(response.holdings)
    ? response.holdings.map(normalizeHolding).filter((item): item is NormalizedHolding => Boolean(item)).slice(0, 100)
    : [];
  const alerts = Array.isArray(response.alerts)
    ? response.alerts.map(normalizeAlert).filter((item): item is NormalizedAlert => Boolean(item)).slice(0, 20)
    : [];
  const issues = normalizeLiveIssues((response as any).issues ?? (response as any).realtimeIssues ?? (response as any).realtime_issues).slice(0, 30);
  const changeEvents = Array.isArray(response.changeEvents)
    ? response.changeEvents
        .map(normalizeChangeEvent)
        .filter((item): item is NormalizedChangeEvent => Boolean(item))
        .slice(0, 50)
    : [];
  return {
    market,
    marketContext: normalizeMarketContext((response as any).marketContext),
    liveIssueSignal: normalizeLiveIssueSignal(response.liveIssueSignal),
    intelligenceBridge: asObject((response as any).intelligenceBridge) as IntelBridge,
    decisionBrief: asObject((response as any).decisionBrief) as DecisionBriefV1,
    optionResearch: ((response as any).optionResearch?.version === "PI_CFI_OPTION_PAIR_RESEARCH_V1" ? (response as any).optionResearch : null) as OptionResearch | null,
    opportunities,
    attentionBanner: normalizeAttentionBanner(response.attentionBanner),
    sectors,
    themes,
    holdings,
    // Market commentary is not a notification and must not inflate alert counts.
    alerts,
    issues,
    changeEvents,
    sourceStatus: normalizeSourceStatus(response.sourceStatus),
    marketSession: cleanText(response.marketSession) || market.phase,
    marketOpen: normalizeBoolean(response.marketOpen),
    // An unknown server timestamp must not be replaced with the browser clock.
    refreshedAt: cleanText(response.generatedAt),
    cacheTtlSeconds: Math.max(0, Math.round(toNumber(response.cacheTtlSeconds, 5))),
  };
}

function mergeFastHoldingSnapshots(
  current: NormalizedHolding[],
  fast: NormalizedHolding[],
): NormalizedHolding[] {
  if (!current.length || !fast.length) return current.length ? current : fast;

  const fastByTicker = new Map(fast.map((holding) => [holding.ticker, holding]));
  return current.map((holding) => {
    const snapshot = fastByTicker.get(holding.ticker);
    if (!snapshot) return holding;
    return {
      ...holding,
      // 5초 lightweight snapshot이 소유하는 장중 필드.
      changeRate: snapshot.changeRate,
      price: snapshot.price,
      tradingValue: snapshot.tradingValue,
      // KRX/NXT session structure now also comes from the fast holding snapshot.
      // Keep the last good value only when the current lightweight row has no data.
      openPrice: snapshot.openPrice ?? holding.openPrice,
      highPrice: snapshot.highPrice ?? holding.highPrice,
      lowPrice: snapshot.lowPrice ?? holding.lowPrice,
      sessionVwap: snapshot.sessionVwap ?? holding.sessionVwap,
      sessionDate: snapshot.sessionDate || holding.sessionDate,
      lastEventAt: snapshot.lastEventAt || holding.lastEventAt,
      dataSource: snapshot.dataSource || holding.dataSource,
      priceAsOf: snapshot.priceAsOf || holding.priceAsOf,
      isRealtime: snapshot.isRealtime,
      delayMinutes: snapshot.delayMinutes,
      status: snapshot.status,
      statusTone: snapshot.statusTone,
      portfolioQuantity: snapshot.portfolioQuantity ?? holding.portfolioQuantity,
      portfolioAvgPrice: snapshot.portfolioAvgPrice ?? holding.portfolioAvgPrice,
      portfolioPositionAvailable: snapshot.portfolioPositionAvailable || holding.portfolioPositionAvailable,

      // 프로그램/외인/기관 최신 집계도 5초 경로가 소유한다.
      // 30분 trend·risk·box 구조는 full HOLDINGS 결과를 보존하고,
      // 당일 OHLC/VWAP 세션 값은 fast snapshot으로 계속 갱신한다.
      programMatched: snapshot.programMatched,
      programNetBuyExecAmount: snapshot.programNetBuyExecAmount,
      programNetBuyToTradingValuePct: snapshot.programNetBuyToTradingValuePct,
      programReceivedAt: snapshot.programReceivedAt || holding.programReceivedAt,
      investorAggregateMatched: snapshot.investorAggregateMatched,
      investorEstimateMatched: snapshot.investorEstimateMatched,
      investorFlowStatus: snapshot.investorFlowStatus,
      foreignNetBuyAmount: snapshot.foreignNetBuyAmount,
      institutionNetBuyAmount: snapshot.institutionNetBuyAmount,
      smartMoneyNetBuyAmount: snapshot.smartMoneyNetBuyAmount,
      estimateForeignNetBuyQty: snapshot.estimateForeignNetBuyQty,
      estimateInstitutionNetBuyQty: snapshot.estimateInstitutionNetBuyQty,
      estimateSmartMoneyNetBuyQty: snapshot.estimateSmartMoneyNetBuyQty,
      investorEstimateBucket: snapshot.investorEstimateBucket || holding.investorEstimateBucket,
      investorAggregateEventAt: snapshot.investorAggregateEventAt || holding.investorAggregateEventAt,
      investorEstimateEventAt: snapshot.investorEstimateEventAt || holding.investorEstimateEventAt,
      investorReceivedAt: snapshot.investorReceivedAt || holding.investorReceivedAt,
      flowDecision: snapshot.flowDecision,
      flowDecisionTone: snapshot.flowDecisionTone,
      flowReason: snapshot.flowReason,
      actionDecision: snapshot.actionDecision.available
        ? snapshot.actionDecision
        : holding.actionDecision,
      riskAssessment: snapshot.riskAssessment.available
        ? snapshot.riskAssessment
        : holding.riskAssessment,
    };
  });
}

function flowSign(value: number | null): -1 | 0 | 1 {
  if (value === null || !Number.isFinite(value)) return 0;
  if (value > 0) return 1;
  if (value < 0) return -1;
  return 0;
}

function mergeChangeEvents(
  ...groups: NormalizedChangeEvent[][]
): NormalizedChangeEvent[] {
  const seen = new Set<string>();
  const out: NormalizedChangeEvent[] = [];

  groups.flat().forEach((event) => {
    const key = [
      event.entityType,
      event.entityKey,
      event.eventType,
      event.title,
      event.message,
      eventTimeLabel(event),
    ].join('|');
    if (seen.has(key)) return;
    seen.add(key);
    out.push(event);
  });

  return out.slice(0, 50);
}

function buildFastHoldingChangeEvents(
  previous: NormalizedHolding[],
  next: NormalizedHolding[],
): NormalizedChangeEvent[] {
  if (!previous.length || !next.length) return [];

  const previousByTicker = new Map(previous.map((holding) => [holding.ticker, holding]));
  const detectedAt = formatNow();
  const events: NormalizedChangeEvent[] = [];

  next.forEach((holding) => {
    const before = previousByTicker.get(holding.ticker);
    if (!before) return;

    const oldSmart = before.smartMoneyNetBuyAmount;
    const newSmart = holding.smartMoneyNetBuyAmount;
    const oldSmartSign = flowSign(oldSmart);
    const newSmartSign = flowSign(newSmart);

    if (oldSmartSign !== 0 && newSmartSign !== 0 && oldSmartSign !== newSmartSign) {
      const positive = newSmartSign > 0;
      events.push({
        id: `fast-holding-smart-${holding.ticker}-${detectedAt}`,
        eventScope: 'USER',
        entityType: 'HOLDING',
        entityKey: holding.ticker,
        eventType: positive ? 'INVESTOR_FLOW_TURN_POSITIVE' : 'INVESTOR_FLOW_TURN_NEGATIVE',
        severity: positive ? 'INFO' : 'WARNING',
        title: `${holding.name} 외인·기관 수급 ${positive ? '회복' : '약화'}`,
        message: `외국인·기관 합산 순수급이 ${positive ? '매수' : '매도'} 방향으로 전환했습니다.`,
        previousValue: oldSmartSign > 0 ? '매수 우위' : '매도 우위',
        currentValue: newSmartSign > 0 ? '매수 우위' : '매도 우위',
        causeLabels: ['외국인·기관 합산 순수급 방향 전환'],
        changeValue: newSmart,
        sourceEventAt: holding.investorReceivedAt || holding.investorAggregateEventAt,
        detectedAt,
      });
    }

    const oldProgramSign = flowSign(
      before.programMatched ? before.programNetBuyExecAmount : null,
    );
    const newProgramSign = flowSign(
      holding.programMatched ? holding.programNetBuyExecAmount : null,
    );

    if (oldProgramSign !== 0 && newProgramSign !== 0 && oldProgramSign !== newProgramSign) {
      const positive = newProgramSign > 0;
      events.push({
        id: `fast-holding-program-${holding.ticker}-${detectedAt}`,
        eventScope: 'USER',
        entityType: 'HOLDING',
        entityKey: holding.ticker,
        eventType: positive ? 'PROGRAM_FLOW_TURN_POSITIVE' : 'PROGRAM_FLOW_TURN_NEGATIVE',
        severity: positive ? 'INFO' : 'WARNING',
        title: `${holding.name} 프로그램 수급 ${positive ? '매수 전환' : '매도 전환'}`,
        message: `프로그램 순수급이 ${positive ? '매수' : '매도'} 방향으로 바뀌었습니다.`,
        previousValue: oldProgramSign > 0 ? '매수 우위' : '매도 우위',
        currentValue: newProgramSign > 0 ? '매수 우위' : '매도 우위',
        causeLabels: ['프로그램 순수급 방향 전환'],
        changeValue: holding.programNetBuyExecAmount,
        sourceEventAt: holding.programReceivedAt,
        detectedAt,
      });
    }

    if (
      before.flowDecisionTone !== holding.flowDecisionTone
      && ['up', 'good', 'warn', 'down'].includes(holding.flowDecisionTone)
    ) {
      events.push({
        id: `fast-holding-flow-${holding.ticker}-${detectedAt}`,
        eventScope: 'USER',
        entityType: 'HOLDING',
        entityKey: holding.ticker,
        eventType: 'FLOW_DECISION_CHANGED',
        severity: holding.flowDecisionTone === 'down'
          ? 'DANGER'
          : holding.flowDecisionTone === 'warn'
            ? 'WARNING'
            : 'INFO',
        title: `${holding.name} 수급 상태 변경`,
        message: holding.flowReason || `${before.flowDecision} → ${holding.flowDecision}`,
        previousValue: cleanText(before.flowDecision) || cleanText(before.flowDecisionTone),
        currentValue: cleanText(holding.flowDecision) || cleanText(holding.flowDecisionTone),
        causeLabels: holding.flowReason ? [cleanText(holding.flowReason)] : [],
        changeValue: null,
        sourceEventAt: holding.investorReceivedAt || holding.programReceivedAt,
        detectedAt,
      });
    }
  });

  return events.slice(0, 20);
}

function sectorKey(sector: NormalizedSector): string {
  return `${sector.marketType || sector.marketLabel || 'M'}:${sector.industryCode || sector.sectorCode || sector.name}`;
}

function sectorMarketLabel(sector: Pick<NormalizedSector, 'marketType' | 'marketLabel'>): string {
  const marketLabel = cleanText(sector.marketLabel).toUpperCase();
  const marketType = cleanText(sector.marketType).toUpperCase();
  const source = marketLabel || marketType;

  if (['0', 'KOSPI', 'KS'].includes(source)) return 'KOSPI';
  if (['1', 'KOSDAQ', 'KQ'].includes(source)) return 'KOSDAQ';
  if (['2', 'KONEX', 'KN'].includes(source)) return 'KONEX';
  return source || '기타';
}

function toneClass(tone: Tone): string {
  if (tone === 'up' || tone === 'good') return 'is-up';
  if (tone === 'down') return 'is-down';
  if (tone === 'warn') return 'is-warn';
  return 'is-neutral';
}

function sourceLabel(sourceValue: string, delayMinutes: number | null): string {
  const source = sourceValue.toUpperCase();
  if (!source || source === 'NO_DATA' || source === 'PENDING') return '준비 중';
  if (source === 'REALTIME' || source === 'KIWOOM_REALTIME_INDUSTRY') return 'LIVE';
  if (source === 'DELAYED_REALTIME') return delayMinutes !== null
    ? `지연 · ${Math.max(1, Math.round(delayMinutes))}분`
    : '지연';
  if (source === 'FINAL_REALTIME') return '장마감';
  if (source === 'NXT_FINAL' || source.includes('NXT_FINAL')) return 'NXT 마감';
  if (source === 'KRX_FINAL' || source.includes('KRX_FINAL')) return '정규장 마감';
  if (source.endsWith('_FINAL')) return '장마감';
  if (source === 'PREVIOUS_CLOSE') return '전일';
  if (source === 'TODAY_EOD' || source.includes('EOD')) return '종가';
  return source.replaceAll('_', ' ');
}

function freshnessMeta(item: {
  isRealtime: boolean;
  delayMinutes: number | null;
  dataSource: string;
}): { label: string; tone: Tone; stale: boolean } {
  const source = cleanText(item.dataSource).toUpperCase();
  const delay = item.delayMinutes;
  if (!source || source === 'NO_DATA' || source === 'PENDING') {
    return { label: '준비 중', tone: 'neutral', stale: true };
  }
  if (source === 'FINAL_REALTIME') return { label: '장 마감', tone: 'neutral', stale: false };
  if (source === 'PREVIOUS_CLOSE') return { label: '전일 데이터', tone: 'warn', stale: true };
  if (source === 'TODAY_EOD' || source.includes('EOD')) return { label: '장 마감', tone: 'neutral', stale: false };
  if (!item.isRealtime) return { label: sourceLabel(item.dataSource, delay), tone: 'warn', stale: true };
  if (delay === null || delay < 1) return { label: 'LIVE', tone: 'good', stale: false };
  if (delay <= 3) return { label: `지연 · ${Math.max(1, Math.round(delay))}분`, tone: 'warn', stale: false };
  return { label: `지연 · ${Math.max(1, Math.round(delay))}분`, tone: 'down', stale: true };
}

function resolvedSmartMoney(item: NormalizedSectorTopStock | NormalizedHolding): number | null {
  if (item.smartMoneyNetBuyAmount !== null) return item.smartMoneyNetBuyAmount;
  // Smart Money는 외국인 + 기관 두 축이 모두 있을 때만 유효하다.
  // 한쪽 누락을 0으로 보정하지 않는다.
  if (item.foreignNetBuyAmount === null || item.institutionNetBuyAmount === null) return null;
  return item.foreignNetBuyAmount + item.institutionNetBuyAmount;
}

function flowInterpretation(item: NormalizedSectorTopStock | NormalizedHolding): {
  headline: string;
  description: string;
  tone: Tone;
} {
  const trend = cleanText(item.investorTrendDirection).toUpperCase();
  const smartMoney = resolvedSmartMoney(item);

  if (trend === 'UP_TO_DOWN') {
    if (smartMoney !== null && smartMoney > 0) {
      return {
        headline: '순매수 유지 · 유입 강도 둔화',
        description: '외국인·기관 합산 수급은 아직 순매수지만 최근 구간의 유입 강도가 약해졌습니다.',
        tone: 'warn',
      };
    }
    return {
      headline: '수급 약화 전환',
      description: '최근 외국인·기관 수급 방향이 상승 흐름에서 약화 쪽으로 전환했습니다.',
      tone: 'down',
    };
  }

  if (trend === 'DOWN_STREAK') {
    return {
      headline: smartMoney !== null && smartMoney > 0 ? '순매수 잔존 · 최근 흐름 약화' : '수급 약화 지속',
      description: smartMoney !== null && smartMoney > 0
        ? '누적 순매수는 남아 있지만 최근 구간에서는 유입 강도가 계속 약해지고 있습니다.'
        : '최근 구간의 외국인·기관 수급 약화가 이어지고 있습니다.',
      tone: 'down',
    };
  }

  if (trend === 'DOWN_TO_UP') {
    return {
      headline: smartMoney !== null && smartMoney < 0 ? '순유출 잔존 · 회복 전환' : '수급 회복 전환',
      description: smartMoney !== null && smartMoney < 0
        ? '합산 수급은 아직 순유출이지만 최근 방향은 회복 쪽으로 바뀌었습니다.'
        : '외국인·기관 수급의 최근 방향이 회복 쪽으로 전환했습니다.',
      tone: 'up',
    };
  }

  if (trend === 'UP_STREAK') {
    return {
      headline: smartMoney !== null && smartMoney > 0 ? '순매수 · 유입 강화' : '최근 수급 개선',
      description: smartMoney !== null && smartMoney > 0
        ? '외국인·기관 합산 순매수와 최근 유입 강화가 함께 나타나고 있습니다.'
        : '최근 구간의 외국인·기관 수급 방향이 개선되고 있습니다.',
      tone: 'up',
    };
  }

  if (smartMoney !== null) {
    if (smartMoney > 0) return { headline: '외국인·기관 합산 순매수', description: '절대 순수급은 유입 우위입니다. 최근 방향이 유지되는지 확인하세요.', tone: 'good' };
    if (smartMoney < 0) return { headline: '외국인·기관 합산 순유출', description: '절대 순수급은 유출 우위입니다. 추가 악화 여부를 먼저 확인하세요.', tone: 'warn' };
  }

  return { headline: '수급 방향 확인 중', description: '절대 순수급과 최근 방향이 충분히 정렬되지 않았습니다.', tone: 'neutral' };
}



function opportunityDisplayReason(item: NormalizedOpportunity): string {
  const flowReason = item.reasons.find((reason) => /외국인|기관|수급|프로그램/.test(reason));
  const contextReason = item.reasons.find((reason) => /업종|시장/.test(reason));
  const raw = flowReason || contextReason || item.warning || item.primaryReason || '가격·수급 흐름 확인';
  return raw.replace(/VWAP/gi, '장중 평균가격');
}

function sectorBreadthText(value: number | null): string {
  return value === null || !Number.isFinite(value) ? '—' : `${value.toFixed(0)}%`;
}

type MonitorCategory = 'ATTENTION' | 'IMPROVING' | 'STABLE';

type ItemMonitorMeta = {
  label: string;
  description: string;
  tone: Tone;
  category: MonitorCategory;
  priority: number;
  evidence: string[];
  changes: Array<{ text: string; detail: string; time: string; tone: Tone }>;
  suppressedChangeCount: number;
  checkpoints: string[];
};

function eventTone(event: NormalizedChangeEvent): Tone {
  const type = cleanText(event.eventType).toUpperCase();
  if (['INVESTOR_FLOW_TURN_POSITIVE', 'FLOW_VELOCITY_SURGE_POSITIVE'].includes(type)) return 'up';
  if (['INVESTOR_FLOW_TURN_NEGATIVE', 'FLOW_VELOCITY_SURGE_NEGATIVE'].includes(type)) return 'down';
  if (event.severity === 'DANGER') return 'down';
  if (event.severity === 'WARNING') return 'warn';
  return 'neutral';
}

function eventTimeLabel(event: NormalizedChangeEvent): string {
  const raw = eventTimestampRaw(event);
  if (!raw) return '';
  const hhmm = raw.match(/(?:T|\s)(\d{2}:\d{2})(?::\d{2})?/);
  if (hhmm) return hhmm[1];
  const compact = raw.match(/(\d{2}:\d{2})/);
  return compact ? compact[1] : raw;
}

function recentEventsForItem(
  events: NormalizedChangeEvent[],
  ticker: string,
  mode: 'stock' | 'holding',
): NormalizedChangeEvent[] {
  if (!ticker) return [];
  return events
    .filter((event) => {
      if (event.entityKey !== ticker) return false;
      if (mode === 'holding') return event.entityType === 'HOLDING' || event.entityType === 'STOCK';
      return event.entityType === 'STOCK';
    })
    .sort((a, b) => (
      (normalizedEventTimestamp(eventTimestampRaw(b)) ?? 0)
      - (normalizedEventTimestamp(eventTimestampRaw(a)) ?? 0)
    ))
    .slice(0, 30);
}

function currentFlowState(
  item: NormalizedSectorTopStock | NormalizedHolding,
): 'RECOVERING' | 'DETERIORATING' | 'NEUTRAL' | 'UNKNOWN' {
  const trend = cleanText(item.investorTrendDirection).toUpperCase();
  if (trend === 'DOWN_TO_UP' || trend === 'UP_STREAK') return 'RECOVERING';
  if (trend === 'UP_TO_DOWN' || trend === 'DOWN_STREAK') return 'DETERIORATING';
  if (trend === 'FLAT') return 'NEUTRAL';

  const delta1m = item.investorTrendDelta1m;
  const velocityRatio = item.investorTrendVelocityRatio;
  if (
    delta1m !== null && Number.isFinite(delta1m) &&
    velocityRatio !== null && Number.isFinite(velocityRatio) &&
    velocityRatio >= 2
  ) {
    if (delta1m > 0) return 'RECOVERING';
    if (delta1m < 0) return 'DETERIORATING';
  }
  return 'UNKNOWN';
}

function itemMonitorMeta(
  item: NormalizedSectorTopStock | NormalizedHolding,
  marketContext: NormalizedMarketContext,
  events: NormalizedChangeEvent[],
  stale = false,
  mode: 'stock' | 'holding' = 'stock',
): ItemMonitorMeta {
  const rawItemEvents = recentEventsForItem(events, item.ticker, mode);
  const itemEvents = dedupeMaterialChanges(rawItemEvents, mode === 'holding' ? 12 : 8);
  const suppressedChangeCount = Math.max(0, rawItemEvents.length - itemEvents.length);
  const latestEvent = itemEvents[0];
  const trend = cleanText(item.investorTrendDirection).toUpperCase();
  const risk = item.riskScore ?? 0;
  const flowState = currentFlowState(item);
  const smartMoney = resolvedSmartMoney(item);
  const cumulativeSellPressure = smartMoney !== null && smartMoney < 0;
  const programSellPressure = item.programMatched && (
    (item.programNetBuyExecAmount !== null && item.programNetBuyExecAmount < 0) ||
    (item.programNetBuyToTradingValuePct !== null && item.programNetBuyToTradingValuePct < 0)
  );
  const currentFlowRecovering = flowState === 'RECOVERING';
  const currentFlowDeteriorating = flowState === 'DETERIORATING';
  const snapshotFlowResolved = flowState !== 'UNKNOWN';
  const negativeVelocitySurge =
    item.investorTrendVelocityRatio !== null &&
    item.investorTrendVelocityRatio >= 2 &&
    item.investorTrendDelta1m !== null &&
    item.investorTrendDelta1m < 0;

  let category: MonitorCategory = 'STABLE';
  let tone: Tone = 'good';
  let label = '상태 안정';
  let priority = 30;

  /*
   * 현재 snapshot이 과거 change-event보다 우선한다.
   * 누적 순매도는 상태이고, DOWN_TO_UP / UP_STREAK / 음의 velocity surge 등은 최근 변화다.
   */
  if (stale) {
    category = 'ATTENTION';
    tone = 'warn';
    label = '데이터 갱신 확인';
    priority = 95;
  } else if (currentFlowDeteriorating) {
    category = 'ATTENTION';
    tone = 'down';
    label = negativeVelocitySurge ? '수급 이탈 가속' : trend === 'UP_TO_DOWN' ? '수급 약화 전환' : '수급 약화 지속';
    priority = negativeVelocitySurge ? 100 : 90;
  } else if (currentFlowRecovering) {
    category = 'IMPROVING';
    if (cumulativeSellPressure || programSellPressure || risk >= 55) {
      tone = 'warn';
      label = cumulativeSellPressure || programSellPressure ? '회복 관찰' : '가격 위험 · 수급 회복';
      priority = 58;
    } else {
      tone = 'up';
      label = trend === 'DOWN_TO_UP' ? '수급 회복 전환' : '수급 개선';
      priority = 52;
    }
  } else if (item.flowDecisionTone === 'down') {
    category = 'ATTENTION';
    tone = 'warn';
    label = '순매도 압력 지속';
    priority = 70;
  } else if (item.flowDecisionTone === 'warn' || risk >= 55) {
    category = 'ATTENTION';
    tone = 'warn';
    label = item.flowDecisionTone === 'warn' ? '수급 혼조 · 변화 감시' : '변동성 주의';
    priority = 65;
  } else if (!snapshotFlowResolved && latestEvent?.severity === 'DANGER') {
    category = 'ATTENTION';
    tone = 'down';
    label = '최근 위험 변화';
    priority = 85;
  } else if (!snapshotFlowResolved && latestEvent?.severity === 'WARNING') {
    category = 'ATTENTION';
    tone = 'warn';
    label = '최근 주의 변화';
    priority = 75;
  } else if (item.flowDecisionTone === 'up' || item.flowDecisionTone === 'good') {
    category = 'IMPROVING';
    tone = 'good';
    label = '수급 개선';
    priority = 45;
  } else if (risk >= 40) {
    tone = 'neutral';
    label = '혼조 · 추가 확인';
    priority = 40;
  }

  const evidence: string[] = [];
  if (item.foreignNetBuyAmount !== null) evidence.push(`외국인 ${formatSignedMoney(item.foreignNetBuyAmount)}`);
  if (item.institutionNetBuyAmount !== null) evidence.push(`기관 ${formatSignedMoney(item.institutionNetBuyAmount)}`);
  if (item.programMatched && item.programNetBuyExecAmount !== null) evidence.push(`프로그램 ${formatSignedMoney(item.programNetBuyExecAmount)}`);
  if ('sectorRelativePct' in item && item.sectorRelativePct !== null) {
    evidence.push(`업종 대비 ${item.sectorRelativePct >= 0 ? '+' : ''}${item.sectorRelativePct.toFixed(2)}%`);
  }

  const changes = itemEvents.map((event) => ({
    text: changeTransitionLabel(event),
    detail: changeReasonLabel(event, item.name),
    time: eventTimeLabel(event),
    tone: eventTone(event),
  }));
  if (!changes.length && !stale && !['INSUFFICIENT_DATA', 'DATA_STALE', 'FLAT', ''].includes(trend)) {
    changes.push({
      text: investorTrendLabel(item),
      detail: item.flowReason || '최근 수급 방향 변화를 확인합니다.',
      time: cleanText(item.investorTrendUpdatedAt || item.investorTrendTurnedAt),
      tone: investorTrendTone(item.investorTrendDirection),
    });
  }

  const checkpoints: string[] = [];
  if (resolvedSmartMoney(item) !== null) {
    checkpoints.push('외인·기관 순수급의 최근 방향이 유지되는지');
  }
  if (item.programMatched && item.programNetBuyExecAmount !== null) {
    checkpoints.push('프로그램 순수급 방향이 바뀌는지');
  }
  if (marketContext.available) {
    checkpoints.push('시장·업종 흐름과 종목 수급이 같은 방향인지');
  }
  if (!checkpoints.length) checkpoints.push('실시간 가격과 수급 데이터가 정상 갱신되는지');

  const flowView = flowInterpretation(item);
  const trendExplainsCurrentFlow = ['UP_TO_DOWN', 'DOWN_STREAK', 'DOWN_TO_UP', 'UP_STREAK'].includes(trend);
  const description = trendExplainsCurrentFlow
    ? flowView.description
    : !snapshotFlowResolved && latestEvent?.message
      ? latestEvent.message
      : item.flowReason || flowView.description || stockStateSummary(item).description;

  return {
    label,
    description,
    tone,
    category,
    priority: priority + Math.min(20, Math.max(0, risk - 40)),
    evidence: Array.from(new Set(evidence)).slice(0, 4),
    changes: changes.slice(0, 20),
    suppressedChangeCount,
    checkpoints: Array.from(new Set(checkpoints)).slice(0, 3),
  };
}


function authErrorMessage(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 401) return '로그인 세션이 만료되었습니다.';
  if (status === 403) return '실시간 요약 조회 권한이 없습니다.';
  return '실시간 데이터를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.';
}


function valuationFamilyLabel(code: string): string {
  const value = cleanText(code).toUpperCase();
  if (value === 'BOOK_RETURN') return '자본수익성';
  if (value === 'RELATIVE_EARNINGS') return '이익가치';
  if (value === 'ENTERPRISE_VALUE') return '영업가치';
  if (value === 'CASH_FLOW') return '현금흐름';
  return value || '가치평가';
}

function valuationFamilyStatusLabel(family: HoldingValuationFamily): string {
  const status = cleanText(family.status).toUpperCase();
  const reason = cleanText(family.reasonCode).toUpperCase();
  if (status === 'ELIGIBLE') return '대표값 적격';
  if (reason === 'BOOK_RETURN_INTERNAL_DISPERSION_HIGH') return '내부 결과 불일치 · 대표값 제외';
  if (reason === 'MARKET_FALLBACK_REFERENCE_ONLY') return '전체 시장 비교 · 참고 전용';
  if (reason === 'HIGH_PEER_DISPERSION') return '비교군 분산 큼 · 참고 전용';
  if (reason === 'INSUFFICIENT_MULTIYEAR_FCF') return '현금흐름 이력 부족 · 참고 전용';
  if (status === 'REFERENCE_ONLY') return '참고 전용';
  if (status === 'INAPPLICABLE') return '적용 불가';
  if (status === 'INVALID_INPUT') return '입력 부족';
  if (status === 'LOW_QUALITY') return '품질 낮음';
  return status || '확인 필요';
}


function valuationChangeNarrative(change: HoldingValuationChange): string {
  const driver = cleanText(change.primaryDriverLabel || change.primaryDriverCode);
  const reason = cleanText(change.reasonSummary);
  if (!reason) return driver || '가치평가 입력 변화';

  const normalizedDriver = driver.replace(/\s+/g, '').toUpperCase();
  const normalizedReasonHead = reason.replace(/\s+/g, '').toUpperCase();
  if (normalizedDriver && normalizedReasonHead.startsWith(normalizedDriver)) return reason;

  const driverPhrase = driver ? `${driver} 변화` : '가치평가 입력 변화';
  if (normalizedReasonHead.startsWith(driverPhrase.replace(/\s+/g, '').toUpperCase())) return reason;
  return `${driverPhrase} · ${reason}`;
}

function valuationReasonCodeLabel(code: string): string {
  switch (cleanText(code).toUpperCase()) {
    case 'NO_ELIGIBLE_FAMILIES': return '현재 대표값에 사용할 수 있는 평가방식이 없습니다.';
    case 'INSUFFICIENT_ELIGIBLE_FAMILIES':
    case 'INSUFFICIENT_FAMILIES': return '대표 적정가를 만들기에는 평가 근거가 아직 부족합니다.';
    case 'INSUFFICIENT_INDEPENDENT_EVIDENCE': return '비슷한 비교대상에 대한 의존이 커 서로 다른 평가 근거가 충분하지 않습니다.';
    case 'BOOK_RETURN_INTERNAL_DISPERSION_HIGH': return '자본수익성 기반 평가 결과의 차이가 커 대표값에서 제외했습니다.';
    case 'MARKET_FALLBACK_REFERENCE_ONLY': return '충분히 유사한 비교 기업을 확보하지 못해 시장 전체 비교값은 참고용으로만 표시합니다.';
    case 'HIGH_PEER_DISPERSION': return '비교 기업들의 평가 수준 차이가 커 참고값으로만 표시합니다.';
    case 'LOW_FINANCIAL_QUALITY': return '원천 재무 데이터의 품질이 충분하지 않습니다.';
    case 'HIGH_MODEL_DISPERSION': return '평가 결과 간 차이가 커 대표 적정가 산출을 보류합니다.';
    case 'INSUFFICIENT_MULTIYEAR_FCF': return '최근 현금흐름 이력이 충분하지 않습니다.';
    case 'NON_POSITIVE_NORMALIZED_FCF': return '정상화한 잉여현금흐름이 양수가 아니어서 현금흐름 평가를 제한합니다.';
    case 'RETENTION_PROXY_UNADJUSTED': return '재투자율 근거가 충분하지 않아 해당 평가값은 참고용으로 제한합니다.';
    case 'COST_OF_EQUITY_UPSTREAM_NOT_VERIFIED': return '요구수익률의 근거가 충분하지 않아 해당 평가값은 참고용으로 제한합니다.';
    default: return '';
  }
}

function centralConfidenceLabel(value: string): string {
  const code = cleanText(value).toUpperCase();
  if (code === 'HIGH') return '높음';
  if (code === 'MEDIUM') return '보통';
  if (code === 'LOW') return '낮음';
  return '산출 제한';
}

function HoldingValuationPanel({
  holding,
  detail,
  loading,
  loadError,
  onLoad,
  onDetailChange,
}: {
  holding: NormalizedHolding;
  detail: HoldingValuationDetail | null;
  loading: boolean;
  loadError: string;
  onLoad: (margin: number, signal?: AbortSignal) => Promise<void>;
  onDetailChange: (detail: HoldingValuationDetail) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [message, setMessage] = useState('');
  const [marginPct, setMarginPct] = useState<number>(holding.valuationAlertMarginPct ?? 0);

  useEffect(() => {
    const persistedMargin = nullableNumber(detail?.alert?.marginPct);
    if (detail?.alert?.enabled && persistedMargin !== null) {
      setMarginPct(persistedMargin);
      return;
    }
    setMarginPct(holding.valuationAlertMarginPct ?? 0);
  }, [detail?.alert?.enabled, detail?.alert?.marginPct, holding.ticker, holding.valuationAlertMarginPct]);

  useEffect(() => {
    setMessage('');
    setSaveError('');
  }, [holding.ticker]);

  const previewMargin = useCallback((next: number) => {
    setMarginPct(next);
    setMessage('');
    setSaveError('');
    void onLoad(next);
  }, [onLoad]);

  const saveAlert = useCallback(async (enabled: boolean) => {
    const ticker = cleanText(holding.ticker);
    if (!/^\d{6}$/.test(ticker)) return;
    setSaving(true);
    setSaveError('');
    setMessage('');
    try {
      const response = await api.post<HoldingValuationDetail>(
        `/live-summary/holdings/${encodeURIComponent(ticker)}/valuation-alert`,
        { enabled, marginPct },
        {
          withCredentials: true,
          headers: { Accept: 'application/json' },
        },
      );
      onDetailChange(response.data);
      if (!enabled) {
        setMessage('가격 알림을 해제했습니다.');
      } else if (Boolean(response.data?.alert?.active)) {
        setMessage('가격 알림 기준을 저장했습니다.');
      } else {
        setMessage('알림 설정은 저장했지만 현재는 기준이 충분하지 않아 일시 중지됩니다.');
      }
    } catch (requestError) {
      const responseMessage = cleanText(
        (requestError as { response?: { data?: { message?: unknown } } })?.response?.data?.message,
      );
      setSaveError(responseMessage || authErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  }, [holding.ticker, marginPct, onDetailChange]);

  // Strict V6 contract: legacy operational/consensus aliases must not promote a reference value.
  const centralEstimate = nullableNumber(detail?.centralEstimate);
  const referenceMedian = nullableNumber(detail?.referenceMedian);
  const representativeFairValue = nullableNumber(detail?.representativeFairValue);
  const targetPrice = nullableNumber(detail?.alertTargetPrice);
  const centralGapPct = nullableNumber(detail?.centralGapPct);
  const modelDispersionPct = nullableNumber(detail?.modelDispersionPct);
  const displayCurrentPrice = holding.price ?? nullableNumber(detail?.currentPrice);
  const alertConfigured = Boolean(detail?.alert?.configured ?? detail?.alert?.enabled);
  const availableFamilyCount = Number(detail?.availableFamilyCount ?? 0);
  const eligibleFamilyCount = Number(detail?.eligibleFamilyCount ?? 0);
  const centralAvailable = centralEstimate !== null && cleanText(detail?.centralEstimateStatus).toUpperCase() === 'AVAILABLE';
  const representativeAvailable = representativeFairValue !== null && cleanText(detail?.consensusStatus).toUpperCase() === 'AVAILABLE';
  const referenceAvailable = !centralAvailable && referenceMedian !== null;
  const alertActive = Boolean(detail?.alert?.active) && centralAvailable;
  const alertPaused = alertConfigured && !alertActive;
  const centralConfidence = centralConfidenceLabel(cleanText(detail?.centralEstimateConfidence || detail?.confidence || 'LOW'));
  const decisionGapPct = representativeAvailable
    ? nullableNumber(detail?.discountPct)
    : centralAvailable ? centralGapPct : null;
  const tone: Tone = decisionGapPct !== null ? (decisionGapPct >= 0 ? 'good' : 'down') : 'neutral';
  const displayValuationBadge = representativeAvailable ? '대표 적정가' : centralAvailable ? '중앙 추정가' : referenceAvailable ? '참고값' : (loading ? '계산 중' : '산출 제한');
  const primaryValue = representativeAvailable ? representativeFairValue : centralAvailable ? centralEstimate : referenceMedian;
  const primaryLabel = representativeAvailable ? 'PolicyIntel 대표 적정가' : centralAvailable ? 'PolicyIntel 중앙 추정가' : '참고 모델 중앙값';
  const comparisonLabel = representativeAvailable ? '현재가 대비 대표 적정가' : centralAvailable ? '현재가 대비 중앙 추정가' : '가격 판단';
  const families: HoldingValuationFamily[] = detail && Array.isArray(detail.families) ? detail.families : [];
  const reasonCodes = Array.from(new Set([
    ...(detail?.consensusReasonCodes ?? []),
    ...(detail?.valuationReasonCodes ?? []),
  ].map((value) => cleanText(value)).filter(Boolean)));
  const gateReasonLabels = Array.from(new Set([
    ...reasonCodes.map((code) => valuationReasonCodeLabel(code) || cleanText(code)),
    ...families
      .filter((family) => cleanText(family.status).toUpperCase() !== 'ELIGIBLE')
      .map((family) => `${valuationFamilyLabel(family.familyCode)} · ${valuationFamilyStatusLabel(family)}`),
  ].map(cleanText).filter(Boolean)));
  const gateHeadline = representativeAvailable
    ? '대표 적정가 Gate 통과'
    : eligibleFamilyCount <= 0
      ? `대표값 적격 0/${Math.max(availableFamilyCount, families.length)} · 투표 가능한 평가방식 없음`
      : `대표값 적격 ${eligibleFamilyCount}/${Math.max(availableFamilyCount, families.length)} · Gate 미충족`;
  const error = saveError || loadError;

  return (
    <section className="live-summary-valuation-card">
      <header>
        <div>
          <small>보유종목 가치평가</small>
          <b>적정가 · 가격 알림</b>
        </div>
        <span className={toneClass(tone)}>{displayValuationBadge}</span>
      </header>

      {loading && !detail ? <p className="live-summary-valuation-state">가치평가 결과를 계산하고 있습니다.</p> : null}
      {error ? <p className="live-summary-valuation-state is-error">{error}</p> : null}

      {detail ? (
        <>
          <div className="live-summary-valuation-hero">
            <div>
              <small>{primaryLabel}</small>
              <strong>{primaryValue !== null ? `${formatPrice(primaryValue)}원` : '-'}</strong>
              <em>{referenceAvailable
                ? `${availableFamilyCount}개 평가방식 산출 · 투자 판단 기준 아님`
                : `${eligibleFamilyCount}개 평가방식 반영 · 신뢰 ${centralConfidence}`}</em>
            </div>
            <div>
              <small>{comparisonLabel}</small>
              <strong className={toneClass(tone)}>
                {decisionGapPct === null ? '판단 보류' : `${decisionGapPct >= 0 ? '+' : ''}${decisionGapPct.toFixed(1)}%`}
              </strong>
              <em>현재가 {displayCurrentPrice !== null ? `${formatPrice(displayCurrentPrice)}원` : '-'}</em>
            </div>
          </div>

          <p className="live-summary-valuation-state">
            대표 적정가 {representativeAvailable ? `${formatPrice(representativeFairValue)}원` : '산출 보류'}
            {' · '}평가방식 {availableFamilyCount}개 산출 · 대표값 적격 {eligibleFamilyCount}개
          </p>

          <div className={`live-summary-valuation-gate ${representativeAvailable ? 'is-pass' : 'is-withheld'}`}>
            <header>
              <small>대표 적정가 Gate</small>
              <b>{gateHeadline}</b>
            </header>
            {!representativeAvailable && gateReasonLabels.length > 0 ? (
              <div>
                {gateReasonLabels.slice(0, 4).map((reason) => <span key={reason}>{reason}</span>)}
              </div>
            ) : (
              <p>독립 평가방식·재무 품질·분산 조건을 통과한 대표값입니다.</p>
            )}
          </div>

          <div className="live-summary-valuation-models">
            {(families.length ? families : []).map((family) => {
              const modelText = (family.models ?? [])
                .filter((model) => nullableNumber(model?.fairPrice) !== null)
                .map((model) => `${model.label} ${formatPrice(nullableNumber(model.fairPrice) as number)}원`)
                .join(' · ');
              return (
                <div className={cleanText(family.status).toUpperCase() === 'ELIGIBLE' ? '' : 'is-muted'} key={family.familyCode}>
                  <span>
                    <b>{valuationFamilyLabel(family.familyCode)}</b>
                    <small>{modelText ? `${modelText} · ${valuationFamilyStatusLabel(family)}` : valuationFamilyStatusLabel(family)}</small>
                  </span>
                  <strong>{family.representativeValue !== null ? `${formatPrice(family.representativeValue)}원` : '-'}</strong>
                </div>
              );
            })}
          </div>

          {!families.length ? (
            <div className="live-summary-valuation-models">
              {(detail.models ?? []).map((model) => (
                <div className={nullableNumber(model.fairPrice) !== null ? '' : 'is-muted'} key={model.code}>
                  <span><b>{model.label}</b><small>{model.basis || model.status}</small></span>
                  <strong>{model.fairPrice !== null ? `${formatPrice(model.fairPrice)}원` : '-'}</strong>
                </div>
              ))}
            </div>
          ) : null}

          <div className="live-summary-valuation-metrics">
            <span><small>ROIC-WACC</small><b>{formatPct(detail.metrics?.roicWaccSpreadPctp)}</b></span>
            <span><small>FCF Yield</small><b>{formatPct(detail.metrics?.fcfYieldPct)}</b></span>
            <span><small>PER</small><b>{detail.metrics?.per ?? '-'}</b></span>
            <span><small>PBR</small><b>{detail.metrics?.pbr ?? '-'}</b></span>
            <span><small>PEG</small><b>{detail.metrics?.peg ?? '-'}</b></span>
          </div>

          <div className="live-summary-valuation-alert-box">
            <div className="live-summary-valuation-alert-head">
              <span>
                <small>알림 기준</small>
                <b>{alertActive && targetPrice !== null ? `${formatPrice(targetPrice)}원 이하` : alertPaused ? '현재 기준 없음' : '설정 안 함'}</b>
              </span>
              <em className={alertActive ? 'is-on' : ''}>{alertActive ? '알림 사용 중' : alertPaused ? '알림 일시 중지' : '알림 꺼짐'}</em>
            </div>
            <p>{centralAvailable
              ? '중앙 추정가에서 선택한 안전마진만큼 낮춘 가격을 기준으로 알림합니다.'
              : alertPaused
                ? '현재는 신뢰할 수 있는 알림 기준을 만들 수 없어 기존 설정을 일시 중지했습니다.'
                : '중앙 추정가가 산출되면 가격 알림을 설정할 수 있습니다.'}</p>
            <div className="live-summary-valuation-margin">
              {[0, 5, 10, 15, 20].map((value) => (
                <button
                  type="button"
                  className={marginPct === value ? 'is-active' : ''}
                  key={value}
                  disabled={loading || saving || !centralAvailable}
                  onClick={() => previewMargin(value)}
                >
                  {value === 0 ? '중앙값' : `-${value}%`}
                </button>
              ))}
            </div>
            <div className="live-summary-valuation-actions">
              <button
                type="button"
                disabled={saving || loading || !centralAvailable}
                onClick={() => void saveAlert(true)}
              >
                {saving ? '저장 중' : alertConfigured ? '기준 다시 저장' : '가격 알림 켜기'}
              </button>
              {alertConfigured ? (
                <button type="button" className="is-secondary" disabled={saving} onClick={() => void saveAlert(false)}>
                  알림 끄기
                </button>
              ) : null}
            </div>
            {detail.belowAlertTarget ? <strong className="live-summary-valuation-triggered">현재 가격이 이미 알림 기준 이하입니다.</strong> : null}
            {message ? <small className="live-summary-valuation-message">{message}</small> : null}
          </div>

          {(reasonCodes.length || (detail.warnings ?? []).length) ? (
            <div className="live-summary-valuation-warnings">
              {reasonCodes.slice(0, 4).map((code) => {
                const label = valuationReasonCodeLabel(code);
                return label ? <p key={code}>{label}</p> : null;
              })}
              {!reasonCodes.length ? (detail.warnings ?? []).slice(0, 4).map((warning) => { const text = cleanText(warning); return <p key={text}>{text}</p>; }) : null}
            </div>
          ) : null}

          <footer>
            <span>재무 기준일 {detail.sourceTradeDate || detail.snapshotDate || '-'}</span>
            {detail.valuationAsOf ? <span>평가 시각 {formatIssuePublishedAt(detail.valuationAsOf)}</span> : null}
            {modelDispersionPct !== null ? <span>평가 결과 차이 {modelDispersionPct.toFixed(1)}%</span> : null}
          </footer>
        </>
      ) : null}
    </section>
  );
}


function validTrackingPct(value: number): boolean {
  return Number.isFinite(value) && value >= 0.1 && value <= 30;
}

function PortfolioTrackingQuickSheet({
  target,
  onClose,
  onOpenChannelSettings,
  onOpenAdvanced,
}: {
  target: TrackingTarget | null;
  onClose: () => void;
  onOpenChannelSettings: () => void;
  onOpenAdvanced: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [upPct, setUpPct] = useState(5);
  const [downPctAbs, setDownPctAbs] = useState(4);
  const [message, setMessage] = useState('');

  const refresh = useCallback(async () => {
    if (!target?.ticker) return;
    setLoading(true);
    setMessage('');
    try {
      const settings = await getPortfolioThresholdSettings();
      const item = settings.items.find((row) => row.ticker === target.ticker);
      setEnabled(Boolean(item?.enabled));
      setUpPct(item?.upPct ?? settings.defaultUpPct);
      setDownPctAbs(Math.abs(item?.downPct ?? settings.defaultDownPct));
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || '종목 알림 설정을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [target?.ticker]);

  useEffect(() => {
    if (!target) return;
    void refresh();
  }, [refresh, target]);

  const save = useCallback(async (nextEnabled: boolean) => {
    if (!target?.ticker || saving) return;

    const nextUp = Math.abs(Number(upPct));
    const nextDownAbs = Math.abs(Number(downPctAbs));
    if (!validTrackingPct(nextUp) || !validTrackingPct(nextDownAbs)) {
      setMessage('상승·하락 기준은 0.1%~30% 범위로 입력해 주세요.');
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      const settings = await updatePortfolioThresholdSettings([{
        ticker: target.ticker,
        upPct: nextUp,
        downPct: -nextDownAbs,
        enabled: nextEnabled,
      }]);
      const saved = settings.items.find((row) => row.ticker === target.ticker);
      setEnabled(saved?.enabled ?? nextEnabled);
      setUpPct(saved?.upPct ?? nextUp);
      setDownPctAbs(Math.abs(saved?.downPct ?? -nextDownAbs));
      setMessage(nextEnabled
        ? `${target.name || target.ticker} 상승·하락 변화 추적을 시작했습니다.`
        : `${target.name || target.ticker} 상승·하락 변화 추적을 껐습니다.`);

      trackUxEvent('live_summary_stock_tracking_toggle', {
        ticker: target.ticker,
        stock_name: target.name || target.ticker,
        enabled: nextEnabled,
        up_pct: nextUp,
        down_pct: -nextDownAbs,
        source: 'live-summary-quick-tracking',
      });
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || '종목 변화 추적 설정을 저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  }, [downPctAbs, saving, target, upPct]);

  if (!target) return null;

  return (
    <div className="live-summary-tracking-sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="live-summary-tracking-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={`${target.name || target.ticker} 변화 추적 설정`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="live-summary-tracking-sheet__head">
          <div>
            <small>CHANGE TRACKING</small>
            <b>{target.name || target.ticker}</b>
            <span>{target.ticker}</span>
          </div>
          <button type="button" onClick={onClose} aria-label="변화 추적 설정 닫기">×</button>
        </header>

        {loading ? (
          <div className="live-summary-tracking-sheet__loading">현재 알림 기준을 확인하고 있습니다.</div>
        ) : (
          <>
            <div className={`live-summary-tracking-sheet__status ${enabled ? 'is-on' : ''}`}>
              <span>
                <small>종목 변화 추적</small>
                <b>{enabled ? '사용 중' : '꺼짐'}</b>
              </span>
              <button
                type="button"
                disabled={saving}
                className={enabled ? 'is-on' : ''}
                onClick={() => void save(!enabled)}
              >
                {saving ? '저장 중' : enabled ? '추적 끄기' : '추적 시작'}
              </button>
            </div>

            <div className="live-summary-tracking-sheet__thresholds">
              <label>
                <span>상승 기준</span>
                <div><b>+</b><input type="number" min="0.1" max="30" step="0.1" value={upPct} onChange={(event) => setUpPct(Number(event.target.value))} /><em>%</em></div>
              </label>
              <label>
                <span>하락 기준</span>
                <div><b>−</b><input type="number" min="0.1" max="30" step="0.1" value={downPctAbs} onChange={(event) => setDownPctAbs(Number(event.target.value))} /><em>%</em></div>
              </label>
            </div>

            <p className="live-summary-tracking-sheet__help">
              단순 현재 등락률 표시가 아니라 설정한 당일 상승·하락 기준에 새로 진입했을 때 알림 대상으로 사용합니다.
              브라우저 Push 수신 여부는 알림 탭의 수신 채널 설정과 별개입니다.
            </p>

            <div className="live-summary-tracking-sheet__actions">
              <button type="button" disabled={saving} onClick={() => void save(enabled)}>
                기준 저장
              </button>
              <button type="button" onClick={onOpenChannelSettings}>
                수신 채널
              </button>
              <button type="button" onClick={onOpenAdvanced}>
                전체 알림 관리
              </button>
            </div>
          </>
        )}

        {message ? <p className="live-summary-tracking-sheet__message">{message}</p> : null}
      </section>
    </div>
  );
}


/** Render heavy diagnostic components only after the reader opens the section. */
function FocusResearchDisclosure({ label, meta, children }: {
  label: string;
  meta?: string;
  children: React.ReactNode;
}) {
  const [opened, setOpened] = useState(false);
  return (
    <details className="pi-focus-research" onToggle={(event) => setOpened(event.currentTarget.open)}>
      <summary>{label}{meta && <span>{meta}</span>}</summary>
      {opened ? children : null}
    </details>
  );
}

export default function LiveSummaryDock({
  enabled,
  userEmail,
  defaultOpen = false,
  standalone = false,
  embedded = false,
  authenticated = false,
  productAccessMode = LIVE_SUMMARY_PRODUCT_ROLLOUT.currentMode,
  proEntitled = false,
  onRequireLogin,
}: LiveSummaryDockProps) {
  const pathname = typeof window !== 'undefined'
    ? window.location.pathname.replace(/\/+$/, '')
    : '';
  const queryMini = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('liveSummaryMini') === '1'
    : false;
  const standaloneMode =
    standalone ||
    pathname.endsWith(LIVE_SUMMARY_POPUP_PATH) ||
    queryMini ||
    (typeof window !== 'undefined' && window.name === LIVE_SUMMARY_POPUP_NAME);
  const panelMode = standaloneMode || embedded;
  const productAccess = useMemo<LiveSummaryProductAccess>(() => ({
    mode: productAccessMode,
    proEntitled,
  }), [productAccessMode, proEntitled]);

  const requestedPopupTab = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('tab')
    : null;
  const requestedChangeEventId = typeof window !== 'undefined'
    ? cleanText(new URLSearchParams(window.location.search).get('changeEventId'))
    : '';
  const requestedChangeEntityType = typeof window !== 'undefined'
    ? cleanText(new URLSearchParams(window.location.search).get('changeEntityType')).toUpperCase()
    : '';
  const requestedChangeEntityKey = typeof window !== 'undefined'
    ? cleanText(new URLSearchParams(window.location.search).get('changeEntityKey'))
    : '';
  const requestedPanelTicker = typeof window !== 'undefined'
    ? cleanText(new URLSearchParams(window.location.search).get('ticker')).replace(/\D/g, '')
    : '';
  const requestedPanelIssueId = typeof window !== 'undefined'
    ? cleanText(new URLSearchParams(window.location.search).get('issueId'))
    : '';
  const requestedPanelIssueQuery = typeof window !== 'undefined'
    ? cleanText(new URLSearchParams(window.location.search).get('issueQuery')).slice(0, 120)
    : '';
  const initialActiveTab = (['SUMMARY', 'ISSUES', 'SECTORS'] as const).includes(
    requestedPopupTab as any,
  )
    ? (requestedPopupTab as 'SUMMARY' | 'ISSUES' | 'SECTORS')
    : 'SUMMARY';

  const [popupBlocked, setPopupBlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState<'SUMMARY' | 'ISSUES' | 'SECTORS' | 'SCANNER' | 'STOCKS' | 'HOLDINGS' | 'ALERTS'>(initialActiveTab);
  const [exploreOpen, setExploreOpen] = useState(['ISSUES', 'SECTORS', 'SCANNER'].includes(initialActiveTab));
  const [issueUnreadCount, setIssueUnreadCount] = useState(0);
  // 종목 탭 이슈 표시는 ISSUES 탭의 현재 검색/페이지와 분리해서 관리한다.
  // page=1, query/sector 없음인 Live Issue head만 보존하므로 이슈 탭에서
  // 검색하거나 과거 페이지로 이동해도 종목 리스트의 표식이 흔들리지 않는다.
  const [liveIssueHead, setLiveIssueHead] = useState<LiveIssue[]>([]);
  const [issueSearchInput, setIssueSearchInput] = useState('');
  const [issueQuery, setIssueQuery] = useState('');
  const [issueSector, setIssueSector] = useState('');
  const [issuePage, setIssuePage] = useState(1);
  const [issuePageLoading, setIssuePageLoading] = useState(false);
  // Distinguish a failed feed request from a verified empty result.
  const [issuePageError, setIssuePageError] = useState(false);
  const [issuePaging, setIssuePaging] = useState<Pick<LiveSummaryIssuesPage, 'page' | 'size' | 'totalElements' | 'totalPages' | 'hasPrevious' | 'hasNext' | 'query' | 'selectedSector' | 'sectorFacets'>>({
    page: 1,
    size: LIVE_SUMMARY_ISSUE_PAGE_SIZE,
    totalElements: 0,
    totalPages: 0,
    hasPrevious: false,
    hasNext: false,
    query: '',
    selectedSector: '',
    sectorFacets: [],
  });
  const [scannerUnreadEnteredCount, setScannerUnreadEnteredCount] = useState(0);
  const [scannerUnreadExitedCount, setScannerUnreadExitedCount] = useState(0);
  const [activityToast, setActivityToast] = useState<LiveActivityToast | null>(null);
  const [offhoursProxy, setOffhoursProxy] = useState<OffhoursProxyResponse | null>(null);

  useEffect(() => {
    if (!authenticated) return;
    if (requestedPopupTab === 'OPPORTUNITY'
      || requestedPopupTab === 'THEME'
      || requestedPopupTab === 'THEMES'
      || requestedPopupTab === 'FUNDAMENTAL'
      || requestedPopupTab === 'FUNDAMENTALS'
      || requestedPopupTab === 'STOCK'
      || requestedPopupTab === 'STOCKS') {
      setActiveTab('STOCKS');
      return;
    }
    if (requestedPopupTab === 'SCANNER' || requestedPopupTab === 'HOLDINGS' || requestedPopupTab === 'ALERTS') {
      setActiveTab(requestedPopupTab);
    }
  }, [authenticated, requestedPopupTab]);

  const requestProtectedTab = useCallback(
    (tab: 'STOCK' | 'HOLDING' | 'ALERT') => {
      if (authenticated) return;

      trackUxEvent('live_summary_locked_click', {
        tab,
        source: embedded ? 'main-risk-desk' : 'live-summary',
        standalone: standaloneMode,
        embedded,
      });

      if (onRequireLogin) {
        onRequireLogin(tab);
        return;
      }

      const target = window.opener && !window.opener.closed ? window.opener : window;
      target.dispatchEvent(
        new CustomEvent('policy-intel:require-auth', {
          detail: { panelTab: tab, openPanel: true, source: 'live-summary' },
        }),
      );
      if (target !== window) target.focus();
    },
    [authenticated, embedded, onRequireLogin, standaloneMode],
  );

  const openProtectedTab = useCallback(
    (tab: 'SCANNER' | 'STOCKS' | 'HOLDINGS' | 'ALERTS') => {
      if (authenticated) {
        setActiveTab(tab);
        return;
      }

      requestProtectedTab(
        tab === 'HOLDINGS' ? 'HOLDING' : tab === 'ALERTS' ? 'ALERT' : 'STOCK',
      );
    },
    [authenticated, requestProtectedTab],
  );
  const [selectedKey, setSelectedKey] = useState('');
  const [sectorMarketFilter, setSectorMarketFilter] = useState<'ALL' | 'KOSPI' | 'KOSDAQ' | 'KONEX'>('ALL');
  const [themeLeaderStocks, setThemeLeaderStocks] = useState<NormalizedThemeStock[]>([]);
  const [, setThemeLeaderLoading] = useState(false);
  const [, setThemeLeaderError] = useState('');
  const [stockSearch, setStockSearch] = useState('');
  const [pinnedStock, setPinnedStock] = useState<StockRadarRow | null>(null);
  const [stockSearchSnapshot, setStockSearchSnapshot] = useState<{ query: string; rows: StockRadarRow[] }>({ query: '', rows: [] });
  const [stockFallbackResults, setStockFallbackResults] = useState<LiveSummaryHoldingSearchItem[]>([]);
  const [stockFallbackLoading, setStockFallbackLoading] = useState(false);
  const [stockFallbackResolvedQuery, setStockFallbackResolvedQuery] = useState('');
  const [stockFallbackError, setStockFallbackError] = useState('');
  const [stockSort, setStockSort] = useState<'RISK' | 'CHANGE' | 'VALUE' | 'FLOW'>('RISK');
  const [onlyRealtimeStocks, setOnlyRealtimeStocks] = useState(false);
  const [expandedStockKey, setExpandedStockKey] = useState('');
  const [focusedHoldingChangeEventId, setFocusedHoldingChangeEventId] = useState(requestedChangeEventId);
  const [trackingTarget, setTrackingTarget] = useState<TrackingTarget | null>(null);

  const openScannerMatchStock = useCallback((match: ScannerMatch) => {
    const normalizedTicker = cleanText(match?.ticker);
    if (!/^\d{6}$/.test(normalizedTicker)) return;

    const scannerStock = scannerMatchToStockRadarRow(match);
    if (!scannerStock) return;

    setPinnedStock(scannerStock);
    setStockSearchSnapshot({ query: normalizedTicker.toLowerCase(), rows: [scannerStock] });
    setStockFallbackResults([]);
    setStockFallbackResolvedQuery('');
    setStockSearch(normalizedTicker);
    setExpandedStockKey(`stocks:${normalizedTicker}`);
    setActiveTab('STOCKS');
    trackUxEvent('live_summary_stock_detail_open', {
      ticker: normalizedTicker,
      name: scannerStock.name,
      source: 'scanner-match',
      scanner_rule: cleanText(match.ruleName) || undefined,
    });
  }, []);


  const openTrackingSettings = useCallback((ticker: string, name: string) => {
    const normalizedTicker = cleanText(ticker);
    if (!/^\d{6}$/.test(normalizedTicker)) return;
    setTrackingTarget({ ticker: normalizedTicker, name: cleanText(name) || normalizedTicker });
    trackUxEvent('live_summary_stock_tracking_open', {
      ticker: normalizedTicker,
      stock_name: cleanText(name) || normalizedTicker,
      source: 'live-summary',
    });
  }, []);

  const openAdvancedNotificationSettings = useCallback(() => {
    const targetWindow = window.opener && !window.opener.closed ? window.opener : window;
    let mobilePrefix = '';
    try {
      mobilePrefix = /^\/m(?:\/|$)/.test(targetWindow.location.pathname) ? '/m' : '';
      targetWindow.location.assign(`${mobilePrefix}/mypage?tab=alerts`);
      if (targetWindow !== window) targetWindow.focus();
    } catch {
      window.location.assign('/mypage?tab=alerts');
    }
  }, []);

  const activityStorageScope = useMemo(
    () => encodeURIComponent(cleanText(userEmail).toLowerCase() || 'anonymous'),
    [userEmail],
  );
  const issueSeenStorageKey = useMemo(
    () => `policyintel:live-summary:seen-issues:${activityStorageScope}`,
    [activityStorageScope],
  );
  const scannerObservedStorageKey = useMemo(
    () => `policyintel:live-summary:scanner-observed:${activityStorageScope}`,
    [activityStorageScope],
  );

  const [expandedIssueId, setExpandedIssueId] = useState('');
  const [issueUrlCopyResult, setIssueUrlCopyResult] = useState<{ id: string; copied: boolean } | null>(null);
  const [pendingMainIssueFocus, setPendingMainIssueFocus] = useState<{ issueId: string; query: string } | null>(null);
  const [boxPopupTicker, setBoxPopupTicker] = useState('');
  const [holdingSearch, setHoldingSearch] = useState('');
  const [holdingSearchResults, setHoldingSearchResults] = useState<LiveSummaryHoldingSearchItem[]>([]);
  const [holdingSearchLoading, setHoldingSearchLoading] = useState(false);
  const [holdingSearchError, setHoldingSearchError] = useState('');
  const [holdingMutationTicker, setHoldingMutationTicker] = useState('');
  const holdingMutationInFlightRef = useRef('');
  const [holdingMessage, setHoldingMessage] = useState('');
  const [holdingsLoaded, setHoldingsLoaded] = useState(false);
  const [deferredLoading, setDeferredLoading] = useState<'HOLDINGS' | ''>('');
  const [state, setState] = useState<LiveSummaryState>(EMPTY_STATE);

  /*
   * Existing tracked users can have Notification permission=granted while the
   * browser PushSubscription was lost (profile reset, browser cleanup, logout cleanup, etc.).
   * If this authenticated user already has tracked holdings, repair the browser
   * subscription automatically. Never prompt from mount: the native permission
   * prompt remains tied to the explicit Track click path.
   */
  const autoWebPushRepairAttemptedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !authenticated || autoWebPushRepairAttemptedRef.current) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

    autoWebPushRepairAttemptedRef.current = true;

    void (async () => {
      try {
        const holdings = await getLiveSummaryHoldings({
          refresh: false,
          timeoutMs: 5_000,
        });

        if (!Array.isArray(holdings) || holdings.length === 0) {
          return;
        }

        const pushStatus = await ensureTrackedStockWebPush();
        trackUxEvent('push_auto_repair_success', {
          source: 'live-summary-authenticated-mount',
          activation: 'TRACK_AUTO_REPAIR',
          holdings_count: holdings.length,
          push_status: pushStatus,
        });
      } catch {
        trackUxEvent('push_auto_repair_failed', {
          source: 'live-summary-authenticated-mount',
          activation: 'TRACK_AUTO_REPAIR',
        });
      }
    })();
  }, [authenticated, enabled]);

  useCallback((item: NormalizedOpportunity) => {
    trackUxEvent('live_summary_opportunity_click', {
      category: item.category,
      score: item.opportunityScore,
      market: item.marketLabel,
      locked: item.detailLocked || !authenticated,
    });
    if (item.detailLocked || !authenticated) {
      requestProtectedTab('STOCK');
      return;
    }
    setPinnedStock(null);
    setExpandedStockKey('');
    setStockSearchSnapshot({ query: '', rows: [] });
    setStockSearch(item.name || item.ticker);
    setActiveTab('STOCKS');
  }, [authenticated, requestProtectedTab]);

  const loadingRef = useRef(false);
  const lastLoadedAtRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const issueAbortRef = useRef<AbortController | null>(null);
  const themeLeaderAbortRef = useRef<AbortController | null>(null);
  const themeLeaderLoadingRef = useRef(false);
  // Polling success is not a user behavior. Emit the load diagnostic once per mounted panel only.
  const themeLeaderAnalyticsLoadedRef = useRef(false);
  const issueLoadingRef = useRef(false);
  const lastIssuesLoadedAtRef = useRef(0);
  const issuePageRef = useRef(1);
  const issueQueryRef = useRef('');
  const issueSectorRef = useRef('');
  const activeTabRef = useRef(activeTab);
  const issueSeenInitializedRef = useRef(false);
  const issueSeenKeysRef = useRef<Set<string>>(new Set());
  const issueNotifiedKeysRef = useRef<Set<string>>(new Set());
  const scannerActivityAbortRef = useRef<AbortController | null>(null);
  const scannerActivityLoadingRef = useRef(false);
  const scannerActivityInitializedRef = useRef(false);
  const scannerPreviousMatchesRef = useRef<Map<string, ScannerActivityItem>>(new Map());
  const activityToastTimerRef = useRef<number | undefined>(undefined);
  const requestSequenceRef = useRef(0);
  const popupRef = useRef<Window | null>(null);
  const authenticatedViewKeyRef = useRef('');
  const stockBriefViewKeyRef = useRef('');
  const stockBriefCompleteKeyRef = useRef('');
  const embeddedOpenTrackedRef = useRef(false);
  const holdingSearchAbortRef = useRef<AbortController | null>(null);
  const stockSearchAbortRef = useRef<AbortController | null>(null);
  const stockSearchTrackedRef = useRef('');
  const fullHoldingsAbortRef = useRef<AbortController | null>(null);
  const holdingsLoadedRef = useRef(false);
  const latestFastHoldingsRef = useRef<NormalizedHolding[]>([]);
  const previousRiskSnapshotRef = useRef<Map<string, StockRiskSnapshot>>(new Map());

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    issuePageRef.current = issuePage;
  }, [issuePage]);

  useEffect(() => {
    issueQueryRef.current = issueQuery;
  }, [issueQuery]);

  useEffect(() => {
    issueSectorRef.current = issueSector;
  }, [issueSector]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nextQuery = cleanText(issueSearchInput).slice(0, 120);
      issuePageRef.current = 1;
      issueQueryRef.current = nextQuery;
      setIssuePage(1);
      setIssueQuery(nextQuery);
      setExpandedIssueId('');
    }, LIVE_SUMMARY_ISSUE_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [issueSearchInput]);

  const showActivityToast = useCallback((toast: Omit<LiveActivityToast, 'id'>) => {
    if (activityToastTimerRef.current !== undefined) {
      window.clearTimeout(activityToastTimerRef.current);
    }
    setActivityToast({ ...toast, id: Date.now() });
    activityToastTimerRef.current = window.setTimeout(() => {
      activityToastTimerRef.current = undefined;
      setActivityToast(null);
    }, 6_000);
  }, []);

  const markCurrentIssuesSeen = useCallback((issues: LiveIssue[]) => {
    if (!issueSeenInitializedRef.current) return;
    const next = new Set(issueSeenKeysRef.current);
    issues.forEach((issue) => next.add(issue.id));
    issueSeenKeysRef.current = next;
    safeWriteStringArray(issueSeenStorageKey, Array.from(next));
    setIssueUnreadCount(0);
  }, [issueSeenStorageKey]);

  const openActivityTarget = useCallback((target: 'ISSUES' | 'SCANNER') => {
    setExploreOpen(true);
    setActivityToast(null);
    if (target === 'ISSUES') {
      setIssueUnreadCount(0);
      setActiveTab('ISSUES');
      return;
    }
    setScannerUnreadEnteredCount(0);
    setScannerUnreadExitedCount(0);
    openProtectedTab('SCANNER');
  }, [openProtectedTab]);

  useEffect(() => () => {
    if (activityToastTimerRef.current !== undefined) {
      window.clearTimeout(activityToastTimerRef.current);
    }
    scannerActivityAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    issueSeenInitializedRef.current = false;
    issueSeenKeysRef.current = new Set();
    issueNotifiedKeysRef.current = new Set();
    scannerActivityInitializedRef.current = false;
    scannerPreviousMatchesRef.current = new Map();
    setIssueUnreadCount(0);
    setScannerUnreadEnteredCount(0);
    setScannerUnreadExitedCount(0);
    setActivityToast(null);
  }, [activityStorageScope]);

  const loadThemeLeaders = useCallback(async () => {
    if (themeLeaderLoadingRef.current) return;
    themeLeaderLoadingRef.current = true;
    themeLeaderAbortRef.current?.abort();
    const controller = new AbortController();
    themeLeaderAbortRef.current = controller;
    setThemeLeaderLoading(true);
    setThemeLeaderError('');

    try {
      const rows = await getLiveSummaryThemeLeaders({
        limit: 15,
        timeoutMs: 7_000,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;

      const normalized = rows
        .map(normalizeThemeStock)
        .filter((item): item is NormalizedThemeStock => Boolean(item))
        .sort((left, right) => {
          const valueGap = (right.tradingValue ?? Number.NEGATIVE_INFINITY)
            - (left.tradingValue ?? Number.NEGATIVE_INFINITY);
          if (valueGap !== 0) return valueGap;
          const rateGap = (right.changeRate ?? Number.NEGATIVE_INFINITY)
            - (left.changeRate ?? Number.NEGATIVE_INFINITY);
          if (rateGap !== 0) return rateGap;
          return left.ticker.localeCompare(right.ticker);
        })
        .slice(0, 15);

      setThemeLeaderStocks(normalized);
      if (!themeLeaderAnalyticsLoadedRef.current) {
        themeLeaderAnalyticsLoadedRef.current = true;
        trackUxEvent('live_summary_money_flow_leaders_loaded', {
          stockCount: normalized.length,
          source: 'trading-value-top15',
          analyticsBasis: 'first-success-only',
        });
      }
    } catch {
      if (controller.signal.aborted) return;
      setThemeLeaderError('거래대금 상위 종목을 불러오지 못했습니다.');
    } finally {
      if (themeLeaderAbortRef.current === controller) {
        themeLeaderAbortRef.current = null;
      }
      themeLeaderLoadingRef.current = false;
      setThemeLeaderLoading(false);
    }
  }, []);

  const initials = useMemo(
    () => cleanText(userEmail).slice(0, 1).toUpperCase() || 'L',
    [userEmail],
  );

  const selectedSector = useMemo(
    () =>
      state.sectors.find((sector) => sectorKey(sector) === selectedKey) ??
      state.sectors[0] ??
      null,
    [selectedKey, state.sectors],
  );

  useMemo(() => {
    const groups = new Map<string, NormalizedSector[]>();

    state.sectors.forEach((sector) => {
      const market = sectorMarketLabel(sector);
      const current = groups.get(market) ?? [];
      current.push(sector);
      groups.set(market, current);
    });

    const marketOrder = ['KOSPI', 'KOSDAQ', 'KONEX', '기타'];
    return Array.from(groups.entries()).sort(([left], [right]) => {
      const leftIndex = marketOrder.indexOf(left);
      const rightIndex = marketOrder.indexOf(right);
      const normalizedLeft = leftIndex < 0 ? marketOrder.length : leftIndex;
      const normalizedRight = rightIndex < 0 ? marketOrder.length : rightIndex;
      return normalizedLeft - normalizedRight || left.localeCompare(right, 'ko');
    });
  }, [state.sectors]);

  const visibleSectors = useMemo(() => {
    if (sectorMarketFilter === 'ALL') return state.sectors;
    return state.sectors.filter((sector) => sectorMarketLabel(sector) === sectorMarketFilter);
  }, [sectorMarketFilter, state.sectors]);

  const marketSectorCounts = useMemo(() => {
    return state.sectors.reduce<Record<string, number>>((counts, sector) => {
      const market = sectorMarketLabel(sector);
      counts[market] = (counts[market] ?? 0) + 1;
      return counts;
    }, {});
  }, [state.sectors]);

  const focusStocks = useMemo(() => {
    const byTicker = new Map<
      string,
      NormalizedSectorTopStock & {
        sectorName: string;
        sectorChangeRate: number;
        breadthPct: number | null;
      }
    >();

    state.sectors.forEach((sector) => {
      sector.topStocks.forEach((stock) => {
        const key = stock.ticker || stock.name;
        const previous = byTicker.get(key);
        const score =
          (stock.changeRate ?? -999) +
          sector.changeRate * 0.35 +
          (sector.breadthPct ?? 0) * 0.015 +
          (stock.tradingValue ? Math.min(3, Math.log10(Math.max(1, stock.tradingValue)) - 7) : 0);
        const previousScore = previous
          ? (previous.changeRate ?? -999) +
            previous.sectorChangeRate * 0.35 +
            (previous.breadthPct ?? 0) * 0.015 +
            (previous.tradingValue
              ? Math.min(3, Math.log10(Math.max(1, previous.tradingValue)) - 7)
              : 0)
          : -9999;

        if (!previous || score > previousScore) {
          byTicker.set(key, {
            ...stock,
            sectorName: sector.industryName,
            sectorChangeRate: sector.changeRate,
            breadthPct: sector.breadthPct,
          });
        }
      });
    });

    return Array.from(byTicker.values())
      .sort((a, b) => {
        const aPriority = stockRiskPriority(a, previousRiskSnapshotRef.current.get(a.ticker || a.name));
        const bPriority = stockRiskPriority(b, previousRiskSnapshotRef.current.get(b.ticker || b.name));
        if (bPriority.score !== aPriority.score) return bPriority.score - aPriority.score;
        if ((b.riskScore ?? -1) !== (a.riskScore ?? -1)) return (b.riskScore ?? -1) - (a.riskScore ?? -1);
        return (b.tradingValue ?? 0) - (a.tradingValue ?? 0);
      })
      .slice(0, 7);
  }, [state.sectors]);


  const leadTheme = useMemo(() => {
    type ThemeMoneyFlowGroup = {
      themeCode: string;
      themeName: string;
      stockCount: number;
      tradingValue: number;
      smartMoneyNetBuyAmount: number | null;
      programNetBuyAmount: number | null;
      themeChangeRate: number | null;
      themeDataStatus: string;
      representative: NormalizedThemeStock;
    };

    const groups = new Map<string, ThemeMoneyFlowGroup>();

    themeLeaderStocks.forEach((stock) => {
      const themeCode = cleanText(stock.themeCode);
      const themeName = cleanText(stock.themeName);
      const tradingValue = stock.tradingValue;

      // Summary theme is derived only from the trading-value Top 15 stocks that
      // actually have a representative theme. Theme mapping is context, not an
      // eligibility condition for the Top 15 stock ranking itself.
      if (!themeName || tradingValue === null || tradingValue <= 0) return;

      const key = themeCode || themeName;
      const current = groups.get(key);
      if (!current) {
        groups.set(key, {
          themeCode,
          themeName,
          stockCount: 1,
          tradingValue,
          smartMoneyNetBuyAmount: stock.smartMoneyNetBuyAmount,
          programNetBuyAmount: stock.programNetBuyAmount,
          themeChangeRate: stock.themeChangeRate,
          themeDataStatus: stock.themeDataStatus,
          representative: stock,
        });
        return;
      }

      current.stockCount += 1;
      current.tradingValue += tradingValue;
      if (stock.smartMoneyNetBuyAmount !== null) {
        current.smartMoneyNetBuyAmount = (current.smartMoneyNetBuyAmount ?? 0) + stock.smartMoneyNetBuyAmount;
      }
      if (stock.programNetBuyAmount !== null) {
        current.programNetBuyAmount = (current.programNetBuyAmount ?? 0) + stock.programNetBuyAmount;
      }
      if (current.themeChangeRate === null && stock.themeChangeRate !== null) {
        current.themeChangeRate = stock.themeChangeRate;
      }
      if (current.themeDataStatus === 'NO_DATA' && stock.themeDataStatus !== 'NO_DATA') {
        current.themeDataStatus = stock.themeDataStatus;
      }
      if ((stock.tradingValue ?? 0) > (current.representative.tradingValue ?? 0)) {
        current.representative = stock;
      }
    });

    return Array.from(groups.values()).sort((left, right) => {
      const valueGap = right.tradingValue - left.tradingValue;
      if (valueGap !== 0) return valueGap;
      const stockGap = right.stockCount - left.stockCount;
      if (stockGap !== 0) return stockGap;
      const flowGap = (right.smartMoneyNetBuyAmount ?? Number.NEGATIVE_INFINITY)
        - (left.smartMoneyNetBuyAmount ?? Number.NEGATIVE_INFINITY);
      if (flowGap !== 0) return flowGap;
      return left.themeName.localeCompare(right.themeName, 'ko');
    })[0] ?? null;
  }, [themeLeaderStocks]);

  const holdingSummary = useMemo(() => {
    let attention = 0;
    let improving = 0;
    let stable = 0;

    state.holdings.forEach((holding) => {
      if (isRiskAssessmentAttention(holding.riskAssessment)) attention += 1;
      else if (holding.riskAssessment.state === 'RECOVERING') improving += 1;
      else stable += 1;
    });

    return { total: state.holdings.length, attention, improving, stable };
  }, [state.holdings]);

  const sortedHoldings = useMemo(
    () => [...state.holdings].sort((a, b) => {
      const contractPriorityGap = holdingRiskStageRank(b.judgmentContract?.riskManagement.state || '')
        - holdingRiskStageRank(a.judgmentContract?.riskManagement.state || '');
      if (contractPriorityGap !== 0) return contractPriorityGap;

      const priorityGap = riskAssessmentPriority(b.riskAssessment) - riskAssessmentPriority(a.riskAssessment);
      if (priorityGap !== 0) return priorityGap;

      const aMeta = itemMonitorMeta(
        a,
        state.marketContext,
        state.changeEvents,
        freshnessMeta(a).stale,
        'holding',
      );
      const bMeta = itemMonitorMeta(
        b,
        state.marketContext,
        state.changeEvents,
        freshnessMeta(b).stale,
        'holding',
      );
      if (bMeta.priority !== aMeta.priority) return bMeta.priority - aMeta.priority;
      const riskGap = (b.riskScore ?? -1) - (a.riskScore ?? -1);
      if (riskGap !== 0) return riskGap;
      return (b.tradingValue ?? 0) - (a.tradingValue ?? 0);
    }),
    [state.changeEvents, state.holdings, state.marketContext],
  );

  const priorityHolding = sortedHoldings.find((holding) => (
    holdingRiskStageRank(holding.judgmentContract?.riskManagement.state || '') >= 3
    || isRiskAssessmentAttention(holding.riskAssessment)
  )) ?? null;

  const alertGuide = useMemo(() => {
    const dangerCount = state.alerts.filter((alert) => alert.level === 'danger').length;
    const warningCount = state.alerts.filter((alert) => alert.level === 'warning').length;
    const infoCount = state.alerts.filter((alert) => alert.level === 'info').length;
    return { dangerCount, warningCount, infoCount };
  }, [state.alerts]);


  const decisionAlerts = useMemo(() => {
    const holdingByTicker = new Map(
      state.holdings.map((holding) => [cleanText(holding.ticker), holding]),
    );
    const today = koreaTodayKey();
    const eligibleTypes = new Set([
      'HOLDING_THESIS_CHANGED',
      'HOLDING_THESIS_TRIGGERED',
      'HOLDING_MARKET_PRESSURE_CHANGED',
      'HOLDING_STATUS_WORSENED',
    ]);

    return state.changeEvents
      .filter((event) => (
        eligibleTypes.has(cleanText(event.eventType).toUpperCase())
        && eventDateKey(eventTimestampRaw(event)) === today
        && holdingByTicker.has(cleanText(event.entityKey))
      ))
      .map((event) => {
        const holding = holdingByTicker.get(cleanText(event.entityKey))!;
        const contract = holding.judgmentContract || EMPTY_HOLDING_JUDGMENT;
        const level: NormalizedAlert['level'] = event.severity === 'DANGER'
          ? 'danger'
          : event.severity === 'WARNING'
            ? 'warning'
            : 'info';
        return { event, holding, contract, level };
      })
      .sort((a, b) => (
        (b.level === 'danger' ? 3 : b.level === 'warning' ? 2 : 1)
        - (a.level === 'danger' ? 3 : a.level === 'warning' ? 2 : 1)
        || (normalizedEventTimestamp(eventTimestampRaw(b.event)) ?? 0)
          - (normalizedEventTimestamp(eventTimestampRaw(a.event)) ?? 0)
      ))
      .slice(0, 20);
  }, [state.changeEvents, state.holdings]);

  const latestDecisionAlerts = useMemo(() => {
    const byTicker = new Map<string, (typeof decisionAlerts)[number]>();
    for (const row of decisionAlerts) {
      const key = cleanText(row.holding.ticker);
      const prev = byTicker.get(key);
      if (!prev || (normalizedEventTimestamp(eventTimestampRaw(row.event)) ?? 0) > (normalizedEventTimestamp(eventTimestampRaw(prev.event)) ?? 0)) {
        byTicker.set(key, row);
      }
    }
    return Array.from(byTicker.values()).sort((a, b) =>
      (b.level === 'danger' ? 3 : b.level === 'warning' ? 2 : 1)
        - (a.level === 'danger' ? 3 : a.level === 'warning' ? 2 : 1)
      || (normalizedEventTimestamp(eventTimestampRaw(b.event)) ?? 0)
        - (normalizedEventTimestamp(eventTimestampRaw(a.event)) ?? 0)
    );
  }, [decisionAlerts]);

  const alertSummaryGuide = useMemo(() => {
    if (!latestDecisionAlerts.length) return alertGuide;
    return {
      dangerCount: latestDecisionAlerts.filter((row) => row.level === 'danger').length,
      warningCount: latestDecisionAlerts.filter((row) => row.level === 'warning').length,
      infoCount: latestDecisionAlerts.filter((row) => row.level === 'info').length,
    };
  }, [alertGuide, latestDecisionAlerts]);

  const leadSector = state.sectors[0] ?? null;

  const afterHoursBrief = useMemo(() => {
    const flowCandidates = state.opportunities.filter((item) => (
      item.reasons.some((reason) => /동반 순매수|순유입|연속 유지|회복/.test(reason))
      || ['UP_STREAK', 'DOWN_TO_UP'].includes(item.investorTrendDirection)
    ));
    const cautionCandidates = state.opportunities.filter((item) => (
      item.category === '급등·추격주의' || Boolean(item.warning)
    ));
    const nextSessionCandidates = state.opportunities.filter((item) => (
      item.category !== '시장 확인 제한'
    ));

    if (priorityHolding) {
            const assessment = priorityHolding.riskAssessment;
      return {
        candidateCount: nextSessionCandidates.length,
        flowCount: flowCandidates.length,
        cautionCount: cautionCandidates.length,
        holdingAttention: holdingSummary.attention,
        leadEyebrow: '우선 확인 보유 위험',
        leadTitle: `${priorityHolding.name} · ${assessment.stateLabel}`,
        leadDescription: assessment.summary,
        buttonLabel: '보유 위험 보기',
        targetTab: 'HOLDINGS' as const,
      };
    }

    const lead = state.opportunities[0] ?? null;
    return {
      candidateCount: nextSessionCandidates.length,
      flowCount: flowCandidates.length,
      cautionCount: cautionCandidates.length,
      holdingAttention: holdingSummary.attention,
      leadEyebrow: lead ? '다음 장 우선 관찰' : '최근 장 정리',
      leadTitle: lead ? `${lead.name}${lead.changeRate === null ? '' : ` ${formatPct(lead.changeRate)}`}` : '최근 장 시장·업종 변화를 정리했습니다.',
      leadDescription: lead ? opportunityDisplayReason(lead) : '다음 장 시작 후 지수·수급·거래대금이 다시 같은 방향으로 이어지는지 확인하세요.',
      buttonLabel: lead ? '종목 전체 보기' : '업종 흐름 보기',
      targetTab: lead ? 'STOCKS' as const : 'SECTORS' as const,
    };
  }, [holdingSummary.attention, priorityHolding, state.opportunities]);

  const preparationGuide = useMemo(() => {
    const hasHoldings = state.holdings.length > 0;
    return {
      badge: '시장 확인 중',
      title: '시장 흐름이 정리되는 동안 종목의 가격과 수급을 먼저 확인하세요.',
      description: '장 초반에는 방향이 빠르게 바뀔 수 있으므로 현재가와 수급 변화를 함께 보세요.',
      steps: ['현재가와 수급 방향 확인', '업종 흐름과 같은 방향인지 확인', '최근 변화가 이어지는지 확인'],
      buttonLabel: hasHoldings ? '보유 변화 확인' : '종목 상태 확인',
      targetTab: hasHoldings ? 'HOLDINGS' as const : 'STOCKS' as const,
    };
  }, [state.holdings.length]);

  const nxtAfterSession = cleanText(state.marketSession).toUpperCase() === 'NXT_AFTER';
  // Do not suppress the whole intraday board because one upstream source is stale.
  // KOSPI/KOSDAQ indices and KOSDAQ150 futures can remain fresh even when the
  // KOSPI200 futures WebSocket is delayed. Render whenever at least one primary
  // domestic index/derivative point is fresh, and let each row expose its own
  // stale state.
  const marketContextHasFreshPrimaryData = [
    state.marketContext.domesticIndices.KOSPI,
    state.marketContext.domesticIndices.KOSDAQ,
    state.marketContext.domesticIndices.KOSPI200,
    state.marketContext.domesticIndices.KOSDAQ150,
  ].some((point) => point.value !== null && !point.stale) || [
    state.marketContext.derivatives.KOSPI200,
    state.marketContext.derivatives.KOSDAQ150,
  ].some((point) => point.futuresPrice !== null && !point.stale);
  const marketContextActionable = state.marketContext.available
    && marketContextHasFreshPrimaryData;

  const priorityStockCount = focusStocks.filter((stock) => {
    const priority = stockRiskPriority(stock, previousRiskSnapshotRef.current.get(stock.ticker || stock.name));
    return priority.tone === 'down' || priority.tone === 'warn';
  }).length;

  const stockRows = useMemo(
    () => {
      const dedup = new Map<string, StockRadarRow>();

      state.sectors.forEach((sector) => {
        sector.topStocks.forEach((stock) => {
          const key = cleanText(stock.ticker || stock.name);
          if (!key) return;
          const candidate = {
            ...stock,
            sectorName: sector.industryName,
            sectorChangeRate: sector.changeRate,
            sectorBreadthPct: sector.breadthPct,
            sectorTone: sector.tone,
          };
          const previous = dedup.get(key);
          if (!previous) {
            dedup.set(key, candidate);
            return;
          }
          const previousRank = (previous.tradingValue ?? 0) + (previous.changeRate ?? 0) * 1000000;
          const candidateRank = (candidate.tradingValue ?? 0) + (candidate.changeRate ?? 0) * 1000000;
          if (candidateRank > previousRank) {
            dedup.set(key, candidate);
          }
        });
      });

      return Array.from(dedup.values());
    },
    [state.sectors],
  );

  const stockSearchUniverse = useMemo(() => {
    if (!pinnedStock) return stockRows;
    const pinnedKey = cleanText(pinnedStock.ticker || pinnedStock.name);
    if (!pinnedKey || stockRows.some((stock) => cleanText(stock.ticker || stock.name) === pinnedKey)) {
      return stockRows;
    }
    return [pinnedStock, ...stockRows];
  }, [pinnedStock, stockRows]);

  const liveRadarSearchMatches = useMemo(() => {
    const keyword = cleanText(stockSearch).toLowerCase();
    if (!keyword) return stockSearchUniverse;

    return stockSearchUniverse.filter((stock) =>
      [stock.name, stock.ticker, stock.sectorName]
        .map((value) => cleanText(value).toLowerCase())
        .some((value) => value.includes(keyword)),
    );
  }, [stockSearch, stockSearchUniverse]);

  useEffect(() => {
    const keyword = cleanText(stockSearch).toLowerCase();
    if (!keyword) {
      if (stockSearchSnapshot.query || stockSearchSnapshot.rows.length) {
        setStockSearchSnapshot({ query: '', rows: [] });
      }
      return;
    }

    if (stockSearchSnapshot.query !== keyword) {
      setStockSearchSnapshot({ query: keyword, rows: liveRadarSearchMatches });
      return;
    }

    if (!stockSearchSnapshot.rows.length && liveRadarSearchMatches.length) {
      setStockSearchSnapshot({ query: keyword, rows: liveRadarSearchMatches });
    }
  }, [liveRadarSearchMatches, stockSearch, stockSearchSnapshot.query, stockSearchSnapshot.rows.length]);

  const radarSearchMatches = useMemo(() => {
    const keyword = cleanText(stockSearch).toLowerCase();
    if (!keyword || stockSearchSnapshot.query !== keyword || !stockSearchSnapshot.rows.length) {
      return liveRadarSearchMatches;
    }

    // Search result membership is frozen for the active query so a 5-second market refresh
    // cannot make a user's result disappear. Existing members still receive fresh values.
    const liveByKey = new Map(
      liveRadarSearchMatches.map((stock) => [cleanText(stock.ticker || stock.name), stock] as const),
    );
    return stockSearchSnapshot.rows.map((stock) => (
      liveByKey.get(cleanText(stock.ticker || stock.name)) ?? stock
    ));
  }, [liveRadarSearchMatches, stockSearch, stockSearchSnapshot]);

  const filteredStocks = useMemo(() => {
    const rows = radarSearchMatches.filter((stock) => !onlyRealtimeStocks || stock.isRealtime);

    return [...rows].sort((a, b) => {
      if (stockSort === 'RISK') {
        const aPriority = stockRiskPriority(a, previousRiskSnapshotRef.current.get(a.ticker || a.name));
        const bPriority = stockRiskPriority(b, previousRiskSnapshotRef.current.get(b.ticker || b.name));
        if (bPriority.score !== aPriority.score) return bPriority.score - aPriority.score;
        return (b.riskScore ?? -1) - (a.riskScore ?? -1);
      }
      if (stockSort === 'VALUE') {
        return (b.tradingValue ?? 0) - (a.tradingValue ?? 0);
      }
      if (stockSort === 'FLOW') {
        // 외국인+기관 순수급을 1차 기준으로 정렬하고, 프로그램은 별도 축으로만 보조합니다.
        // 프로그램 주문은 외국인·기관 주문과 겹칠 수 있으므로 금액을 합산하지 않습니다.
        const aSmartMoney = resolvedSmartMoney(a);
        const bSmartMoney = resolvedSmartMoney(b);
        if (aSmartMoney !== null || bSmartMoney !== null) {
          const aValue = aSmartMoney ?? Number.NEGATIVE_INFINITY;
          const bValue = bSmartMoney ?? Number.NEGATIVE_INFINITY;
          if (bValue !== aValue) return bValue - aValue;
        }

        const aProgramRatio = a.programNetBuyToTradingValuePct ?? 0;
        const bProgramRatio = b.programNetBuyToTradingValuePct ?? 0;
        if (bProgramRatio !== aProgramRatio) return bProgramRatio - aProgramRatio;
      }
      const bRate = b.changeRate ?? -999;
      const aRate = a.changeRate ?? -999;
      if (bRate !== aRate) return bRate - aRate;
      return (b.tradingValue ?? 0) - (a.tradingValue ?? 0);
    });
  }, [onlyRealtimeStocks, radarSearchMatches, stockSort]);

  const selectedHolding = useMemo(() => {
    if (!expandedStockKey.startsWith('holding:')) return null;
    const key = expandedStockKey.slice('holding:'.length);
    return sortedHoldings.find((holding) => cleanText(holding.ticker || holding.name) === cleanText(key)) ?? null;
  }, [expandedStockKey, sortedHoldings]);

  const holdingChangeBriefEvents = useMemo(() => {
    const holdingTickers = new Set(
      state.holdings
        .map((holding) => cleanText(holding.ticker))
        .filter(Boolean),
    );
    if (!holdingTickers.size) return [];
    const today = koreaTodayKey();

    return state.changeEvents
      .filter((event) => {
        if (!holdingTickers.has(cleanText(event.entityKey))) return false;
        if (!(event.entityType === 'HOLDING' || event.entityType === 'STOCK')) return false;
        return eventDateKey(eventTimestampRaw(event)) === today;
      })
      .sort((a, b) => (
        (normalizedEventTimestamp(eventTimestampRaw(b)) ?? 0)
        - (normalizedEventTimestamp(eventTimestampRaw(a)) ?? 0)
      ));
  }, [state.changeEvents, state.holdings]);

  const holdingChangeBriefGroups = useMemo(() => {
    const material = dedupeMaterialChanges(holdingChangeBriefEvents, 20);
    const grouped = new Map<string, {
      ticker: string;
      latest: (typeof state.changeEvents)[number];
      eventCount: number;
    }>();

    material.forEach((event) => {
      const ticker = cleanText(event.entityKey);
      if (!ticker) return;
      const previous = grouped.get(ticker);
      if (previous) {
        previous.eventCount += 1;
        const currentScore = materialChangeScore(event);
        const previousScore = materialChangeScore(previous.latest);
        const currentTime = normalizedEventTimestamp(eventTimestampRaw(event)) ?? 0;
        const previousTime = normalizedEventTimestamp(eventTimestampRaw(previous.latest)) ?? 0;
        if (currentScore > previousScore || (currentScore === previousScore && currentTime > previousTime)) {
          previous.latest = event;
        }
        return;
      }
      grouped.set(ticker, { ticker, latest: event, eventCount: 1 });
    });

    return Array.from(grouped.values())
      .sort((a, b) => (
        materialChangeScore(b.latest) - materialChangeScore(a.latest)
        || (normalizedEventTimestamp(eventTimestampRaw(b.latest)) ?? 0)
          - (normalizedEventTimestamp(eventTimestampRaw(a.latest)) ?? 0)
      ))
      .slice(0, 5);
  }, [holdingChangeBriefEvents, state]);

  const holdingChangeBriefEventCount = holdingChangeBriefGroups.length;

  const holdingJudgmentChangeBriefEvents = useMemo(
    () => holdingChangeBriefEvents.filter((event) => {
      const type = cleanText(event.eventType).toUpperCase();

      return type === 'HOLDING_JUDGMENT_CHANGED'
        || type === 'HOLDING_THESIS_CHANGED';
    }),
    [holdingChangeBriefEvents],
  );

  const holdingJudgmentChangeBriefGroups = useMemo(() => {
    const grouped = new Map<string, {
      ticker: string;
      latest: (typeof state.changeEvents)[number];
      eventCount: number;
    }>();

    /*
     * holdingJudgmentChangeBriefEvents는 이미 최신순이다.
     * 중요도(score)가 아니라 최신 transition을 current history head로 사용한다.
     */
    holdingJudgmentChangeBriefEvents.forEach((event) => {
      const ticker = cleanText(event.entityKey);
      if (!ticker) return;

      const previous = grouped.get(ticker);

      if (previous) {
        previous.eventCount += 1;
        return;
      }

      grouped.set(ticker, {
        ticker,
        latest: event,
        eventCount: 1,
      });
    });

    return Array.from(grouped.values())
      .sort((a, b) => (
        (normalizedEventTimestamp(eventTimestampRaw(b.latest)) ?? 0)
        - (normalizedEventTimestamp(eventTimestampRaw(a.latest)) ?? 0)
      ))
      .slice(0, 5);
  }, [holdingJudgmentChangeBriefEvents, state]);

  const holdingJudgmentChangeBriefEventCount =
    holdingJudgmentChangeBriefEvents.length;

  const holdingJudgmentChangeBriefHistoryCount = Math.max(
    0,
    holdingJudgmentChangeBriefEvents.length
      - holdingJudgmentChangeBriefGroups.length,
  );

  const dailyPortfolioOutcome = useMemo(() => {
    let reviewCount = 0;
    let cautionCount = 0;
    let preservedCount = 0;
    let limitedCount = 0;
    let coverageGapCount = 0;

    const changeGroupByTicker = new Map(
      holdingChangeBriefGroups.map((group) => [cleanText(group.ticker), group]),
    );

    const priorities = state.holdings.map((holding) => {
      const stale = freshnessMeta(holding).stale;
      const contract = holding.judgmentContract || EMPTY_HOLDING_JUDGMENT;
      const coverage = contract.available
        ? {
            ready: contract.confidence.readyCount,
            total: contract.confidence.totalCount,
            missing: contract.confidence.missingEvidenceLabels,
          }
        : judgmentCoverageMeta(holding, stale);
      if (coverage.ready < coverage.total) coverageGapCount += 1;
      const thesis = holding.holdingThesis || EMPTY_HOLDING_THESIS;
      const riskState = contract.available
        ? contract.riskManagement.state
        : thesis.coreState === 'DAMAGED'
          ? 'REVIEW'
          : thesis.coreState === 'WEAKENED' || thesis.displayState === 'PRESERVED_CAUTION'
            ? 'CAUTION'
            : thesis.coreState === 'LIMITED'
              ? 'WATCH'
              : 'NORMAL';

      if (riskState === 'REVIEW') reviewCount += 1;
      else if (riskState === 'DEFENSE' || riskState === 'CAUTION') cautionCount += 1;
      else if ((contract.available ? contract.thesis.state : thesis.coreState) === 'PRESERVED') preservedCount += 1;
      else limitedCount += 1;

      const group = changeGroupByTicker.get(cleanText(holding.ticker));
      const changeCount = group?.eventCount ?? contract.changes.length;
      const nextCheck = contract.nextChecks[0]
        || thesis.recheckConditions[0]
        || thesis.monitorConditions[0]
        || '다음 확인 조건 산출 중';
      const invalidationTriggered = contract.available ? contract.invalidation.triggered : thesis.triggeredCount;
      const invalidationTotal = contract.available
        ? contract.invalidation.total
        : Math.max(thesis.invalidationConditions.length, thesis.monitorConditionCount);
      const thesisLabel = contract.available ? contract.thesis.label : thesis.coreLabel;
      const riskLabel = contract.available ? contract.riskManagement.label : (
        riskState === 'REVIEW' ? '재검토'
          : riskState === 'DEFENSE' ? '방어'
            : riskState === 'CAUTION' ? '경계'
              : riskState === 'WATCH' ? '관찰'
                : '정상'
      );
      const todayChanges = contract.available
        ? contract.changes.map((change) => change.label)
        : thesis.todayChanges;
      const companyEvidenceLabel = contract.available
        ? contract.companyEvidence.label
        : thesis.damageConfirmed
          ? '회사 논리 훼손 확인'
          : thesis.weakeningConfirmed
            ? '회사 근거 약화 징후'
            : thesis.contextIssuePending
              ? '신규 이슈 영향 검증 중'
              : thesis.verifiedBaselineAvailable
                ? '핵심 회사 근거 유지'
                : '회사 판단 근거 부족';
      const projectedImpactLabel = contract.available
        ? contract.projectedImpact.label
        : '예상 영향 확인 중';
      const projectedImpactTone = contract.available
        ? contract.projectedImpact.tone
        : 'neutral';
      const stressSpread = contract.available
        ? contract.projectedImpact.stressRoicWaccSpreadPctp
        : null;

      return {
        holding,
        contract,
        riskState,
        riskLabel,
        thesisLabel,
        changeCount,
        latestChange: group?.latest ?? null,
        todayChanges,
        companyEvidenceLabel,
        projectedImpactLabel,
        projectedImpactTone,
        stressSpread,
        invalidationTriggered,
        invalidationTotal,
        nextCheck,
        score: holdingRiskStageRank(riskState) * 100
          + Math.min(20, changeCount * 5)
          + Math.min(20, Math.abs(holding.changeRate ?? 0)),
      };
    }).sort((a, b) => b.score - a.score);

    return {
      total: state.holdings.length,
      importantChangeCount: holdingChangeBriefGroups.length,
      reviewCount,
      cautionCount,
      preservedCount,
      limitedCount,
      coverageGapCount,
      priorities: priorities.slice(0, 3),
    };
  }, [holdingChangeBriefGroups, state.holdings]);

  const holdingNameByTicker = useMemo(
    () => new Map(
      state.holdings.map((holding) => [
        cleanText(holding.ticker),
        cleanText(holding.name) || cleanText(holding.ticker),
      ]),
    ),
    [state.holdings],
  );

  const liveIssueBadgeIndex = useMemo(() => {
    const tickers = new Set<string>();
    const names = new Set<string>();

    liveIssueHead.forEach((issue) => {
      issue.relatedTickers.forEach((value) => {
        const ticker = normalizeWhyMovingTicker(value);
        if (ticker) tickers.add(ticker);
      });
      issue.relatedStockNames.forEach((value) => {
        const name = compactNameKey(value);
        if (name) names.add(name);
      });
      issue.structuredUnits.forEach((unit) => {
        const ticker = normalizeWhyMovingTicker(unit.ticker);
        const name = compactNameKey(unit.company);
        if (ticker) tickers.add(ticker);
        if (name) names.add(name);
      });
    });

    return { tickers, names };
  }, [liveIssueHead]);

  const selectedStock = useMemo(() => {
    if (!expandedStockKey.startsWith('stocks:')) return null;
    const key = expandedStockKey.slice('stocks:'.length);
    return filteredStocks.find((stock) => cleanText(stock.ticker || stock.name) === cleanText(key))
      ?? (pinnedStock && cleanText(pinnedStock.ticker || pinnedStock.name) === cleanText(key) ? pinnedStock : null);
  }, [expandedStockKey, filteredStocks, pinnedStock]);

  const changeDeepLinkHandledRef = useRef('');
  useEffect(() => {
    if (!authenticated || !requestedChangeEventId) return;
    const entityType = requestedChangeEntityType;
    const entityKey = requestedChangeEntityKey;
    const deepLinkKey = `${requestedChangeEventId}|${entityType}|${entityKey}`;

    if (changeDeepLinkHandledRef.current !== deepLinkKey) {
      changeDeepLinkHandledRef.current = deepLinkKey;
      trackUxEvent('change_deep_link_open', {
        event_id: requestedChangeEventId,
        entity_type: entityType || undefined,
        entity_key: entityKey || undefined,
        source: 'live-summary-deep-link',
      });
    }

    if (entityType === 'HOLDING' && entityKey) {
      setExploreOpen(false);
      setActiveTab('HOLDINGS');
      setExpandedStockKey(`holding:${entityKey}`);
      setFocusedHoldingChangeEventId(requestedChangeEventId);
      return;
    }
    if (entityType === 'STOCK' && entityKey) {
      setExploreOpen(false);
      setActiveTab('STOCKS');
      setStockSearch(entityKey);
      setExpandedStockKey(`stocks:${entityKey}`);
      return;
    }
    if (entityType === 'SECTOR' && entityKey) {
      setExploreOpen(true);
      setActiveTab('SECTORS');
      setSelectedKey(entityKey);
      return;
    }
    if (entityType === 'ISSUE') {
      setExploreOpen(true);
      setActiveTab('ISSUES');
      return;
    }
    setExploreOpen(false);
    setActiveTab('ALERTS');
  }, [
    authenticated,
    requestedChangeEntityKey,
    requestedChangeEntityType,
    requestedChangeEventId,
  ]);

  const clearStockSearch = useCallback(() => {
    setStockSearch('');
    setPinnedStock(null);
    setExpandedStockKey('');
    setStockSearchSnapshot({ query: '', rows: [] });
    setStockFallbackResults([]);
    setStockFallbackResolvedQuery('');
  }, []);

  const handleStockSearchChange = useCallback((value: string) => {
    setStockSearch(value);
    const keyword = cleanText(value).toLowerCase();
    if (!keyword) {
      setPinnedStock(null);
      setExpandedStockKey('');
      setStockSearchSnapshot({ query: '', rows: [] });
      return;
    }

    if (pinnedStock) {
      const stillMatches = [pinnedStock.name, pinnedStock.ticker, pinnedStock.sectorName]
        .map((item) => cleanText(item).toLowerCase())
        .some((item) => item.includes(keyword));
      if (!stillMatches) {
        setPinnedStock(null);
        if (expandedStockKey.startsWith('stocks:')) setExpandedStockKey('');
      }
    }
  }, [expandedStockKey, pinnedStock]);

  const riskStrip = useMemo(() => {
    const dedup = new Map<string, RiskStripCandidate>();

    const addCandidate = (candidate: RiskStripCandidate | null) => {
      if (!candidate) return;
      const key = cleanText(candidate.ticker || candidate.name);
      if (!key) return;
      const previous = dedup.get(key);
      if (!previous || candidate.score > previous.score || (candidate.holding && !previous.holding)) {
        dedup.set(key, candidate);
      }
    };

    state.holdings.forEach((holding) => {
      const sector = state.sectors.find(
        (item) => cleanText(item.industryName) === cleanText(holding.industryName),
      ) ?? null;
      addCandidate(immediateRiskCandidate(holding, state.marketContext, sector, true));
    });

    stockRows.forEach((stock) => {
      const sector = state.sectors.find((item) => item.industryName === stock.sectorName) ?? null;
      addCandidate(immediateRiskCandidate(stock, state.marketContext, sector, false));
    });

    const items = Array.from(dedup.values())
      .sort((a, b) => b.score - a.score)
      .slice(0, 2);

    const marketPressureScore = state.marketContext.marketPressureScore;

    const marketTone: Tone = !state.marketContext.available
      ? 'neutral'
      : marketPressureScore !== null
        ? marketPressureScore <= 30
          ? 'down'
          : marketPressureScore <= 42
            ? 'warn'
            : marketPressureScore >= 58
              ? 'up'
              : 'neutral'
        : state.marketContext.tone === 'down'
          ? 'down'
          : state.marketContext.tone === 'warn'
            ? 'warn'
            : state.marketContext.tone === 'up'
              ? 'up'
              : 'neutral';

    const marketPressureLabel = state.marketContext.available
      ? cleanText(state.marketContext.pressureLabel) || '시장 압력 확인 중'
      : '시장 압력 확인 중';

    return {
      tone: marketTone,
      marketLabel: '시장 압력',
      marketHeadline: state.marketContext.available
        ? `${state.marketOpen ? '장중' : '장 마감'} · ${marketPressureLabel}`
        : marketPressureLabel,
      items,
    };
  }, [state.holdings, state.marketContext, state.sectors, stockRows, state.marketOpen]);

  const summaryOpportunities = useMemo(() => {
    const blocked = new Set(
      riskStrip.items
        .map((item) => cleanText(item.ticker || item.name))
        .filter(Boolean),
    );
    const fresh = state.opportunities.filter((item) => !blocked.has(cleanText(item.ticker || item.name)));
    const repeated = state.opportunities.filter((item) => blocked.has(cleanText(item.ticker || item.name)));
    return [...fresh, ...repeated].slice(0, 3);
  }, [riskStrip.items, state.opportunities]);

  const summaryReservedTickerKeys = useMemo(() => {
    const keys = new Set<string>();
    const add = (ticker?: string | null, name?: string | null) => {
      const key = cleanText(ticker || name);
      if (key) keys.add(key);
    };

    riskStrip.items.forEach((item) => add(item.ticker, item.name));
    summaryOpportunities.forEach((item) => add(item.ticker, item.name));
    if (leadTheme?.representative) {
      add(leadTheme.representative.ticker, leadTheme.representative.name);
    }
    return keys;
  }, [leadTheme, riskStrip.items, summaryOpportunities]);

  const summaryQueuePriorityHolding = useMemo(() => (
    sortedHoldings.find((holding) => (
      isRiskAssessmentAttention(holding.riskAssessment)
      && !summaryReservedTickerKeys.has(cleanText(holding.ticker || holding.name))
    )) ?? null
  ), [sortedHoldings, summaryReservedTickerKeys]);

  const summaryQueueFocusStocks = useMemo(() => (
    focusStocks
      .filter((stock) => !summaryReservedTickerKeys.has(cleanText(stock.ticker || stock.name)))
      .slice(0, 2)
  ), [focusStocks, summaryReservedTickerKeys]);

  const currentFocusStock = summaryQueueFocusStocks[0] ?? null;
  const secondaryFocusStock = summaryQueueFocusStocks[1] ?? null;
  const currentFocusRisk = currentFocusStock
    ? stockRiskPriority(currentFocusStock, previousRiskSnapshotRef.current.get(currentFocusStock.ticker || currentFocusStock.name))
    : null;
  const secondaryFocusRisk = secondaryFocusStock
    ? stockRiskPriority(secondaryFocusStock, previousRiskSnapshotRef.current.get(secondaryFocusStock.ticker || secondaryFocusStock.name))
    : null;

  useEffect(() => {
    const keyword = stockSearch.trim();
    stockSearchAbortRef.current?.abort();

    if (!authenticated || activeTab !== 'STOCKS' || !keyword) {
      setStockFallbackResults([]);
      setStockFallbackLoading(false);
      setStockFallbackResolvedQuery('');
      setStockFallbackError('');
      return undefined;
    }

    if (radarSearchMatches.length > 0) {
      setStockFallbackResults([]);
      setStockFallbackLoading(false);
      setStockFallbackResolvedQuery('');
      setStockFallbackError('');

      const timer = window.setTimeout(() => {
        const trackKey = `${keyword.toLowerCase()}|RADAR|${radarSearchMatches.length}`;
        if (stockSearchTrackedRef.current === trackKey) return;
        stockSearchTrackedRef.current = trackKey;
        trackUxEvent('live_summary_stock_search_result', {
          query: keyword,
          source: 'radar',
          radarResultCount: radarSearchMatches.length,
          masterResultCount: 0,
          radarUniverseCount: stockRows.length,
          empty: false,
          marketSession: state.marketSession,
        });
      }, 350);

      return () => window.clearTimeout(timer);
    }

    setStockFallbackResults([]);
    setStockFallbackResolvedQuery('');
    setStockFallbackError('');
    setStockFallbackLoading(true);

    const controller = new AbortController();
    stockSearchAbortRef.current = controller;
    const timer = window.setTimeout(async () => {
      try {
        const rows = await searchLiveSummaryHoldings(keyword, {
          limit: 5,
          timeoutMs: 5_000,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        setStockFallbackResults(rows);
        setStockFallbackResolvedQuery(keyword);
        setStockFallbackError('');

        const trackKey = `${keyword.toLowerCase()}|MASTER|${rows.length}`;
        if (stockSearchTrackedRef.current !== trackKey) {
          stockSearchTrackedRef.current = trackKey;
          trackUxEvent('live_summary_stock_search_result', {
            query: keyword,
            source: 'master-fallback',
            radarResultCount: 0,
            masterResultCount: rows.length,
            radarUniverseCount: stockRows.length,
            empty: rows.length === 0,
            marketSession: state.marketSession,
          });
        }
      } catch (error) {
        const name = (error as { name?: string })?.name;
        if (name !== 'CanceledError' && name !== 'AbortError' && !controller.signal.aborted) {
          setStockFallbackResults([]);
          setStockFallbackResolvedQuery(keyword);
          setStockFallbackError('전체 종목 검색 서버 오류입니다. 잠시 후 다시 확인해 주세요.');

          const trackKey = `${keyword.toLowerCase()}|MASTER_ERROR`;
          if (stockSearchTrackedRef.current !== trackKey) {
            stockSearchTrackedRef.current = trackKey;
            trackUxEvent('live_summary_stock_search_result', {
              query: keyword,
              source: 'master-fallback',
              radarResultCount: 0,
              masterResultCount: 0,
              radarUniverseCount: stockRows.length,
              empty: true,
              error: true,
              marketSession: state.marketSession,
            });
          }
        }
      } finally {
        if (!controller.signal.aborted) setStockFallbackLoading(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    activeTab,
    authenticated,
    radarSearchMatches.length,
    state.marketSession,
    stockRows.length,
    stockSearch,
  ]);

  const loadIssues = useCallback(async (options?: {
    refresh?: boolean;
    bypassGap?: boolean;
    page?: number;
    query?: string;
    sector?: string;
    preferCache?: boolean;
    /** Background revalidation keeps the current DOM mounted and never shows a loading row. */
    silent?: boolean;
  }) => {
    const now = Date.now();
    const requestedPage = Math.max(1, Math.trunc(options?.page ?? issuePageRef.current));
    const requestedQuery = cleanText(options?.query ?? issueQueryRef.current).slice(0, 120);
    const requestedSector = cleanText(options?.sector ?? issueSectorRef.current).slice(0, 80);
    const refresh = options?.refresh ?? false;
    const bypassGap = options?.bypassGap ?? refresh;
    const preferCache = options?.preferCache ?? false;
    const silent = options?.silent ?? false;

    if (issueLoadingRef.current) {
      // Background refresh never interrupts a visible page/search request. A user action,
      // on the other hand, may cancel an older background request and take ownership.
      if (silent || !bypassGap) return;
      issueAbortRef.current?.abort();
    }
    if (
      !bypassGap
      && lastIssuesLoadedAtRef.current
      && now - lastIssuesLoadedAtRef.current < LIVE_SUMMARY_ISSUES_REFRESH_MS
    ) {
      return;
    }

    issueAbortRef.current?.abort();
    const controller = new AbortController();
    issueAbortRef.current = controller;
    issueLoadingRef.current = true;
    if (!silent) {
      setIssuePageError(false);
      setIssuePageLoading(true);
    }

    try {
      const response = await getLiveSummaryIssues({
        page: requestedPage,
        size: LIVE_SUMMARY_ISSUE_PAGE_SIZE,
        query: requestedQuery,
        sector: requestedSector,
        view: 'raw',
        refresh,
        preferCache,
        timeoutMs: LIVE_SUMMARY_ISSUES_TIMEOUT_MS,
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;
      if (!silent) setIssuePageError(false);

      const issues = normalizeLiveIssues(response.items);
      lastIssuesLoadedAtRef.current = Date.now();
      const responseSector = cleanText(response.selectedSector);
      const isLiveHead = response.page === 1 && !cleanText(response.query) && !responseSector;

      if (isLiveHead) {
        // STOCKS 탭의 이슈 표식 authority. 실패 시에는 마지막 정상 head를 유지한다.
        setLiveIssueHead(issues);
        if (!issueSeenInitializedRef.current) {
          const persisted = safeReadStringArray(issueSeenStorageKey);
          if (persisted === null) {
            const baseline = issues.map((issue) => issue.id);
            issueSeenKeysRef.current = new Set(baseline);
            safeWriteStringArray(issueSeenStorageKey, baseline);
          } else {
            issueSeenKeysRef.current = new Set(persisted);
          }
          issueSeenInitializedRef.current = true;
        }

        const unseen = issues.filter((issue) => (
          !issueSeenKeysRef.current.has(issue.id)
            && !issueNotifiedKeysRef.current.has(issue.id)
        ));

        if (activeTabRef.current === 'ISSUES') {
          const nextSeen = new Set(issueSeenKeysRef.current);
          issues.forEach((issue) => nextSeen.add(issue.id));
          issueSeenKeysRef.current = nextSeen;
          safeWriteStringArray(issueSeenStorageKey, Array.from(nextSeen));
          setIssueUnreadCount(0);
        } else if (unseen.length > 0) {
          unseen.forEach((issue) => issueNotifiedKeysRef.current.add(issue.id));
          setIssueUnreadCount((current) => Math.min(99, current + unseen.length));
          const newest = unseen[0];
          showActivityToast({
            kind: 'issue',
            title: unseen.length > 1 ? `새 실시간 이슈 ${unseen.length}건` : '새 실시간 이슈',
            message: unseen.length > 1
              ? `${newest.title} 외 ${unseen.length - 1}건이 추가되었습니다.`
              : newest.title,
            target: 'ISSUES',
          });
        }
      }

      // A background page-1 refresh is stale-while-revalidate: keep every rendered
      // issue mounted while the request is running, and once it resolves prepend only
      // genuinely new rows. This avoids the visible remove/rebuild flash every 5s.
      if (silent && (issuePageRef.current !== 1 || issueQueryRef.current || issueSectorRef.current)) return;

      issuePageRef.current = response.page;
      issueQueryRef.current = cleanText(response.query);
      issueSectorRef.current = responseSector;
      setIssuePage(response.page);
      setIssueSector(responseSector);
      setIssuePaging({
        page: response.page,
        size: response.size,
        totalElements: response.totalElements,
        totalPages: response.totalPages,
        hasPrevious: response.hasPrevious,
        hasNext: response.hasNext,
        query: response.query,
        selectedSector: responseSector,
        sectorFacets: response.sectorFacets,
      });
      setState((current) => {
        if (!silent || !isLiveHead) return { ...current, issues };

        const currentIds = new Set(current.issues.map((issue) => issue.id));
        const additions = issues.filter((issue) => !currentIds.has(issue.id));
        if (!additions.length) return current;

        return {
          ...current,
          issues: [...additions, ...current.issues].slice(0, response.size),
        };
      });

      // Warm the pages users are most likely to click next. At most two new pages
      // are requested, so this removes the ~2s navigation wait without fetching the
      // entire issue history. The API layer deduplicates a click that arrives while
      // a prefetch is still in flight.
      const candidates = response.totalPages > 1
        ? [
            response.page > 1 ? response.page - 1 : 0,
            response.page < response.totalPages ? response.page + 1 : 0,
            response.page !== 1 ? 1 : 0,
            response.page !== response.totalPages ? response.totalPages : 0,
          ]
        : [];
      const prefetchPages = Array.from(new Set(candidates.filter((page) => page > 0))).slice(0, 2);
      prefetchPages.forEach((page) => {
        void prefetchLiveSummaryIssues({
          page,
          size: LIVE_SUMMARY_ISSUE_PAGE_SIZE,
          query: response.query,
          sector: responseSector,
          view: 'raw',
          timeoutMs: LIVE_SUMMARY_ISSUES_TIMEOUT_MS,
        });
      });
    } catch (error) {
      const name = (error as { name?: string })?.name;
      if (controller.signal.aborted || name === 'CanceledError' || name === 'AbortError') return;
      if (!silent) setIssuePageError(true);
      console.warn('[LiveSummary] issues refresh failed', error);
    } finally {
      if (issueAbortRef.current === controller) {
        issueAbortRef.current = null;
        issueLoadingRef.current = false;
        if (!silent) setIssuePageLoading(false);
      }
    }
  }, [issueSeenStorageKey, showActivityToast]);

  const loadScannerActivity = useCallback(async () => {
    if (!authenticated || scannerActivityLoadingRef.current) return;
    scannerActivityLoadingRef.current = true;
    scannerActivityAbortRef.current?.abort();
    const controller = new AbortController();
    scannerActivityAbortRef.current = controller;

    try {
      const dashboard: ScannerDashboard = await getScannerDashboard(controller.signal);
      if (controller.signal.aborted) return;

      const current = new Map<string, ScannerActivityItem>();
      (dashboard.matches || []).forEach((match) => {
        const item = scannerActivityItem(match);
        if (item) current.set(item.key, item);
      });

      if (!scannerActivityInitializedRef.current) {
        const persisted = safeReadScannerActivity(scannerObservedStorageKey);
        if (persisted === null) {
          scannerPreviousMatchesRef.current = current;
          safeWriteScannerActivity(scannerObservedStorageKey, current);
          scannerActivityInitializedRef.current = true;
          return;
        }
        scannerPreviousMatchesRef.current = persisted;
        scannerActivityInitializedRef.current = true;
      }

      const previous = scannerPreviousMatchesRef.current;
      const entered = Array.from(current.values()).filter((item) => {
        const previousItem = previous.get(item.key);
        if (!previousItem) return true;
        return item.episodeNo > 0 && item.episodeNo > previousItem.episodeNo;
      });
      const exited = Array.from(previous.values()).filter((item) => !current.has(item.key));

      scannerPreviousMatchesRef.current = current;
      safeWriteScannerActivity(scannerObservedStorageKey, current);

      if (activeTabRef.current === 'SCANNER') {
        setScannerUnreadEnteredCount(0);
        setScannerUnreadExitedCount(0);
        return;
      }

      if (entered.length > 0) {
        setScannerUnreadEnteredCount((count) => Math.min(99, count + entered.length));
        const lead = entered[0];
        showActivityToast({
          kind: 'scanner-enter',
          title: entered.length > 1 ? `스캐너 신규 진입 ${entered.length}종목` : '스캐너 신규 진입',
          message: entered.length > 1
            ? `${lead.name} 외 ${entered.length - 1}종목이 저장한 조건에 진입했습니다.`
            : `${lead.name} · ${lead.ruleName} 조건에 진입했습니다.`,
          target: 'SCANNER',
        });
      } else if (exited.length > 0) {
        setScannerUnreadExitedCount((count) => Math.min(99, count + exited.length));
        const lead = exited[0];
        showActivityToast({
          kind: 'scanner-exit',
          title: exited.length > 1 ? `스캐너 조건 이탈 ${exited.length}종목` : '스캐너 조건 이탈',
          message: exited.length > 1
            ? `${lead.name} 외 ${exited.length - 1}종목이 조건에서 이탈했습니다.`
            : `${lead.name} · ${lead.ruleName} 조건에서 이탈했습니다.`,
          target: 'SCANNER',
        });
      }
    } catch (error) {
      const name = (error as { name?: string })?.name;
      if (controller.signal.aborted || name === 'CanceledError' || name === 'AbortError') return;
      console.warn('[LiveSummary] scanner activity refresh failed', error);
    } finally {
      if (scannerActivityAbortRef.current === controller) {
        scannerActivityAbortRef.current = null;
      }
      if (!controller.signal.aborted) scannerActivityLoadingRef.current = false;
    }
  }, [authenticated, scannerObservedStorageKey, showActivityToast]);

  const load = useCallback(async (force = false, bypassGap = force) => {
    /*
     * force=true:
     *   사용자가 명시적으로 새로고침한 경우 서버 SUMMARY cache까지 비운다.
     *
     * bypassGap=true, force=false:
     *   focus/visibility/online/pageshow 복귀 시 프론트 최소 간격만 우회하고
     *   V9의 서버 cache reuse 정책은 유지한다.
     */
    if (loadingRef.current) {
      if (!bypassGap) return;
      abortRef.current?.abort();
    }

    const now = Date.now();
    if (
      !bypassGap &&
      lastLoadedAtRef.current &&
      now - lastLoadedAtRef.current < MIN_REFRESH_GAP_MS
    ) {
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestSequence = ++requestSequenceRef.current;
    loadingRef.current = true;
    // Automatic 2s polling is background work. Keep the current panel interactive and
    // do not flip the global "갱신 중" state on every cycle; only initial/manual load
    // is user-visible.
    const visibleLoading = force || lastLoadedAtRef.current === 0;
    if (visibleLoading) setLoading(true);

    try {
      const response = await getLiveSummary({
        phase: 'AUTO',
        refresh: force,
        includeIssues: false,
        timeoutMs: LIVE_SUMMARY_TIMEOUT_MS,
        signal: controller.signal,
      });

      if (controller.signal.aborted || requestSequence !== requestSequenceRef.current) return;

      lastLoadedAtRef.current = Date.now();
      const normalized = normalizeResponse(response);
      const fastHoldingChanges = buildFastHoldingChangeEvents(
        latestFastHoldingsRef.current,
        normalized.holdings,
      );
      latestFastHoldingsRef.current = normalized.holdings;

      setState((current) => ({
        ...normalized,
        // 이슈는 별도 endpoint가 소유한다. 2초 SUMMARY polling이 마지막 정상 이슈를 지우지 않는다.
        issues: current.issues,
        // full HOLDINGS의 risk/box/30분 trend는 보존하고,
        // 현재가·거래대금·프로그램/외인/기관 최신 수급은 2초 snapshot으로 즉시 갱신한다.
        holdings: holdingsLoadedRef.current
          ? mergeFastHoldingSnapshots(current.holdings, normalized.holdings)
          : normalized.holdings,
        changeEvents: mergeChangeEvents(
          fastHoldingChanges,
          normalized.changeEvents,
          current.changeEvents,
        ),
      }));

      // Live Issue background refresh has its own 5s scheduler. Do not trigger a
      // second issue request from the 2s SUMMARY poll. A manual refresh still refreshes
      // issues, but silently keeps the current list mounted until the response arrives.
      if (force && issuePageRef.current === 1 && !issueQueryRef.current && !issueSectorRef.current) {
        void loadIssues({ refresh: true, bypassGap: true, silent: true, sector: '' });
      }
    } catch (error) {
      const name = (error as { name?: string })?.name;
      if (
        controller.signal.aborted ||
        requestSequence !== requestSequenceRef.current ||
        name === 'CanceledError' ||
        name === 'AbortError'
      ) {
        return;
      }

      const warning: NormalizedAlert = {
        level: 'warning',
        message: authErrorMessage(error),
      };
      setState((current) => ({
        ...current,
        sourceStatus: 'error',
        // Preserve the last confirmed response time on a failed request.
        alerts: current.alerts.some((row) => row.level === warning.level && row.message === warning.message)
          ? current.alerts
          : [warning, ...current.alerts].slice(0, 5),
      }));
    } finally {
      if (requestSequence === requestSequenceRef.current) {
        if (visibleLoading) setLoading(false);
        loadingRef.current = false;
      }
    }
  }, [loadIssues]);

  const loadFullHoldings = useCallback(async (force = false) => {
    if (!authenticated) return;
    if (!force && holdingsLoadedRef.current) return;

    fullHoldingsAbortRef.current?.abort();
    const controller = new AbortController();
    fullHoldingsAbortRef.current = controller;
    setDeferredLoading('HOLDINGS');

    try {
      const holdingRows = await getLiveSummaryHoldings({
        refresh: force,
        timeoutMs: 12_000,
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      const fullHoldings = holdingRows
        .map(normalizeHolding)
        .filter((item): item is NormalizedHolding => Boolean(item))
        .slice(0, 100);

      // full endpoint가 60초 주기의 무거운 계산 결과를 반환하더라도
      // 방금 받은 5초 fast snapshot보다 오래된 가격/수급으로 화면을 되돌리지 않는다.
      const holdings = mergeFastHoldingSnapshots(
        fullHoldings,
        latestFastHoldingsRef.current,
      );

      holdingsLoadedRef.current = true;
      setHoldingsLoaded(true);
      setState((current) => ({
        ...current,
        holdings,
      }));

      // 변화 이벤트 조회는 보유 목록 표시를 막지 않는다.
      const eventRows = await getLiveSummaryChangeEvents({
        limit: 50,
        timeoutMs: 5_000,
        signal: controller.signal,
      }).catch(() => []);

      if (controller.signal.aborted) return;

      const changeEvents = eventRows
        .map(normalizeChangeEvent)
        .filter((item): item is NormalizedChangeEvent => Boolean(item))
        .slice(0, 50);

      if (changeEvents.length) {
        setState((current) => ({
          ...current,
          changeEvents: mergeChangeEvents(changeEvents, current.changeEvents),
        }));
      }
    } catch (error) {
      const name = (error as { name?: string })?.name;
      if (name !== 'CanceledError' && name !== 'AbortError' && !controller.signal.aborted) {
        setHoldingMessage('보유 상세 데이터를 불러오지 못했습니다. 잠시 후 다시 확인해 주세요.');
      }
    } finally {
      if (!controller.signal.aborted) setDeferredLoading('');
    }
  }, [authenticated]);

  useEffect(() => {
    if (!authenticated) {
      fullHoldingsAbortRef.current?.abort();
      holdingsLoadedRef.current = false;
      latestFastHoldingsRef.current = [];
      setHoldingsLoaded(false);
      setDeferredLoading('');
      return undefined;
    }

    if (activeTab !== 'HOLDINGS') return undefined;

    void loadFullHoldings(false);

    // 무거운 보유 상세 계산은 사용자가 실제 HOLDINGS 탭을 보는 동안에만 60초 주기로 갱신한다.
    const timer = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void loadFullHoldings(true);
    }, 60_000);

    return () => window.clearInterval(timer);
  }, [activeTab, authenticated, loadFullHoldings]);

  useEffect(() => () => {
    fullHoldingsAbortRef.current?.abort();
  }, []);

  useEffect(() => () => {
    issueAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    const keyword = holdingSearch.trim();
    holdingSearchAbortRef.current?.abort();

    if (keyword.length < 1) {
      setHoldingSearchResults([]);
      setHoldingSearchLoading(false);
      setHoldingSearchError('');
      return undefined;
    }

    const controller = new AbortController();
    holdingSearchAbortRef.current = controller;
    const timer = window.setTimeout(async () => {
      setHoldingSearchLoading(true);
      setHoldingSearchError('');
      try {
        const rows = await searchLiveSummaryHoldings(keyword, {
          limit: 10,
          timeoutMs: 5_000,
          signal: controller.signal,
        });
        setHoldingSearchResults(rows);
        setHoldingSearchError('');
      } catch (error) {
        const name = (error as { name?: string })?.name;
        if (name !== 'CanceledError' && name !== 'AbortError') {
          setHoldingSearchResults([]);
          setHoldingSearchError('전체 종목 검색 서버 오류입니다. 검색 결과 없음과는 다른 상태입니다.');
        }
      } finally {
        if (!controller.signal.aborted) setHoldingSearchLoading(false);
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [holdingSearch]);

  const openHoldingFromSearch = useCallback(async (item: LiveSummaryHoldingSearchItem) => {
    if (!item.ticker) return;
    setStockSearch('');
    setPinnedStock(null);
    setStockSearchSnapshot({ query: '', rows: [] });
    setStockFallbackResults([]);
    setStockFallbackResolvedQuery('');
    setHoldingSearch('');
    setHoldingSearchResults([]);
    setHoldingMessage(`${item.name || item.ticker} 관심종목을 확인합니다.`);
    setActiveTab('HOLDINGS');
    setExpandedStockKey(`holding:${item.ticker}`);

    trackUxEvent('live_summary_watchlist_open', {
      ticker: item.ticker,
      name: item.name || item.ticker,
      source: 'stock-search',
    });

    await loadFullHoldings(true);
  }, [loadFullHoldings]);

  const addHolding = useCallback(async (
    item: LiveSummaryHoldingSearchItem,
    source: 'holding-search' | 'stock-search' = 'holding-search',
  ) => {
    if (!item.ticker || item.alreadyAdded || holdingMutationTicker || holdingMutationInFlightRef.current) return;
    holdingMutationInFlightRef.current = item.ticker;
    setHoldingMutationTicker(item.ticker);
    setHoldingMessage('');

    const analyticsSource = `live-summary-${source}`;
    trackUxEvent('stock_track_click', {
      ticker: item.ticker,
      stock_code: item.ticker,
      stock_name: item.name || item.ticker,
      source: analyticsSource,
      surface: 'live-summary-dock',
      funnel_version: 'organic-v3',
    });

    try {
      const mutation = await addLiveSummaryHolding(item.ticker);
      const stateChanged = mutation.action === 'CREATED' || mutation.action === 'RESTORED';
      const mutationEvent = mutation.action === 'CREATED'
        ? 'stock_track_created'
        : mutation.action === 'RESTORED'
          ? 'stock_track_restored'
          : 'stock_track_already_active';

      if (!stateChanged) {
        trackUxEvent(mutationEvent, {
          ticker: item.ticker,
          stock_code: item.ticker,
          stock_name: item.name || item.ticker,
          source: analyticsSource,
          surface: 'live-summary-dock',
          mutation_action: mutation.action,
          state_changed: false,
          funnel_version: 'organic-v3',
        });
        setHoldingMessage(`${item.name || item.ticker} 종목은 이미 관심종목에 등록되어 있습니다.`);
        setActiveTab('HOLDINGS');
        setExpandedStockKey(`holding:${item.ticker}`);
        await loadFullHoldings(true);
        return;
      }

      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        trackUxEvent('push_prompt_view', {
          ticker: item.ticker,
          stock_code: item.ticker,
          source: analyticsSource,
          activation: 'TRACK_AUTO',
        });
      }

      try {
        await requestTrackingWebPushPermission();
      } catch {
        // 웹 알림 권한 문제로 관심종목 등록 자체가 실패하면 안 된다.
      }

      let pushStatus: TrackedStockWebPushStatus = 'server_unavailable';
      try {
        pushStatus = await ensureTrackedStockWebPush();
      } catch {
        pushStatus = 'server_unavailable';
      }

      trackUxEvent(mutationEvent, {
        ticker: item.ticker,
        stock_code: item.ticker,
        stock_name: item.name || item.ticker,
        source: analyticsSource,
        surface: 'live-summary-dock',
        mutation_action: mutation.action,
        state_changed: true,
        push_status: pushStatus,
        funnel_version: 'organic-v3',
      });
      trackUxEvent('watchlist_save', {
        measurement_version: PAID_VALUE_MEASUREMENT_VERSION,
        view_date_kst: koreaTodayKey(),
        ticker: item.ticker,
        stock_code: item.ticker,
        stock_name: item.name || item.ticker,
        source: analyticsSource,
        result: mutation.action,
        push_status: pushStatus,
      });

      const pushReady = pushStatus === 'enabled' || pushStatus === 'already_enabled';
      const actionLabel = mutation.action === 'RESTORED' ? '다시 추가했습니다' : '추가했습니다';
      setHoldingMessage(
        pushReady
          ? `${item.name || item.ticker} 관심종목을 ${actionLabel}. 중요한 변화는 웹 알림으로 알려드립니다.`
          : `${item.name || item.ticker} 관심종목을 ${actionLabel}. 웹 알림을 사용할 수 없는 환경에서는 사이트에서 변화를 확인합니다.`,
      );
      setStockSearch('');
      setStockFallbackResults([]);
      setStockFallbackResolvedQuery('');
      setHoldingSearch('');
      setHoldingSearchResults([]);
      setActiveTab('HOLDINGS');
      setExpandedStockKey(`holding:${item.ticker}`);
      await loadFullHoldings(true);
    } catch {
      trackUxEvent('stock_track_failed', {
        ticker: item.ticker,
        stock_code: item.ticker,
        stock_name: item.name || item.ticker,
        source: analyticsSource,
        surface: 'live-summary-dock',
        funnel_version: 'organic-v3',
      });
      setHoldingMessage('관심종목을 추가하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      if (holdingMutationInFlightRef.current === item.ticker) {
        holdingMutationInFlightRef.current = '';
      }
      setHoldingMutationTicker('');
    }
  }, [holdingMutationTicker, loadFullHoldings]);

  const removeHolding = useCallback(async (holding: NormalizedHolding) => {
    if (!holding.ticker || holdingMutationTicker || holdingMutationInFlightRef.current) return;
    if (!window.confirm(`${holding.name}을(를) 보유 목록에서 삭제하시겠습니까?`)) return;

    holdingMutationInFlightRef.current = holding.ticker;
    setHoldingMutationTicker(holding.ticker);
    setHoldingMessage('');
    try {
      await removeLiveSummaryHolding(holding.ticker);
      setExpandedStockKey('');
      setHoldingMessage(`${holding.name} 종목을 보유 목록에서 삭제했습니다.`);
      await loadFullHoldings(true);
    } catch {
      setHoldingMessage('보유 종목을 삭제하지 못했습니다.');
    } finally {
      if (holdingMutationInFlightRef.current === holding.ticker) {
        holdingMutationInFlightRef.current = '';
      }
      setHoldingMutationTicker('');
    }
  }, [holdingMutationTicker, loadFullHoldings]);

  const normalizePopupTab = useCallback((tab?: string) => {
    if (tab === 'STOCK' || tab === 'STOCKS') return 'STOCKS';
    if (tab === 'HOLDING' || tab === 'HOLDINGS') return 'HOLDINGS';
    if (tab === 'ALERT' || tab === 'ALERTS') return 'ALERTS';
    if (tab === 'SCANNER' || tab === 'MY_SCANNER') return 'SCANNER';
    if (tab === 'OPPORTUNITY') return 'STOCKS';
    if (tab === 'ISSUES') return 'ISSUES';
    if (tab === 'SECTOR' || tab === 'SECTORS') return 'SECTORS';
    if (tab === 'THEME' || tab === 'THEMES') return 'STOCKS';
    if (tab === 'FUNDAMENTAL' || tab === 'FUNDAMENTALS') return 'STOCKS';
    return 'SUMMARY';
  }, []);

  const applyMainPanelTarget = useCallback((
    requestedTab: string,
    target: { ticker?: string; issueId?: string; issueQuery?: string },
  ) => {
    const normalizedTab = normalizePopupTab(requestedTab);
    const ticker = cleanText(target.ticker).replace(/\D/g, '');
    const issueId = cleanText(target.issueId);
    const issueQueryText = cleanText(target.issueQuery).slice(0, 120);

    if (normalizedTab === 'ISSUES') {
      setExploreOpen(true);
      setActiveTab('ISSUES');
      issuePageRef.current = 1;
      issueSectorRef.current = '';
      setIssuePage(1);
      setIssueSector('');
      setExpandedIssueId('');
      setPendingMainIssueFocus({ issueId, query: issueQueryText });
      if (issueQueryText) setIssueSearchInput(issueQueryText);
      return true;
    }

    if ((normalizedTab === 'STOCKS' || normalizedTab === 'HOLDINGS') && !authenticated) {
      return false;
    }

    if (normalizedTab === 'HOLDINGS' && /^\d{6}$/.test(ticker)) {
      setExploreOpen(false);
      setActiveTab('HOLDINGS');
      setExpandedStockKey(`holding:${ticker}`);
      return true;
    }

    if (normalizedTab === 'STOCKS' && /^\d{6}$/.test(ticker)) {
      setExploreOpen(false);
      setPinnedStock(null);
      setStockSearchSnapshot({ query: '', rows: [] });
      setStockFallbackResults([]);
      setStockFallbackResolvedQuery('');
      setStockSearch(ticker);
      setExpandedStockKey(`stocks:${ticker}`);
      setActiveTab('STOCKS');
      return true;
    }

    return false;
  }, [authenticated, normalizePopupTab]);

  useEffect(() => {
    if (!panelMode) return;
    if (!requestedPanelTicker && !requestedPanelIssueId && !requestedPanelIssueQuery) return;

    applyMainPanelTarget(requestedPopupTab || (requestedPanelTicker ? 'STOCKS' : 'ISSUES'), {
      ticker: requestedPanelTicker || undefined,
      issueId: requestedPanelIssueId || undefined,
      issueQuery: requestedPanelIssueQuery || undefined,
    });
  }, [
    applyMainPanelTarget,
    panelMode,
    requestedPanelIssueId,
    requestedPanelIssueQuery,
    requestedPanelTicker,
    requestedPopupTab,
  ]);

  const resolvePopupUrl = useCallback((
    requestedTab = 'SUMMARY',
    target: { ticker?: string; issueId?: string; issueQuery?: string } = {},
  ) => {
    const base = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
    const normalizedPath = `${base}${LIVE_SUMMARY_POPUP_PATH}`.replace(/\/{2,}/g, '/');
    const url = new URL(normalizedPath, window.location.origin);
    url.searchParams.set('liveSummaryMini', '1');
    url.searchParams.set('tab', normalizePopupTab(requestedTab));
    if (/^\d{6}$/.test(cleanText(target.ticker))) url.searchParams.set('ticker', cleanText(target.ticker));
    if (cleanText(target.issueId)) url.searchParams.set('issueId', cleanText(target.issueId));
    if (cleanText(target.issueQuery)) url.searchParams.set('issueQuery', cleanText(target.issueQuery).slice(0, 120));
    return url.toString();
  }, [normalizePopupTab]);

  const openStandalonePanel = useCallback((
    forceRefresh = false,
    source = 'launcher',
    requestedTab = 'SUMMARY',
    target: { ticker?: string; issueId?: string; issueQuery?: string } = {},
  ) => {
    const normalizedTab = normalizePopupTab(requestedTab);
    const width = 376;
    const height = Math.min(800, Math.max(620, window.screen.availHeight - 48));
    const screenLeft = Number.isFinite(window.screenX) ? window.screenX : 0;
    const screenTop = Number.isFinite(window.screenY) ? window.screenY : 0;
    const left = Math.max(0, screenLeft + window.outerWidth - width - 18);
    const top = Math.max(0, screenTop + 18);

    const features = [
      'popup=yes',
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      'resizable=yes',
      'scrollbars=no',
    ].join(',');

    const recordOpen = () => {
      trackUxEvent('live_summary_open', {
        source,
        tab: normalizedTab,
        authenticated,
      });
    };

    if (popupRef.current && !popupRef.current.closed) {
      if (forceRefresh || normalizedTab !== 'SUMMARY') {
        try {
          popupRef.current.location.replace(resolvePopupUrl(normalizedTab));
        } catch {
          popupRef.current.location.href = resolvePopupUrl(normalizedTab);
        }
      }
      popupRef.current.focus();
      setPopupBlocked(false);
      recordOpen();
      return;
    }

    const popup = window.open('', LIVE_SUMMARY_POPUP_NAME, features);
    if (!popup) {
      setPopupBlocked(true);
      trackUxEvent('live_summary_popup_blocked', { source, tab: normalizedTab });
      return;
    }

    popupRef.current = popup;
    setPopupBlocked(false);

    try {
      popup.document.title = 'PolicyIntel 시장 요약';
      popup.document.body.innerHTML =
        '<div style="font-family:system-ui;padding:18px;color:#44566c">실시간 시장 요약을 여는 중입니다...</div>';
      popup.location.replace(resolvePopupUrl(normalizedTab, target));
      popup.focus();
    } catch {
      popup.location.href = resolvePopupUrl(normalizedTab, target);
    }

    recordOpen();
  }, [authenticated, normalizePopupTab, resolvePopupUrl]);

  useEffect(() => {
    if (!embedded || !enabled || embeddedOpenTrackedRef.current) return;
    embeddedOpenTrackedRef.current = true;

    trackUxEvent('live_summary_open', {
      source: 'main-embedded',
      tab: 'SUMMARY',
      authenticated,
      embedded: true,
    });
  }, [authenticated, embedded, enabled]);

  useEffect(() => {
    // LiveSummary API는 SecurityConfig에서 authenticated()이므로
    // 비회원 패널에서는 절대 호출하지 않는다. 로그인 전에는 UI preview만 렌더링한다.
    if (!panelMode || !enabled || !authenticated) return;
    void load();
  }, [authenticated, enabled, panelMode, load]);

  useEffect(() => {
    if (!panelMode || !enabled || !authenticated) return undefined;

    const timer = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void load();
    }, REFRESH_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [authenticated, enabled, panelMode, load]);

  useEffect(() => {
    if (!panelMode || !enabled || !authenticated) return undefined;

    const refreshLiveIssueHead = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (issuePageRef.current !== 1 || issueQueryRef.current || issueSectorRef.current) return;
      void loadIssues({ silent: true, sector: '' });
    };

    // ISSUES tab owns its first visible load. Other tabs can warm the live head in the
    // background so opening the tab is immediate without showing a refresh cycle.
    if (activeTabRef.current !== 'ISSUES') refreshLiveIssueHead();
    const timer = window.setInterval(refreshLiveIssueHead, LIVE_SUMMARY_ISSUES_REFRESH_MS);

    return () => window.clearInterval(timer);
  }, [authenticated, enabled, panelMode, loadIssues]);

  useEffect(() => {
    if (!panelMode || !enabled || !authenticated) return undefined;

    void loadScannerActivity();
    const timer = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void loadScannerActivity();
    }, LIVE_SUMMARY_SCANNER_ACTIVITY_REFRESH_MS);

    return () => {
      window.clearInterval(timer);
      scannerActivityAbortRef.current?.abort();
      scannerActivityLoadingRef.current = false;
    };
  }, [authenticated, enabled, loadScannerActivity, panelMode]);

  useEffect(() => {
    if (activeTab === 'ISSUES') {
      markCurrentIssuesSeen(state.issues);
    }
    if (activeTab === 'SCANNER') {
      setScannerUnreadEnteredCount(0);
      setScannerUnreadExitedCount(0);
    }
  }, [activeTab, markCurrentIssuesSeen, state.issues]);

  useEffect(() => {
    if (!panelMode || !enabled || !authenticated) return undefined;

    let resumeTimer: number | undefined;
    const refreshOnResume = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

      // visibilitychange + focus가 연달아 발생하는 브라우저에서 중복 요청을 합친다.
      if (resumeTimer !== undefined) {
        window.clearTimeout(resumeTimer);
      }
      resumeTimer = window.setTimeout(() => {
        resumeTimer = undefined;
        // 서버 cache clear 없이 즉시 재조회한다.
        void load(false, true);
      }, 150);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshOnResume();
    };
    const handleFocus = () => refreshOnResume();
    const handleOnline = () => refreshOnResume();
    const handlePageShow = () => refreshOnResume();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      if (resumeTimer !== undefined) {
        window.clearTimeout(resumeTimer);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [authenticated, enabled, load, panelMode]);

  useEffect(() => {
    if (panelMode || !enabled || !defaultOpen) return;
    const timer = window.setTimeout(
      () => openStandalonePanel(false, 'default-open', 'SUMMARY'),
      0,
    );
    return () => window.clearTimeout(timer);
  }, [defaultOpen, enabled, openStandalonePanel, panelMode]);

  useEffect(() => {
    if (!standaloneMode) return undefined;

    const previousTitle = document.title;
    document.title = 'PolicyIntel 시장 요약';
    document.documentElement.classList.add('live-summary-popup-document');
    document.body.classList.add('live-summary-popup-body');

    return () => {
      document.title = previousTitle;
      document.documentElement.classList.remove('live-summary-popup-document');
      document.body.classList.remove('live-summary-popup-body');
    };
  }, [standaloneMode]);

  useEffect(() => {
    if (!panelMode || !enabled || !authenticated || activeTab !== 'ISSUES') return;
    void loadIssues({
      bypassGap: true,
      page: issuePage,
      query: issueQuery,
      sector: issueSector,
      preferCache: true,
    });
  }, [activeTab, authenticated, enabled, issuePage, issueQuery, issueSector, panelMode, loadIssues]);

  useEffect(() => {
    if (!pendingMainIssueFocus || activeTab !== 'ISSUES') return;

    const normalizedQuery = cleanText(pendingMainIssueFocus.query).toLowerCase();
    if (normalizedQuery && cleanText(issueQuery).toLowerCase() !== normalizedQuery) return;

    const exact = pendingMainIssueFocus.issueId
      ? state.issues.find((issue) => cleanText(issue.id) === pendingMainIssueFocus.issueId)
      : undefined;
    const byTitle = !exact && normalizedQuery
      ? state.issues.find((issue) => {
          const title = cleanText(issue.title).toLowerCase();
          return title === normalizedQuery
            || title.includes(normalizedQuery)
            || normalizedQuery.includes(title);
        })
      : undefined;
    const targetIssue = exact ?? byTitle;
    if (!targetIssue) return;

    setExpandedIssueId(targetIssue.id);
    setPendingMainIssueFocus(null);
  }, [activeTab, issueQuery, pendingMainIssueFocus, state.issues]);

  useEffect(() => {
    if (!panelMode || !enabled || activeTab !== 'SUMMARY') return undefined;
    void loadThemeLeaders();
    const timer = window.setInterval(() => {
      void loadThemeLeaders();
    }, 5_000);
    return () => {
      window.clearInterval(timer);
      themeLeaderAbortRef.current?.abort();
      themeLeaderLoadingRef.current = false;
    };
  }, [activeTab, enabled, loadThemeLeaders, panelMode]);

  useEffect(() => {
    if (!selectedKey && state.sectors[0]) {
      setSelectedKey(sectorKey(state.sectors[0]));
      return;
    }

    if (
      selectedKey &&
      !state.sectors.some((sector) => sectorKey(sector) === selectedKey)
    ) {
      setSelectedKey(state.sectors[0] ? sectorKey(state.sectors[0]) : '');
    }
  }, [selectedKey, state.sectors]);

  useEffect(() => {
    const next = new Map<string, StockRiskSnapshot>();
    state.sectors.forEach((sector) => {
      sector.topStocks.forEach((stock) => {
        const key = stock.ticker || stock.name;
        if (!key) return;
        next.set(key, {
          riskScore: stock.riskScore,
          flowTone: stock.flowDecisionTone,
          derivativeStructure: stock.riskDerivativeStructure,
        });
      });
    });
    previousRiskSnapshotRef.current = next;
  }, [state.refreshedAt, state.sectors]);

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const detail = (event as CustomEvent<{
        refresh?: boolean;
        source?: string;
        tab?: string;
        snapshot?: LiveSummaryApiResponse;
        ticker?: string;
        issueId?: string;
        issueQuery?: string;
      }>).detail;
      const requestedTab = normalizePopupTab(detail?.tab || 'SUMMARY');

      if (panelMode) {
        if (authenticated && detail?.snapshot) {
          const normalized = normalizeResponse(detail.snapshot);
          const fastHoldingChanges = buildFastHoldingChangeEvents(
            latestFastHoldingsRef.current,
            normalized.holdings,
          );
          latestFastHoldingsRef.current = normalized.holdings;
          lastLoadedAtRef.current = Date.now();
          setState((current) => ({
            ...normalized,
            issues: current.issues,
            holdings: holdingsLoadedRef.current
              ? mergeFastHoldingSnapshots(current.holdings, normalized.holdings)
              : normalized.holdings,
            changeEvents: mergeChangeEvents(
              fastHoldingChanges,
              normalized.changeEvents,
              current.changeEvents,
            ),
          }));
        }

        const hasMainTarget = Boolean(
          cleanText(detail?.ticker)
          || cleanText(detail?.issueId)
          || cleanText(detail?.issueQuery)
        );
        const targetApplied = hasMainTarget
          ? applyMainPanelTarget(requestedTab, {
              ticker: detail?.ticker,
              issueId: detail?.issueId,
              issueQuery: detail?.issueQuery,
            })
          : false;

        if (!targetApplied) {
          const protectedTab = ['SCANNER', 'STOCKS', 'HOLDINGS', 'ALERTS'].includes(requestedTab);
          if (!protectedTab || authenticated) {
            setActiveTab(requestedTab as 'SUMMARY' | 'ISSUES' | 'SECTORS' | 'SCANNER' | 'STOCKS' | 'HOLDINGS' | 'ALERTS');
          } else if (embedded) {
            requestProtectedTab(
              requestedTab === 'HOLDINGS' ? 'HOLDING' : requestedTab === 'ALERTS' ? 'ALERT' : 'STOCK',
            );
          }
        }
        if (detail?.refresh && authenticated) {
          // 패널 열기 자체는 강제 갱신 사유가 아니다.
          // 이미 MainPage에서 만들어진 서버 캐시를 그대로 재사용한다.
          void load(false);
        }
        return;
      }

      openStandalonePanel(
        Boolean(detail?.refresh),
        detail?.source || 'app-event',
        requestedTab,
        {
          ticker: detail?.ticker,
          issueId: detail?.issueId,
          issueQuery: detail?.issueQuery,
        },
      );
    };
    window.addEventListener('policy-intel:live-summary-open', handleOpen);
    return () => window.removeEventListener('policy-intel:live-summary-open', handleOpen);
  }, [
    applyMainPanelTarget,
    authenticated,
    embedded,
    load,
    normalizePopupTab,
    openStandalonePanel,
    panelMode,
    requestProtectedTab,
  ]);

  useEffect(() => {
    if (!panelMode || !authenticated) {
      authenticatedViewKeyRef.current = '';
      return;
    }

    if (!['SCANNER', 'STOCKS', 'HOLDINGS', 'ALERTS'].includes(activeTab)) return;

    const key = `${activeTab}:${pathname}`;
    if (authenticatedViewKeyRef.current === key) return;
    authenticatedViewKeyRef.current = key;

    trackUxEvent('live_summary_authenticated_view', {
      tab: activeTab,
      source: embedded ? 'main-risk-desk' : 'live-summary',
      standalone: standaloneMode,
      embedded,
    });
  }, [activeTab, authenticated, embedded, panelMode, pathname, standaloneMode]);

  useEffect(() => {
    if (!panelMode || !authenticated || activeTab !== 'HOLDINGS' || !holdingsLoaded) return;

    const today = koreaTodayKey();
    const latestEventId = cleanText(holdingChangeBriefGroups[0]?.latest?.id);
    const status = state.sourceStatus === 'error' ? 'ERROR' : 'OK';
    const completeKey = `${today}:${latestEventId}:${holdingChangeBriefGroups.length}:${holdingChangeBriefEventCount}:${status}`;

    if (stockBriefCompleteKeyRef.current !== completeKey) {
      stockBriefCompleteKeyRef.current = completeKey;
      trackUxEvent('stock_brief_complete', {
        measurement_version: PAID_VALUE_MEASUREMENT_VERSION,
        view_date_kst: today,
        source: 'live-summary-holdings-panel',
        stock_count: holdingChangeBriefGroups.length,
        change_count: holdingChangeBriefEventCount,
        latest_event_id: latestEventId || undefined,
        status,
      });
    }

    // 정확한 활성사용자/재방문 authority:
    // 조회 오류인 경우 stock_brief_view를 기록하지 않는다.
    if (status !== 'OK') {
      const errorViewKey = `${today}:${pathname}:ERROR`;
      if (stockBriefViewKeyRef.current !== errorViewKey) {
        stockBriefViewKeyRef.current = errorViewKey;
        trackUxEvent('stock_brief_load_error', {
          measurement_version: PAID_VALUE_MEASUREMENT_VERSION,
          view_date_kst: today,
          source: 'live-summary-holdings-panel',
        });
      }
      return;
    }

    // 하루 1회 사용자-일(user-day) authority.
    // 상세 change 클릭은 별도 stock_brief_change_open으로 분리한다.
    const viewKey = `${today}:${pathname}:OK`;
    if (stockBriefViewKeyRef.current === viewKey) return;
    stockBriefViewKeyRef.current = viewKey;

    trackUxEvent('stock_brief_view', {
      measurement_version: PAID_VALUE_MEASUREMENT_VERSION,
      view_date_kst: today,
      source: 'live-summary-holdings-panel',
      stock_count: holdingChangeBriefGroups.length,
      change_count: holdingChangeBriefEventCount,
      latest_event_id: latestEventId || undefined,
      status: 'OK',
    });

    // return_brief_view는 브라우저 진단 이벤트일 뿐,
    // 서버 retention KPI는 stock_brief_view의 user_id + view_date_kst로 재계산한다.
    try {
      const storageKey = `policy-intel:stock-brief-last-view-date:${PAID_VALUE_MEASUREMENT_VERSION}`;
      const previousDate = window.localStorage.getItem(storageKey);
      if (previousDate && previousDate !== today) {
        trackUxEvent('return_brief_view', {
          measurement_version: PAID_VALUE_MEASUREMENT_VERSION,
          view_date_kst: today,
          source: 'live-summary-holdings-panel',
          previous_view_date: previousDate,
          visit_date: today,
          authority: 'CLIENT_DIAGNOSTIC_ONLY',
        });
      }
      window.localStorage.setItem(storageKey, today);
    } catch {
      // Storage 제한 환경에서는 server-side stock_brief_view authority만 유지한다.
    }
  }, [
    activeTab,
    authenticated,
    holdingChangeBriefEventCount,
    holdingChangeBriefGroups,
    holdingsLoaded,
    panelMode,
    pathname,
    state.sourceStatus,
  ]);

  useEffect(() => () => {
    requestSequenceRef.current += 1;
    abortRef.current?.abort();
    themeLeaderAbortRef.current?.abort();
    themeLeaderLoadingRef.current = false;
    loadingRef.current = false;
  }, []);

  useEffect(() => {
    if (!panelMode || typeof document === 'undefined') {
      setOffhoursProxy(null);
      return undefined;
    }

    let disposed = false;
    let controller: AbortController | null = null;

    const poll = async () => {
      if (document.visibilityState === 'hidden') return;
      controller?.abort();
      controller = new AbortController();
      try {
        const payload = await getOffhoursMarketProxy({ timeoutMs: 4_000, signal: controller.signal });
        if (disposed) return;
        setOffhoursProxy(payload?.mode === 'ACTIVE' && payload?.displayEligible ? payload : null);
      } catch (error) {
        if (disposed || (error instanceof DOMException && error.name === 'AbortError')) return;
        setOffhoursProxy(null);
      }
    };

    void poll();
    const timer = window.setInterval(() => { void poll(); }, 15_000);
    const onVisibility = () => { if (document.visibilityState === 'visible') void poll(); };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      disposed = true;
      controller?.abort();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [panelMode]);

  if (!enabled && !panelMode) return null;

  // 비회원은 잠금 목록이 아니라 실제 무료 범위를 먼저 이해한다.
  // 상세/개인화 데이터는 인증 뒤에만 LiveSummary API에서 이어진다.
  if (panelMode && !authenticated) {
    const guestCardStyle: React.CSSProperties = {
      border: '1px solid #d9e3ee',
      borderRadius: 14,
      background: '#ffffff',
      padding: 14,
      minWidth: 0,
      boxShadow: '0 8px 22px rgba(21,42,65,.05)',
    };
    const blurLine = (width: string) => (
      <span aria-hidden="true" style={{ display: 'block', width, height: 10, marginTop: 7, borderRadius: 5, background: '#dce4ec', filter: 'blur(3px)', opacity: .86 }} />
    );

    return (
      <div className={`live-summary-root ${standaloneMode ? 'is-standalone' : 'is-embedded'} is-terminal-pro is-market-grid-v2 is-modern-workspace-v1 is-guest-preview`}>
        <aside className={`live-summary-panel ${standaloneMode ? 'is-standalone' : 'is-embedded'} is-guest-preview`}>
          <header className="live-summary-header">
            <div className="live-summary-title-wrap">
              <div className="live-summary-title-line">
                <h2>Position Risk Desk</h2>
                <span className="live-summary-exclusive-badge">GUEST</span>
              </div>
            </div>
            <div className="live-summary-header-actions">
              {standaloneMode && (
                <button
                  type="button"
                  className="is-close"
                  onClick={() => {
                    const handled = requestUserFeedback({
                      source: 'PANEL',
                      trigger: 'PANEL_CLOSE',
                      context: { mode: 'GUEST_PREVIEW' },
                      closeWindowAfter: true,
                    });
                    if (!handled) window.close();
                  }}
                  aria-label="패널 닫기"
                  title="패널 닫기"
                >
                  <PanelIcon name="close" />
                </button>
              )}
            </div>
          </header>

          <main className="live-summary-content" style={{ padding: 14, background: '#f4f7fa' }}>
            <section style={{ borderRadius: 18, padding: '20px 18px', background: '#14283d', color: '#fff', boxShadow: '0 14px 30px rgba(20,40,61,.16)' }} aria-labelledby="guest-risk-desk-title">
              <span style={{ display: 'inline-flex', padding: '4px 8px', borderRadius: 999, background: 'rgba(255,255,255,.10)', color: '#cbd8e5', fontSize: 9, fontWeight: 900, letterSpacing: '.08em' }}>FREE BEFORE LOGIN</span>
              <h1 id="guest-risk-desk-title" style={{ margin: '10px 0 7px', color: '#fff', fontSize: 22, lineHeight: 1.22, letterSpacing: '-.035em' }}>종목 하나는 로그인 없이<br />실제 판단까지 확인합니다.</h1>
              <p style={{ margin: 0, maxWidth: 560, color: '#b9c8d6', fontSize: 11.5, lineHeight: 1.58 }}>현재 판단, 최근 이슈·공시 1건, 재무 상태 요약을 먼저 확인하세요. 로그인은 데이터를 보기 위한 입구가 아니라 전체 근거와 지속 추적을 연결하는 단계입니다.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.35fr) minmax(150px,.65fr)', gap: 8, marginTop: 14 }}>
                <button type="button" onClick={() => window.location.assign('/stocks')} style={{ minHeight: 42, border: 0, borderRadius: 10, background: '#ffffff', color: '#173f65', fontSize: 11, fontWeight: 950, cursor: 'pointer' }}>종목 검색해서 무료 확인</button>
                <button type="button" onClick={() => requestProtectedTab('STOCK')} style={{ minHeight: 42, border: '1px solid rgba(255,255,255,.28)', borderRadius: 10, background: 'rgba(255,255,255,.07)', color: '#fff', fontSize: 10, fontWeight: 900, cursor: 'pointer' }}>내 종목 연결</button>
              </div>
            </section>

            <section style={{ marginTop: 11 }} aria-labelledby="guest-preview-title">
              <div style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 10, marginBottom: 7 }}>
                <div>
                  <small style={{ color: '#788a9c', fontSize: 8.5, fontWeight: 900, letterSpacing: '.09em' }}>WHAT YOU SEE FREE</small>
                  <h3 id="guest-preview-title" style={{ margin: '3px 0 0', color: '#20364c', fontSize: 14 }}>로그인 전에 여기까지 봅니다</h3>
                </div>
                <span style={{ color: '#087f5b', fontSize: 9, fontWeight: 900 }}>실제 데이터 일부 공개</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 7 }}>
                <article style={guestCardStyle}>
                  <small style={{ color: '#8293a4', fontSize: 8, fontWeight: 900 }}>01 · 현재 판단</small>
                  <strong style={{ display: 'block', marginTop: 6, color: '#173f65', fontSize: 15 }}>FREE</strong>
                  <p style={{ margin: '6px 0 0', color: '#617589', fontSize: 9.5, lineHeight: 1.45 }}>기업가치·투자논리·시장 반영의 현재 상태와 핵심 이유</p>
                </article>
                <article style={guestCardStyle}>
                  <small style={{ color: '#8293a4', fontSize: 8, fontWeight: 900 }}>02 · 최근 이슈/공시</small>
                  <strong style={{ display: 'block', marginTop: 6, color: '#173f65', fontSize: 15 }}>1건 공개</strong>
                  {blurLine('90%')}{blurLine('68%')}
                  <p style={{ margin: '7px 0 0', color: '#8293a4', fontSize: 8.5 }}>영향 분석·과거 이력은 로그인 후</p>
                </article>
                <article style={guestCardStyle}>
                  <small style={{ color: '#8293a4', fontSize: 8, fontWeight: 900 }}>03 · 재무 상태</small>
                  <strong style={{ display: 'block', marginTop: 6, color: '#173f65', fontSize: 15 }}>핵심 공개</strong>
                  {blurLine('78%')}{blurLine('88%')}
                  <p style={{ margin: '7px 0 0', color: '#8293a4', fontSize: 8.5 }}>상세 수치·추이는 로그인 후</p>
                </article>
              </div>
            </section>

            <section style={{ ...guestCardStyle, marginTop: 10, padding: 15, background: '#fffaf1', borderColor: '#f0dfbd' }}>
              <small style={{ color: '#9a6a1c', fontSize: 8.5, fontWeight: 950 }}>로그인 후 달라지는 것</small>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 7, marginTop: 9 }}>
                {[
                  ['전체 근거', '이슈·재무·사업 근거 상세'],
                  ['과거 변화', '이전 판단과 변화 이력'],
                  ['지속 감시', '판단이 바뀌는 순간 알림'],
                ].map(([title, description]) => (
                  <div key={title} style={{ padding: 10, borderRadius: 10, background: '#fff', border: '1px solid #f0e2c7' }}>
                    <b style={{ display: 'block', color: '#4e3f27', fontSize: 10 }}>{title}</b>
                    <span style={{ display: 'block', marginTop: 4, color: '#78684e', fontSize: 8.8, lineHeight: 1.4 }}>{description}</span>
                  </div>
                ))}
              </div>
            </section>

            <section style={{ marginTop: 11, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 9, alignItems: 'center', padding: 12, borderRadius: 13, background: '#ffffff', border: '1px solid #d9e3ee' }}>
              <div>
                <b style={{ color: '#25384c', fontSize: 10.5 }}>먼저 무료로 확인한 뒤 판단하세요.</b>
                <p style={{ margin: '3px 0 0', color: '#7a8b9d', fontSize: 8.8, lineHeight: 1.42 }}>개인화·보유종목·알림만 계정에 연결합니다.</p>
              </div>
              <button type="button" onClick={() => window.location.assign('/stocks')} style={{ minHeight: 36, padding: '0 13px', border: 0, borderRadius: 9, background: '#2962ff', color: '#fff', fontSize: 9.5, fontWeight: 950, cursor: 'pointer', whiteSpace: 'nowrap' }}>무료 종목 확인</button>
            </section>
          </main>
          <footer className="live-summary-footer">
            <div>
              <i className="is-ready" />
              비로그인 피드백 가능
            </div>
            <UserFeedbackLauncher
              source="PANEL"
              variant="panel"
              context={{ mode: 'GUEST_PREVIEW' }}
            />
            <span>GUEST</span>
          </footer>
        </aside>
      </div>
    );
  }

  if (!panelMode) {
    return (
      <div className="live-summary-root is-launcher-only is-terminal-pro is-market-grid-v2 is-modern-workspace-v1">
        <button
          type="button"
          className={`live-summary-trigger ${popupBlocked ? 'is-blocked' : ''}`}
          data-tour-id="live-summary-trigger"
          onClick={() => openStandalonePanel(false, 'launcher', 'SUMMARY')}
          aria-label="실시간 시장 요약 패널 열기"
          title={popupBlocked ? '팝업 허용 후 다시 여세요.' : '실시간 시장 요약 패널 열기'}
        >
          <span className="live-summary-trigger__pulse" />
          <span className="live-summary-trigger__icon" aria-hidden="true"><PanelIcon name="panel" /></span>
          <span className="live-summary-trigger__label">MARKET</span>
          {authenticated && (
            <span className="live-summary-trigger__initial">{initials}</span>
          )}
        </button>

        {popupBlocked && (
          <button
            type="button"
            className="live-summary-popup-blocked"
            onClick={() => openStandalonePanel(false, 'popup-blocked-retry', 'SUMMARY')}
          >
            팝업 허용 후 다시 열기
          </button>
        )}
      </div>
    );
  }


  return (
    <div className={`live-summary-root ${standaloneMode ? 'is-standalone' : 'is-embedded'} is-terminal-pro is-market-grid-v2 is-modern-workspace-v1 is-focus-v3 is-direction-v4`} data-product-access={productAccess.mode} data-pro-entitled={productAccess.proEntitled ? '1' : '0'}>
      <aside className={`live-summary-panel ${standaloneMode ? 'is-standalone' : 'is-embedded'} ${minimized ? 'is-minimized' : ''}`}>
        <header className="live-summary-header">
          <div className="live-summary-title-wrap">
            <div className="live-summary-title-line">
              <h2>
                {embedded
                  ? 'PolicyIntel'
                  : minimized
                    ? '시장 판단 Mini'
                    : '시장 판단'}
              </h2>
            </div>
          </div>

          <div className="live-summary-header-actions">
            <button
              type="button"
              className="is-refresh"
              onClick={() => load(true)}
              disabled={loading}
              aria-label="새로고침"
              title="데이터 새로고침"
            >
              <PanelIcon name="refresh" />
            </button>
            {standaloneMode && (
              <>
                <button
                  type="button"
                  className="is-window"
                  onClick={() => setMinimized((value) => !value)}
                  aria-label={minimized ? '확대' : '최소화'}
                  title={minimized ? '확대' : '최소화'}
                >
                  <PanelIcon name={minimized ? 'maximize' : 'minimize'} />
                </button>
                <button
                  type="button"
                  className="is-close"
                  onClick={() => {
                    const handled = requestUserFeedback({
                      source: 'PANEL',
                      trigger: 'PANEL_CLOSE',
                      ticker: selectedStock?.ticker || pinnedStock?.ticker || undefined,
                      context: {
                        activeTab,
                        minimized,
                        sourceStatus: state.sourceStatus,
                        marketOpen: state.marketOpen,
                      },
                      closeWindowAfter: true,
                    });
                    if (!handled) window.close();
                  }}
                  aria-label="패널 닫기"
                  title="패널 닫기"
                >
                  <PanelIcon name="close" />
                </button>
              </>
            )}
          </div>
        </header>

        {minimized ? (
          <div className="live-summary-mini-strip">
            <div>
              <b>{leadSector?.industryName || '업종 확인 중'}</b>
              <small>
                {leadSector
                  ? `${formatPct(leadSector.changeRate)} · 확산 ${sectorBreadthText(leadSector.breadthPct)}`
                  : '선택 업종과 종목을 확인하세요.'}
              </small>
            </div>
            <em>{loading ? '갱신중' : state.refreshedAt || '-'}</em>
            <UserFeedbackLauncher
              source="PANEL"
              variant="panel"
              ticker={selectedStock?.ticker || pinnedStock?.ticker || undefined}
              context={{ activeTab, minimized: true, sourceStatus: state.sourceStatus }}
            />
          </div>
        ) : (
          <>
            {(riskStrip.marketHeadline || riskStrip.items.length > 0) && (
              <section className={`live-summary-risk-ticker ${toneClass(riskStrip.tone)}`} aria-label="실시간 위험 요약">
                <div className="live-summary-risk-ticker__line">
                  <span className="live-summary-risk-ticker__market">
                    <b>{riskStrip.marketLabel}</b>
                    {riskStrip.marketHeadline && <em>{riskStrip.marketHeadline}</em>}
                  </span>
                  {riskStrip.items.slice(0, 2).map((candidate) => (
                    <button
                      type="button"
                      key={candidate.key}
                      className={toneClass(candidate.tone)}
                      onClick={() => {
                        if (candidate.holding && authenticated) {
                          openProtectedTab('HOLDINGS');
                        } else {
                          setPinnedStock(null);
                          setExpandedStockKey('');
                          setStockSearchSnapshot({ query: '', rows: [] });
                          openProtectedTab('STOCKS');
                          if (candidate.ticker) setStockSearch(candidate.ticker);
                        }
                      }}
                    >
                      <b>{candidate.name}</b>
                      <span>{candidate.label}</span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            <nav className="live-summary-tabs is-primary-five" aria-label="시장 요약 주요 메뉴">
              <button className={activeTab === 'SUMMARY' ? 'is-active' : ''} onClick={() => { setExploreOpen(false); setActiveTab('SUMMARY'); }}>오늘</button>
              <button
                className={`${['ISSUES', 'SECTORS', 'SCANNER'].includes(activeTab) ? 'is-active' : ''} ${(issueUnreadCount + scannerUnreadEnteredCount + scannerUnreadExitedCount) > 0 ? 'has-unread' : ''}`}
                onClick={() => setExploreOpen((open) => !open)}
                aria-expanded={exploreOpen}
              >
                탐색
                {(issueUnreadCount + scannerUnreadEnteredCount + scannerUnreadExitedCount) > 0 && (
                  <span className="live-summary-unread-badge" aria-label={`새 탐색 알림 ${issueUnreadCount + scannerUnreadEnteredCount + scannerUnreadExitedCount}건`}>
                    {Math.min(99, issueUnreadCount + scannerUnreadEnteredCount + scannerUnreadExitedCount)}
                  </span>
                )}
              </button>
              <button className={activeTab === 'STOCKS' ? 'is-active' : ''} onClick={() => { setExploreOpen(false); openProtectedTab('STOCKS'); }}>종목</button>
              <button className={activeTab === 'HOLDINGS' ? 'is-active' : ''} onClick={() => { setExploreOpen(false); openProtectedTab('HOLDINGS'); }}>보유 {authenticated && <b>{state.holdings.length}</b>}</button>
              <button className={`${activeTab === 'ALERTS' ? 'is-active' : ''} ${authenticated && state.attentionBanner.visible ? 'has-urgent' : ''}`} onClick={() => { setExploreOpen(false); openProtectedTab('ALERTS'); }}>알림 {authenticated && state.alerts.length > 0 && <b>{state.alerts.length}</b>}</button>
            </nav>

            {exploreOpen && (
              <nav className="live-summary-explore-nav" aria-label="탐색 하위 메뉴">
                <button
                  type="button"
                  className={`${activeTab === 'ISSUES' ? 'is-active' : ''} ${issueUnreadCount > 0 ? 'has-unread' : ''}`}
                  onClick={() => { setIssueUnreadCount(0); setActiveTab('ISSUES'); }}
                >
                  이슈
                  {issueUnreadCount > 0 && <span className="live-summary-unread-badge">{Math.min(99, issueUnreadCount)}</span>}
                </button>
                <button type="button" className={activeTab === 'SECTORS' ? 'is-active' : ''} onClick={() => setActiveTab('SECTORS')}>업종</button>
                <button type="button" onClick={() => { setStockSort('VALUE'); setExploreOpen(false); openProtectedTab('STOCKS'); }}>거래대금·테마</button>
                <button
                  type="button"
                  className={`${activeTab === 'SCANNER' ? 'is-active' : ''} ${(scannerUnreadEnteredCount + scannerUnreadExitedCount) > 0 ? 'has-unread' : ''}`}
                  onClick={() => { setScannerUnreadEnteredCount(0); setScannerUnreadExitedCount(0); openProtectedTab('SCANNER'); }}
                >
                  스캐너
                  {scannerUnreadEnteredCount > 0 && <span className="live-summary-unread-badge is-scanner-enter">+{Math.min(99, scannerUnreadEnteredCount)}</span>}
                  {scannerUnreadExitedCount > 0 && <span className="live-summary-unread-badge is-scanner-exit">−{Math.min(99, scannerUnreadExitedCount)}</span>}
                </button>
              </nav>
            )}

            <main className={`live-summary-content is-${activeTab.toLowerCase()}-tab`}>

              {activeTab === 'STOCKS' && (
                <FocusResearchDisclosure key={activeTab} label="상세 근거">
                  <LiveSummaryIntelligencePanel
                  data={state.intelligenceBridge}
                  defaultTab="STOCK"
                  onSector={(key) => {
                    const sector = state.sectors.find((item) => `${item.marketType}:${item.industryCode}` === key);
                    if (sector) setSelectedKey(sectorKey(sector));
                    setActiveTab('SECTORS');
                    setExploreOpen(false);
                  }}
                  onStock={(ticker) => {
                    setStockSearch(ticker);
                    openProtectedTab('STOCKS');
                    setExploreOpen(false);
                  }}
                  />
                </FocusResearchDisclosure>
              )}

              {activeTab === 'SUMMARY' && <LiveSummaryDecisionBriefCard brief={state.decisionBrief} optionResearch={state.optionResearch} />}

              {activeTab === 'SUMMARY' && (
                <div className="pi-direction-home" aria-label="시장과 내 종목 핵심 판단">
                  {!authenticated && (
                    <div className="pi-direction-home__guest">
                      <span>내 종목 변화까지 확인</span>
                      <button type="button" onClick={() => requestProtectedTab('STOCK')}>로그인 후 이어보기 <span aria-hidden="true">↗</span></button>
                    </div>
                  )}
                  <section className="pi-direction-home__context" aria-label="시장 기준">
                    <div className="pi-direction-home__context-top">
                      <span className="pi-direction-home__section-caption">시장 기준</span>
                      <span className="pi-direction-home__session">{state.marketOpen ? '장중' : '최근 장 마감'}</span>
                    </div>
                    <p>{state.marketOpen && !marketContextActionable
                      ? '실시간 시장 상태 확인 중'
                      : state.marketContext.available
                        ? state.marketContext.pressureLabel || state.marketContext.headline || '시장 상태 확인 중'
                        : '시장 판단에 필요한 자료 확인 중'}</p>
                    <div className="pi-direction-home__context-foot">
                      <span>기준 {state.marketOpen ? (marketContextActionable ? '확인된 실시간 시세' : '실시간 자료 확인 중') : '마지막 유효 거래'}</span>
                      <button type="button" onClick={() => setActiveTab('SECTORS')}>업종 흐름 <span aria-hidden="true">↗</span></button>
                    </div>
                  </section>
                  {authenticated && (
                    <section className="pi-direction-home__holdings" aria-label="먼저 확인할 내 종목">
                      <div className="pi-direction-home__section-head">
                        <div>
                          <span className="pi-direction-home__section-caption">보유·관심</span>
                          <h3>먼저 볼 종목</h3>
                        </div>
                        <button type="button" onClick={() => openProtectedTab('HOLDINGS')}>전체 {dailyPortfolioOutcome.total} <span aria-hidden="true">↗</span></button>
                      </div>
                      {state.sourceStatus === 'error' ? (
                        <p className="pi-direction-home__empty">종목 정보를 불러오지 못했습니다.</p>
                      ) : dailyPortfolioOutcome.priorities.length ? (
                        <div className="pi-direction-home__holding-list">
                          {dailyPortfolioOutcome.priorities.slice(0, 2).map((priority) => {
                            const { holding } = priority;
                            return (
                              <button
                                type="button"
                                key={`pi-direction-${holding.ticker}`}
                                className="pi-direction-home__holding"
                                onClick={() => {
                                  setActiveTab('HOLDINGS');
                                  setExpandedStockKey(`holding:${holding.ticker}`);
                                  setFocusedHoldingChangeEventId(priority.latestChange ? cleanText(priority.latestChange.id) : '');
                                  setBoxPopupTicker('');
                                }}
                              >
                                <span className="pi-direction-home__holding-main">
                                  <b>{holding.name}</b>
                                  <span>{priority.thesisLabel} · {priority.riskLabel}</span>
                                </span>
                                <span className="pi-direction-home__holding-right">
                                  <strong className={toneClass(holding.statusTone)}>{formatPct(holding.changeRate, 2)}</strong>
                                  <span aria-hidden="true">↗</span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="pi-direction-home__empty">확인이 필요한 보유 변화가 없습니다.</p>
                      )}
                    </section>
                  )}
                  <section className="pi-direction-home__changes" aria-label="최근 판단 및 이슈 변화">
                    <div className="pi-direction-home__section-head">
                      <div>
                        <span className="pi-direction-home__section-caption">
                          {holdingJudgmentChangeBriefGroups.length ? '내 종목 판단' : '이슈 흐름'}
                        </span>
                        <h3>최근 변화</h3>
                      </div>
                      <button type="button" onClick={() => holdingJudgmentChangeBriefGroups.length
                        ? openProtectedTab('HOLDINGS')
                        : setActiveTab('ISSUES')}>전체 보기 <span aria-hidden="true">↗</span></button>
                    </div>
                    {holdingJudgmentChangeBriefGroups.length ? (() => {
                      // Reuse the existing, today-scoped holding judgment transition feed.
                      // This is navigation to the original record, NOT another calculation.
                      const group = holdingJudgmentChangeBriefGroups[0];
                      const ticker = group.ticker;
                      const stockName = holdingNameByTicker.get(ticker) || ticker;
                      return (
                        <button type="button" className="pi-direction-home__change-row" onClick={() => {
                          setActiveTab('HOLDINGS');
                          setExpandedStockKey(`holding:${ticker}`);
                          setFocusedHoldingChangeEventId(cleanText(group.latest.id));
                          setBoxPopupTicker('');
                        }}>
                          <span className="pi-direction-home__change-indicator" aria-hidden="true" />
                          <b>{stockName} · {changeTransitionLabel(group.latest) || '판단 변경'}</b>
                          <span aria-hidden="true">↗</span>
                        </button>
                      );
                    })() : state.liveIssueSignal.signals.length ? (
                      <button type="button" className="pi-direction-home__change-row" onClick={() => setActiveTab('ISSUES')}>
                        <span className="pi-direction-home__change-indicator" aria-hidden="true" />
                        <b>{state.liveIssueSignal.signals[0].headline || '새로운 시장 이벤트'}</b>
                        <span aria-hidden="true">↗</span>
                      </button>
                    ) : (
                      <p className="pi-direction-home__empty">지금 확인된 중요 변화가 없습니다.</p>
                    )}
                  </section>
                  <details className="pi-direction-home__secondary">
                    <summary>상세 시장 데이터 <span aria-hidden="true">⌄</span></summary>
                    <div className="pi-direction-home__secondary-body">
                      {state.marketContext.available
                        ? (state.marketOpen
                            ? <IntradayMarketBoard context={state.marketContext} />
                            : <div className="pi-direction-home__last-session">
                                <span>최근 장 기준</span>
                                <strong>{state.marketContext.headline || '마감 시장 정보'}</strong>
                                <p>{state.marketContext.description || '다음 거래일 수급과 가격 변화를 확인하세요.'}</p>
                              </div>)
                        : <p className="pi-direction-home__empty">최근 시세를 확인할 수 없습니다.</p>}
                      {!state.marketOpen && offhoursProxy && <OffhoursMarketProxyCard data={offhoursProxy} />}
                    </div>
                  </details>
                </div>
              )}

              {activeTab === 'ISSUES' && (
                <div className="live-summary-issues-view" aria-label="실시간 이슈 목록">
                  <DailyIssueStockExplorer />
                  <section className="live-summary-issue-toolbar is-paged">
                    <label className="live-summary-issue-search">
                      <span>이슈 검색</span>
                      <div>
                        <input
                          type="search"
                          value={issueSearchInput}
                          onChange={(event) => setIssueSearchInput(event.target.value)}
                          placeholder="제목·요약·종목명·종목코드 검색"
                          aria-label="라이브 이슈 검색"
                          maxLength={120}
                        />
                        {issueSearchInput && (
                          <button
                            type="button"
                            className="live-summary-issue-search__clear"
                            onClick={() => setIssueSearchInput('')}
                            aria-label="이슈 검색어 지우기"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </label>
                    <div className="live-summary-issue-page-meta is-secondary-count">
                      <small>{issuePageError ? '마지막 정상 조회 기준' : '전체 이슈'}</small>
                      <strong>{issuePaging.totalElements.toLocaleString()}건</strong>
                      <span>
                        {issuePaging.totalElements > 0
                          ? `${((issuePaging.page - 1) * issuePaging.size + 1).toLocaleString()}–${Math.min(issuePaging.page * issuePaging.size, issuePaging.totalElements).toLocaleString()} / ${issuePaging.totalElements.toLocaleString()}`
                          : (issueQuery || issueSector) ? '필터 결과 없음' : '새 이슈 대기 중'}
                      </span>
                    </div>
                  </section>

                  {(issueSector || issuePaging.sectorFacets.length > 0) && (
                    <section className="live-summary-issue-sector-filter" aria-label="이슈 산업 필터">
                      <header>
                        <div>
                          <small>산업 필터</small>
                          <b>검증된 종목-업종 연결</b>
                        </div>
                        {issueSector && (
                          <button
                            type="button"
                            className="live-summary-issue-sector-filter__reset"
                            onClick={() => {
                              issueSectorRef.current = '';
                              issuePageRef.current = 1;
                              setIssueSector('');
                              setIssuePage(1);
                              setExpandedIssueId('');
                            }}
                          >
                            전체 보기
                          </button>
                        )}
                      </header>
                      <nav aria-label="산업 선택">
                        {issuePaging.sectorFacets.map((facet) => {
                          const selected = issueSector === facet.name;
                          return (
                            <button
                              type="button"
                              key={facet.name}
                              className={selected ? 'is-active' : ''}
                              aria-pressed={selected}
                              onClick={() => {
                                const nextSector = selected ? '' : facet.name;
                                issueSectorRef.current = nextSector;
                                issuePageRef.current = 1;
                                setIssueSector(nextSector);
                                setIssuePage(1);
                                setExpandedIssueId('');
                              }}
                              title={`${facet.name} 연결 이슈 ${facet.count.toLocaleString()}건`}
                            >
                              <b>{facet.name}</b>
                              <span>{facet.count.toLocaleString()}</span>
                            </button>
                          );
                        })}
                      </nav>
                    </section>
                  )}

                  <div
                    className={`live-summary-issue-page-loading ${issuePageLoading ? 'is-active' : ''}`}
                    role="status"
                    aria-live="polite"
                    aria-hidden={!issuePageLoading}
                  >
                    {issuePageLoading ? '이슈를 불러오는 중입니다.' : '\u00A0'}
                  </div>

                  {issuePageError && (
                    <section className="pi-issue-feed-failure" role="alert" aria-label="이슈 목록 조회 실패">
                      <div>
                        <strong>이슈 조회에 실패했습니다.</strong>
                        <p>새로운 데이터가 없다는 뜻이 아닙니다. 표시 중인 건수와 목록은 마지막 정상 조회 기준일 수 있습니다.</p>
                      </div>
                      <button type="button" onClick={() => void loadIssues({
                        refresh: true, bypassGap: true, preferCache: false,
                        page: issuePage, query: issueQuery, sector: issueSector,
                      })}>다시 조회</button>
                    </section>
                  )}

                  {issuePageError && (
                    issuePage !== issuePaging.page
                    || cleanText(issueQuery) !== cleanText(issuePaging.query)
                    || cleanText(issueSector) !== cleanText(issuePaging.selectedSector)
                  ) ? (
                    <section className="live-summary-issue-empty-card" role="status">
                      <b>현재 검색 조건의 결과를 확인하지 못했습니다.</b>
                      <p>이전 조건에서 조회한 목록은 혼동을 피하기 위해 표시하지 않습니다. 다시 조회해 주세요.</p>
                    </section>
                  ) : !state.issues.length ? (
                    <section className="live-summary-issue-empty-card">
                      <b>{(issueQuery || issueSector) ? '검색·산업 조건에 맞는 이슈가 없습니다.' : '표시할 실시간 이슈가 없습니다.'}</b>
                      <p>{(issueQuery || issueSector) ? '검색어를 바꾸거나 산업 필터를 해제해보세요.' : '새로운 이슈가 들어오면 이곳에 표시됩니다.'}</p>
                    </section>
                  ) : (
                    <section className="live-summary-issue-feed is-compact-feed" aria-live="polite">
                      {state.issues.map((issue) => {
                        const expanded = expandedIssueId === issue.id;
                        const linkedNames = issue.relatedStockNames;
                        const sectorLabels = issueSectorLabels(issue);
                        const publishedAtLabel = formatIssuePublishedAt(issue.publishedAt);
                        const ageLabel = issue.ageMinutes === null
                          ? publishedAtLabel
                          : issue.ageMinutes < 1
                            ? '방금 전'
                            : issue.ageMinutes < 60
                              ? `${Math.round(issue.ageMinutes)}분 전`
                              : publishedAtLabel;
                        const originalLink = issueOriginalLink(issue);
                        const stockLinkKind = cleanText(issue.eventLinkType).toUpperCase();
                        const exposureOnly = ['EXPOSURE', 'SECTOR', 'THEME'].includes(stockLinkKind);
                        const telegramOrigin = issue.sourceType.toLowerCase().startsWith('telegram_');
                        const sourceEvidenceLabel = telegramOrigin
                          ? issue.sourceVerificationStatus.toUpperCase() === 'SOURCE_LINKED'
                            ? '외부 링크 있음 · 본문 미검증'
                            : '텔레그램 속보 · 외부 확인 전'
                          : '';
                        const structuredPreview = issueStructuredPreview(issue);
                        const displaySummary = issueSummaryForDisplay(issue.title, issue.summary);
                        const structuredSummaries = issue.structuredUnits.length
                          ? cleanText(issue.summary).split(/\s*\|\s*/).map(cleanText).filter(Boolean)
                          : [];
                        const compactPreview = structuredPreview || displaySummary;
                        const wireProfile = ['financialjuice', 'first_squawk'].includes(issue.formatProfile.toLowerCase());
                        const formatClass = issue.structuredUnits.length
                          ? 'is-structured'
                          : wireProfile
                            ? 'is-wire'
                            : issue.formatType === 'LINK_PREVIEW'
                              ? 'is-link-preview'
                              : '';
                        return (
                          <article className={`${issue.realtime ? 'is-realtime' : ''} ${expanded ? 'is-expanded' : ''} ${formatClass}`} key={issue.id}>
                            <button
                              type="button"
                              className="live-summary-issue-feed__main"
                              onClick={() => setExpandedIssueId(expanded ? '' : issue.id)}
                              aria-expanded={expanded}
                            >
                              <div className="live-summary-issue-feed__meta">
                                {issue.realtime && <em>LIVE</em>}
                                {sourceEvidenceLabel && <span title="외부 링크가 있다고 해서 기사 본문이 검증된 것은 아닙니다.">{sourceEvidenceLabel}</span>}
                                {isHighPriorityIssue(issue) && <b>핵심</b>}
                                <time>{ageLabel || '-'}</time>
                                {sectorLabels.slice(0, 2).map((sector) => (
                                  <span className="is-sector" key={`${issue.id}-sector-${sector}`}>{sector}</span>
                                ))}
                                {issue.duplicateCount > 1 && <span>관련 보도 {issue.duplicateCount}건</span>}
                                {issue.relatedStockNames.length > 0 && !['EXPOSURE', 'SECTOR', 'THEME'].includes(cleanText(issue.eventLinkType).toUpperCase()) && (
                                  <span title={issue.relatedStockNames.join(', ')}>직접 연결 {issue.relatedStockNames.length}종목 · {issue.relatedStockNames.slice(0, 2).join(' · ')}{issue.relatedStockNames.length > 2 ? ' 외' : ''}</span>
                                )}
                              </div>
                              <h3>{issue.title}</h3>
                              {compactPreview && <p className="live-summary-issue-feed__preview">{compactPreview}</p>}
                              <i aria-hidden="true">{expanded ? '−' : '＋'}</i>
                            </button>

                            {expanded && (
                              <div className="live-summary-issue-feed__detail">
                                {issue.structuredUnits.length > 0 ? (
                                  <div className="live-summary-issue-feed__structured" aria-label="구조화 이슈 상세">
                                    {issue.structuredUnits.map((unit, unitIndex) => (
                                      <section key={`${issue.id}-unit-${unit.ticker || unit.company || unitIndex}`}>
                                        <div>
                                          {unit.ticker && <b>{unit.ticker}</b>}
                                          {unit.company && <strong>{unit.company}</strong>}
                                          {unit.stage && <em>{unit.stage}</em>}
                                        </div>
                                        {unit.drug && <small>{unit.drug}</small>}
                                        {(structuredSummaries[unitIndex] || unit.catalyst) && (
                                          <p>{structuredSummaries[unitIndex] || unit.catalyst}</p>
                                        )}
                                      </section>
                                    ))}
                                  </div>
                                ) : displaySummary ? (
                                  <p>{displaySummary}</p>
                                ) : null}
                                {(sectorLabels.length > 0 || issue.marketGroupNames.length > 0) && (
                                  <div className="live-summary-issue-feed__detail-meta">
                                    {sectorLabels.length > 0 && <span>산업 {sectorLabels.slice(0, 3).join(' · ')}</span>}
                                    {issue.marketGroupNames.length > 0 && <span>시장 그룹 {issue.marketGroupNames.slice(0, 2).join(' · ')}</span>}
                                  </div>
                                )}
                                <footer>
                                  <div className="live-summary-issue-feed__stocks">
                                    {exposureOnly && linkedNames.length > 0 && (
                                      <small>업종·사업 노출 후보 · 직접 사건 연결 아님</small>
                                    )}
                                    {linkedNames.length ? linkedNames.map((name, index) => (
                                      <span key={`${issue.id}-${name}-${index}`}>{name}</span>
                                    )) : <small>확인된 연결 종목 없음</small>}
                                    {issue.stockLinkInferred && <small>제목 기반 종목 추정 · 확인 전</small>}
                                    {issue.relatedStockCount > linkedNames.length && (
                                      <small>추가 연결 후보 {issue.relatedStockCount - linkedNames.length}건</small>
                                    )}
                                  </div>
                                  {originalLink && (
                                    <div className="live-summary-issue-feed__source-actions">
                                      <a
                                        href={originalLink.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        referrerPolicy="no-referrer"
                                      >
                                        {originalLink.kind === 'TELEGRAM' ? '텔레그램 게시물' : '원문 링크'} ↗
                                      </a>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          void copyIssueOriginalUrl(originalLink.url).then((copied) => {
                                            setIssueUrlCopyResult({ id: issue.id, copied });
                                          });
                                        }}
                                      >
                                        주소 복사
                                      </button>
                                      {issueUrlCopyResult?.id === issue.id && (
                                        <small role="status">{issueUrlCopyResult.copied ? '주소 복사됨' : '복사 실패 · 링크를 길게 눌러주세요'}</small>
                                      )}
                                      {originalLink.kind === 'TELEGRAM' && <small>비공개 게시물은 로그인 필요</small>}
                                    </div>
                                  )}
                                </footer>
                              </div>
                            )}
                          </article>
                        );
                      })}
                    </section>
                  )}

                  {issuePaging.totalPages > 1 && (
                    <nav className="live-summary-issue-pagination" aria-label="라이브 이슈 페이지">
                      <button
                        type="button"
                        className="is-edge"
                        disabled={issuePaging.page <= 1 || issuePageLoading}
                        onClick={() => {
                          issuePageRef.current = 1;
                          setIssuePage(1);
                          setExpandedIssueId('');
                        }}
                        aria-label="맨 처음 이슈 페이지"
                        title="맨 처음"
                      >
                        « 처음
                      </button>
                      <button
                        type="button"
                        disabled={!issuePaging.hasPrevious || issuePageLoading}
                        onClick={() => {
                          const next = Math.max(1, issuePaging.page - 1);
                          issuePageRef.current = next;
                          setIssuePage(next);
                          setExpandedIssueId('');
                        }}
                        aria-label="이전 이슈 페이지"
                        title="이전"
                      >
                        ‹
                      </button>
                      <div>
                        {issuePaginationPages(issuePaging.page, issuePaging.totalPages).map((page) => (
                          <button
                            type="button"
                            key={page}
                            className={page === issuePaging.page ? 'is-active' : ''}
                            aria-current={page === issuePaging.page ? 'page' : undefined}
                            disabled={issuePageLoading}
                            onClick={() => {
                              issuePageRef.current = page;
                              setIssuePage(page);
                              setExpandedIssueId('');
                            }}
                          >
                            {page}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        disabled={!issuePaging.hasNext || issuePageLoading}
                        onClick={() => {
                          const next = Math.min(issuePaging.totalPages, issuePaging.page + 1);
                          issuePageRef.current = next;
                          setIssuePage(next);
                          setExpandedIssueId('');
                        }}
                        aria-label="다음 이슈 페이지"
                        title="다음"
                      >
                        ›
                      </button>
                      <button
                        type="button"
                        className="is-edge"
                        disabled={issuePaging.page >= issuePaging.totalPages || issuePageLoading}
                        onClick={() => {
                          const last = Math.max(1, issuePaging.totalPages);
                          issuePageRef.current = last;
                          setIssuePage(last);
                          setExpandedIssueId('');
                        }}
                        aria-label="맨 끝 이슈 페이지"
                        title="맨 끝"
                      >
                        끝 »
                      </button>
                    </nav>
                  )}
                </div>
              )}

              {activeTab === 'SECTORS' && (
                <div className="live-summary-sectors-view">
                  <section className="live-summary-sector-terminal-nav" aria-label="업종 탐색기">
                    <div className="live-summary-market-segments" role="tablist" aria-label="시장 구분">
                      {(['ALL', 'KOSPI', 'KOSDAQ', 'KONEX'] as const).map((market) => {
                        const count = market === 'ALL' ? state.sectors.length : (marketSectorCounts[market] ?? 0);
                        if (market !== 'ALL' && count === 0) return null;
                        return (
                          <button
                            type="button"
                            role="tab"
                            aria-selected={sectorMarketFilter === market}
                            className={sectorMarketFilter === market ? 'is-active' : ''}
                            key={market}
                            onClick={() => setSectorMarketFilter(market)}
                          >
                            <span>{market === 'ALL' ? '전체' : market}</span>
                            <b>{count}</b>
                          </button>
                        );
                      })}
                    </div>

                    <div className="live-summary-sector-rank-list">
                      {visibleSectors.map((sector, index) => {
                        const active = selectedSector ? sectorKey(selectedSector) === sectorKey(sector) : false;
                        return (
                          <button
                            type="button"
                            className={active ? 'is-active' : ''}
                            key={sectorKey(sector)}
                            onClick={() => setSelectedKey(sectorKey(sector))}
                          >
                            <em>{String(index + 1).padStart(2, '0')}</em>
                            <div>
                              <b>{sector.industryName}</b>
                              <small>{sectorMarketLabel(sector)} · 대장 후보 {sector.topStocks[0]?.name || '확인 중'}</small>
                            </div>
                            <span>
                              <strong className={toneClass(sector.tone)}>{formatPct(sector.changeRate)}</strong>
                              <small>확산 {sectorBreadthText(sector.breadthPct)}</small>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  {selectedSector ? (
                    <section className="live-summary-sector-stock-panel">
                      <header className="live-summary-sector-analysis-head is-kpi-dense">
                        <div className="live-summary-sector-analysis-head__identity">
                          <small>{sectorMarketLabel(selectedSector)} · {selectedSector.industryCode}{nxtAfterSession ? ' · NXT 종목 흐름 / KRX 업종지수' : ''}</small>
                          <h3>{selectedSector.industryName}</h3>
                        </div>
                        <div className="live-summary-sector-analysis-head__metrics">
                          <span><small>상승/하락</small><b>{state.marketOpen && selectedSector.realtimeTickerCount > 0 ? `${selectedSector.upTickerCount}/${selectedSector.downTickerCount}` : `—/—`}</b></span>
                          <span><small>실시간</small><b>{state.marketOpen ? `${selectedSector.realtimeCoveragePct.toFixed(0)}%` : `장 마감`}</b></span>
                          <span><small>확산도</small><b>{state.marketOpen ? sectorBreadthText(selectedSector.breadthPct) : `—`}</b></span>
                        </div>
                        <div className="live-summary-sector-analysis-head__score">
                          <span>업종 등락</span>
                          <strong className={toneClass(selectedSector.tone)}>{formatPct(selectedSector.changeRate)}</strong>
                        </div>
                      </header>

                      {(() => {
                        const relatedIssues = state.issues.filter((issue) => issueSectorLabels(issue).includes(selectedSector.industryName));
                        const linkedHoldings = state.holdings.filter((holding) => cleanText(holding.industryName) === cleanText(selectedSector.industryName));
                        const leadIssue = relatedIssues[0] ?? null;
                        return (
                          <section className="live-summary-sector-decision-brief" aria-label={`${selectedSector.industryName} 판단 요약`}>
                            <span>
                              <small>업종지수</small>
                              <b>{`${selectedSector.changeRate >= 0 ? '상승' : '하락'} ${formatPct(selectedSector.changeRate, 2)}`}</b>
                            </span>
                            <span>
                              <small>확산</small>
                              <b>{state.marketOpen && selectedSector.realtimeTickerCount > 0 && selectedSector.breadthPct !== null
                                ? sectorBreadthText(selectedSector.breadthPct)
                                : '확산 데이터 미확인'}</b>
                            </span>
                            <span>
                              <small>원인 Event</small>
                              <b>{leadIssue ? leadIssue.title : '원인 Event 미확인'}</b>
                            </span>
                            <span><small>내 종목</small><b>{linkedHoldings.length > 0 ? linkedHoldings.slice(0, 3).map((holding) => holding.name).join(' · ') : '직접 연결 없음'}</b></span>
                          </section>
                        );
                      })()}

                      <div className="live-summary-stock-list is-compact-list is-decision-list is-discovery-list">
                        {selectedSector.topStocks.length ? (
                          selectedSector.topStocks.map((stock) => {
                            const freshness = freshnessMeta(stock);
                            const rowKey = `sector:${sectorKey(selectedSector)}:${stock.ticker || stock.name}`;
                            const expanded = expandedStockKey === rowKey;
                            return (
                              <article className={expanded ? 'is-expanded' : ''} key={stock.ticker || stock.name}>
                                <button type="button" className="live-summary-compact-row" onClick={() => setExpandedStockKey(expanded ? '' : rowKey)} aria-expanded={expanded}>
                                  <div className="live-summary-compact-row__identity">
                                    <b>{stock.name}</b>
                                    <small>{stock.ticker} · {selectedSector.industryName || '업종 확인 중'}</small>
                                  </div>
                                  <div className="live-summary-compact-row__right">
                                    <div className="live-summary-compact-row__quote">
                                      <strong>{formatPrice(stock.price)}원</strong>
                                      <em className={toneClass(stock.tone)}>{formatPct(stock.changeRate)}</em>
                                    </div>
                                  </div>
                                  <DiscoveryDecisionLine
                                    item={stock}
                                    sectorName={selectedSector.industryName}
                                    freshnessLabel={freshness.label}
                                  />
                                </button>
                                {expanded && (
                                  <div className="live-summary-row-detail">
                                    <div className="live-summary-quick-facts">
                                      <span><small>데이터</small><b className={toneClass(freshness.tone)}>{freshness.label}</b></span>
                                    </div>
                                    <MonitoringDetailPanel item={stock} marketContext={state.marketContext} marketOpen={state.marketOpen} marketSession={state.marketSession} changeEvents={state.changeEvents} issues={state.issues} sectorContext={selectedSector} stale={freshness.stale} mode="stock" productAccess={productAccess} />
                                  </div>
                                )}
                              </article>
                            );
                          })
                        ) : <p className="live-summary-empty">표시할 종목이 없습니다.</p>}
                      </div>
                    </section>
                  ) : (
                    <p className="live-summary-empty">업종 데이터를 불러오는 중입니다.</p>
                  )}
                </div>
              )}

              {activeTab === 'STOCKS' && (
                <div className={`live-summary-stocks-view is-master-detail ${selectedStock ? 'has-selection' : ''}`}>
                  <div className="live-summary-master-pane">
                    <section className="live-summary-stock-toolbar is-ultra-compact">
                      <div className="live-summary-stock-toolbar__search">
                        <span aria-hidden="true">⌕</span>
                        <input value={stockSearch} onChange={(event) => handleStockSearchChange(event.target.value)} placeholder="종목명·코드 검색" aria-label="종목 검색" />
                        {stockSearch && <button type="button" className="live-summary-stock-toolbar__clear" onClick={clearStockSearch} aria-label="종목 검색 지우기" title="검색 지우기">×</button>}
                      </div>
                      <div className="live-summary-stock-toolbar__filters">
                        <button type="button" className={stockSort === 'RISK' ? 'is-active' : ''} onClick={() => setStockSort('RISK')}>위험</button>
                        <button type="button" className={stockSort === 'CHANGE' ? 'is-active' : ''} onClick={() => setStockSort('CHANGE')}>등락</button>
                        <button type="button" className={stockSort === 'VALUE' ? 'is-active' : ''} onClick={() => setStockSort('VALUE')}>대금</button>
                        <button type="button" className={stockSort === 'FLOW' ? 'is-active' : ''} onClick={() => setStockSort('FLOW')}>수급</button>
                        <button type="button" className={onlyRealtimeStocks ? 'is-active' : ''} onClick={() => setOnlyRealtimeStocks((current) => !current)}>LIVE</button>
                      </div>
                      <div className="live-summary-stock-toolbar__meta"><b>{filteredStocks.length}</b></div>
                    </section>

                    {stockSearch.trim() && radarSearchMatches.length === 0 && (
                      <section className="live-summary-stock-fallback" aria-live="polite">
                        {stockFallbackLoading ? (
                          <div className="live-summary-stock-fallback__status"><b>오늘 감지 종목 밖에서 찾는 중</b><span>전체 종목 기준으로 검색하고 있습니다.</span></div>
                        ) : stockFallbackResolvedQuery === stockSearch.trim() && stockFallbackError ? (
                          <div className="live-summary-stock-fallback__status is-error"><b>전체 종목 검색에 실패했습니다</b><span>{stockFallbackError}</span></div>
                        ) : stockFallbackResolvedQuery === stockSearch.trim() && stockFallbackResults.length ? (
                          <><header><div><small>WATCHLIST FALLBACK</small><b>오늘 감지 종목에는 없습니다</b><span>관심종목에 추가하면 실시간 수급·리스크 분석 데이터를 준비합니다.</span></div></header><div className="live-summary-stock-fallback__results">{stockFallbackResults.map((item) => (
                            <div className="live-summary-stock-fallback__row" key={item.ticker}><span><b>{item.name || item.ticker}</b><small>{item.ticker} · {item.market || '시장 확인 중'}</small></span>{item.alreadyAdded ? <button type="button" onClick={() => void openHoldingFromSearch(item)}>관심종목에서 확인</button> : <button type="button" disabled={holdingMutationTicker === item.ticker} onClick={() => void addHolding(item, 'stock-search')}>{holdingMutationTicker === item.ticker ? '추가 중' : '+ 관심종목 추가'}</button>}</div>
                          ))}</div><p>추가 후 보유·관심 탭에서 수급·리스크 변화를 이어서 확인할 수 있습니다.</p></>
                        ) : stockFallbackResolvedQuery === stockSearch.trim() ? (
                          <div className="live-summary-stock-fallback__status is-empty"><b>등록된 종목을 찾지 못했습니다</b><span>종목명 또는 종목코드를 다시 확인해 주세요.</span></div>
                        ) : null}
                      </section>
                    )}

                    <div className="live-summary-stock-rows is-compact-list is-decision-list is-discovery-list">
                      {filteredStocks.length ? filteredStocks.map((stock, index) => {
                        const freshness = freshnessMeta(stock);
                        const rowKey = `stocks:${stock.ticker || stock.name}`;
                        const selected = expandedStockKey === rowKey;
                        const issueMarked = (
                          liveIssueBadgeIndex.tickers.has(normalizeWhyMovingTicker(stock.ticker))
                          || liveIssueBadgeIndex.names.has(compactNameKey(stock.name))
                        );
                        return (
                          <article className={`live-summary-stock-row ${selected ? 'is-selected' : ''}`} key={`${stock.ticker}-${index}`}>
                            <button type="button" className="live-summary-compact-row" onClick={() => { setExpandedStockKey(rowKey); setPinnedStock(stock); const detailSource = stockSearch.trim() ? 'stock-search' : 'radar-list'; trackUxEvent('live_summary_stock_detail_open', { ticker: stock.ticker, name: stock.name, source: detailSource, searchQuery: stockSearch.trim() || undefined, issueMarked }); trackUxEvent('stock_detail_view', { ticker: stock.ticker, stock_code: stock.ticker, stock_name: stock.name, source: `live-summary-${detailSource}`, access_mode: 'authenticated', issueMarked }); }} aria-pressed={selected}>
                              <div className="live-summary-compact-row__identity">
                                <b>{stock.name}</b>
                                <small>
                                  {issueMarked ? (
                                    <span
                                      title="최근 이슈 있음"
                                      aria-label="최근 이슈 있음"
                                      style={{
                                        display: 'inline-block',
                                        position: 'relative',
                                        paddingRight: 11,
                                        lineHeight: 'inherit',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {stock.ticker}
                                      <span
                                        role="img"
                                        aria-hidden="true"
                                        style={{
                                          position: 'absolute',
                                          right: 0,
                                          top: '50%',
                                          width: 9,
                                          height: 9,
                                          transform: 'translateY(-50%)',
                                          display: 'grid',
                                          placeItems: 'center',
                                          fontSize: 8,
                                          lineHeight: 1,
                                          pointerEvents: 'none',
                                        }}
                                      >
                                        📰
                                      </span>
                                    </span>
                                  ) : stock.ticker}
                                  {' · '}
                                  {stock.sectorName || '업종 확인 중'}
                                </small>
                              </div>
                              <div className="live-summary-compact-row__right"><div className="live-summary-compact-row__quote"><strong>{formatPrice(stock.price)}원</strong><em className={toneClass(stock.tone)}>{formatPct(stock.changeRate)}</em></div></div>
                              <DiscoveryDecisionLine
                                item={stock}
                                sectorName={stock.sectorName}
                                freshnessLabel={freshness.label}
                              />
                            </button>
                          </article>
                        );
                      }) : !stockSearch.trim() ? <p className="live-summary-empty">표시할 종목이 없습니다.</p> : radarSearchMatches.length > 0 && onlyRealtimeStocks ? <p className="live-summary-empty">검색한 종목은 감지 목록에 있지만 LIVE 필터에서 제외되었습니다.</p> : null}
                    </div>
                  </div>

                  <div className="live-summary-detail-pane">
                    {selectedStock ? (() => {
                      const freshness = freshnessMeta(selectedStock);
                      return (
                        <>
                          <header className="live-summary-master-detail-head">
                            <button type="button" onClick={() => { setExpandedStockKey(''); setPinnedStock(null); }} aria-label="종목 목록으로 돌아가기">←</button>
                            <div><b>{selectedStock.name}</b><small>실시간 확인 · {selectedStock.ticker} · {selectedStock.sectorName || '업종 확인 중'}</small></div>
                            <span><strong>{formatPrice(selectedStock.price)}원</strong><em className={toneClass(selectedStock.tone)}>{formatPct(selectedStock.changeRate)}</em></span>
                          </header>
                          <MonitoringDetailPanel item={selectedStock} marketContext={state.marketContext} marketOpen={state.marketOpen} marketSession={state.marketSession} changeEvents={state.changeEvents} issues={state.issues} sectorContext={state.sectors.find((sector) => sector.industryName === selectedStock.sectorName) ?? null} stale={freshness.stale} mode="stock" focusChangeEventId={requestedChangeEventId} productAccess={productAccess} />
                        </>
                      );
                    })() : <div className="live-summary-master-detail-empty"><b>확인할 종목을 선택하세요.</b><span>현재 판단·가치·수급을 먼저 보고, 변화 추적과 근거는 종목 선택 후 이어서 확인합니다.</span></div>}
                  </div>
                </div>
              )}

              {activeTab === 'HOLDINGS' && (
                <div className={`live-summary-holdings-view is-master-detail ${selectedHolding ? 'has-selection' : ''}`}>
                  <div className="live-summary-master-pane">
                    {deferredLoading === 'HOLDINGS' && !holdingsLoaded && (
                      <div className="live-summary-deferred-loading" role="status" aria-live="polite"><b>보유 상세 계산을 불러오는 중입니다.</b><span>박스권·수급·변동성 상세 데이터는 별도로 연결합니다.</span></div>
                    )}

                    <section className="live-summary-holding-change-brief" aria-label="내 종목 변화 브리프">
                      <header className="live-summary-holding-change-brief__head">
                        <div>
                          <small>내 종목 변화</small>
                          <b>판단·대응 변화</b>
                        </div>
                        <span>{holdingJudgmentChangeBriefEventCount}건</span>
                      </header>

                      {state.sourceStatus === 'error' ? (
                        <p className="live-summary-holding-change-brief__state is-error">
                          변화 데이터를 확인하지 못했습니다. 조회 실패를 변화 없음으로 처리하지 않습니다.
                        </p>
                      ) : holdingJudgmentChangeBriefGroups.length ? (
                        <div className="live-summary-holding-change-brief__list">
                          {holdingJudgmentChangeBriefGroups.slice(0, 3).map((group) => {
                            const change = group.latest;
                            const ticker = group.ticker;
                            const stockName = holdingNameByTicker.get(ticker) || ticker || '내 종목';
                            const targetHolding = state.holdings.find((holding) => cleanText(holding.ticker) === ticker) ?? null;
                            const contract = targetHolding?.judgmentContract || EMPTY_HOLDING_JUDGMENT;
                            const headline = changeTransitionLabel(change);
                            const message = changeReasonLabel(change, stockName);
                            const thesisImpact = contract.available
                              ? contract.thesis.label
                              : targetHolding?.holdingThesis.coreLabel || '판단 영향 확인 중';
                            const nextCheck = contract.available
                              ? contract.nextChecks[0] || '다음 확인 조건 산출 중'
                              : targetHolding?.holdingThesis.recheckConditions[0]
                                || targetHolding?.holdingThesis.monitorConditions[0]
                                || '다음 확인 조건 산출 중';
                            const time = formatIssuePublishedAt(eventTimestampRaw(change));

                            return (
                              <button
                                type="button"
                                className="live-summary-holding-change-brief__row"
                                key={`${ticker}-${cleanText(change.id) || eventTimestampRaw(change)}`}
                                onClick={() => {
                                  setExploreOpen(false);
                                  setActiveTab('HOLDINGS');
                                  setExpandedStockKey(`holding:${ticker}`);
                                  setFocusedHoldingChangeEventId(cleanText(change.id));
                                  setBoxPopupTicker('');
                                  trackUxEvent('stock_brief_change_open', {
                                    measurement_version: PAID_VALUE_MEASUREMENT_VERSION,
                                    view_date_kst: koreaTodayKey(),
                                    source: 'live-summary-holdings-change',
                                    ticker,
                                    stock_code: ticker,
                                    event_id: cleanText(change.id) || undefined,
                                    change_type: cleanText(change.eventType) || undefined,
                                    event_count: group.eventCount,
                                  });
                                }}
                              >
                                <span className="live-summary-holding-change-brief__stock">
                                  <b>{stockName}</b>
                                  <small>{ticker}</small>
                                </span>

                                <span className="live-summary-holding-change-brief__summary">
                                  <b>{headline || '판단 변화'}</b>
                                  <small>원인 · {message}</small>
                                  <small>보유 논리 · {thesisImpact}</small>
                                  <small>다음 확인 · {nextCheck}</small>
                                </span>

                                <span className="live-summary-holding-change-brief__meta">
                                  <small>{time || '최근'}</small>
                                  <i aria-hidden="true">›</i>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      ) : holdingsLoaded ? (
                        <p className="live-summary-holding-change-brief__state">
                          판단·대응 변화 없음
                        </p>
                      ) : (
                        <p className="live-summary-holding-change-brief__state">
                          내 종목 변화 데이터를 불러오는 중입니다.
                        </p>
                      )}
                      {holdingJudgmentChangeBriefHistoryCount > 0 && (
                        <p className="live-summary-holding-change-brief__history">
                          이전 판단 변화 {holdingJudgmentChangeBriefHistoryCount}건은 종목별 변화 탭에서 확인합니다.
                        </p>
                      )}
                    </section>

                    <details className="live-summary-holding-add-disclosure">
                      <summary>
                        <span><b>＋ 종목 추가</b></span>
                        <em>추가</em>
                      </summary>
                      <section className="live-summary-holding-search is-ultra-compact">
                        <div className="live-summary-holding-search__input"><span className="live-summary-holding-search__plus" aria-hidden="true">⌕</span><input value={holdingSearch} onChange={(event) => setHoldingSearch(event.target.value)} placeholder="종목명·코드 검색" aria-label="보유 종목 검색" />{holdingSearchLoading && <small>검색 중</small>}{holdingSearch && <button type="button" onClick={() => setHoldingSearch('')} aria-label="검색어 지우기">×</button>}</div>
                        {holdingMessage && <p className="live-summary-holding-message">{holdingMessage}</p>}
                        {holdingSearchError && <p className="live-summary-holding-message is-error">{holdingSearchError}</p>}
                        {holdingSearch.trim() && <div className="live-summary-holding-search__results">{holdingSearchResults.length ? holdingSearchResults.map((item) => <button type="button" key={item.ticker} disabled={Boolean(item.alreadyAdded) || holdingMutationTicker === item.ticker} onClick={() => void addHolding(item)}><span><b>{item.name}</b><small>{item.ticker} · {item.market || '시장 확인 중'}</small></span><strong>{item.alreadyAdded ? '추가됨' : holdingMutationTicker === item.ticker ? '추가 중' : '+ 추가'}</strong></button>) : !holdingSearchLoading && !holdingSearchError ? <p>검색 결과가 없습니다.</p> : null}</div>}
                      </section>
                    </details>

                    <PortfolioRiskOverview holdings={state.holdings} />

                    <div className="live-summary-holding-list is-compact-list is-decision-list">
                      {sortedHoldings.length ? sortedHoldings.map((holding) => {
                        const rowKey = `holding:${holding.ticker || holding.name}`;
                        const selected = expandedStockKey === rowKey;
                        const thesis = holding.holdingThesis || EMPTY_HOLDING_THESIS;
                        const contract = holding.judgmentContract || EMPTY_HOLDING_JUDGMENT;
                        const decisionHeadline = contract.available
                          ? contract.thesis.label
                          : thesis.contractVersion
                            ? thesis.coreLabel
                            : (holding.actionDecision.headline || holding.actionDecision.actionLabel || holding.riskAssessment.stateLabel);
                        const todayChangeCount = contract.available
                          ? contract.changes.filter((change) => !isJudgmentCheckResult(change)).length
                          : thesis.todayChanges.length;
                        return (
                          <article className={selected ? 'is-selected' : ''} key={holding.ticker || holding.name}>
                            <div className="live-summary-holding-row-shell">
                              <div className="live-summary-compact-row" role="button" tabIndex={0} onClick={() => { setFocusedHoldingChangeEventId(''); setExpandedStockKey(rowKey); if (boxPopupTicker !== holding.ticker) setBoxPopupTicker(''); }} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setFocusedHoldingChangeEventId(''); setExpandedStockKey(rowKey); } }} aria-pressed={selected}>
                                <div className="live-summary-compact-row__identity"><div className="live-summary-holding-name-line"><b>{holding.name}</b><em className={holding.portfolioPositionAvailable ? 'is-position' : 'is-watch'}>{holding.portfolioPositionAvailable ? '보유' : '관심'}</em></div><small>{holding.ticker}</small></div>
                                <div className="live-summary-compact-row__right"><div className="live-summary-compact-row__quote"><strong>{formatPrice(holding.price)}원</strong><em className={toneClass(holding.statusTone)}>{formatPct(holding.changeRate)}</em></div></div>
                                <div className="pi-v4-holding-foot">
                                  {decisionHeadline && !/판단 제한|확인 중|준비 중|미확인/.test(decisionHeadline) && (
                                    <strong className={toneClass(contract.available ? contract.thesis.tone : thesis.tone)}>{decisionHeadline}</strong>
                                  )}
                                  {todayChangeCount > 0 && <span>변화 {todayChangeCount}</span>}
                                  {holding.portfolioPositionAvailable && holdingPositionReturnPct(holding) !== null && (
                                    <b className={toneClass((holdingPositionReturnPct(holding) ?? 0) > 0 ? 'up' : (holdingPositionReturnPct(holding) ?? 0) < 0 ? 'down' : 'neutral')}>
                                      수익 {formatPct(holdingPositionReturnPct(holding), 2)}
                                    </b>
                                  )}
                                </div>
                              </div>
                            </div>
                          </article>
                        );
                      }) : <p className="live-summary-empty">등록된 보유 종목이 없습니다.</p>}
                    </div>
                  </div>

                  <div className="live-summary-detail-pane">
                    {selectedHolding ? (() => {
                      const freshness = freshnessMeta(selectedHolding);
                      return (
                        <>
                          <header className="live-summary-master-detail-head">
                            <button type="button" onClick={() => { setFocusedHoldingChangeEventId(''); setExpandedStockKey(''); setBoxPopupTicker(''); }} aria-label="보유 목록으로 돌아가기">←</button>
                            <div><b>{selectedHolding.name}</b><small>{selectedHolding.portfolioPositionAvailable ? '보유 중' : '관심 추적'} · {selectedHolding.ticker} · {selectedHolding.industryName || '업종 확인 중'}</small></div>
                            <span><strong>{formatPrice(selectedHolding.price)}원</strong><em className={toneClass(selectedHolding.statusTone)}>{formatPct(selectedHolding.changeRate)}</em></span>
                            <button type="button" className="live-summary-detail-track-trigger" data-pro-feature="NOTIFICATION_TRACKING" data-access={liveSummaryFeatureAllowed(productAccess, 'NOTIFICATION_TRACKING') ? 'allowed' : 'locked'} onClick={() => { if (!liveSummaryFeatureAllowed(productAccess, 'NOTIFICATION_TRACKING')) { trackUxEvent('pro_offer_view', { source: 'live-summary-track-button', feature: 'NOTIFICATION_TRACKING', ticker: selectedHolding.ticker }); return; } openTrackingSettings(selectedHolding.ticker, selectedHolding.name); }} aria-label={`${selectedHolding.name} 변화 추적 설정`} title="변화 추적">{liveSummaryFeatureAllowed(productAccess, 'NOTIFICATION_TRACKING') ? '추적' : 'PRO'}</button>
                            <button type="button" className="live-summary-detail-delete-trigger" disabled={holdingMutationTicker === selectedHolding.ticker} onClick={() => void removeHolding(selectedHolding)} aria-label={`${selectedHolding.name} 보유 종목 삭제`} title="보유 종목 삭제">{holdingMutationTicker === selectedHolding.ticker ? '…' : '×'}</button>
                          </header>
                          <MonitoringDetailPanel item={selectedHolding} marketContext={state.marketContext} marketOpen={state.marketOpen} marketSession={state.marketSession} changeEvents={state.changeEvents} issues={state.issues} sectorContext={state.sectors.find((sector) => cleanText(sector.industryName) === cleanText(selectedHolding.industryName)) ?? null} stale={freshness.stale} mode="holding" focusChangeEventId={focusedHoldingChangeEventId} onManualDetailTabChange={() => setFocusedHoldingChangeEventId('')} productAccess={productAccess} />
                          <ShortCoveringEvidenceCard ticker={selectedHolding.ticker} name={selectedHolding.name} optionResearch={state.optionResearch} />
                        </>
                      );
                    })() : <div className="live-summary-master-detail-empty"><b>보유·관심 종목을 선택하세요.</b></div>}
                  </div>
                </div>
              )}

              {activeTab === 'SCANNER' && (
                <div className="live-summary-scanner-shell is-results-first">
                  <MyScannerPanel onOpenStock={openScannerMatchStock} />
                </div>
              )}

              {activeTab === 'ALERTS' && (
                <div className="live-summary-alert-view">
                  <section className="live-summary-alert-summary is-value-first">
                    <div>
                      <small>긴급</small>
                      <b>{alertSummaryGuide.dangerCount}</b>
                    </div>
                    <div>
                      <small>주의</small>
                      <b>{alertSummaryGuide.warningCount}</b>
                    </div>
                    <div>
                      <small>정보</small>
                      <b>{alertSummaryGuide.infoCount}</b>
                    </div>
                  </section>

                  {latestDecisionAlerts.length > 0 && (
                    <section className="live-summary-decision-alerts" aria-label="판단 변화 알림">
                      <header><b>종목별 최신 변화</b><span>{latestDecisionAlerts.length}종목</span></header>
                      <div className="live-summary-decision-alerts__list">
                        {latestDecisionAlerts.slice(0, 6).map(({ event, holding, level }) => {
                          return (
                            <button
                              type="button"
                              className={`is-${level}`}
                              key={`decision-alert-${cleanText(event.id) || `${holding.ticker}-${eventTimestampRaw(event)}`}`}
                              onClick={() => {
                                setActiveTab('HOLDINGS');
                                setExpandedStockKey(`holding:${holding.ticker}`);
                                setFocusedHoldingChangeEventId(cleanText(event.id));
                                setBoxPopupTicker('');
                              }}
                            >
                              <span className="live-summary-decision-alerts__icon">{level === 'danger' ? '!' : level === 'warning' ? '△' : 'i'}</span>
                              <div>
                                <small>{formatIssuePublishedAt(eventTimestampRaw(event)) || '최근'}</small>
                                <b>{holding.name} · {changeTransitionLabel(event)}</b>
                                <p>{changeReasonLabel(event, holding.name)}</p>
                              </div>
                              <em aria-hidden="true">›</em>
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  )}

                  <div className="live-summary-alert-list is-timeline">
                    {latestDecisionAlerts.length > 0 && state.alerts.length > 0 && <h3 className="live-summary-alert-list__secondary-title">기타 정보 알림</h3>}
                    {state.alerts.length ? state.alerts.map((alert, index) => (
                      <article
                        className={`is-${alert.level}`}
                        key={`${alert.message}-${index}`}
                      >
                        <span>
                          {alert.level === 'danger'
                            ? '!'
                            : alert.level === 'warning'
                              ? '⚠'
                              : 'i'}
                        </span>
                        <div>
                          <small className="pi-alert-asof">요약 조회 기준 {state.refreshedAt || '시각 미확인'}</small>
                          <p>{alert.message}</p>
                        </div>
                      </article>
                    )) : (
                      <p className="live-summary-alert-empty">알림 없음</p>
                    )}
                  </div>

                  <details className="live-summary-alert-settings">
                    <summary>
                      <span><b>설정</b></span>
                      <em>설정</em>
                    </summary>
                    <div className="live-summary-alert-settings__body">
                      <section className="live-summary-tracking-hub">
                        <header>
                          <div><b>종목 추적</b></div>
                          <button type="button" onClick={openAdvancedNotificationSettings}>관리</button>
                        </header>
                        <div className="live-summary-tracking-hub__rows">
                          {state.holdings.length ? state.holdings.map((holding) => (
                            <button
                              type="button"
                              key={`tracking:${holding.ticker}`}
                              onClick={() => openTrackingSettings(holding.ticker, holding.name)}
                            >
                              <span><b>{holding.name}</b><small>{holding.ticker}</small></span>
                              <strong>설정</strong>
                            </button>
                          )) : (
                            <p>등록 종목 없음</p>
                          )}
                        </div>
                      </section>

                      <WebPushSettingsPanel authenticated={authenticated} />
                    </div>
                  </details>
                </div>
              )}

            </main>

            {activityToast && (
              <button
                type="button"
                className={`live-summary-activity-toast is-${activityToast.kind}`}
                onClick={() => openActivityTarget(activityToast.target)}
                role="status"
                aria-live="polite"
              >
                <span className="live-summary-activity-toast__dot" aria-hidden="true" />
                <span className="live-summary-activity-toast__copy">
                  <b>{activityToast.title}</b>
                  <small>{activityToast.message}</small>
                </span>
                <strong>보기</strong>
              </button>
            )}

            <PortfolioTrackingQuickSheet
              target={trackingTarget}
              onClose={() => setTrackingTarget(null)}
              onOpenChannelSettings={() => {
                setTrackingTarget(null);
                setExploreOpen(false);
                setActiveTab('ALERTS');
              }}
              onOpenAdvanced={() => {
                setTrackingTarget(null);
                openAdvancedNotificationSettings();
              }}
            />

            <footer className="live-summary-footer">
              <div>
                <i className={`is-${state.sourceStatus}`} />
                {nxtAfterSession ? 'NXT 애프터 데이터 자동 갱신' : state.marketOpen ? '장중 데이터 자동 갱신' : '최근 데이터 기준'}
              </div>
              <UserFeedbackLauncher
                source="PANEL"
                variant="panel"
                ticker={selectedStock?.ticker || pinnedStock?.ticker || undefined}
                context={{
                  activeTab,
                  minimized: false,
                  sourceStatus: state.sourceStatus,
                  marketOpen: state.marketOpen,
                }}
              />
              <span>{loading ? '갱신 중' : state.refreshedAt || '-'}</span>
            </footer>
          </>
        )}
      </aside>
    </div>
  );
}

